import z$1, { z, ZodError } from 'zod';
import { toHeaderCase, toKebabCase } from 'js-convert-case';
import humanparser from 'humanparser';
import jsonata from 'jsonata';
import deepEqual from 'fast-deep-equal';
import structuredClonePolyfill from '@ungap/structured-clone';
import urljoin from 'url-join';
import { fromError } from 'zod-validation-error/v4';
import { createTwoFilesPatch } from 'diff';
import * as yaml from 'js-yaml';
import qs from 'qs';
import { connectToChild } from 'penpal';
import axiosOriginal from 'axios';
import { fromZodError } from 'zod-validation-error';

var ErrorType;
(function (ErrorType) {
    ErrorType["BAD_REQUEST"] = "bad_request";
    ErrorType["CONNECTION"] = "connection";
    ErrorType["CONFIGURATION"] = "configuration";
    ErrorType["DEPENDENCY_ERROR"] = "dependency_error";
    ErrorType["FLOW_RUN"] = "flow_run";
    ErrorType["FLOW_INSTANCE_SETUP"] = "flow_instance_setup";
    ErrorType["CONCURRENCY"] = "concurrency";
    ErrorType["INTERNAL"] = "internal";
    ErrorType["ACTION_RUN"] = "action_run";
    ErrorType["ACTION_INSTANCE_SETUP"] = "action_instance_setup";
    ErrorType["UNIT_RUN"] = "unit_run";
    ErrorType["CUSTOM_CODE"] = "custom_code";
    ErrorType["INSUFFICIENT_CREDIT"] = "insufficient_credit";
    ErrorType["OPENROUTER_NOT_CONFIGURED"] = "openrouter_not_configured";
})(ErrorType || (ErrorType = {}));
var BadRequestErrorKey;
(function (BadRequestErrorKey) {
    BadRequestErrorKey["NOT_FOUND"] = "not_found";
    BadRequestErrorKey["NOT_AUTHENTICATED"] = "not_authenticated";
    BadRequestErrorKey["ACCESS_DENIED"] = "access_denied";
    BadRequestErrorKey["ALREADY_EXISTS"] = "already_exists";
    BadRequestErrorKey["RATE_LIMIT_EXCEEDED"] = "rate_limit_exceeded";
    BadRequestErrorKey["WORKSPACE_TIER_GATED"] = "workspace_tier_gated";
})(BadRequestErrorKey || (BadRequestErrorKey = {}));
var ConnectionErrorKey;
(function (ConnectionErrorKey) {
    ConnectionErrorKey["CONNECTION_IS_DEFUNCT"] = "connection_is_defunct";
    ConnectionErrorKey["ACCESS_TOKEN_EXPIRED"] = "access_token_expired";
    ConnectionErrorKey["RATE_LIMIT_EXCEEDED"] = "rate_limit_exceeded";
})(ConnectionErrorKey || (ConnectionErrorKey = {}));
var ConcurrencyErrorKey;
(function (ConcurrencyErrorKey) {
    ConcurrencyErrorKey["LOCK_TIMEOUT"] = "lock_timeout";
})(ConcurrencyErrorKey || (ConcurrencyErrorKey = {}));
const ErrorDataSchema = z.lazy(() => z.object({
    type: z.enum(ErrorType).optional(),
    key: z.string().optional(),
    message: z.string(),
    data: z.any().optional(),
    stack: z.any().optional(),
    causedByError: z.lazy(() => ErrorDataSchema).optional(),
    logs: z.array(z.any()).optional(),
}));
function isMembraneError(error) {
    return error && error.isMembraneError;
}
class ErrorData {
    constructor(arg) {
        if (arg instanceof Error) {
            this.message = arg.message;
            this.stack = arg.stack;
            this.type = ErrorType.INTERNAL;
        }
        else if (typeof arg === 'string') {
            this.message = arg;
            this.type = ErrorType.INTERNAL;
        }
        else if (typeof arg === 'object' && arg !== null) {
            Object.assign(this, arg);
        }
        else {
            throw new Error('Invalid argument when creating BaseErrorData: ' + JSON.stringify(arg));
        }
    }
}
class MembraneError extends Error {
    constructor(arg) {
        const errorData = new ErrorData(arg);
        super(errorData.message);
        this.isMembraneError = true;
        this.data = errorData;
        if (!this.data.type) {
            this.data.type = ErrorType.INTERNAL;
        }
    }
}
class InternalError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.INTERNAL;
    }
}
class BadRequestError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.BAD_REQUEST;
    }
}
class ConcurrencyError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.CONCURRENCY;
    }
}
class ConfigurationError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.CONFIGURATION;
    }
}
class FlowInstanceSetupError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.FLOW_INSTANCE_SETUP;
    }
}
class FlowRunError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.FLOW_RUN;
    }
}
class ActionRunError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.ACTION_RUN;
    }
}
class ActionInstanceSetupError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.ACTION_INSTANCE_SETUP;
    }
}
class UnitRunError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.UNIT_RUN;
    }
}
class ConnectionError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.CONNECTION;
    }
}
class NotFoundError extends BadRequestError {
    constructor(arg) {
        super(arg);
        this.data.key = BadRequestErrorKey.NOT_FOUND;
    }
}
class RateLimitExceededError extends BadRequestError {
    constructor(arg) {
        super(arg);
        this.data.key = BadRequestErrorKey.RATE_LIMIT_EXCEEDED;
    }
}
class NotAuthenticatedError extends BadRequestError {
    constructor(arg) {
        super(arg);
        this.data.key = BadRequestErrorKey.NOT_AUTHENTICATED;
    }
}
class AccessDeniedError extends BadRequestError {
    constructor(arg) {
        super(arg);
        this.data.key = BadRequestErrorKey.ACCESS_DENIED;
    }
}
class RemoteConnectorCodeUnavailableError extends BadRequestError {
    constructor(arg) {
        super(arg !== null && arg !== void 0 ? arg : 'Operation execution requires connector code, which is not available for connectors served from the remote repository.');
    }
}
class CustomCodeError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.CUSTOM_CODE;
    }
}
class InsufficientCreditsError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.INSUFFICIENT_CREDIT;
    }
}
class OpenRouterNotConfiguredError extends MembraneError {
    constructor(arg) {
        super(arg);
        this.data.type = ErrorType.OPENROUTER_NOT_CONFIGURED;
    }
}
const IntegrationAppError = MembraneError;

var DataBuilderFormulaType;
(function (DataBuilderFormulaType) {
    DataBuilderFormulaType["VAR"] = "var";
    DataBuilderFormulaType["COPY"] = "copy";
    DataBuilderFormulaType["TPL"] = "tpl";
    DataBuilderFormulaType["RECORD"] = "record";
    DataBuilderFormulaType["LOOKUP"] = "lookup";
    DataBuilderFormulaType["PLAIN"] = "plain";
    DataBuilderFormulaType["MAP"] = "map";
    DataBuilderFormulaType["CONCAT"] = "concat";
    DataBuilderFormulaType["FIRST_NAME"] = "firstName";
    DataBuilderFormulaType["LAST_NAME"] = "lastName";
    DataBuilderFormulaType["ITERATE"] = "iterate";
    DataBuilderFormulaType["CASE"] = "case";
    DataBuilderFormulaType["EXTRACT_DATE"] = "extractDate";
    DataBuilderFormulaType["EXTRACT_TIME"] = "extractTime";
    DataBuilderFormulaType["FIND_EXTERNAL_RECORD_ID"] = "findExternalRecordId";
    DataBuilderFormulaType["FIND_APP_RECORD_ID"] = "findAppRecordId";
    DataBuilderFormulaType["EVAL"] = "eval";
    DataBuilderFormulaType["AND"] = "and";
    DataBuilderFormulaType["OR"] = "or";
    DataBuilderFormulaType["MERGE_OBJECTS"] = "mergeObjects";
    DataBuilderFormulaType["FIRST_NOT_EMPTY"] = "firstNotEmpty";
    DataBuilderFormulaType["DOMAIN_FROM_EMAIL"] = "domainFromEmail";
    DataBuilderFormulaType["FORMULA"] = "formula";
    DataBuilderFormulaType["JSONATA"] = "jsonata";
    DataBuilderFormulaType["DATA_SCHEMA_REF"] = "dataSchemaRef";
})(DataBuilderFormulaType || (DataBuilderFormulaType = {}));
Object.values(DataBuilderFormulaType);

function makeObjectPropertyLocator(objectLocator, propertyName) {
    const escape = (str) => str.replace(/[\\[\].]/g, '\\$&');
    return `${objectLocator !== null && objectLocator !== void 0 ? objectLocator : '$'}.${escape(propertyName)}`;
}
var DataLocatorStepType;
(function (DataLocatorStepType) {
    DataLocatorStepType["OBJECT_PROPERTY"] = "object_property";
    DataLocatorStepType["ARRAY_ITEM"] = "array_item";
})(DataLocatorStepType || (DataLocatorStepType = {}));
class DataLocatorStep {
}
class DataLocatorStepObjectProperty extends DataLocatorStep {
    constructor(propertyName) {
        super();
        this.propertyName = propertyName;
        this.type = DataLocatorStepType.OBJECT_PROPERTY;
    }
}
class DataLocatorStepArrayItem extends DataLocatorStep {
    constructor(index) {
        super();
        this.index = index;
        this.type = DataLocatorStepType.ARRAY_ITEM;
    }
}
class InvalidLocatorError extends Error {
}
function locatorToField(locator) {
    if (typeof locator == 'string') {
        return locator.replace(/^\$\./, '');
    }
    else {
        return locator;
    }
}
function getLocatorsFromSchema(schema, rootLocator = '$') {
    var _a;
    switch (schema === null || schema === void 0 ? void 0 : schema.type) {
        case 'object':
            return Object.entries((_a = schema.properties) !== null && _a !== void 0 ? _a : {})
                .map(([key, value]) => {
                const locator = makeObjectPropertyLocator(rootLocator, key);
                return [locator, ...getLocatorsFromSchema(value, locator)];
            })
                .flat();
        default:
            return [];
    }
}
function getSchemaByLocator(schema, locator) {
    if (locator === undefined || locator === null || !isValidLocator(locator))
        return undefined;
    if (schema) {
        try {
            const schemaSteps = walkSchema$1(schema, locator);
            if (schemaSteps) {
                if (schemaSteps.length > 0) {
                    return schemaSteps[schemaSteps.length - 1].schema;
                }
                else {
                    return schema;
                }
            }
            else {
                return null;
            }
        }
        catch (e) {
            if (e instanceof InvalidLocatorError) {
                return null;
            }
            else {
                throw e;
            }
        }
    }
    else {
        return null;
    }
}
function getValueByLocator(variables, locator) {
    if (locator === undefined || locator === null || !isValidLocator(locator))
        return undefined;
    let curValue = variables;
    const locatorSteps = locatorToSteps(locator);
    for (let i = 0; i < locatorSteps.length; ++i) {
        const locatorStep = locatorSteps[i];
        if (locatorStep.type == DataLocatorStepType.OBJECT_PROPERTY) {
            curValue = executeLocatorStepObjectProperty(curValue, locatorStep);
        }
        else if (locatorStep.type == DataLocatorStepType.ARRAY_ITEM) {
            curValue = executeLocatorStepArrayItem(curValue, locatorStep);
        }
        else {
            throw new BadRequestError(`Unexpected locator step type: ${locatorStep.type}`);
        }
        if (curValue === undefined) {
            break;
        }
    }
    return curValue;
}
function findValueLocators(data, value) {
    function doFindValueLocator(data, value, locatorSoFar) {
        if (JSON.stringify(data) === JSON.stringify(value)) {
            return [locatorSoFar];
        }
        else {
            if (Array.isArray(data)) {
                const result = [];
                for (let i = 0; i < data.length; ++i) {
                    result.push(...doFindValueLocator(data[i], value, `${locatorSoFar}[${i}]`));
                }
                return result;
            }
            else if (typeof data === 'object' && data) {
                const result = [];
                for (const key in data) {
                    result.push(...doFindValueLocator(data[key], value, makeObjectPropertyLocator(locatorSoFar, key)));
                }
                return result;
            }
            else {
                return [];
            }
        }
    }
    return doFindValueLocator(data, value, '$');
}
function getValueAtLocator(data, locator) {
    if (data === null || data === undefined || !isValidLocator(locator))
        return undefined;
    if (!locator)
        return undefined;
    let curValue = data;
    const locatorSteps = locatorToSteps(locator);
    for (let i = 0; i < locatorSteps.length; ++i) {
        const locatorStep = locatorSteps[i];
        switch (locatorStep.type) {
            case DataLocatorStepType.OBJECT_PROPERTY:
                const propStep = locatorStep;
                if (typeof curValue == 'object' &&
                    curValue !== null &&
                    curValue !== undefined &&
                    curValue[propStep.propertyName] !== undefined) {
                    curValue = curValue[propStep.propertyName];
                }
                else {
                    return undefined;
                }
                break;
            case DataLocatorStepType.ARRAY_ITEM:
                const itemStep = locatorStep;
                if (Array.isArray(curValue) && curValue[itemStep.index] !== undefined) {
                    curValue = curValue[itemStep.index];
                }
                else {
                    return undefined;
                }
                break;
            default:
                throw new Error(`Unknown locator step type: ${locatorStep.type}`);
        }
    }
    return curValue;
}
function setValueAtLocator(obj, locator, value) {
    const locatorSteps = locatorToSteps(locator);
    let result = obj;
    if (locatorSteps.length > 0) {
        let cursor = result;
        let setValue = (value) => {
            result = value;
        };
        for (let i = 0; i < locatorSteps.length; i++) {
            const locatorStep = locatorSteps[i];
            if (locatorStep instanceof DataLocatorStepArrayItem) {
                if (!Array.isArray(cursor)) {
                    cursor = [];
                    setValue(cursor);
                }
                const index = locatorStep instanceof DataLocatorStepArrayItem ? locatorStep.index : 0;
                setValue = ((cursor) => {
                    return (value) => {
                        if (value !== undefined) {
                            cursor[index] = value;
                        }
                        else {
                            cursor.splice(index, 1);
                        }
                    };
                })(cursor);
                cursor = cursor[index];
            }
            else if (locatorStep instanceof DataLocatorStepObjectProperty) {
                if (typeof cursor !== 'object' || cursor === null) {
                    cursor = {};
                    setValue(cursor);
                }
                setValue = ((cursor) => {
                    return (value) => {
                        if (value !== undefined) {
                            cursor[locatorStep.propertyName] = value;
                        }
                        else {
                            delete cursor[locatorStep.propertyName];
                        }
                    };
                })(cursor);
                cursor = cursor[locatorStep.propertyName];
            }
            else {
                throw new Error(`Unexpected locator step type in "setValueAtLocator": ${locatorStep}`);
            }
        }
        setValue(value);
        return result;
    }
    else {
        return value;
    }
}
function pickFieldsFromValue(value, fields) {
    if (!Array.isArray(fields)) {
        return undefined;
    }
    let result;
    for (const field of fields) {
        const fieldValue = getValueAtLocator(value, field);
        if (fieldValue !== undefined) {
            result = setValueAtLocator(result, field, fieldValue);
        }
    }
    return result;
}
function excludeFieldsFromValue(value, fields) {
    if (!Array.isArray(fields)) {
        return value;
    }
    for (const field of fields) {
        setValueAtLocator(value, field, undefined);
    }
    return value;
}
function setSchemaAtLocator(schema, locator, schemaAtLocator) {
    var _a, _b, _c;
    const locatorSteps = locatorToSteps(locator);
    const result = schema !== null && schema !== void 0 ? schema : {};
    let curSchema = result;
    let curSchemaDataLocator = '$';
    for (const locatorStep of locatorSteps) {
        if (locatorStep.type == DataLocatorStepType.OBJECT_PROPERTY) {
            curSchema.type = 'object';
            const propertyName = locatorStep.propertyName;
            if (!((_a = curSchema.properties) === null || _a === void 0 ? void 0 : _a.hasOwnProperty(propertyName))) {
                curSchema.properties = {
                    ...((_b = curSchema.properties) !== null && _b !== void 0 ? _b : {}),
                    [propertyName]: {},
                };
            }
            curSchema = curSchema.properties[propertyName];
            curSchemaDataLocator = makeObjectPropertyLocator(curSchemaDataLocator + '.properties', propertyName);
        }
        else if (locatorStep.type == DataLocatorStepType.ARRAY_ITEM) {
            curSchema.type = 'array';
            curSchema.items = (_c = curSchema.items) !== null && _c !== void 0 ? _c : {};
            curSchema = curSchema.items;
            curSchemaDataLocator = `${curSchemaDataLocator}.items`;
        }
        else {
            throw new Error(`Unexpected locator step type: ${locatorStep.type}`);
        }
    }
    return setValueAtLocator(result, curSchemaDataLocator, schemaAtLocator);
}
function extractFieldLocator(locator) {
    const steps = locatorToSteps(locator);
    const fieldSteps = [];
    for (const step of steps) {
        if (step.type === DataLocatorStepType.OBJECT_PROPERTY &&
            step.propertyName.startsWith('$')) {
            break;
        }
        fieldSteps.push(step);
    }
    return stepsToLocator(fieldSteps);
}
function makeSchemaForLocator(locator, locatorSchema) {
    return setSchemaAtLocator(null, locator, locatorSchema);
}
function getNameForLocator(schema, locator) {
    if (locator === undefined || locator === null || !isValidLocator(locator))
        return undefined;
    locator = locatorToSteps(locator);
    const schemaSteps = walkSchema$1(schema, locator);
    if (schemaSteps) {
        const titleParts = schemaSteps.map((s) => s.title).filter((t) => !!t);
        return titleParts[titleParts.length - 1];
    }
    else {
        return null;
    }
}
function getNameComponentsForLocator(schema, locator) {
    if (locator === undefined || locator === null || !isValidLocator(locator))
        return undefined;
    locator = locatorToSteps(locator);
    const schemaSteps = walkSchema$1(schema, locator);
    return schemaSteps === null || schemaSteps === void 0 ? void 0 : schemaSteps.map((s) => s.title).filter((t) => !!t);
}
function getFullNameForLocator(schema, locator) {
    var _a;
    return (_a = getNameComponentsForLocator(schema, locator)) === null || _a === void 0 ? void 0 : _a.join(' > ');
}
function getIconUriForLocator(schema, locator) {
    if (locator === undefined || locator === null || !isValidLocator(locator))
        return undefined;
    locator = locatorToSteps(locator);
    const schemaSteps = walkSchema$1(schema, locator);
    return (schemaSteps &&
        schemaSteps
            .map((s) => s.iconUri)
            .filter((t) => !!t)
            .pop());
}
function getLocatorsFromData(data, parentLocator = '$') {
    if (data === null || data === undefined) {
        return [];
    }
    if (Array.isArray(data)) {
        return data.flatMap((item, idx) => getLocatorsFromData(item, `${parentLocator}[${idx}]`));
    }
    if (typeof data === 'object') {
        const locators = [];
        for (const [key, value] of Object.entries(data)) {
            locators.push(...getLocatorsFromData(value, makeObjectPropertyLocator(parentLocator, key)));
        }
        return locators;
    }
    return [parentLocator];
}
const getFullTitleForLocator = getFullNameForLocator;
function walkSchema$1(schema, locator) {
    var _a, _b;
    locator = locatorToSteps(locator);
    const schemaSteps = [];
    let curSchema = schema;
    if (schema) {
        schemaSteps.push({
            schema,
            title: schema.title,
            iconUri: schema.iconUri,
        });
    }
    for (let idx = 0; idx < locator.length; ++idx) {
        const schemaStep = {};
        const step = locator[idx];
        let defaultTitle;
        if (!curSchema) {
            return null;
        }
        if (step.type == DataLocatorStepType.OBJECT_PROPERTY) {
            if (curSchema.type == 'object') {
                const objectPropertyStep = step;
                curSchema = (_a = curSchema.properties) === null || _a === void 0 ? void 0 : _a[objectPropertyStep.propertyName];
                defaultTitle = toHeaderCase(objectPropertyStep.propertyName);
            }
            else {
                return null;
            }
        }
        else if (step.type == DataLocatorStepType.ARRAY_ITEM) {
            if (curSchema.type == 'array') {
                const arrayItemStep = step;
                curSchema = curSchema.items;
                defaultTitle = `Item #${arrayItemStep.index + 1}`;
            }
            else {
                return null;
            }
        }
        if (!curSchema) {
            return null;
        }
        schemaStep.schema = Object.assign({}, curSchema);
        schemaStep.title = (_b = schemaStep.schema.title) !== null && _b !== void 0 ? _b : defaultTitle;
        schemaStep.iconUri = schemaStep.schema.iconUri;
        schemaSteps.push(schemaStep);
    }
    return schemaSteps;
}
function isValidLocator(locator) {
    if (!locator) {
        return false;
    }
    try {
        locatorToSteps(locator);
        return true;
    }
    catch (_a) {
        return false;
    }
}
function locatorToSteps(sourceLocator) {
    if (Array.isArray(sourceLocator))
        return sourceLocator;
    if (typeof sourceLocator !== 'string')
        throw new BadRequestError(`Unexpected locator value: ${JSON.stringify(sourceLocator)}`);
    if (sourceLocator === '$')
        return [];
    if (!sourceLocator.startsWith('$')) {
        sourceLocator = `$.${sourceLocator}`;
    }
    const locators = [];
    let cursorPosition = 1;
    while (cursorPosition < sourceLocator.length) {
        if (sourceLocator[cursorPosition] === '.') {
            const closingIndex = findNextPosition(sourceLocator, cursorPosition + 1, '[.');
            const part = unescape(sourceLocator.slice(cursorPosition + 1, closingIndex));
            locators.push(new DataLocatorStepObjectProperty(part));
            cursorPosition = closingIndex;
        }
        else if (sourceLocator[cursorPosition] === '[') {
            const closingIndex = sourceLocator.indexOf(']', cursorPosition);
            if (closingIndex === -1) {
                throw new BadRequestError(`Could not find a closing bracket for the bracket at position ${cursorPosition}. Locator: ${sourceLocator}`);
            }
            const arrayIndex = +sourceLocator.slice(cursorPosition + 1, closingIndex);
            if (isNaN(arrayIndex) || arrayIndex < 0) {
                throw new BadRequestError(`Invalid index [${sourceLocator.slice(cursorPosition + 1, closingIndex)}]. Index should be a positive number`);
            }
            locators.push(new DataLocatorStepArrayItem(arrayIndex));
            cursorPosition = closingIndex + 1;
        }
        else {
            throw new BadRequestError(`Unexpected symbol at position ${cursorPosition}. Locator: ${sourceLocator}`);
        }
    }
    return locators;
}
function locatorToString(locator) {
    if (typeof locator === 'object' && Array.isArray(locator)) {
        return stepsToLocator(locator);
    }
    else {
        return locator;
    }
}
function stepsToLocator(steps) {
    let locator = '$';
    (steps !== null && steps !== void 0 ? steps : []).forEach((step) => {
        switch (step.type) {
            case DataLocatorStepType.OBJECT_PROPERTY:
                locator = makeObjectPropertyLocator(locator, step.propertyName);
                break;
            case DataLocatorStepType.ARRAY_ITEM:
                locator += `[${step.index}]`;
                break;
            default:
                throw new Error(`Unknown locator step type: ${step.type}`);
        }
    });
    return locator;
}
function createObjectFromLocators(locators = [], placeholderValue = '') {
    const locatorsWithoutNested = (locators !== null && locators !== void 0 ? locators : []).filter((locator) => !locators.some((otherLocator) => locator !== otherLocator && locator.startsWith(otherLocator + '.')));
    const obj = {};
    locatorsWithoutNested.forEach((locator) => {
        setValueAtLocator(obj, locator, placeholderValue);
    });
    return obj;
}
function getFormulaLocators(formulaType, data) {
    function doGetFormulaLocators(data, locator) {
        if (data && typeof data === 'object') {
            if (`$${formulaType}` in data && Object.keys(data).length === 1) {
                return [locator];
            }
            else if (Array.isArray(data)) {
                const locators = [];
                data.map((item, idx) => {
                    locators.push(...doGetFormulaLocators(item, `${locator}[${idx}]`));
                });
                return locators;
            }
            else {
                const locators = [];
                for (const [key, value] of Object.entries(data)) {
                    locators.push(...doGetFormulaLocators(value, makeObjectPropertyLocator(locator, key)));
                }
                return locators;
            }
        }
        else {
            return [];
        }
    }
    return doGetFormulaLocators(data, '$');
}
function findNextPosition(str, startPosition, symbols) {
    let escaped = false;
    for (let i = startPosition; i < str.length; i++) {
        if (str[i] === '\\') {
            escaped = !escaped;
            continue;
        }
        if (escaped) {
            escaped = false;
            continue;
        }
        if (symbols.includes(str[i])) {
            return i;
        }
    }
}
function unescape(str) {
    return str.replace(/\\(.)/g, '$1');
}
function executeLocatorStepObjectProperty(value, step) {
    if (typeof value == 'object' && value !== null) {
        return value[step.propertyName];
    }
    else {
        return undefined;
    }
}
function executeLocatorStepArrayItem(value, step) {
    if (Array.isArray(value) && value[step.index]) {
        return value[step.index];
    }
    else {
        return undefined;
    }
}

let getFormula$1;
let isFormula$1;
let hasFormulas$1;
function injectFormulaCatalog(getFormulaFn, isFormulaFn, hasFormulasFn) {
    getFormula$1 = getFormulaFn;
    isFormula$1 = isFormulaFn;
    hasFormulas$1 = hasFormulasFn;
}
function buildData(value, variables) {
    const result = buildValue(value, variables);
    processCopy(result);
    return result;
}
function buildValue(value, variables) {
    const formula = getFormula$1(value);
    if (formula) {
        return formula.getValue(variables);
    }
    else {
        if (typeof value === 'object') {
            if (Array.isArray(value)) {
                if (!(value === null || value === void 0 ? void 0 : value.length))
                    return value;
                const result = [];
                for (const valueItem of value) {
                    const processedVal = buildValue(valueItem, variables);
                    if (processedVal !== undefined) {
                        result.push(processedVal);
                    }
                }
                return result.length > 0 ? result : undefined;
            }
            else if (value === null) {
                return value;
            }
            else {
                if (Object.keys(value).length === 0)
                    return value;
                const result = {};
                for (const [key, val] of Object.entries(value)) {
                    const processedVal = buildValue(val, variables);
                    if (processedVal !== undefined) {
                        result[key] = processedVal;
                    }
                }
                if (Object.keys(result).length > 0) {
                    return result;
                }
                else {
                    return undefined;
                }
            }
        }
        else {
            return value;
        }
    }
}
async function __resolveValue(value, params) {
    var _a, _b, _c;
    const formula = getFormula$1(value);
    if (formula) {
        return formula.resolveValue(params);
    }
    else {
        if (typeof value === 'object') {
            if (Array.isArray(value)) {
                if (!(value === null || value === void 0 ? void 0 : value.length))
                    return value;
                const result = [];
                for (const valueItem of value) {
                    const itemSchema = (_a = params.schema) === null || _a === void 0 ? void 0 : _a.items;
                    const processedVal = await __resolveValue(valueItem, { ...params, schema: itemSchema });
                    if (processedVal !== undefined) {
                        result.push(processedVal);
                    }
                }
                return (result.length > 0 ? result : undefined);
            }
            else if (value === null) {
                return value;
            }
            else {
                if (Object.keys(value).length === 0)
                    return value;
                const result = {};
                for (const [key, val] of Object.entries(value)) {
                    const itemSchema = (_c = (_b = params.schema) === null || _b === void 0 ? void 0 : _b.properties) === null || _c === void 0 ? void 0 : _c[key];
                    const processedVal = await __resolveValue(val, { ...params, schema: itemSchema });
                    if (processedVal !== undefined) {
                        result[key] = processedVal;
                    }
                }
                if (Object.keys(result).length > 0) {
                    return result;
                }
                else {
                    return undefined;
                }
            }
        }
        else {
            return value;
        }
    }
}
function transformVars(value, transformations) {
    const formula = getFormula$1(value);
    if (formula) {
        return formula.transformVars(transformations);
    }
    else if (Array.isArray(value)) {
        if (value.length === 0) {
            return value;
        }
        const result = [];
        for (const valueItem of value) {
            const processedVal = transformVars(valueItem, transformations);
            if (processedVal !== undefined) {
                result.push(processedVal);
            }
        }
        return result.length > 0 ? result : undefined;
    }
    else if (typeof value === 'object') {
        if (value === null) {
            return value;
        }
        else {
            if (Object.keys(value).length === 0) {
                return value;
            }
            const result = {};
            for (const [key, val] of Object.entries(value)) {
                const processedVal = transformVars(val, transformations);
                if (processedVal !== undefined) {
                    result[key] = processedVal;
                }
            }
            if (Object.keys(result).length > 0) {
                return result;
            }
            else {
                return undefined;
            }
        }
    }
    else {
        return value;
    }
}
function valueToString(value, variablesSchema) {
    const formula = getFormula$1(value);
    if (formula) {
        return formula.toString(variablesSchema);
    }
    else if (value === null || value === void 0 ? void 0 : value.toString) {
        return value.toString();
    }
    else {
        return '';
    }
}
async function resolveFormulas(value, params) {
    return __resolveValue(value, {
        ...(params !== null && params !== void 0 ? params : {}),
        fullValue: value,
    });
}
function processCopy(data) {
    const copyLocators = getFormulaLocators(DataBuilderFormulaType.COPY, data);
    for (const locator of copyLocators) {
        const value = getValueAtLocator(data, locator);
        const formula = getFormula$1(value);
        const valueToCopy = getValueAtLocator(data, formula.locator);
        const valueHasCopy = getFormulaLocators(DataBuilderFormulaType.COPY, valueToCopy).length > 0;
        if (valueHasCopy) {
            throw new ConfigurationError(`${locator} is copying ${formula.locator}, which contains a copy formula`);
        }
        setValueAtLocator(data, locator, valueToCopy);
    }
}
function getVariableLocators(data) {
    function doGetVariableLocators(data, locator) {
        if (data && typeof data === 'object') {
            const formula = getFormula$1(data);
            if (formula) {
                const locators = formula.getVariableLocators();
                return locators.map((loc) => `${locator}${loc.replace(/^\$/, '')}`);
            }
            else if (Array.isArray(data)) {
                const locators = [];
                data.map((item, idx) => {
                    locators.push(...doGetVariableLocators(item, `${locator}[${idx}]`));
                });
                return locators;
            }
            else {
                const locators = [];
                for (const [key, value] of Object.entries(data)) {
                    locators.push(...doGetVariableLocators(value, makeObjectPropertyLocator(locator, key)));
                }
                return locators;
            }
        }
        else {
            return [];
        }
    }
    return doGetVariableLocators(data, '$');
}
function removeNonExistentVars(value, schema) {
    const fieldLocators = getLocatorsFromSchema(schema);
    const transformations = {};
    for (const fieldLocator of fieldLocators) {
        setValueAtLocator(transformations, fieldLocator, {
            $var: fieldLocator,
        });
    }
    return transformVars(value, transformations);
}
function transformVariablesWith(data, transformFunction) {
    let result = structuredClone(data);
    const variableLocators = getVariableLocators(data);
    for (const formulaLocator of variableLocators) {
        const value = getValueAtLocator(result, `${formulaLocator}.$var`);
        const newValue = transformFunction(value);
        result = setValueAtLocator(result, `${formulaLocator}.$var`, newValue);
    }
    return result;
}

class DataBuilderFormulaBase {
    constructor(type, args) {
        this.type = type;
        this.args = args;
    }
    async resolveValue(params) {
        return this.getValue(params.variables);
    }
    toObject() {
        return {
            [`$${this.type}`]: this.args,
        };
    }
    toString(_variablesSchema) {
        return toHeaderCase(this.type);
    }
    getVariableLocators() {
        return getVariableLocators(this.args).map((loc) => `$.$${this.type}${loc.replace(/^\$/, '')}`);
    }
    transformVars(transformations) {
        const transformedArgs = transformVars(this.args, transformations);
        if (transformedArgs !== undefined) {
            return {
                [`$${this.type}`]: transformedArgs,
            };
        }
        else {
            return undefined;
        }
    }
}

class And extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.AND, args);
    }
    getValue(variables) {
        const args = buildValue(this.args, variables);
        if (Array.isArray(args) && args.some((arg) => arg !== null && arg !== undefined)) {
            return args.filter((arg) => arg !== null && arg !== undefined).every((arg) => !!arg);
        }
        else {
            return undefined;
        }
    }
    async resolveValue(params) {
        const args = await __resolveValue(this.args, params);
        if (Array.isArray(args) && args.some((arg) => arg !== null && arg !== undefined)) {
            return args.filter((arg) => arg !== null && arg !== undefined).every((arg) => !!arg);
        }
        else {
            return undefined;
        }
    }
    getSchema() {
        return { type: 'boolean' };
    }
}

function mergeSchemas(schemas) {
    const mergedSchema = combineSchemas(schemas);
    return mergedSchema;
}
function patchSchema(source, patch, options) {
    var _a, _b, _c, _d;
    const mergedSchema = combineSchemas([source, patch]);
    if (options === null || options === void 0 ? void 0 : options.readOnly) {
        const properties = Object.keys((_a = patch === null || patch === void 0 ? void 0 : patch.properties) !== null && _a !== void 0 ? _a : {});
        for (const prop of properties) {
            const sourceProperty = (_b = source === null || source === void 0 ? void 0 : source.properties) === null || _b === void 0 ? void 0 : _b[prop];
            if (!sourceProperty) {
                mergedSchema.properties[prop].readOnly = true;
            }
        }
    }
    else {
        const locators = getLocatorsFromSchema(patch);
        for (const locator of locators) {
            const sourceProperty = getSchemaByLocator(source, locator);
            const patchProperty = getSchemaByLocator(patch, locator);
            if ((sourceProperty === null || sourceProperty === void 0 ? void 0 : sourceProperty.readOnly) && !(patchProperty === null || patchProperty === void 0 ? void 0 : patchProperty.readOnly)) {
                const mergedProperty = getSchemaByLocator(mergedSchema, locator);
                delete mergedProperty.readOnly;
                setSchemaAtLocator(mergedSchema, locator, mergedProperty);
            }
        }
    }
    if (options === null || options === void 0 ? void 0 : options.writeOnly) {
        const properties = Object.keys((_c = patch === null || patch === void 0 ? void 0 : patch.properties) !== null && _c !== void 0 ? _c : {});
        for (const prop of properties) {
            const sourceProperty = (_d = source === null || source === void 0 ? void 0 : source.properties) === null || _d === void 0 ? void 0 : _d[prop];
            if (!sourceProperty) {
                mergedSchema.properties[prop].writeOnly = true;
            }
        }
    }
    else {
        const locators = getLocatorsFromSchema(patch);
        for (const locator of locators) {
            const sourceProperty = getSchemaByLocator(source, locator);
            const patchProperty = getSchemaByLocator(patch, locator);
            if ((sourceProperty === null || sourceProperty === void 0 ? void 0 : sourceProperty.writeOnly) && !(patchProperty === null || patchProperty === void 0 ? void 0 : patchProperty.writeOnly)) {
                const mergedProperty = getSchemaByLocator(mergedSchema, locator);
                delete mergedProperty.writeOnly;
                setSchemaAtLocator(mergedSchema, locator, mergedProperty);
            }
        }
    }
    return mergedSchema;
}
function combineSchemas(schemas) {
    const schemasByType = {
        undefined: [],
        ['null']: [],
        ['boolean']: [],
        ['integer']: [],
        ['number']: [],
        ['string']: [],
        ['array']: [],
        ['object']: [],
    };
    const unwrappedSchemas = unwrapSchemas(schemas);
    for (const unwrappedSchema of unwrappedSchemas) {
        const type = unwrappedSchema.type;
        if (!(type in schemasByType)) {
            continue;
        }
        schemasByType[type].push(unwrappedSchema);
    }
    if (schemasByType['number'].length > 0) {
        schemasByType['number'].push(...schemasByType['integer'].map((r) => ({
            ...r,
            type: 'number',
        })));
        delete schemasByType['integer'];
    }
    const resultSchemasByType = {
        undefined: combineSameTypeScalarSchemas(schemasByType['undefined']),
        ['null']: combineSameTypeScalarSchemas(schemasByType['null']),
        ['boolean']: combineSameTypeScalarSchemas(schemasByType['boolean']),
        ['number']: combineSameTypeScalarSchemas(schemasByType['number']),
        ['integer']: combineSameTypeScalarSchemas(schemasByType['integer']),
        ['string']: combineSameTypeScalarSchemas(schemasByType['string']),
        ['array']: combineArraySchemas(schemasByType['array']),
        ['object']: combineObjectSchemas(schemasByType['object']),
    };
    let schemasFound = Object.values(resultSchemasByType).filter(Boolean);
    const schemaTypesFound = new Set(schemasFound.map((s) => s.type));
    if (schemaTypesFound.size > 1 && schemaTypesFound.has(undefined)) {
        schemasFound = schemasFound.filter((s) => !!(s === null || s === void 0 ? void 0 : s.type));
    }
    const multiType = schemasFound.length > 1;
    if (multiType) {
        const wrapped = wrapAnyOfSchema({ anyOf: schemasFound });
        return wrapped;
    }
    return schemasFound[0];
}
function combineSameTypeScalarSchemas(schemas) {
    if (!schemas || schemas.length === 0) {
        return undefined;
    }
    let schema = {};
    for (const s of schemas) {
        schema = {
            ...schema,
            ...s,
        };
    }
    return schema;
}
function combineArraySchemas(schemas) {
    if (!schemas || schemas.length === 0) {
        return undefined;
    }
    const itemSchemas = [];
    for (const schema of schemas) {
        if (!schema.items)
            continue;
        const unwrappedSchemas = unwrapSchema(schema.items);
        itemSchemas.push(...unwrappedSchemas);
    }
    const schema = combineSameTypeScalarSchemas(schemas);
    if (itemSchemas.length > 0) {
        schema.items = combineSchemas(itemSchemas);
    }
    return schema;
}
function combineObjectSchemas(schemas) {
    if (!schemas || schemas.length === 0) {
        return undefined;
    }
    const allPropSchemas = [];
    const allRequiredProps = new Set();
    for (const schema of schemas) {
        if (!schema)
            continue;
        if (schema.properties) {
            allPropSchemas.push(schema.properties);
        }
        if (Array.isArray(schema.required)) {
            for (const prop of schema.required) {
                allRequiredProps.add(prop);
            }
        }
    }
    const schemasByProp = Object.create(null);
    for (const propSchemas of allPropSchemas) {
        for (const [prop, schema] of Object.entries(propSchemas)) {
            if (!schemasByProp[prop]) {
                schemasByProp[prop] = [];
            }
            const unwrappedSchemas = unwrapSchema(schema);
            schemasByProp[prop].push(...unwrappedSchemas);
        }
    }
    const properties = Object.entries(schemasByProp).reduce((props, [prop, schemas]) => {
        if (schemas.length === 1) {
            props[prop] = schemas[0];
        }
        else {
            props[prop] = combineSchemas(schemas);
        }
        return props;
    }, {});
    const combinedSchema = combineSameTypeScalarSchemas(schemas);
    if (Object.keys(properties).length > 0) {
        combinedSchema.properties = properties;
    }
    if (allRequiredProps.size > 0) {
        combinedSchema.required = Array.from(allRequiredProps);
    }
    return combinedSchema;
}
function unwrapSchema(schema) {
    if (!schema)
        return [];
    if (schema.anyOf) {
        return unwrapSchemas(schema.anyOf);
    }
    if (Array.isArray(schema.type)) {
        return schema.type.map((x) => ({ type: x }));
    }
    return [schema];
}
function unwrapSchemas(schemas) {
    if (!schemas || schemas.length === 0)
        return [];
    const unwrappedSchemas = schemas.flatMap((schema) => unwrapSchema(schema));
    return unwrappedSchemas;
}
function wrapAnyOfSchema(schema) {
    const simpleSchemas = [];
    const complexSchemas = [];
    for (const subSchema of schema.anyOf) {
        if (Array.isArray(subSchema.type)) {
            simpleSchemas.push(...subSchema.type);
        }
        else if (isSimpleSchema(subSchema)) {
            simpleSchemas.push(subSchema.type);
        }
        else {
            complexSchemas.push(subSchema);
        }
    }
    if (complexSchemas.length === 0) {
        return { type: simpleSchemas };
    }
    const anyOf = [];
    if (simpleSchemas.length > 0) {
        anyOf.push({
            type: simpleSchemas.length > 1 ? simpleSchemas : simpleSchemas[0],
        });
    }
    anyOf.push(...complexSchemas);
    return { anyOf };
}
function isSimpleSchema(schema) {
    const keys = Object.keys(schema);
    return keys.length === 1 && keys[0] === 'type';
}

function createSchema(value) {
    if (typeof value === 'undefined')
        value = null;
    const clone = JSON.parse(JSON.stringify(value));
    return createSchemaFor(clone);
}
function createCompoundSchema(values) {
    const schemas = values.map((value) => createSchema(value));
    return mergeSchemas(schemas);
}
function createSchemaFor(value) {
    switch (typeof value) {
        case 'number':
            if (Number.isInteger(value)) {
                return { type: 'integer' };
            }
            return { type: 'number' };
        case 'boolean':
            return { type: 'boolean' };
        case 'string':
            return { type: 'string' };
        case 'object':
            if (value === null) {
                return {};
            }
            if (Array.isArray(value)) {
                return createSchemaForArray(value);
            }
            return createSchemaForObject(value);
        default:
            return null;
    }
}
function createSchemaForArray(arr) {
    if (arr.length === 0) {
        return { type: 'array' };
    }
    const elementSchemas = arr.map((value) => createSchemaFor(value));
    const items = mergeSchemas(elementSchemas);
    return { type: 'array', items };
}
function createSchemaForObject(obj) {
    const keys = Object.keys(obj);
    if (keys.length === 0) {
        return {
            type: 'object',
        };
    }
    const properties = Object.entries(obj).reduce((props, [key, val]) => {
        props[key] = createSchemaFor(val);
        return props;
    }, {});
    const schema = { type: 'object', properties };
    return schema;
}

const DataSchema = z.lazy(() => z
    .object({
    title: z.string().optional(),
    description: z.string().optional(),
    type: z.union([z.string(), z.array(z.string())]).optional(),
    format: z.string().optional(),
    properties: z.record(z.string(), DataSchema).optional(),
    items: DataSchema.optional(),
    additionalProperties: z.union([z.boolean(), DataSchema]).optional(),
    enum: z.array(z.union([z.string(), z.number()])).optional(),
    referenceRecords: z.array(z.any()).optional(),
    referenceCollection: z
        .object({
        key: z.any().optional(),
        parameters: z.record(z.string(), z.any()).optional(),
    })
        .optional(),
    referenceUdm: z.string().optional(),
    default: z.any().optional(),
    allowCustom: z.boolean().optional(),
    $ref: z.string().optional(),
    required: z.array(z.string()).optional(),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    maxItems: z.number().optional(),
    readOnly: z.boolean().optional(),
    writeOnly: z.boolean().optional(),
    examples: z.array(z.any()).optional(),
    anyOf: z.array(DataSchema).optional(),
    isImplied: z.boolean().optional(),
    isSensitive: z.boolean().optional(),
    referenceCollectionPath: z.string().optional(),
    referenceCollectionUri: z.string().optional(),
})
    .passthrough());

function updateImpliedSchema({ schema: sourceSchema, value, variablesSchema, }) {
    let resultSchema = sourceSchema ? JSON.parse(JSON.stringify(sourceSchema)) : undefined;
    resultSchema = walkSchema(resultSchema, (schema) => (schema.isImplied ? undefined : schema));
    const schemaFromValue = getSchemaFromValue(value, variablesSchema);
    walkSchema(schemaFromValue, (schema, locator) => {
        const existingSchema = getSchemaByLocator(resultSchema, locator);
        if (existingSchema) {
            if ((existingSchema === null || existingSchema === void 0 ? void 0 : existingSchema.type) !== schema.type) {
                throw new StopWalkError();
            }
        }
        else {
            const impliedSchema = walkSchema(schema, (schema) => {
                delete schema.readOnly;
                delete schema.title;
                return {
                    ...schema,
                    isImplied: true,
                };
            });
            resultSchema = setSchemaAtLocator(resultSchema, locator, impliedSchema);
        }
        return schema;
    });
    return resultSchema;
}
function schemaHasProperties(schema) {
    var _a;
    if (!schema) {
        return false;
    }
    if ((schema === null || schema === void 0 ? void 0 : schema.type) !== 'object') {
        return false;
    }
    if (Object.keys((_a = schema === null || schema === void 0 ? void 0 : schema.properties) !== null && _a !== void 0 ? _a : {}).length > 0) {
        return true;
    }
    return false;
}
function nonEmptyObjectProperties(properties) {
    if (!properties) {
        return {};
    }
    const result = {};
    for (const [key, value] of Object.entries(properties)) {
        if (value) {
            result[key] = value;
        }
    }
    return result;
}
function schemaTypeFromValue(value) {
    switch (typeof value) {
        case 'object':
            if (Array.isArray(value)) {
                return 'array';
            }
            else if (value === null) {
                return undefined;
            }
            else {
                return 'object';
            }
        case 'boolean':
            return 'boolean';
        case 'bigint':
        case 'number':
            return 'number';
        case 'undefined':
            return undefined;
        default:
            return 'string';
    }
}
function buildDataSchema(value, variablesSchema) {
    return getSchemaFromValue(value, variablesSchema);
}
function getSchemaFromValue(value, variablesSchema, ignoreFormulas = false) {
    if (isFormula$1(value) && !ignoreFormulas) {
        const formula = getFormula$1(value);
        return formula === null || formula === void 0 ? void 0 : formula.getSchema(variablesSchema);
    }
    else {
        const type = schemaTypeFromValue(value);
        switch (type) {
            case 'object':
                const properties = {};
                for (const [propKey, propValue] of Object.entries(value)) {
                    properties[propKey] = getSchemaFromValue(propValue, variablesSchema, ignoreFormulas);
                }
                return {
                    type: 'object',
                    properties,
                };
            case 'array':
                const schemas = [];
                for (const item of value) {
                    schemas.push(getSchemaFromValue(item, variablesSchema, ignoreFormulas));
                }
                return {
                    type: 'array',
                    items: mergeSchemas(schemas),
                };
            case undefined:
                return undefined;
            default:
                return { type: type };
        }
    }
}
function valueToSchema(value, schema, options, field = '') {
    var _a;
    if (!schema)
        return value;
    if ((value === null || value === undefined) && (schema === null || schema === void 0 ? void 0 : schema.default) !== undefined && (options === null || options === void 0 ? void 0 : options.addDefaults)) {
        value = schema.default;
    }
    if ((schema === null || schema === void 0 ? void 0 : schema.type) === 'object') {
        return valueToObject();
    }
    else if ((schema === null || schema === void 0 ? void 0 : schema.type) === 'array') {
        if (Array.isArray(value)) {
            const hasItems = value.length > 0;
            const newValue = [];
            for (const item of value) {
                const newItem = valueToSchema(item, schema.items, options, field);
                if (item === undefined || newItem !== undefined) {
                    newValue.push(newItem);
                }
            }
            if (hasItems && newValue.length === 0) {
                return undefined;
            }
            return newValue;
        }
        else {
            return value;
        }
    }
    else {
        if (options === null || options === void 0 ? void 0 : options.normalizeValues) {
            switch (schema === null || schema === void 0 ? void 0 : schema.type) {
                case 'string':
                    return (_a = value === null || value === void 0 ? void 0 : value.toString()) !== null && _a !== void 0 ? _a : value;
                case 'integer':
                case 'number':
                    return valueToNumber();
                case 'boolean':
                    return valueToBoolean();
                default:
                    return value;
            }
        }
        else {
            return value;
        }
    }
    function valueToNumber() {
        if (value === null || value === undefined || typeof value === 'object')
            return value;
        const result = Number(value);
        if (isNaN(result)) {
            return value;
        }
        if ((schema === null || schema === void 0 ? void 0 : schema.type) === 'integer' && !Number.isInteger(result)) {
            return value;
        }
        return result;
    }
    function valueToBoolean() {
        if (value === null || value === undefined) {
            return value;
        }
        if (typeof value === 'boolean') {
            return value;
        }
        else if (typeof value === 'string') {
            if (value === 'true') {
                return true;
            }
            else if (value === 'false') {
                return false;
            }
            else {
                return value;
            }
        }
        else {
            return value;
        }
    }
    function valueToObject() {
        var _a;
        if ((options === null || options === void 0 ? void 0 : options.valueHasFormulas) && isFormula$1(value)) {
            return value;
        }
        const result = {};
        const properties = schema === null || schema === void 0 ? void 0 : schema.properties;
        if (properties) {
            for (const [key, propertySchema] of Object.entries(properties)) {
                if ((propertySchema === null || propertySchema === void 0 ? void 0 : propertySchema.readOnly) && (options === null || options === void 0 ? void 0 : options.skipReadOnlyFields))
                    continue;
                const propertyValue = valueToSchema(value === null || value === void 0 ? void 0 : value[key], propertySchema, options, field ? `${field}.${key}` : key);
                const propertyWasUndefined = ((_a = value === null || value === void 0 ? void 0 : value.hasOwnProperty) === null || _a === void 0 ? void 0 : _a.call(value, key)) && value[key] === undefined;
                if (propertyWasUndefined || propertyValue !== undefined) {
                    result[key] = propertyValue;
                }
            }
        }
        if (Object.keys(result).length === 0) {
            if (typeof value !== 'object' || Array.isArray(value) || !value)
                return value;
        }
        for (const [key, fieldValue] of Object.entries(value !== null && value !== void 0 ? value : {})) {
            if (properties === null || properties === void 0 ? void 0 : properties[key])
                continue;
            if ((options === null || options === void 0 ? void 0 : options.skipUnknownFields) && !(schema === null || schema === void 0 ? void 0 : schema.additionalProperties))
                continue;
            result[key] = fieldValue;
        }
        const valueHasKeys = typeof value === 'object' && value && Object.keys(value).length > 0;
        const resultHasKeys = typeof result === 'object' && result && Object.keys(result).length > 0;
        if (valueHasKeys && !resultHasKeys) {
            return undefined;
        }
        return result;
    }
}
function schemaIsScalar(schema) {
    if (!schema || typeof schema !== 'object') {
        return false;
    }
    return schema.type != 'array' && schema.type != 'object' && schema.type != 'null';
}
function schemaHasFixedValues(schema) {
    if (!schema) {
        return false;
    }
    return !!(schema.enum || schema.referenceRecords || schema.referenceCollection || schema.type === 'boolean');
}
function schemaAllowsCustomValue(schema) {
    return !schemaHasFixedValues(schema) || schema.allowCustom;
}
function schemaIsNumber(schema) {
    return schema && (schema.type === 'number' || schema.type === 'integer');
}
function schemaWithTitle(schema, title) {
    return typeof schema === 'object'
        ? {
            ...schema,
            title,
        }
        : schema;
}
function pickFieldsFromSchema(schema, fields) {
    const result = {
        type: 'object',
        properties: {},
    };
    if (!Array.isArray(fields)) {
        return result;
    }
    if ((schema === null || schema === void 0 ? void 0 : schema.type) === 'object' && (schema === null || schema === void 0 ? void 0 : schema.properties)) {
        for (const field of fields) {
            const fieldSchema = getSchemaByLocator(schema, field);
            if (fieldSchema) {
                setSchemaAtLocator(result, field, getSchemaByLocator(schema, field));
            }
        }
        if (schema.required) {
            const normalizedFields = fields.map(locatorToField);
            result.required = schema.required.filter((field) => normalizedFields.includes(field));
        }
    }
    return result;
}
function excludeReadOnlyFieldsFromSchema(schema) {
    var _a;
    if (!schema) {
        return schema;
    }
    let result = JSON.parse(JSON.stringify(schema));
    if (result.type === 'object') {
        const properties = {};
        for (const [key, propertySchema] of Object.entries((_a = result.properties) !== null && _a !== void 0 ? _a : {})) {
            if (!(propertySchema === null || propertySchema === void 0 ? void 0 : propertySchema.readOnly)) {
                properties[key] = excludeReadOnlyFieldsFromSchema(propertySchema);
            }
        }
        result = {
            ...result,
            properties,
        };
    }
    else if (result.type === 'array') {
        result.items = excludeReadOnlyFieldsFromSchema(result.items);
    }
    return result;
}
function excludeWriteOnlyFieldsFromSchema(schema) {
    var _a;
    if (!schema) {
        return schema;
    }
    let result = JSON.parse(JSON.stringify(schema));
    if (schema.type === 'object') {
        const properties = {};
        for (const [key, propertySchema] of Object.entries((_a = schema.properties) !== null && _a !== void 0 ? _a : {})) {
            if (!propertySchema.writeOnly) {
                properties[key] = excludeWriteOnlyFieldsFromSchema(propertySchema);
            }
        }
        result = {
            ...result,
            properties,
        };
    }
    else if (schema.type === 'array') {
        result.items = excludeWriteOnlyFieldsFromSchema(schema.items);
    }
    return result;
}
function excludeFieldsFromSchema(schema, fields) {
    if (!schema) {
        return schema;
    }
    if (!Array.isArray(fields)) {
        return schema;
    }
    let result = JSON.parse(JSON.stringify(schema));
    for (const field of fields !== null && fields !== void 0 ? fields : []) {
        if (field) {
            result = setSchemaAtLocator(result, field, undefined);
        }
    }
    return result;
}
function getRequiredFieldsFromSchema(schema) {
    var _a, _b;
    if (!schema) {
        return [];
    }
    if (schema.type === 'object') {
        const requiredFields = [...((_a = schema.required) !== null && _a !== void 0 ? _a : [])];
        for (const [key, propertySchema] of Object.entries((_b = schema.properties) !== null && _b !== void 0 ? _b : {})) {
            requiredFields.push(...getRequiredFieldsFromSchema(propertySchema).map((field) => `${key}.${field}`));
        }
        return requiredFields;
    }
    else {
        return [];
    }
}
function addRequiredFieldsToSchema(schema, fields) {
    if (!schema) {
        return undefined;
    }
    if (!Array.isArray(fields)) {
        return schema;
    }
    const newSchema = JSON.parse(JSON.stringify(schema));
    for (const field of fields) {
        if (!isValidLocator(field)) {
            continue;
        }
        const steps = locatorToSteps(field);
        const lastStep = steps.pop();
        if (lastStep.type === DataLocatorStepType.OBJECT_PROPERTY) {
            const objectLocator = stepsToLocator(steps);
            const objectSchema = getSchemaByLocator(newSchema, objectLocator);
            if (objectSchema) {
                objectSchema.required = [
                    ...(objectSchema.required || []),
                    lastStep.propertyName,
                ];
                setSchemaAtLocator(newSchema, objectLocator, objectSchema);
            }
        }
    }
    return newSchema;
}
function removeRequiredFieldsFromSchema(schema) {
    var _a;
    if (!schema)
        return schema;
    const newSchema = JSON.parse(JSON.stringify(schema));
    if (newSchema.type === 'object') {
        delete newSchema.required;
        for (const [key, propertySchema] of Object.entries((_a = newSchema.properties) !== null && _a !== void 0 ? _a : {})) {
            setSchemaAtLocator(newSchema, key, removeRequiredFieldsFromSchema(propertySchema));
        }
    }
    return newSchema;
}
function getMissingRequiredFields(schema, value) {
    if (!schema) {
        return [];
    }
    const requiredFields = getRequiredFieldsFromSchema(schema);
    const missingFields = [];
    const excuseCache = new Map();
    const requiredFieldSet = new Set(requiredFields);
    const isPathExcused = (path) => {
        if (excuseCache.has(path)) {
            return excuseCache.get(path);
        }
        if (!path.includes('.')) {
            excuseCache.set(path, false);
            return false;
        }
        const parentPath = path.substring(0, path.lastIndexOf('.'));
        const isParentOptional = !requiredFieldSet.has(parentPath);
        const isParentUndefined = getValueByLocator(value, parentPath) === undefined;
        if (isParentOptional && isParentUndefined) {
            excuseCache.set(path, true);
            return true;
        }
        const result = isPathExcused(parentPath);
        excuseCache.set(path, result);
        return result;
    };
    for (const field of requiredFields) {
        if (getValueByLocator(value, field) !== undefined) {
            continue;
        }
        if (isPathExcused(field)) {
            continue;
        }
        missingFields.push(field);
    }
    return missingFields;
}
function populateSchemaTitles(schema) {
    var _a;
    if (!schema) {
        return schema;
    }
    if (schema.type === 'object') {
        for (const field of Object.keys((_a = schema.properties) !== null && _a !== void 0 ? _a : {})) {
            const fieldSchema = getSchemaByLocator(schema, field);
            if (fieldSchema && !fieldSchema.title) {
                fieldSchema.title = getNameForLocator(schema, field);
                setSchemaAtLocator(schema, field, populateSchemaTitles(fieldSchema));
            }
        }
    }
    else if (schema.type === 'array') {
        if (schema.items) {
            schema.items = populateSchemaTitles(schema.items);
        }
    }
    return schema;
}
function generateExampleFromSchema(schema) {
    var _a;
    if (!schema || typeof schema !== 'object') {
        return undefined;
    }
    switch (schema.type) {
        case 'array':
            return schema.items ? [generateExampleFromSchema(schema.items)] : [];
        case 'object':
            const result = {};
            for (const [key, propertySchema] of Object.entries((_a = schema.properties) !== null && _a !== void 0 ? _a : {})) {
                result[key] = generateExampleFromSchema(propertySchema);
            }
            return result;
        default:
            return '';
    }
}
function isSchemaEmpty(schema) {
    if (!schema || typeof schema !== 'object' || !schema.type) {
        return true;
    }
    const isObject = schema.type === 'object';
    if (!isObject) {
        return false;
    }
    return !(schema.properties && Object.keys(schema.properties).length > 0);
}
function compressDataSchema(schema) {
    var _a, _b, _c;
    if (!schema || typeof schema !== 'object') {
        return schema;
    }
    if (schema.type === 'object') {
        const result = {
            type: 'object',
            properties: {},
        };
        for (const [key, propertySchema] of Object.entries((_a = schema.properties) !== null && _a !== void 0 ? _a : {})) {
            const compressedSchema = compressDataSchema(propertySchema);
            if (compressedSchema) {
                result.properties[key] = compressedSchema;
            }
        }
        return result;
    }
    else if (schema.type === 'array') {
        return {
            type: 'array',
            items: compressDataSchema(schema.items),
        };
    }
    else {
        return {
            type: schema.type,
            title: schema.title,
            description: schema.description
                ? `${(_b = schema.description) === null || _b === void 0 ? void 0 : _b.substring(0, 300)}${((_c = schema.description) === null || _c === void 0 ? void 0 : _c.length) > 50 ? '...' : ''}`
                : undefined,
        };
    }
}
function walkSchema(schema, callback, locator = '$') {
    if (!schema) {
        return undefined;
    }
    let result = schema;
    try {
        result = callback(result, locator);
        if (!result) {
            return undefined;
        }
        if (result.type === 'object') {
            if (result.properties) {
                for (const [propertyKey, propertySchema] of Object.entries(result.properties)) {
                    try {
                        const propertyResult = walkSchema(propertySchema, callback, makeObjectPropertyLocator(locator, propertyKey));
                        if (propertyResult) {
                            result.properties[propertyKey] = propertyResult;
                        }
                        else {
                            delete result.properties[propertyKey];
                        }
                    }
                    catch (e) {
                        if (e instanceof StopWalkError) {
                            continue;
                        }
                        else {
                            throw e;
                        }
                    }
                }
            }
        }
        else if (result.type === 'array') {
            if (result.items) {
                result.items = walkSchema(schema.items, callback, `${locator}[0]`);
            }
        }
        return result;
    }
    catch (e) {
        if (e instanceof StopWalkError) {
            return result;
        }
        else {
            throw e;
        }
    }
}
class StopWalkError extends Error {
}

const OPERATORS = {
    is: {
        name: 'is',
        handler: (value, operand) => value == operand,
        applicableToSchema: (schema) => schemaIsScalar(schema) && schema.type !== 'boolean',
        getOperandSchema: (schema) => schema,
    },
    is_not: {
        name: 'is not',
        handler: (value, operand) => value != operand,
        applicableToSchema: (schema) => schemaIsScalar(schema) && schema.type !== 'boolean',
        getOperandSchema: (schema) => schema,
    },
    is_one_of: {
        name: 'is one of',
        handler: (value, operand) => Array.isArray(operand) && operand.includes(value),
        applicableToSchema: (schema) => schemaIsScalar(schema) && schema.type !== 'boolean',
        getOperandSchema: (schema) => ({ type: 'array', items: schema }),
    },
    is_not_one_of: {
        name: 'is not one of',
        handler: (value, operand) => !Array.isArray(operand) || !operand.includes(value),
        applicableToSchema: (schema) => schemaIsScalar(schema) && schema.type !== 'boolean',
        getOperandSchema: (schema) => ({ type: 'array', items: schema }),
    },
    is_true: {
        name: 'is true',
        handler: (value) => !!value,
        applicableToSchema: (schema) => schema.type === 'boolean',
        getOperandSchema: () => null,
    },
    is_false: {
        name: 'is false',
        handler: (value) => value === false,
        applicableToSchema: (schema) => schema.type === 'boolean',
        getOperandSchema: () => null,
    },
    contains: {
        name: 'contains',
        handler: (value, operand) => (value ? value.toString().includes(operand) : false),
        applicableToSchema: (schema) => (schema === null || schema === void 0 ? void 0 : schema.type) === 'string' && schemaAllowsCustomValue(schema),
        getOperandSchema: () => ({ type: 'string' }),
    },
    does_not_contain: {
        name: 'does not contain',
        handler: (value, operand) => (value ? !value.toString().includes(operand) : false),
        applicableToSchema: (schema) => (schema === null || schema === void 0 ? void 0 : schema.type) === 'string' && schemaAllowsCustomValue(schema),
        getOperandSchema: () => ({ type: 'string' }),
    },
    array_includes: {
        name: 'includes',
        handler: (value, operand) => Array.isArray(value) && value.indexOf(operand) !== -1,
        applicableToSchema: (schema) => (schema === null || schema === void 0 ? void 0 : schema.type) === 'array' && schema.items,
        getOperandSchema: (schema) => schema.items,
    },
    array_does_not_include: {
        name: 'does not include',
        handler: (value, operand) => !Array.isArray(value) || value.indexOf(operand) === -1,
        applicableToSchema: (schema) => (schema === null || schema === void 0 ? void 0 : schema.type) === 'array' && schema.items,
        getOperandSchema: (schema) => schema.items,
    },
    less_than: {
        name: 'less than',
        handler: (value, operand) => {
            return isNaN(parseFloat(value)) || isNaN(parseFloat(operand)) ? false : parseFloat(value) < parseFloat(operand);
        },
        applicableToSchema: (schema) => schemaIsNumber(schema),
        getOperandSchema: () => ({ type: 'number' }),
    },
    less_than_or_equal: {
        name: 'less than or equal',
        handler: (value, operand) => {
            return isNaN(parseFloat(value)) || isNaN(parseFloat(operand)) ? false : parseFloat(value) <= parseFloat(operand);
        },
        applicableToSchema: (schema) => schemaIsNumber(schema),
        getOperandSchema: () => ({ type: 'number' }),
    },
    more_than: {
        name: 'more than',
        handler: (value, operand) => {
            return isNaN(parseFloat(value)) || isNaN(parseFloat(operand)) ? false : parseFloat(value) > parseFloat(operand);
        },
        applicableToSchema: (schema) => schemaIsNumber(schema),
        getOperandSchema: () => ({ type: 'number' }),
    },
    more_than_or_equal: {
        name: 'more than or equal',
        handler: (value, operand) => {
            return isNaN(parseFloat(value)) || isNaN(parseFloat(operand)) ? false : parseFloat(value) >= parseFloat(operand);
        },
        applicableToSchema: (schema) => schemaIsNumber(schema),
        getOperandSchema: () => ({ type: 'number' }),
    },
    is_not_empty: {
        name: 'is not empty',
        handler: (value) => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            else if (typeof value === 'object') {
                return value !== null && Object.keys(value).length > 0;
            }
            else {
                return value !== undefined && value !== '';
            }
        },
        applicableToSchema: () => true,
        getOperandSchema: () => null,
    },
    is_empty: {
        name: 'is empty',
        handler: (value) => {
            if (Array.isArray(value)) {
                return value.length === 0;
            }
            else if (typeof value === 'object') {
                return value === null || Object.keys(value).length === 0;
            }
            else {
                return value === undefined || value === '';
            }
        },
        applicableToSchema: () => true,
        getOperandSchema: () => null,
    },
};

function backwardCompatibleFilterMatch(filter, variables) {
    var _a;
    if (Array.isArray(filter)) {
        return doesMatchFilter(variables, filter);
    }
    else {
        return (_a = buildData(filter, variables)) !== null && _a !== void 0 ? _a : true;
    }
}
function doesMatchFilter(data, filter) {
    if (filter && Array.isArray(filter)) {
        for (const condition of filter) {
            const operator = OPERATORS[condition.operator];
            if (!operator) {
                continue;
            }
            let fieldValues = getFilterFieldValuesByLocator(data, condition.field);
            if (!(fieldValues === null || fieldValues === void 0 ? void 0 : fieldValues.length)) {
                fieldValues = [undefined];
            }
            const conditionMatches = fieldValues.some((fieldValue) => operator.handler(fieldValue, condition.value));
            if (!conditionMatches) {
                return false;
            }
        }
        return true;
    }
    else {
        return true;
    }
}
class DataFilterCondition {
    constructor(field, operator, value) {
        this.field = field;
        this.operator = operator;
        this.value = value;
    }
}
function getOperatorsBySchema(schema) {
    if (!schema || typeof schema !== 'object') {
        return [];
    }
    const results = [];
    for (const [type, operator] of Object.entries(OPERATORS)) {
        if (operator.applicableToSchema(schema)) {
            results.push({
                type,
                name: operator.name,
                operandSchema: operator.getOperandSchema(schema),
            });
        }
    }
    return results;
}
function getFilterFieldValuesByLocator(data, locator) {
    const locatorSteps = locatorToSteps(locator);
    const result = [];
    let curValue = data;
    for (let idx = 0; idx < locatorSteps.length; ++idx) {
        if (curValue === null || curValue === undefined) {
            return [];
        }
        const locatorStep = locatorSteps[idx];
        switch (locatorStep.type) {
            case DataLocatorStepType.ARRAY_ITEM:
                if (Array.isArray(curValue)) {
                    curValue = curValue[locatorStep.index];
                    break;
                }
                else {
                    return [];
                }
            case DataLocatorStepType.OBJECT_PROPERTY:
                if (Array.isArray(curValue)) {
                    for (const val of curValue) {
                        result.push(...getFilterFieldValuesByLocator(val, locatorSteps.slice(idx)));
                    }
                    return result;
                }
                else {
                    curValue = curValue[locatorStep.propertyName];
                    break;
                }
        }
    }
    if (curValue !== null && curValue !== undefined) {
        result.push(curValue);
    }
    return result;
}

class DataBuilderFormulaCase extends DataBuilderFormulaBase {
    constructor(value) {
        super(DataBuilderFormulaType.CASE, value);
        if (!value && !Array.isArray(value) && typeof value !== 'object') {
            this.value = {
                cases: [],
                default: undefined,
            };
        }
        else if (Array.isArray(value)) {
            this.value = {
                cases: value,
                default: undefined,
            };
        }
        else if (typeof value === 'object' && Array.isArray(value === null || value === void 0 ? void 0 : value.cases)) {
            this.value = value;
        }
    }
    getValue(variables) {
        var _a;
        if (!this.isValueValid())
            return undefined;
        for (const item of this.value.cases) {
            if (!item || typeof item !== 'object')
                continue;
            let filterMatched = true;
            if (item.filter) {
                filterMatched = backwardCompatibleFilterMatch(item.filter, variables);
            }
            if (filterMatched) {
                return buildValue(item.value, variables);
            }
        }
        return (_a = buildValue(this.value.default, variables)) !== null && _a !== void 0 ? _a : undefined;
    }
    async resolveValue(params) {
        var _a, _b;
        if (!this.isValueValid())
            return undefined;
        for (const item of this.value.cases) {
            if (!item || typeof item !== 'object')
                continue;
            let filterMatched = true;
            if (item.filter) {
                filterMatched = (_a = (await __resolveValue(item.filter, params))) !== null && _a !== void 0 ? _a : true;
            }
            if (filterMatched) {
                return await __resolveValue(item.value, params);
            }
        }
        return (_b = (await __resolveValue(this.value.default, params))) !== null && _b !== void 0 ? _b : undefined;
    }
    getSchema(variablesSchema) {
        if (!this.isValueValid())
            return undefined;
        const schemas = this.value.cases
            .map((item) => item.value)
            .filter((value) => value !== undefined)
            .map((value) => getSchemaFromValue(value, variablesSchema));
        if (this.value.default) {
            schemas.push(getSchemaFromValue(this.value.default, variablesSchema));
        }
        return mergeSchemas(schemas);
    }
    isValueValid() {
        var _a;
        return this.value && typeof this.value === 'object' && Array.isArray((_a = this.value) === null || _a === void 0 ? void 0 : _a.cases);
    }
    transformVars(transformations) {
        const transformedCases = this.value.cases
            .map((item) => transformVars(item, transformations))
            .filter((item) => (item === null || item === void 0 ? void 0 : item.filter) !== undefined);
        const transformedDefault = transformVars(this.value.default, transformations);
        if ((transformedCases === null || transformedCases === void 0 ? void 0 : transformedCases.length) > 0) {
            const result = {
                $case: {
                    cases: transformedCases,
                },
            };
            if (transformedDefault !== undefined) {
                result.$case.default = transformedDefault;
            }
            return result;
        }
        else {
            return transformVars(this.value.default, transformations);
        }
    }
}

const DEFAULT_DELIMITER = '';
class DataBuilderFormulaConcat extends DataBuilderFormulaBase {
    constructor(options) {
        var _a, _b;
        super(DataBuilderFormulaType.CONCAT, options);
        this.options = options;
        this.delimiter = DEFAULT_DELIMITER;
        this.values = [];
        if (!options) {
            return;
        }
        this.values = Array.isArray(options) ? options : ((_a = options === null || options === void 0 ? void 0 : options.values) !== null && _a !== void 0 ? _a : []);
        this.delimiter = (_b = options === null || options === void 0 ? void 0 : options.delimiter) !== null && _b !== void 0 ? _b : DEFAULT_DELIMITER;
    }
    getValue(variables) {
        const processedValues = buildValue(this.values, variables);
        if (!Array.isArray(processedValues) || !processedValues.length) {
            return undefined;
        }
        const processedDelimiter = buildValue(this.delimiter, variables);
        return processedValues
            .filter((v) => v !== null && v !== undefined && v.toString() !== '')
            .join(String(processedDelimiter));
    }
    async resolveValue(params) {
        const processedValues = await __resolveValue(this.values, params);
        if (!Array.isArray(processedValues) || !processedValues.length) {
            return undefined;
        }
        const processedDelimiter = await __resolveValue(this.delimiter, params);
        return processedValues
            .filter((v) => v !== null && v !== undefined && v.toString() !== '')
            .join(String(processedDelimiter));
    }
    getSchema() {
        return { type: 'string' };
    }
    toString(variablesSchema) {
        const valueStrings = this.values.map((value) => valueToString(value, variablesSchema));
        if (valueStrings.length) {
            return `Concatenate(${valueStrings.join(', ')})`;
        }
        else {
            return `Concatenate()`;
        }
    }
}

class DataBuilderFormulaCopy extends DataBuilderFormulaBase {
    constructor(locator) {
        super(DataBuilderFormulaType.COPY, locator);
        this.locator = locator;
    }
    getValue(variables) {
        return {
            $copy: buildValue(this.locator, variables),
        };
    }
    async resolveValue(params) {
        return {
            $copy: await __resolveValue(this.locator, params),
        };
    }
    getSchema() {
        return null;
    }
}

class DataSchemaRef extends DataBuilderFormulaBase {
    constructor(value) {
        super(DataBuilderFormulaType.DATA_SCHEMA_REF, value);
        this.value = value;
    }
    getValue() {
        return undefined;
    }
    async resolveValue(params) {
        var _a;
        const args = await __resolveValue(this.args, params);
        if ((args === null || args === void 0 ? void 0 : args.type) === 'data-collection') {
            return this.resolveDataCollection(args, params);
        }
        else if (((_a = this.args) === null || _a === void 0 ? void 0 : _a.type) === 'internal-data-schema') {
            return this.resolveInternalDataSchema(args, params);
        }
        else {
            return undefined;
        }
    }
    async resolveDataCollection(args, params) {
        if (!params.getDataCollection) {
            return undefined;
        }
        const spec = await params.getDataCollection(args.key, args.parameters);
        return spec.fieldsSchema;
    }
    async resolveInternalDataSchema(args, params) {
        if (!params.getInternalDataSchema) {
            return undefined;
        }
        return params.getInternalDataSchema(args.key);
    }
    getSchema() {
        return {
            type: 'object',
        };
    }
}

class DomainFromEmail extends DataBuilderFormulaBase {
    constructor(value) {
        super(DataBuilderFormulaType.DOMAIN_FROM_EMAIL, value);
        this.value = value;
    }
    getValue(variables) {
        const value = buildValue(this.value, variables);
        if (typeof value !== 'string') {
            return undefined;
        }
        const emailParts = value.split('@');
        if (emailParts.length !== 2) {
            return undefined;
        }
        return emailParts[1];
    }
    async resolveValue(params) {
        const value = await __resolveValue(this.value, params);
        if (typeof value !== 'string') {
            return undefined;
        }
        const emailParts = value.split('@');
        if (emailParts.length !== 2) {
            return undefined;
        }
        return emailParts[1];
    }
    getSchema() {
        return { type: 'string' };
    }
}

class Eval extends DataBuilderFormulaBase {
    constructor(args, formulaValue) {
        super(DataBuilderFormulaType.EVAL, args);
        this.value = args;
        const keys = Object.keys(formulaValue);
        this.operatorKey = keys[1];
        this.operand = formulaValue[this.operatorKey];
    }
    getAvailableOperators(variablesSchema) {
        const operators = [];
        const valueSchema = getSchemaFromValue(this.value, variablesSchema);
        for (const key in OPERATOR_SPECS) {
            const operatorSpec = OPERATOR_SPECS[key];
            if (operatorSpec.applicableToSchema(valueSchema)) {
                const operandSchema = operatorSpec.getOperandSchema(valueSchema);
                operators.push({
                    key,
                    name: operatorSpec.name,
                    operandSchema: operandSchema,
                    hasOperand: !!operandSchema,
                });
            }
        }
        return operators;
    }
    getOperator(variablesSchema) {
        const valueSchema = getSchemaFromValue(this.value, variablesSchema);
        const operatorSpec = OPERATOR_SPECS[this.operatorKey];
        if (operatorSpec) {
            const operandSchema = operatorSpec.getOperandSchema(valueSchema);
            return {
                key: this.operatorKey,
                name: operatorSpec.name,
                operandSchema: operandSchema,
                hasOperand: !!operandSchema,
            };
        }
        else {
            return null;
        }
    }
    getValue(variables) {
        const value = buildValue(this.value, variables);
        const operatorSpec = OPERATOR_SPECS[this.operatorKey];
        if (operatorSpec) {
            const operand = buildValue(this.operand, variables);
            return operatorSpec.handler(value, operand);
        }
        else {
            return true;
        }
    }
    async resolveValue(params) {
        const value = await __resolveValue(this.value, params);
        const operatorSpec = OPERATOR_SPECS[this.operatorKey];
        if (operatorSpec) {
            const operand = await __resolveValue(this.operand, params);
            return operatorSpec.handler(value, operand);
        }
        else {
            return true;
        }
    }
    getSchema() {
        return { type: 'boolean' };
    }
    toObject() {
        const object = {
            $eval: this.value,
        };
        if (this.operatorKey) {
            object[this.operatorKey] = this.operand;
        }
        return object;
    }
    transformVars(transformations) {
        const transformedValue = buildValue(this.value, transformations);
        if (transformedValue !== undefined) {
            const transformedOperand = buildValue(this.operand, transformations);
            return {
                $eval: transformedValue,
                [this.operatorKey]: transformedOperand,
            };
        }
        else {
            return undefined;
        }
    }
}
function asLiteralOperandSchema(schema) {
    if (!schema)
        return schema;
    const { readOnly: _readOnly, ...rest } = schema;
    return rest;
}
const OPERATOR_SPECS = {
    is: {
        name: 'is',
        handler: (value, operand) => value == operand,
        applicableToSchema: (schema) => schemaIsScalar(schema),
        getOperandSchema: (schema) => asLiteralOperandSchema(schema),
    },
    isNot: {
        name: 'is not',
        handler: (value, operand) => value != operand,
        applicableToSchema: (schema) => schemaIsScalar(schema),
        getOperandSchema: (schema) => asLiteralOperandSchema(schema),
    },
    oneOf: {
        name: 'is one of',
        handler: (value, operand) => Array.isArray(operand) && operand.includes(value),
        applicableToSchema: (schema) => schemaIsScalar(schema) && schema.type !== 'boolean',
        getOperandSchema: (schema) => ({ type: 'array', items: asLiteralOperandSchema(schema) }),
    },
    noneOf: {
        name: 'is not one of',
        handler: (value, operand) => !Array.isArray(operand) || !operand.includes(value),
        applicableToSchema: (schema) => schemaIsScalar(schema) && schema.type !== 'boolean',
        getOperandSchema: (schema) => ({ type: 'array', items: asLiteralOperandSchema(schema) }),
    },
    contains: {
        name: 'contains',
        handler: (value, operand) => {
            var _a;
            return value
                ? Array.isArray(value)
                    ? value.includes(operand)
                    : value.toString().toLowerCase().includes((_a = operand === null || operand === void 0 ? void 0 : operand.toString()) === null || _a === void 0 ? void 0 : _a.toLowerCase())
                : false;
        },
        applicableToSchema: (schema) => ((schema === null || schema === void 0 ? void 0 : schema.type) === 'string' && schemaAllowsCustomValue(schema)) || (schema === null || schema === void 0 ? void 0 : schema.type) === 'array',
        getOperandSchema: (schema) => ((schema === null || schema === void 0 ? void 0 : schema.type) === 'array' ? schema.items : { type: 'string' }),
    },
    doesNotContain: {
        name: 'does not contain',
        handler: (value, operand) => {
            var _a;
            return value
                ? Array.isArray(value)
                    ? !value.includes(operand)
                    : !value.toString().toLowerCase().includes((_a = operand === null || operand === void 0 ? void 0 : operand.toString()) === null || _a === void 0 ? void 0 : _a.toLowerCase())
                : true;
        },
        applicableToSchema: (schema) => ((schema === null || schema === void 0 ? void 0 : schema.type) === 'string' && schemaAllowsCustomValue(schema)) || (schema === null || schema === void 0 ? void 0 : schema.type) === 'array',
        getOperandSchema: (schema) => ((schema === null || schema === void 0 ? void 0 : schema.type) === 'array' ? schema.items : { type: 'string' }),
    },
    lt: {
        name: 'less than',
        handler: (value, operand) => {
            return isNaN(parseFloat(value)) || isNaN(parseFloat(operand)) ? false : parseFloat(value) < parseFloat(operand);
        },
        applicableToSchema: (schema) => schemaIsNumber(schema),
        getOperandSchema: () => ({ type: 'number' }),
    },
    lte: {
        name: 'less than or equal',
        handler: (value, operand) => {
            return isNaN(parseFloat(value)) || isNaN(parseFloat(operand)) ? false : parseFloat(value) <= parseFloat(operand);
        },
        applicableToSchema: (schema) => schemaIsNumber(schema),
        getOperandSchema: () => ({ type: 'number' }),
    },
    gt: {
        name: 'more than',
        handler: (value, operand) => {
            return isNaN(parseFloat(value)) || isNaN(parseFloat(operand)) ? false : parseFloat(value) > parseFloat(operand);
        },
        applicableToSchema: (schema) => schemaIsNumber(schema),
        getOperandSchema: () => ({ type: 'number' }),
    },
    gte: {
        name: 'more than or equal',
        handler: (value, operand) => {
            return isNaN(parseFloat(value)) || isNaN(parseFloat(operand)) ? false : parseFloat(value) >= parseFloat(operand);
        },
        applicableToSchema: (schema) => schemaIsNumber(schema),
        getOperandSchema: () => ({ type: 'number' }),
    },
    isNotEmpty: {
        name: 'is not empty',
        handler: (value) => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            else if (typeof value === 'object') {
                return value !== null && Object.keys(value).length > 0;
            }
            else {
                return value !== undefined && value !== '';
            }
        },
        applicableToSchema: () => true,
        getOperandSchema: () => null,
    },
    isEmpty: {
        name: 'is empty',
        handler: (value) => {
            if (Array.isArray(value)) {
                return value.length === 0;
            }
            else if (typeof value === 'object') {
                return value === null || Object.keys(value).length === 0;
            }
            else {
                return value === undefined || value === '';
            }
        },
        applicableToSchema: () => true,
        getOperandSchema: () => null,
    },
};

function parseDatetime(str) {
    if (typeof process !== 'undefined') {
        process.env.TZ = 'UTC';
    }
    const timestamp = Date.parse(str);
    if (!isNaN(timestamp)) {
        return new Date(timestamp);
    }
    else {
        return null;
    }
}
function getDatetimeParts(date, timezone) {
    if (!timezone) {
        const iso = date.toISOString();
        return { date: iso.substring(0, 10), time: iso.substring(11, 19) };
    }
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    })
        .formatToParts(date)
        .map(({ type, value }) => [type, value]));
    return {
        date: `${parts.year}-${parts.month}-${parts.day}`,
        time: `${parts.hour}:${parts.minute}:${parts.second}`,
    };
}

function parseDatetimeFormulaArgs(args) {
    if (args && typeof args === 'object' && !Array.isArray(args) && 'value' in args && 'timezone' in args) {
        return { value: args.value, timezone: args.timezone };
    }
    return { value: args };
}

class DataBuilderFormulaExtractDate extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.EXTRACT_DATE, args);
        ({ value: this.value, timezone: this.timezone } = parseDatetimeFormulaArgs(args));
    }
    getValue(variables) {
        return this.extract(buildValue(this.value, variables), buildValue(this.timezone, variables));
    }
    async resolveValue(params) {
        const value = await __resolveValue(this.value, params);
        const timezone = (await __resolveValue(this.timezone, params));
        return this.extract(String(value), timezone);
    }
    getSchema() {
        return {
            type: 'string',
            format: 'date',
        };
    }
    extract(value, timezone) {
        const date = parseDatetime(value);
        return date ? getDatetimeParts(date, timezone || undefined).date : undefined;
    }
}

class DataBuilderFormulaExtractTime extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.EXTRACT_TIME, args);
        ({ value: this.value, timezone: this.timezone } = parseDatetimeFormulaArgs(args));
    }
    getValue(variables) {
        return this.extract(buildValue(this.value, variables), buildValue(this.timezone, variables));
    }
    async resolveValue(params) {
        const value = await __resolveValue(this.value, params);
        const timezone = (await __resolveValue(this.timezone, params));
        return this.extract(String(value), timezone);
    }
    getSchema() {
        return {
            type: 'string',
            format: 'time',
        };
    }
    extract(value, timezone) {
        const date = parseDatetime(value);
        return date ? getDatetimeParts(date, timezone || undefined).time : undefined;
    }
}

class DataBuilderFormulaFindAppRecordId extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.FIND_APP_RECORD_ID, args);
        this.args = args;
    }
    getValue(variables) {
        return {
            $findAppRecordId: buildValue(this.args, variables),
        };
    }
    async resolveValue(params) {
        if (!params.resolveImportDataLink) {
            return { $findAppRecordId: await __resolveValue(this.args, params) };
        }
        const processedArgs = await __resolveValue(this.args, params);
        if (typeof processedArgs !== 'object' || !processedArgs) {
            return undefined;
        }
        const { dataLinkTable, recordId } = processedArgs;
        if (!(dataLinkTable === null || dataLinkTable === void 0 ? void 0 : dataLinkTable.key) || !recordId) {
            return undefined;
        }
        return params.resolveImportDataLink({
            dataLinkTableKey: dataLinkTable.key,
            recordId,
        });
    }
    getSchema() {
        return {
            type: 'string',
        };
    }
}

class DataBuilderFormulaFindExternalRecordId extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.FIND_EXTERNAL_RECORD_ID, args);
        this.args = args;
    }
    getValue(variables) {
        return {
            $findExternalRecordId: buildValue(this.args, variables),
        };
    }
    async resolveValue(params) {
        if (!params.resolveExportDataLink) {
            return { $findExternalRecordId: await __resolveValue(this.args, params) };
        }
        const processedArgs = await __resolveValue(this.args, params);
        if (typeof processedArgs !== 'object' || !processedArgs) {
            return undefined;
        }
        const { dataLinkTable, recordId } = processedArgs;
        if (!(dataLinkTable === null || dataLinkTable === void 0 ? void 0 : dataLinkTable.key) || !recordId) {
            return undefined;
        }
        return params.resolveExportDataLink({
            dataLinkTableKey: dataLinkTable.key,
            recordId,
        });
    }
    getSchema() {
        return {
            type: 'string',
        };
    }
}

class DataBuilderFormulaFirstName extends DataBuilderFormulaBase {
    constructor(value) {
        super(DataBuilderFormulaType.FIRST_NAME, value);
        this.value = value;
    }
    getValue(variables) {
        var _a;
        const value = buildValue(this.value, variables);
        const { firstName, lastName } = humanparser.parseName((_a = value === null || value === void 0 ? void 0 : value.toString()) !== null && _a !== void 0 ? _a : '');
        if (lastName) {
            return firstName;
        }
        else {
            return undefined;
        }
    }
    async resolveValue(params) {
        const value = await __resolveValue(this.value, params);
        const { firstName, lastName } = humanparser.parseName(String(value !== null && value !== void 0 ? value : ''));
        if (lastName) {
            return firstName;
        }
        else {
            return undefined;
        }
    }
    getSchema() {
        return { type: 'string' };
    }
}

class FirstNotEmpty extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.FIRST_NOT_EMPTY, args);
    }
    getValue(variables) {
        const values = buildValue(this.args, variables);
        if (!Array.isArray(values)) {
            return undefined;
        }
        for (const value of values) {
            if (value !== undefined && value !== null) {
                return value;
            }
        }
        return undefined;
    }
    async resolveValue(params) {
        const values = await __resolveValue(this.args, params);
        if (!Array.isArray(values)) {
            return undefined;
        }
        for (const value of values) {
            if (value !== undefined && value !== null) {
                return value;
            }
        }
        return undefined;
    }
    getSchema(variablesSchema) {
        if (Array.isArray(this.args)) {
            return buildDataSchema(this.args[0], variablesSchema);
        }
        else {
            return undefined;
        }
    }
}

class FormulaFormula extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.FORMULA, args);
    }
    getValue(variables) {
        const varLocators = getVariableLocators(this.args);
        const result = this.args;
        for (const locator of varLocators) {
            const valueAtLocator = buildValue(getValueAtLocator(result, locator), variables);
            setValueAtLocator(result, locator, valueAtLocator);
        }
        return result;
    }
    async resolveValue(params) {
        const varLocators = getVariableLocators(this.args);
        const result = this.args;
        for (const locator of varLocators) {
            const valueAtLocator = await __resolveValue(getValueAtLocator(result, locator), params);
            setValueAtLocator(result, locator, valueAtLocator);
        }
        return result;
    }
    getSchema() {
        return undefined;
    }
    transformVars(transformations) {
        return transformVars(this.args, transformations);
    }
}

class DataBuilderFormulaIterate extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.ITERATE, args);
        this.args = args;
        this.source = args === null || args === void 0 ? void 0 : args.source;
        this.item = args === null || args === void 0 ? void 0 : args.item;
    }
    getValue(variables) {
        const source = buildValue(this.source, variables);
        if (!Array.isArray(source)) {
            return undefined;
        }
        const result = [];
        for (let index = 0; index < source.length; index++) {
            const newItem = buildValue(this.item, {
                item: source[index],
                parent: variables,
                index,
            });
            if (newItem) {
                result.push(newItem);
            }
        }
        if (result.length > 0) {
            return result;
        }
        else {
            return undefined;
        }
    }
    async resolveValue(params) {
        const source = await __resolveValue(this.source, params);
        if (!Array.isArray(source)) {
            return undefined;
        }
        const result = [];
        for (let index = 0; index < source.length; index++) {
            const newItem = await __resolveValue(this.item, {
                ...params,
                variables: {
                    item: source[index],
                    parent: params.variables,
                    index,
                },
            });
            if (newItem) {
                result.push(newItem);
            }
        }
        if (result.length > 0) {
            return result;
        }
        else {
            return undefined;
        }
    }
    getSchema(variablesSchema) {
        const sourceSchema = buildDataSchema(this.source, variablesSchema);
        const itemSchema = sourceSchema === null || sourceSchema === void 0 ? void 0 : sourceSchema.items;
        const itemVariablesSchema = {
            type: 'object',
            properties: {
                parent: variablesSchema,
                item: itemSchema,
                index: { type: 'number' },
            },
        };
        return {
            type: 'array',
            items: buildDataSchema(this.item, itemVariablesSchema),
        };
    }
    getVariableLocators() {
        const sourceLocators = getVariableLocators(this.source);
        return sourceLocators.map((locator) => `$.$iterate.source${locator.replace(/^\$/, '')}`);
    }
    transformVars(transformations) {
        const source = transformVars(this.source, transformations);
        if (source !== undefined) {
            return {
                $iterate: {
                    ...this.args,
                    source,
                },
            };
        }
        else {
            return undefined;
        }
    }
}

class JsonataFormula extends DataBuilderFormulaBase {
    constructor(expression) {
        super(DataBuilderFormulaType.JSONATA, expression);
        this.expression = expression;
    }
    getValue(variables) {
        if (!this.expression)
            return undefined;
        try {
            const expression = jsonata(buildValue(this.expression, variables));
            const value = expression.evaluate(variables);
            return value;
        }
        catch (_e) {
            return undefined;
        }
    }
    async resolveValue(params) {
        if (!this.expression)
            return undefined;
        try {
            const expression = jsonata(await __resolveValue(this.expression, params));
            const value = expression.evaluate(params.variables);
            return value;
        }
        catch (_e) {
            return undefined;
        }
    }
    getSchema() {
        return getSchemaFromValue(this.getValue(null), null, true);
    }
}

class DataBuilderFormulaLastName extends DataBuilderFormulaBase {
    constructor(value) {
        super(DataBuilderFormulaType.LAST_NAME, value);
        this.value = value;
    }
    getValue(variables) {
        var _a;
        const value = buildValue(this.value, variables);
        const { firstName, lastName } = humanparser.parseName((_a = value === null || value === void 0 ? void 0 : value.toString()) !== null && _a !== void 0 ? _a : '');
        if (lastName) {
            return lastName;
        }
        if (firstName) {
            return firstName;
        }
        return undefined;
    }
    async resolveValue(params) {
        const value = await __resolveValue(this.value, params);
        const { firstName, lastName } = humanparser.parseName(String(value !== null && value !== void 0 ? value : ''));
        if (lastName) {
            return lastName;
        }
        if (firstName) {
            return firstName;
        }
        return undefined;
    }
    getSchema() {
        return { type: 'string' };
    }
    toString(variablesSchema) {
        return `ExtractLastName(${valueToString(this.value, variablesSchema)})`;
    }
}

class DataBuilderFormulaLookup extends DataBuilderFormulaBase {
    constructor(value) {
        super(DataBuilderFormulaType.LOOKUP, value);
        this.value = value;
    }
    getValue(variables) {
        return {
            $lookup: buildValue(this.value, variables),
        };
    }
    async resolveValue(params) {
        if (!params.resolveLookup) {
            return { $lookup: await __resolveValue(this.value, params) };
        }
        const lookupArgs = await __resolveValue(this.value, params);
        return params.resolveLookup({
            lookup: lookupArgs,
            schema: params.schema,
            fullValue: params.fullValue,
        });
    }
    getSchema() {
        return {
            type: 'string',
        };
    }
}

class DataBuilderFormulaMap extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.MAP, args);
        this.args = args;
    }
    getValue(variables) {
        if (!this.args || typeof this.args !== 'object') {
            return;
        }
        const value = buildValue(this.args.value, variables);
        if (Array.isArray(this.args.mapping)) {
            for (const mappingItem of this.args.mapping) {
                const from = buildValue(mappingItem.from, variables);
                if (deepEqual(value, from) || value == from) {
                    return buildValue(mappingItem.to, variables);
                }
            }
        }
        return buildValue(this.args.default, variables);
    }
    async resolveValue(params) {
        if (!this.args || typeof this.args !== 'object') {
            return;
        }
        const value = await __resolveValue(this.args.value, params);
        if (Array.isArray(this.args.mapping)) {
            for (const mappingItem of this.args.mapping) {
                const from = await __resolveValue(mappingItem.from, params);
                if (deepEqual(value, from) || value == from) {
                    return await __resolveValue(mappingItem.to, params);
                }
            }
        }
        return await __resolveValue(this.args.default, params);
    }
    getSchema(variablesSchema) {
        var _a;
        return getSchemaFromValue((_a = this.args) === null || _a === void 0 ? void 0 : _a.value, variablesSchema);
    }
}

class MergeObjects extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.MERGE_OBJECTS, args);
    }
    getValue(variables) {
        const objects = buildValue(this.args, variables);
        if (!Array.isArray(objects)) {
            return undefined;
        }
        const result = {};
        for (const object of objects) {
            if (object && typeof object === 'object') {
                Object.assign(result, object);
            }
        }
        return Object.keys(result).length > 0 ? result : undefined;
    }
    async resolveValue(params) {
        const objects = await __resolveValue(this.args, params);
        if (!Array.isArray(objects)) {
            return undefined;
        }
        const result = {};
        for (const object of objects) {
            if (object && typeof object === 'object') {
                Object.assign(result, object);
            }
        }
        return Object.keys(result).length > 0 ? result : undefined;
    }
    getSchema(variablesSchema) {
        if (!Array.isArray(this.args)) {
            return { type: 'object' };
        }
        const schemas = this.args.map((arg) => buildDataSchema(arg, variablesSchema));
        const result = {
            type: 'object',
            properties: {},
        };
        for (const schema of schemas) {
            if ((schema === null || schema === void 0 ? void 0 : schema.type) === 'object') {
                Object.assign(result.properties, schema.properties);
            }
        }
        return result;
    }
}

class Or extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.OR, args);
    }
    getValue(variables) {
        const args = buildValue(this.args, variables);
        if (Array.isArray(args) && args.some((arg) => arg !== null && arg !== undefined)) {
            return args.some((arg) => !!arg);
        }
        else {
            return undefined;
        }
    }
    async resolveValue(params) {
        const args = await __resolveValue(this.args, params);
        if (Array.isArray(args) && args.some((arg) => arg !== null && arg !== undefined)) {
            return args.some((arg) => !!arg);
        }
        else {
            return undefined;
        }
    }
    getSchema() {
        return { type: 'boolean' };
    }
}

class DataBuilderFormulaPlain extends DataBuilderFormulaBase {
    constructor(value) {
        super(DataBuilderFormulaType.PLAIN, value);
        this.value = value;
    }
    getValue() {
        return this.value;
    }
    async resolveValue(_params) {
        return this.value;
    }
    getSchema() {
        return getSchemaFromValue(this.value, null, true);
    }
}

class DataBuilderFormulaRecord extends DataBuilderFormulaBase {
    constructor(record) {
        super(DataBuilderFormulaType.RECORD, record);
        this.record = record;
    }
    getValue() {
        var _a;
        return (_a = this.record) === null || _a === void 0 ? void 0 : _a.id;
    }
    async resolveValue(_params) {
        var _a;
        return (_a = this.record) === null || _a === void 0 ? void 0 : _a.id;
    }
    getSchema() {
        return {
            type: 'string',
        };
    }
}

class DataBuilderFormulaTpl extends DataBuilderFormulaBase {
    constructor(args) {
        super(DataBuilderFormulaType.TPL, args);
        this.args = args;
    }
    getValue(variables) {
        var _a, _b, _c;
        let result = (_a = this.args) === null || _a === void 0 ? void 0 : _a.template;
        if (typeof result !== 'string') {
            return result;
        }
        for (const key of Object.keys((_c = (_b = this.args) === null || _b === void 0 ? void 0 : _b.values) !== null && _c !== void 0 ? _c : {})) {
            const value = buildValue(this.args.values[key], variables);
            result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value !== null && value !== void 0 ? value : '');
        }
        return result;
    }
    async resolveValue(params) {
        var _a, _b, _c;
        let result = (_a = this.args) === null || _a === void 0 ? void 0 : _a.template;
        if (typeof result !== 'string') {
            return undefined;
        }
        for (const key of Object.keys((_c = (_b = this.args) === null || _b === void 0 ? void 0 : _b.values) !== null && _c !== void 0 ? _c : {})) {
            const value = await __resolveValue(this.args.values[key], params);
            result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value !== null && value !== void 0 ? value : ''));
        }
        return result;
    }
    getSchema() {
        return {
            type: 'string',
        };
    }
}

class DataBuilderFormulaVar extends DataBuilderFormulaBase {
    constructor(locator) {
        super(DataBuilderFormulaType.VAR, locator);
        this.locator = locator;
    }
    getValue(variables) {
        if (!isValidLocator(this.locator)) {
            return null;
        }
        return getValueByLocator(variables, locatorToSteps(this.locator));
    }
    async resolveValue(params) {
        if (!isValidLocator(this.locator)) {
            return null;
        }
        return getValueByLocator(params.variables, locatorToSteps(this.locator));
    }
    getSchema(variablesSchema) {
        return getSchemaByLocator(variablesSchema, this.locator);
    }
    toString(variablesSchema) {
        var _a;
        return (_a = getFullNameForLocator(variablesSchema, this.locator)) !== null && _a !== void 0 ? _a : this.locator;
    }
    getVariableLocators() {
        return ['$'];
    }
    transformVars(transformations) {
        if (!transformations || !isValidLocator(this.locator) || !this.locator) {
            return undefined;
        }
        let curValue = transformations;
        const locatorSteps = locatorToSteps(this.locator);
        while (true) {
            const formula = getFormula$1(curValue);
            if (formula) {
                const remainingLocator = stepsToLocator(locatorSteps);
                if (formula.type === DataBuilderFormulaType.VAR) {
                    const varFormula = formula;
                    return {
                        $var: `${varFormula.locator}${remainingLocator.replace('$', '')}`,
                    };
                }
                else {
                    return undefined;
                }
            }
            const locatorStep = locatorSteps.shift();
            if (!locatorStep) {
                return curValue;
            }
            switch (locatorStep.type) {
                case DataLocatorStepType.OBJECT_PROPERTY:
                    const propStep = locatorStep;
                    if (typeof curValue == 'object' &&
                        curValue !== null &&
                        curValue !== undefined &&
                        curValue[propStep.propertyName] !== undefined) {
                        curValue = curValue[propStep.propertyName];
                    }
                    else {
                        return undefined;
                    }
                    break;
                case DataLocatorStepType.ARRAY_ITEM:
                    const itemStep = locatorStep;
                    if (Array.isArray(curValue) && curValue[itemStep.index] !== undefined) {
                        curValue = curValue[itemStep.index];
                    }
                    else {
                        return undefined;
                    }
                    break;
                default:
                    throw new Error(`Unknown locator step type: ${locatorStep.type}`);
            }
            if (locatorSteps.length === 0) {
                return curValue;
            }
        }
    }
}

const Formula = {
    Var: DataBuilderFormulaVar,
    Record: DataBuilderFormulaRecord,
    Tpl: DataBuilderFormulaTpl,
    Lookup: DataBuilderFormulaLookup,
    Plain: DataBuilderFormulaPlain,
    Map: DataBuilderFormulaMap,
    And: And,
    Or: Or,
    MergeObjects: MergeObjects,
    FirstNotEmpty: FirstNotEmpty,
    DomainFromEmail: DomainFromEmail,
    Formula: FormulaFormula,
    Base: DataBuilderFormulaBase,
    Case: DataBuilderFormulaCase,
    Concat: DataBuilderFormulaConcat,
    Copy: DataBuilderFormulaCopy,
    Eval: Eval,
    ExtractDate: DataBuilderFormulaExtractDate,
    ExtractTime: DataBuilderFormulaExtractTime,
    FindAppRecordId: DataBuilderFormulaFindAppRecordId,
    FindExternalRecordId: DataBuilderFormulaFindExternalRecordId,
    FirstName: DataBuilderFormulaFirstName,
    LastName: DataBuilderFormulaLastName,
    Iterate: DataBuilderFormulaIterate,
    Jsonata: JsonataFormula,
    DataSchemaRef: DataSchemaRef,
};
function getFormulas() {
    return {
        [DataBuilderFormulaType.VAR]: Formula.Var,
        [DataBuilderFormulaType.RECORD]: Formula.Record,
        [DataBuilderFormulaType.TPL]: Formula.Tpl,
        [DataBuilderFormulaType.LOOKUP]: Formula.Lookup,
        [DataBuilderFormulaType.PLAIN]: Formula.Plain,
        [DataBuilderFormulaType.MAP]: Formula.Map,
        [DataBuilderFormulaType.CONCAT]: Formula.Concat,
        [DataBuilderFormulaType.FIRST_NAME]: Formula.FirstName,
        [DataBuilderFormulaType.LAST_NAME]: Formula.LastName,
        [DataBuilderFormulaType.ITERATE]: Formula.Iterate,
        [DataBuilderFormulaType.CASE]: Formula.Case,
        [DataBuilderFormulaType.EXTRACT_DATE]: Formula.ExtractDate,
        [DataBuilderFormulaType.EXTRACT_TIME]: Formula.ExtractTime,
        [DataBuilderFormulaType.FIND_EXTERNAL_RECORD_ID]: Formula.FindExternalRecordId,
        [DataBuilderFormulaType.FIND_APP_RECORD_ID]: Formula.FindAppRecordId,
        [DataBuilderFormulaType.COPY]: Formula.Copy,
        [DataBuilderFormulaType.EVAL]: Formula.Eval,
        [DataBuilderFormulaType.AND]: Formula.And,
        [DataBuilderFormulaType.OR]: Formula.Or,
        [DataBuilderFormulaType.MERGE_OBJECTS]: Formula.MergeObjects,
        [DataBuilderFormulaType.FIRST_NOT_EMPTY]: Formula.FirstNotEmpty,
        [DataBuilderFormulaType.DOMAIN_FROM_EMAIL]: Formula.DomainFromEmail,
        [DataBuilderFormulaType.FORMULA]: Formula.Formula,
        [DataBuilderFormulaType.JSONATA]: Formula.Jsonata,
        [DataBuilderFormulaType.DATA_SCHEMA_REF]: Formula.DataSchemaRef,
    };
}
function isFormula(value) {
    return !!getFormula(value);
}
function hasFormulas(value) {
    if (isFormula(value)) {
        return true;
    }
    if (Array.isArray(value)) {
        return value.some((v) => hasFormulas(v));
    }
    else if (typeof value === 'object' && value !== null) {
        return Object.values(value).some((v) => hasFormulas(v));
    }
    else {
        return false;
    }
}
function getFormula(value) {
    if (typeof value === 'object' && value !== null) {
        const firstKey = Object.keys(value)[0];
        if (firstKey === null || firstKey === void 0 ? void 0 : firstKey.startsWith('$')) {
            const formulaType = firstKey.slice(1);
            const args = value[firstKey];
            const formulas = getFormulas();
            if (formulaType in formulas) {
                return new formulas[formulaType](args, value);
            }
        }
    }
    return undefined;
}

const zodDateCoercion = () => z.preprocess((val) => {
    if (val instanceof Date)
        return val.toISOString();
    if (typeof val === 'number')
        return new Date(val).toISOString();
    return val;
}, z.string().transform((s) => new Date(s)));
const zodIsoDatetime = (params = {}) => {
    const withSeconds = z.iso.datetime(params);
    const withoutSeconds = z.iso.datetime({ ...params, precision: -1 });
    return z
        .string()
        .refine((value) => withSeconds.safeParse(value).success || withoutSeconds.safeParse(value).success, {
        message: 'Invalid ISO datetime',
    })
        .meta({ format: 'date-time' });
};
const zodBooleanCoercion = () => z.preprocess((val) => {
    if (typeof val === 'boolean')
        return val;
    if (typeof val === 'string') {
        const lower = val.toLowerCase();
        if (lower === 'true' || lower === '1')
            return true;
        if (lower === 'false' || lower === '0')
            return false;
        return Boolean(val);
    }
    if (typeof val === 'number')
        return Boolean(val);
    return Boolean(val);
}, z.boolean());

const PaginationQuery = z.object({
    limit: z.coerce.number().int().min(1).max(1000).optional().describe('Maximum number of items to return (1-1000)'),
    cursor: z.string().optional().describe('Pagination cursor from a previous response'),
});
function createPaginationResponseSchema(itemSchema) {
    return z.object({
        items: z.array(itemSchema),
        cursor: z.string().optional(),
    });
}
class PaginationResponse {
}
const IncludeArchivedQuery = z.object({
    includeArchived: zodBooleanCoercion().optional(),
});
const SearchQuery = z.object({
    search: z.string().optional().describe('Text search query to filter results'),
});
const CommonListElementsQuery = SearchQuery.merge(PaginationQuery).merge(IncludeArchivedQuery);
const CommonFindElementsQuery = CommonListElementsQuery;
const CommonInstancesListQuery = CommonListElementsQuery.extend({
    userId: z.string().optional(),
    instanceKey: z.string().optional(),
});
const CommonFindInstancesQuery = CommonFindElementsQuery.extend({
    id: z.string().optional(),
    userId: z.string().optional(),
    instanceKey: z.string().optional(),
});
const CommonIntegrationOrConnectionQuery = z.object({
    connectionId: z.string().optional(),
    integrationId: z.string().optional(),
    integrationKey: z.string().optional(),
});

var ConfigurationState;
(function (ConfigurationState) {
    ConfigurationState["CONFIGURED"] = "CONFIGURED";
    ConfigurationState["NOT_CONFIGURED"] = "NOT_CONFIGURED";
})(ConfigurationState || (ConfigurationState = {}));
var WorkspaceElementType;
(function (WorkspaceElementType) {
    WorkspaceElementType["Customer"] = "customer";
    WorkspaceElementType["App"] = "app";
    WorkspaceElementType["Connector"] = "connector";
    WorkspaceElementType["Integration"] = "integration";
    WorkspaceElementType["Flow"] = "flow";
    WorkspaceElementType["FlowInstance"] = "flow-instance";
    WorkspaceElementType["FlowRun"] = "flow-run";
    WorkspaceElementType["Action"] = "action";
    WorkspaceElementType["Package"] = "package";
    WorkspaceElementType["ActionInstance"] = "action-instance";
    WorkspaceElementType["Connection"] = "connection";
    WorkspaceElementType["FieldMapping"] = "field-mapping";
    WorkspaceElementType["FieldMappingInstance"] = "field-mapping-instance";
    WorkspaceElementType["DataSource"] = "data-source";
    WorkspaceElementType["DataSourceInstance"] = "data-source-instance";
    WorkspaceElementType["DataLinkTable"] = "data-link-table";
    WorkspaceElementType["DataLinkTableInstance"] = "data-link-table-instance";
    WorkspaceElementType["AppEventType"] = "app-event-type";
    WorkspaceElementType["AppEventSubscription"] = "app-event-subscription";
    WorkspaceElementType["AppEventLogRecord"] = "app-event-log-record";
    WorkspaceElementType["AppDataSchema"] = "app-data-schema";
    WorkspaceElementType["AppDataSchemaInstance"] = "app-data-schema-instance";
    WorkspaceElementType["ExternalEventSubscription"] = "external-event-subscription";
    WorkspaceElementType["ExternalEventLogRecord"] = "external-event-log-record";
    WorkspaceElementType["ExternalEventPull"] = "external-event-pull";
    WorkspaceElementType["ExternalEventType"] = "external-event-type";
    WorkspaceElementType["Screen"] = "screen";
    WorkspaceElementType["ActionRunLogRecord"] = "action-run-log-record";
    WorkspaceElementType["ExternalApiLogRecord"] = "external-api-log-record";
    WorkspaceElementType["IncomingWebhookLogRecord"] = "incoming-webhook-log-record";
    WorkspaceElementType["ConnectedProduct"] = "connected-product";
    WorkspaceElementType["AgentHarness"] = "agent-harness";
    WorkspaceElementType["AgentSkill"] = "agent-skill";
    WorkspaceElementType["AgentInstruction"] = "agent-instruction";
})(WorkspaceElementType || (WorkspaceElementType = {}));
var WorkspaceEventType;
(function (WorkspaceEventType) {
    WorkspaceEventType["ConnectionCreated"] = "connection.created";
    WorkspaceEventType["ConnectionDeleted"] = "connection.deleted";
    WorkspaceEventType["ConnectionDisconnected"] = "connection.disconnected";
    WorkspaceEventType["ConnectionReconnected"] = "connection.reconnected";
    WorkspaceEventType["FlowRunQueued"] = "flowRun.queued";
    WorkspaceEventType["FlowRunStarted"] = "flowRun.started";
    WorkspaceEventType["FlowRunCompleted"] = "flowRun.completed";
    WorkspaceEventType["FlowRunFailed"] = "flowRun.failed";
    WorkspaceEventType["FlowRunStopped"] = "flowRun.stopped";
    WorkspaceEventType["AlertCreated"] = "alert.created";
})(WorkspaceEventType || (WorkspaceEventType = {}));
var WorkspaceElementState;
(function (WorkspaceElementState) {
    WorkspaceElementState["BUILDING"] = "BUILDING";
    WorkspaceElementState["CLIENT_ACTION_REQUIRED"] = "CLIENT_ACTION_REQUIRED";
    WorkspaceElementState["CONFIGURATION_ERROR"] = "CONFIGURATION_ERROR";
    WorkspaceElementState["SETUP_FAILED"] = "SETUP_FAILED";
    WorkspaceElementState["READY"] = "READY";
    WorkspaceElementState["DISCONNECTING"] = "DISCONNECTING";
})(WorkspaceElementState || (WorkspaceElementState = {}));
var WorkspaceElementDependencyType;
(function (WorkspaceElementDependencyType) {
    WorkspaceElementDependencyType["Configuration"] = "CONFIGURATION";
    WorkspaceElementDependencyType["Parent"] = "PARENT";
})(WorkspaceElementDependencyType || (WorkspaceElementDependencyType = {}));
const BaseWorkspaceElement = z.object({
    id: z.string().describe('Internal database ID of the element. Assigned by the API; unique per element.'),
    name: z.string().describe('Display name of the workspace element.'),
});
const BaseMembraneInterfaceEditableProperties = z.object({
    uuid: z
        .string()
        .optional()
        .describe('Stable unique identifier (UUID). Can be provided on create; otherwise generated. Must be unique across elements of this type.'),
    key: z
        .string()
        .optional()
        .describe('Stable key for referencing the element (e.g. in selectors). Unique per (parent, integration) scope. Generated from name if not provided on create.'),
    name: z.string().optional().describe('Display name. Optional on create; used to generate key when key is omitted.'),
    description: z.string().optional().describe('Optional human-readable description.'),
    meta: z.record(z.string(), z.any()).optional().describe('Optional key-value metadata.'),
});
const BaseMembraneInterfaceReadOnlyProperties = z.object({
    name: z.string().describe('Display name. Always present in API responses (filled by API if not set on create).'),
    state: z
        .enum(WorkspaceElementState)
        .optional()
        .describe('Current lifecycle or health state (e.g. READY, SETUP_FAILED, CONFIGURATION_ERROR, BUILDING). Set by the engine during setup and validation.'),
    errors: z.array(ErrorDataSchema).optional().describe('Validation or setup errors when state is not READY.'),
    revision: z
        .string()
        .optional()
        .describe('Opaque revision token; changes on each update. Used for optimistic concurrency.'),
    createdAt: z.string().optional().describe('ISO date when the element was created.'),
    updatedAt: z.string().optional().describe('ISO date when the element was last updated.'),
    archivedAt: z
        .string()
        .optional()
        .describe('When set, the element is archived (soft-deleted). Archived elements cannot be patched.'),
    isDeactivated: z
        .boolean()
        .optional()
        .describe('When true, setup is skipped and the element is treated as inactive (e.g. when dependencies are deactivated or the element is archived).'),
    isReadOnly: z
        .boolean()
        .optional()
        .describe('When true, the element cannot be modified (e.g. published package elements or elements from another workspace).'),
});
const BaseMembraneInterface = BaseWorkspaceElement.merge(BaseMembraneInterfaceEditableProperties).merge(BaseMembraneInterfaceReadOnlyProperties);
const IntentProperties = z.object({
    intent: z
        .string()
        .optional()
        .describe('[Experimental] Intent-based instruction for an agent to configure this element.'),
});
const BaseIntegrationLevelMembraneInterfaceEditableProperties = BaseMembraneInterfaceEditableProperties.extend({
    integrationId: z
        .string()
        .optional()
        .describe('Internal ID of the integration this element belongs to. Omit for universal elements; set for integration-specific elements. Uniqueness of key is scoped per integration.'),
    integrationUuid: z
        .string()
        .optional()
        .describe('UUID of the integration; alternative to integrationId when creating from export. Resolved to integrationId by the API.'),
    integrationKey: z
        .string()
        .optional()
        .describe('Key of the integration; alternative to integrationId. Resolved to integrationId by the API.'),
    parentId: z
        .string()
        .optional()
        .describe('Internal ID of the parent workspace element. Used for hierarchy (e.g. integration-level under universal). Child key uniqueness is scoped by parent and integration.'),
    parentUuid: z
        .string()
        .optional()
        .describe('UUID of the parent element; alternative to parentId when creating (e.g. from export). Resolved to parentId by the API.'),
    parentKey: z
        .string()
        .optional()
        .describe('Key of the parent element; alternative to parentId. Resolved to parentId by the API.'),
    connectionId: z
        .string()
        .optional()
        .describe('Connection ID. Used when filtering by connection in list/selector queries, or when creating or identifying connection-level instances.'),
    instanceKey: z
        .string()
        .optional()
        .describe('Key identifying a specific connection-level instance when multiple instances exist per (element, connection). Used in selector queries and when creating instances.'),
    isUniversal: z
        .boolean()
        .optional()
        .describe('When true, the element is universal (shared across all integrations). Requires universal-element access. Omit or false for integration-specific elements.'),
    externalAppId: z
        .string()
        .optional()
        .describe('Internal ID of the external app this element belongs to. Auto-populated from the integration when integrationId is set and the integration has an externalAppId.'),
    externalAppUuid: z
        .string()
        .optional()
        .describe('UUID of the external app; alternative to externalAppId when creating from export. Resolved to externalAppId by the API.'),
    externalAppKey: z
        .string()
        .optional()
        .describe('Key of the external app; alternative to externalAppId. Resolved to externalAppId by the API.'),
});
const BaseIntegrationLevelMembraneInterfaceReadOnlyProperties = BaseMembraneInterfaceReadOnlyProperties.extend({
    isCustomized: z.boolean().optional(),
});
const BaseExportProperties = BaseMembraneInterfaceEditableProperties.required({ uuid: true, key: true });
const BaseIntegrationLevelMembraneInterfaceExportProperties = BaseExportProperties.extend({
    integrationUuid: z.string().optional(),
    parentUuid: z.string().optional(),
    externalAppUuid: z.string().optional(),
    isCustomized: z.boolean().optional(),
    isUniversal: z.boolean().optional(),
});
const MembraneElementLayer = z.enum(['universal', 'integration', 'connection']);
const IntegrationLevelMembraneInterfaceSelectorQuery = z.object({
    layer: MembraneElementLayer.optional().meta({
        filterTitle: 'Layer',
    }),
    integrationKey: z
        .string()
        .optional()
        .meta({
        hidden: true,
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    connectionKey: z
        .string()
        .optional()
        .meta({
        hidden: true,
    }),
    instanceKey: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Instance Key',
    }),
});
const FindIntegrationLevelMembraneInterfaceQuery = IntegrationLevelMembraneInterfaceSelectorQuery.extend(CommonListElementsQuery.shape).extend({
    parentId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Parent',
        referenceElementType: 'self',
    }),
    universalParentId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Universal Parent',
        referenceElementType: 'self',
    }),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    isUniversal: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Is Universal',
        isFlag: true,
    }),
    layer: MembraneElementLayer.optional(),
    externalAppId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'External App',
        referenceElementType: WorkspaceElementType.App,
    }),
});
const BaseIntegrationLevelMembraneInterface = BaseMembraneInterface.merge(BaseIntegrationLevelMembraneInterfaceEditableProperties).merge(BaseIntegrationLevelMembraneInterfaceReadOnlyProperties);
const TenantLayerElement = z.object({
    userId: z.string(),
    tenantId: z.string().optional(),
});
const ActivityLogRecord = TenantLayerElement.extend({
    id: z.string(),
    integrationId: z.string().optional(),
    connectionId: z.string().optional(),
    externalAppId: z.string().optional(),
});
const WorkspaceElementSearchQuery = z.object({
    q: z.string().min(1).max(200),
    elementType: z.enum(WorkspaceElementType).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    cursor: z.string().optional(),
});
const WorkspaceElementSearchResult = z.object({
    elementType: z.enum(WorkspaceElementType),
    element: z.any(),
});

function mergeWithFormulas(a, b) {
    if (Array.isArray(a) && Array.isArray(b)) {
        const length = Math.max(a.length, b.length);
        const merged = [];
        for (let i = 0; i < length; i++) {
            merged.push(mergeWithFormulas(a[i], b[i]));
        }
        return merged;
    }
    if (isObject(a) && isObject(b)) {
        if (isFormula$1(a) || isFormula$1(b)) {
            return typeof b === 'undefined' ? a : b;
        }
        const merged = { ...a };
        for (const key in b) {
            merged[key] = mergeWithFormulas(a[key], b[key]);
        }
        return merged;
    }
    return typeof b === 'undefined' ? a : b;
}
function isObject(value) {
    return (!!value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Number) &&
        !(value instanceof String) &&
        !(value instanceof Boolean));
}

var FieldMappingDirection;
(function (FieldMappingDirection) {
    FieldMappingDirection["IMPORT"] = "import";
    FieldMappingDirection["EXPORT"] = "export";
    FieldMappingDirection["BOTH"] = "both";
})(FieldMappingDirection || (FieldMappingDirection = {}));
const FieldMappingSpecificProperties = z.object({
    connectionId: z.string().optional(),
    instanceKey: z.string().optional(),
    fieldMappingId: z.string().optional(),
    universalFieldMappingId: z.string().optional(),
    dataSourceId: z.string().optional(),
    dataSourceUuid: z.string().optional(),
    dataSourceKey: z.string().optional().describe('[INTERNAL] Deprecated: Use dataSourceId instead'),
    appSchema: DataSchema.optional(),
    direction: z.enum(FieldMappingDirection).optional(),
    defaultImportValue: z.any().optional(),
    defaultExportValue: z.any().optional(),
    importValue: z.any().optional(),
    exportValue: z.any().optional(),
    unifiedImportValue: z.any().optional(),
    unifiedExportValue: z.any().optional(),
    frozenImportFields: z.any().optional(),
    frozenExportFields: z.any().optional(),
    frozenUnifiedExportFields: z.any().optional(),
});
const FieldMappingEditableProperties = BaseIntegrationLevelMembraneInterfaceEditableProperties.omit({
    parentId: true,
}).extend(FieldMappingSpecificProperties.shape);
const FieldMappingExportProperties = BaseIntegrationLevelMembraneInterfaceExportProperties.extend(FieldMappingSpecificProperties.shape).omit({
    connectionId: true,
    fieldMappingId: true,
    universalFieldMappingId: true,
    dataSourceId: true,
});
const FieldMappingReadOnlyProperties = BaseIntegrationLevelMembraneInterfaceReadOnlyProperties.extend({
    ...TenantLayerElement.partial().shape,
    fieldMappingRevision: z.string().optional(),
    universalFieldMappingRevision: z.string().optional(),
    dataSourceInstanceId: z.string().optional(),
    dataSourceSchema: DataSchema.optional(),
    isCustomized: z.boolean().optional(),
    state: z.enum(WorkspaceElementState).optional(),
    error: ErrorDataSchema.optional(),
});
const BaseFieldMapping = BaseMembraneInterface.merge(FieldMappingEditableProperties).merge(FieldMappingReadOnlyProperties);
const FieldMappingUnitConfig = z.object({
    key: z.string().optional(),
    input: z.any().optional(),
    includeRawFields: z.any().optional(),
    importValue: z.any().optional(),
    exportValue: z.any().optional(),
    default: z.any().optional(),
    defaultUnifiedValue: z.any().optional(),
    defaultValue: z.any().optional(),
    appSchema: DataSchema.optional(),
});
const BaseFieldMappingInstance = BaseFieldMapping;

var ActionType;
(function (ActionType) {
    ActionType["ListDataRecords"] = "list-data-records";
    ActionType["FindDataRecordById"] = "find-data-record-by-id";
    ActionType["MatchDataRecord"] = "match-data-record";
    ActionType["CreateDataRecord"] = "create-data-record";
    ActionType["FindOrCreateDataRecord"] = "find-or-create-data-record";
    ActionType["UpdateDataRecord"] = "update-data-record";
    ActionType["DeleteDataRecord"] = "delete-data-record";
    ActionType["SearchDataRecords"] = "search-data-record";
    ActionType["ConnectorOperation"] = "connector-operation";
    ActionType["ApiRequestToExternalApp"] = "api-request-to-external-app";
    ActionType["ApiRequestToYourApp"] = "api-request-to-your-app";
    ActionType["HttpRequest"] = "http-request";
    ActionType["RunJavascript"] = "run-javascript";
    ActionType["ApiRequest"] = "api-request";
})(ActionType || (ActionType = {}));
function isDataActionType(type) {
    var _a;
    return (_a = ACTIONS[type]) === null || _a === void 0 ? void 0 : _a.isDataAction;
}
const ACTIONS = {
    [ActionType.ListDataRecords]: {
        name: 'List Data Records',
        description: 'Return a list of all the data records in the collection, page by page',
        isDataAction: true,
        direction: FieldMappingDirection.IMPORT,
    },
    [ActionType.FindDataRecordById]: {
        name: 'Find Data Record by ID',
        description: 'Find record in a data collection by ID',
        isDataAction: true,
        direction: FieldMappingDirection.IMPORT,
    },
    [ActionType.MatchDataRecord]: {
        name: 'Match Data Record',
        description: 'Find a matching record in a data collection given its field(s) values',
        isDataAction: true,
        direction: FieldMappingDirection.IMPORT,
    },
    [ActionType.CreateDataRecord]: {
        name: 'Create Data Record',
        description: 'Create a new record in a data collection',
        isDataAction: true,
        direction: FieldMappingDirection.EXPORT,
    },
    [ActionType.FindOrCreateDataRecord]: {
        name: 'Find or Create Data Record',
        description: 'Find record in a data collection or create a new one if it does not exist yet',
        isDataAction: true,
        direction: FieldMappingDirection.EXPORT,
    },
    [ActionType.UpdateDataRecord]: {
        name: 'Update Data Record',
        description: 'Update data record by ID',
        isDataAction: true,
        direction: FieldMappingDirection.EXPORT,
    },
    [ActionType.DeleteDataRecord]: {
        name: 'Delete Data Record',
        description: 'Delete a data record by ID',
        isDataAction: true,
        direction: FieldMappingDirection.EXPORT,
    },
    [ActionType.SearchDataRecords]: {
        name: 'Search Data Records',
        description: 'Search records in a data collection using a text query',
        isDataAction: true,
        direction: FieldMappingDirection.IMPORT,
    },
    [ActionType.ConnectorOperation]: {
        name: 'Connector Operation',
        description: 'Perform a custom operation on a connector',
    },
    [ActionType.ApiRequestToExternalApp]: {
        name: 'API Request to External App',
        description: 'Make an API request to an external app',
    },
    [ActionType.ApiRequestToYourApp]: {
        name: 'API Request to Your App',
        description: 'Make an API request to your app',
    },
    [ActionType.HttpRequest]: {
        name: 'HTTP Request',
        description: 'Make an arbitrary HTTP request to any endpoint',
    },
    [ActionType.RunJavascript]: {
        name: 'Run Javascript',
        description: 'Run custom Javascript code',
    },
    [ActionType.ApiRequest]: {
        name: 'API Request',
        description: 'Make an API request',
        isDeprecated: true,
    },
};

var HttpRequestMethod;
(function (HttpRequestMethod) {
    HttpRequestMethod["GET"] = "GET";
    HttpRequestMethod["POST"] = "POST";
    HttpRequestMethod["PATCH"] = "PATCH";
    HttpRequestMethod["PUT"] = "PUT";
    HttpRequestMethod["DELETE"] = "DELETE";
})(HttpRequestMethod || (HttpRequestMethod = {}));
const HttpRequestSpec = z.object({
    method: z.string().toUpperCase().pipe(z.enum(HttpRequestMethod)).optional(),
    uri: z.any(),
    headers: z.record(z.string(), z.any()).optional(),
    query: z.record(z.string(), z.any()).optional(),
    body: z.any().optional(),
});
const HTTP_REQUEST_SCHEMA = {
    type: 'object',
    properties: {
        uri: { type: 'string' },
        method: {
            type: 'string',
            enum: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
        },
        body: {},
        query: {
            type: 'object',
            section: true,
            additionalProperties: true,
        },
        headers: {
            type: 'object',
            section: true,
            additionalProperties: true,
        },
    },
};

const ActionApiPayload = z.object({
    method: z.enum(HttpRequestMethod),
    path: z.string().optional(),
    url: z.string().optional(),
    body: z.any().optional(),
    headers: z.record(z.string(), z.string()).optional(),
    query: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});
const ActionSnapshot = z.object({
    id: z.string().optional(),
    integrationKey: z.string().optional(),
    key: z.string().optional(),
    connectionKey: z.string().optional(),
    connectionId: z.string().optional(),
    type: z.string(),
    api: ActionApiPayload.optional(),
    code: z.string().optional(),
    input: z.any().optional(),
    meta: z.record(z.string(), z.any()).optional(),
});
function configToNamedPayload(type, config) {
    var _a, _b;
    if (config == null)
        return {};
    switch (type) {
        case ActionType.ApiRequestToExternalApp:
        case ActionType.ApiRequestToYourApp:
        case ActionType.ApiRequest: {
            const req = config.request;
            if (!req)
                return {};
            return {
                api: {
                    method: req.method,
                    path: req.path,
                    body: (_a = req.data) !== null && _a !== void 0 ? _a : req.body,
                    headers: req.headers,
                    query: req.query,
                },
            };
        }
        case ActionType.HttpRequest: {
            const req = config.request;
            if (!req)
                return {};
            return {
                api: {
                    method: req.method,
                    url: req.uri,
                    body: (_b = req.body) !== null && _b !== void 0 ? _b : req.data,
                    headers: req.headers,
                    query: req.query,
                },
            };
        }
        case ActionType.RunJavascript: {
            const code = config.code;
            if (typeof code !== 'string')
                return {};
            return { code };
        }
        default:
            return {};
    }
}
function buildActionSnapshot(args) {
    const { api, code } = configToNamedPayload(args.type, args.config);
    return {
        ...(args.id != null && { id: args.id }),
        ...(args.integrationKey != null && { integrationKey: args.integrationKey }),
        ...(args.key != null && { key: args.key }),
        ...(args.connectionId != null && { connectionId: args.connectionId }),
        ...(args.connectionKey != null && { connectionKey: args.connectionKey }),
        type: args.type,
        ...(api != null && { api }),
        ...(code != null && { code }),
        ...(args.input != null && { input: args.input }),
        ...(args.meta != null && { meta: args.meta }),
    };
}

var ActionDependencyType;
(function (ActionDependencyType) {
    ActionDependencyType["FieldMapping"] = "FieldMapping";
    ActionDependencyType["DataSource"] = "DataSource";
})(ActionDependencyType || (ActionDependencyType = {}));
const ActionDependency = z.object({
    type: z.enum(ActionDependencyType),
    key: z.string(),
    element: z.any().optional(),
});
const ActionSpecificProperties = z.object({
    connectionId: z
        .string()
        .optional()
        .describe('Connection ID for filtering or for connection-level instances. Same semantics as base integration-level property.'),
    instanceKey: z
        .string()
        .optional()
        .describe('Key for the connection-level instance when multiple exist per connection. Same semantics as base integration-level property.'),
    inputSchema: DataSchema.optional().describe('JSON Schema for the action input. Used by the action engine for template output schema, run-time variables, and output mapping.'),
    type: z
        .enum(ActionType)
        .optional()
        .describe('Action type (e.g. ConnectorOperation, RunJavascript). Determines which handler runs and which config schema is used for validation.'),
    config: z
        .object({})
        .loose()
        .optional()
        .describe("Type-specific configuration object. Validated by the handler's config schema; passed to handler setup/run and getTemplateOutputSchema."),
    outputMapping: z
        .any()
        .optional()
        .describe("Mapping expression to transform the action's raw output. Evaluated with access to input, output, and user; used to compute transformed output schema and at run time."),
    customOutputSchema: DataSchema.optional().describe('Explicit output schema for the action. Takes precedence over the transformed or default output schema when resolving outputSchema.'),
});
const ActionEditableProperties = BaseIntegrationLevelMembraneInterfaceEditableProperties.extend(ActionSpecificProperties.shape).describe('Action editable properties: key, name, type, config, inputSchema, outputMapping, optional connectionId and instanceKey for connection-level instances.');
const ActionExportProperties = BaseIntegrationLevelMembraneInterfaceExportProperties.extend(ActionSpecificProperties.shape).omit({
    connectionId: true,
});
const ActionReadOnlyProperties = BaseIntegrationLevelMembraneInterfaceReadOnlyProperties.extend({
    ...TenantLayerElement.partial().shape,
    agentSessionId: z
        .string()
        .optional()
        .describe('[INTERNAL] ID of the agent session building this action. Present when the action was created or updated with an intent.'),
    universalParentId: z
        .string()
        .optional()
        .describe('For connection-level instances: ID of the universal (root) action in the hierarchy. Set automatically when creating an instance; used in list/selector queries to filter by universal parent.'),
    outputSchema: z
        .any()
        .optional()
        .describe('Resolved output schema (cached after setup). Derived from customOutputSchema, transformed schema from outputMapping, default output schema, or parent custom output schema. Read-only.'),
    isPublic: z
        .boolean()
        .optional()
        .describe('When true, the element is publicly listed (e.g. in universe). Used for published packages; publish/unpublish updates this on published elements.'),
    externalAppUuid: z
        .string()
        .optional()
        .describe('UUID of the external app when this action is published in a package that has an external app. Populated in API responses for published actions.'),
});
const BaseAction = BaseMembraneInterface.merge(ActionEditableProperties).merge(ActionReadOnlyProperties);
const BaseActionInstance = BaseAction;

const CONNECTION_REQUEST_SCREEN_PATH = 'screens/connect';
const AGENTIC_CONNECTION_REQUEST_SCREEN_PATH = 'screens/connections';
const CONNECTION_REQUEST_ID_PARAM = 'connectionRequestId';
function getConnectionRequestUrl(baseUri, requestId) {
    return `${baseUri}/${AGENTIC_CONNECTION_REQUEST_SCREEN_PATH}?${CONNECTION_REQUEST_ID_PARAM}=${requestId}`;
}
const CreateConnectionRequestPayload = z.object({
    integrationId: z.string().optional(),
    integrationKey: z.string().optional(),
    connectorId: z.string().optional(),
    connectorKey: z.string().optional(),
    connectorVersion: z.string().optional(),
    externalAppId: z.string().optional(),
    externalAppKey: z.string().optional(),
    connectionId: z.string().optional(),
    connectionKey: z.string().min(1).max(100).optional(),
    name: z.string().optional(),
    allowMultipleConnections: z.boolean().optional(),
    connectorParameters: z.record(z.string(), z.unknown()).optional(),
    redirectUri: z.string().url().optional(),
    customState: z.string().optional(),
    prompt: z.string().optional(),
});
const PatchConnectionRequestPayload = z.object({});
const ConnectionRequest = z.object({
    requestId: z.string(),
    tenantId: z.string(),
    integrationId: z.string().optional(),
    integrationKey: z.string().optional(),
    connectorId: z.string().optional(),
    connectorKey: z.string().optional(),
    connectorVersion: z.string().optional(),
    externalAppId: z.string().optional(),
    externalAppKey: z.string().optional(),
    connectionId: z.string().optional(),
    connectionKey: z.string().optional(),
    name: z.string().optional(),
    allowMultipleConnections: z.boolean().optional(),
    connectorParameters: z.record(z.string(), z.unknown()).optional(),
    redirectUri: z.string().url().optional(),
    customState: z.string().optional(),
    prompt: z.string().optional(),
    status: z.enum(['pending', 'success', 'cancelled', 'error']),
    resultConnectionId: z.string().optional(),
    resultError: ErrorDataSchema.optional(),
    createdAt: z.coerce.date(),
    url: z.string(),
});

const ConnectionType = z.enum(['connector', 'mcp']);
const ConnectionEditableProperties = z.object({
    type: ConnectionType.optional(),
    name: z.string().optional(),
    meta: z.record(z.string(), z.any()).optional(),
    authOptionKey: z.string().optional(),
    key: z.string().min(1).max(100).optional().nullable(),
    integrationId: z.string().optional(),
    integrationUuid: z.string().optional(),
    integrationKey: z.string().optional(),
    connectorId: z.string().optional(),
    connectorUuid: z.string().optional(),
    connectorKey: z.string().optional(),
    connectorVersion: z.string().optional(),
    externalAppId: z.string().optional(),
    externalAppUuid: z.string().optional(),
    credentials: z.unknown().optional(),
    connectorParameters: z.unknown().optional(),
    input: z.unknown().optional(),
});
const ConnectionClientAction = z.object({
    type: z.enum(['connect', 'provide-input']),
    description: z.string(),
    uiUrl: z.string().optional(),
    agentInstructions: z.string().optional(),
    handoffAgentInstructions: z.string().optional(),
    reason: z
        .enum(['authenticate', 'reauthenticate', 'provide-input'])
        .optional()
        .describe('[INTERNAL] Cause that triggered this client action; persisted so prompts can be re-rendered on read.'),
});
const BaseConnection = BaseWorkspaceElement.extend({
    ...TenantLayerElement.shape,
    type: ConnectionType.optional(),
    name: z.string(),
    key: z.string().optional(),
    isTest: z.boolean().optional(),
    connected: z.boolean().optional(),
    disconnected: z.boolean().optional().describe('[DEPRECATED] Use `connected` instead.'),
    isDefunct: z.boolean().optional(),
    state: z.enum(WorkspaceElementState).optional(),
    errors: z.array(ErrorDataSchema).optional(),
    requestError: ErrorDataSchema.optional(),
    error: ErrorDataSchema.optional().describe('[DEPRECATED] Use `requestError` and `errors` instead.'),
    integrationId: z.string().optional(),
    connectorId: z.string().optional(),
    externalAppId: z.string().optional(),
    connectorVersion: z.string().optional(),
    authOptionKey: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    lastActiveAt: z.string().optional(),
    nextCredentialsRefreshAt: z.string().optional(),
    nextRetryTimestamp: z.string().optional(),
    retryAttempts: z.number().optional(),
    canTest: z.boolean().optional(),
    canRefreshCredentials: z.boolean().optional(),
    archivedAt: z.string().optional(),
    isDeactivated: z.boolean().optional(),
    meta: z.record(z.string(), z.any()).optional(),
    buildingAgentSessionId: z
        .string()
        .optional()
        .describe('Session ID for building the element via intent-based endpoints (create/update with intent). Present when state is BUILDING.'),
    clientAction: ConnectionClientAction.optional().describe('Action the client must perform to advance this connection. Present if and only if state is CLIENT_ACTION_REQUIRED.'),
});
class ConnectionSpec {
    constructor(opts) {
        Object.assign(this, opts);
    }
}

var FunctionType;
(function (FunctionType) {
    FunctionType["mapping"] = "mapping";
    FunctionType["operationMapping"] = "operation-mapping";
    FunctionType["restApiMapping"] = "rest-api-mapping";
    FunctionType["graphqlApiMapping"] = "graphql-api-mapping";
    FunctionType["javascript"] = "javascript";
})(FunctionType || (FunctionType = {}));
const BaseFunctionDefinition = z.object({
    type: z.enum([
        FunctionType.mapping,
        FunctionType.operationMapping,
        FunctionType.restApiMapping,
        FunctionType.graphqlApiMapping,
        FunctionType.javascript,
    ]),
});
const GenericFunctionDefinition = z
    .object({
    type: z.enum(['mapping', 'operation-mapping', 'rest-api-mapping', 'graphql-api-mapping', 'javascript']).optional(),
})
    .loose();
function validateFunctionDefinitions(functions, schema) {
    const errors = [];
    for (const [name, funcDef] of Object.entries(functions)) {
        const result = schema.safeParse(funcDef);
        if (!result.success) {
            errors.push(`"${name}": ${formatZodUnionError(result.error, funcDef)}`);
        }
    }
    if (errors.length > 0) {
        throw new BadRequestError(`Invalid function definition(s): ${errors.join('; ')}`);
    }
}
function formatZodUnionError(error, funcDef) {
    for (const issue of error.issues) {
        const branchErrors = issue.errors;
        if (issue.code === 'invalid_union' && branchErrors) {
            const type = funcDef.type;
            if (type) {
                for (const branchIssues of branchErrors) {
                    const hasTypeError = branchIssues.some((i) => i.path.includes('type') && i.code === 'invalid_type');
                    if (!hasTypeError) {
                        return branchIssues.map((i) => `${i.path.join('.') || 'root'}: ${i.message}`).join('; ');
                    }
                }
            }
        }
    }
    return error.issues.map((i) => `${i.path.join('.') || 'root'}: ${i.message}`).join('; ');
}

const GraphQLFieldMappingSchema = z.lazy(() => z.object({
    field: z.string(),
    args: z.record(z.string(), z.any()).optional(),
    subFields: z.array(GraphQLFieldMappingSchema).optional(),
}));
const GraphQLApiMappingSchema = z.object({
    path: z.string(),
    operationType: z.enum(['query', 'mutation']),
    requestMapping: z.array(GraphQLFieldMappingSchema),
    responseMapping: z.any().optional(),
});
const GraphqlApiMappingFunction = z.object({
    type: z.literal(FunctionType.graphqlApiMapping),
    mapping: GraphQLApiMappingSchema,
});

const JavascriptFunction = z.object({
    type: z.literal(FunctionType.javascript),
    code: z.string(),
});

const MappingSchema = z.any();
const MappingFunction = z.object({
    type: z.literal(FunctionType.mapping),
    mapping: MappingSchema,
});

const OperationMappingSchema = z.object({
    operationKey: z.string(),
    inputMapping: z.any().optional(),
    outputMapping: z.any().optional(),
});
const OperationMappingFunction = z.object({
    type: z.literal(FunctionType.operationMapping),
    mapping: OperationMappingSchema,
});

const RequestMappingSchema = z.object({
    pathParameters: z.any().optional(),
    query: z.any().optional(),
    data: z.any().optional(),
    headers: z.any().optional(),
});
const RestApiMappingSchema = z.object({
    path: z.string(),
    method: z.string(),
    requestMapping: RequestMappingSchema.optional(),
    responseMapping: z.any().optional(),
});
const OpenapiMappingSchema = z.object({
    path: z.string(),
    method: z.string(),
    requestMapping: RequestMappingSchema.optional(),
    responseMapping: z.any().optional(),
});
const RestApiMappingFunction = z.object({
    type: z.literal(FunctionType.restApiMapping),
    mapping: RestApiMappingSchema,
});

const FunctionDefinition = z.union([
    MappingFunction,
    OperationMappingFunction,
    RestApiMappingFunction,
    GraphqlApiMappingFunction,
    JavascriptFunction,
]);

var ConnectorMethodImplementationType;
(function (ConnectorMethodImplementationType) {
    ConnectorMethodImplementationType["mapping"] = "mapping";
    ConnectorMethodImplementationType["operationMapping"] = "operation-mapping";
    ConnectorMethodImplementationType["restApiMapping"] = "rest-api-mapping";
    ConnectorMethodImplementationType["graphqlApiMapping"] = "graphql-api-mapping";
    ConnectorMethodImplementationType["javascript"] = "javascript";
    ConnectorMethodImplementationType["notSupported"] = "not-supported";
})(ConnectorMethodImplementationType || (ConnectorMethodImplementationType = {}));
const CONNECTOR_METHOD_IMPLEMENTATION_SUFFIXES = {
    [ConnectorMethodImplementationType.restApiMapping]: ['.rest.yml', '.api.yml'],
    [ConnectorMethodImplementationType.graphqlApiMapping]: ['.graphql.yml'],
    [ConnectorMethodImplementationType.operationMapping]: ['.op.yml'],
    [ConnectorMethodImplementationType.javascript]: ['.js', '.ts'],
    [ConnectorMethodImplementationType.mapping]: ['.map.yml', '.yml'],
};
const ConnectorMethodImplementationBase = z.object({
    implementationType: z.nativeEnum(ConnectorMethodImplementationType).optional(),
    filePath: z.string().optional(),
    inheritedFromPath: z.array(z.string()).optional(),
    isIncomplete: z.boolean().optional(),
    parametersSchema: DataSchema.optional(),
});
const ConnectorMethodImplementationMapping = ConnectorMethodImplementationBase.extend({
    implementationType: z.literal(ConnectorMethodImplementationType.mapping),
    mapping: z.any(),
});
const ConnectorMethodImplementationOperationMapping = ConnectorMethodImplementationBase.extend({
    implementationType: z.literal(ConnectorMethodImplementationType.operationMapping),
    mapping: z.any(),
});
const ConnectorMethodImplementationRestApiMapping = ConnectorMethodImplementationBase.extend({
    implementationType: z.literal(ConnectorMethodImplementationType.restApiMapping),
    mapping: z.any(),
});
const ConnectorMethodImplementationGraphqlApiMapping = ConnectorMethodImplementationBase.extend({
    implementationType: z.literal(ConnectorMethodImplementationType.graphqlApiMapping),
    mapping: z.any(),
});
const ConnectorMethodImplementationJavascript = ConnectorMethodImplementationBase.extend({
    implementationType: z.literal(ConnectorMethodImplementationType.javascript),
    code: z.string(),
});
const ConnectorMethodImplementationNotSupported = ConnectorMethodImplementationBase.extend({
    implementationType: z.literal(ConnectorMethodImplementationType.notSupported),
});
const ConnectorMethodImplementation = z.union([
    ConnectorMethodImplementationBase,
    ConnectorMethodImplementationMapping,
    ConnectorMethodImplementationOperationMapping,
    ConnectorMethodImplementationRestApiMapping,
    ConnectorMethodImplementationGraphqlApiMapping,
    ConnectorMethodImplementationJavascript,
    ConnectorMethodImplementationNotSupported,
]);

const CONNECTOR_AUTH_TYPES = [
    'integration-app-token',
    'membrane-token',
    'oauth2',
    'oauth1',
    'client-credentials',
    'proxy',
];
const ConnectorAuthSpec = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    type: z.enum(CONNECTOR_AUTH_TYPES),
    inputSchema: DataSchema.optional(),
    ui: z
        .object({
        schema: DataSchema.optional(),
        helpUri: z.string().optional(),
    })
        .optional(),
});
const ConnectorAuthHandlerBase = ConnectorAuthSpec.extend({
    parametersSchema: DataSchema.optional(),
    credentialsSchema: DataSchema.optional(),
    customCredentialsSchema: DataSchema.optional(),
    makeApiClient: ConnectorMethodImplementationBase.optional(),
    refreshCredentials: ConnectorMethodImplementationBase.optional(),
    test: ConnectorMethodImplementationBase.optional(),
    disconnect: ConnectorMethodImplementationBase.optional(),
    enabled: z.any().optional(),
});
function getAuthSpec(spec, authOptionKey) {
    var _a, _b;
    let authSpec;
    if (authOptionKey) {
        authSpec = (_b = (_a = spec === null || spec === void 0 ? void 0 : spec.auth) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b[authOptionKey];
    }
    else {
        authSpec = spec === null || spec === void 0 ? void 0 : spec.auth;
    }
    return authSpec;
}
const getDefaultAuthInputSchema = (args) => {
    var _a, _b, _c, _d, _e, _f;
    const authSpec = getAuthSpec(args.connectorSpec, args.authOptionKey);
    const inputSchema = (_b = (_a = authSpec === null || authSpec === void 0 ? void 0 : authSpec.ui) === null || _a === void 0 ? void 0 : _a.schema) !== null && _b !== void 0 ? _b : (_d = (_c = args.connectorSpec) === null || _c === void 0 ? void 0 : _c.ui) === null || _d === void 0 ? void 0 : _d.schema;
    return {
        type: 'object',
        properties: {
            connectorParameters: (_f = (_e = args.connectorSpec) === null || _e === void 0 ? void 0 : _e.parametersSchema) !== null && _f !== void 0 ? _f : {},
            connectionInput: inputSchema,
        },
    };
};
const ConnectorAuthMethodTypes = {
    getOAuthConfig: {
        authTypes: ['oauth2', 'oauth1'],
        fileKeys: ['get-oauth-config', 'oauth-config'],
        supportedImplementationTypes: [
            ConnectorMethodImplementationType.mapping,
            ConnectorMethodImplementationType.javascript,
        ],
        getInputSchema: (args) => {
            const schema = getDefaultAuthInputSchema(args);
            return {
                ...schema,
                properties: {
                    ...schema.properties,
                    redirectUri: { type: 'string' },
                    state: { type: 'string' },
                },
            };
        },
        getOutputSchema: ({ connectorSpec, authOptionKey }) => {
            const authSpec = getAuthSpec(connectorSpec, authOptionKey);
            return (authSpec === null || authSpec === void 0 ? void 0 : authSpec.type) === 'oauth1' ? OAUTH1_CONFIG_SCHEMA : OAUTH_CONFIG_SCHEMA;
        },
        isRequired: true,
    },
    getTokenData: {
        authTypes: ['oauth2'],
        supportedImplementationTypes: [
            ConnectorMethodImplementationType.mapping,
            ConnectorMethodImplementationType.javascript,
        ],
        getInputSchema: getDefaultAuthInputSchema,
        getOutputSchema: ({ connectorSpec, authOptionKey }) => {
            const authSpec = getAuthSpec(connectorSpec, authOptionKey);
            return authSpec === null || authSpec === void 0 ? void 0 : authSpec.credentialsSchema;
        },
        isRequired: false,
    },
    getCredentialsFromAccessTokenResponse: {
        authTypes: ['oauth2'],
        supportedImplementationTypes: [
            ConnectorMethodImplementationType.mapping,
            ConnectorMethodImplementationType.javascript,
        ],
        isRequired: false,
    },
    getCredentialsFromRefreshTokenResponse: {
        authTypes: ['oauth2'],
        supportedImplementationTypes: [
            ConnectorMethodImplementationType.mapping,
            ConnectorMethodImplementationType.javascript,
        ],
        isRequired: false,
    },
    getCredentialsFromConnectionParameters: {
        authTypes: ['client-credentials', 'oauth2'],
        getInputSchema: getDefaultAuthInputSchema,
        getOutputSchema: ({ connectorSpec, authOptionKey }) => {
            var _a;
            const authSpec = getAuthSpec(connectorSpec, authOptionKey);
            return (_a = authSpec === null || authSpec === void 0 ? void 0 : authSpec.auth) === null || _a === void 0 ? void 0 : _a.credentialsSchema;
        },
        supportedImplementationTypes: [
            ConnectorMethodImplementationType.mapping,
            ConnectorMethodImplementationType.javascript,
        ],
        isRequired: false,
    },
    refreshCredentials: {
        authTypes: ['oauth2', 'oauth1', 'client-credentials'],
        getInputSchema: getDefaultAuthInputSchema,
        getOutputSchema: ({ connectorSpec, authOptionKey }) => {
            var _a;
            const authSpec = getAuthSpec(connectorSpec, authOptionKey);
            return (_a = authSpec === null || authSpec === void 0 ? void 0 : authSpec.auth) === null || _a === void 0 ? void 0 : _a.credentialsSchema;
        },
        supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
        isRequired: false,
    },
    makeApiClient: {
        supportedImplementationTypes: [
            ConnectorMethodImplementationType.mapping,
            ConnectorMethodImplementationType.javascript,
        ],
        getInputSchema: ({ connectorSpec, authOptionKey }) => {
            var _a, _b;
            const authSpec = getAuthSpec(connectorSpec, authOptionKey);
            return {
                type: 'object',
                properties: {
                    credentials: mergeSchemas([
                        ((_a = authSpec === null || authSpec === void 0 ? void 0 : authSpec.customCredentialsSchema) !== null && _a !== void 0 ? _a : authSpec === null || authSpec === void 0 ? void 0 : authSpec.credentialsSchema),
                        (_b = authSpec === null || authSpec === void 0 ? void 0 : authSpec.ui) === null || _b === void 0 ? void 0 : _b.schema,
                    ]),
                },
            };
        },
        getOutputSchema: () => REST_API_CLIENT_SCHEMA$1,
        isRequired: true,
    },
    test: {
        supportedImplementationTypes: [
            ConnectorMethodImplementationType.operationMapping,
            ConnectorMethodImplementationType.restApiMapping,
            ConnectorMethodImplementationType.javascript,
        ],
        getOutputSchema: () => ({
            title: 'Connection Is Active',
            type: 'boolean',
        }),
        isRequired: true,
    },
    disconnect: {
        authTypes: ['oauth2', 'oauth1', 'client-credentials'],
        getInputSchema: getDefaultAuthInputSchema,
        supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
        isRequired: false,
    },
};
const ConnectorAuthOAuth2 = ConnectorAuthHandlerBase.extend({
    type: z.literal('oauth2'),
    getOAuthConfig: ConnectorMethodImplementation.optional(),
    getTokenData: ConnectorMethodImplementation.optional(),
    getCredentialsFromAccessTokenResponse: ConnectorMethodImplementation.optional(),
    getCredentialsFromRefreshTokenResponse: ConnectorMethodImplementation.optional(),
    oAuthConfig: ConnectorMethodImplementation.optional(),
});
const ConnectorAuthOAuth1 = ConnectorAuthHandlerBase.extend({
    type: z.literal('oauth1'),
    getOAuthConfig: ConnectorMethodImplementation.optional(),
});
const ConnectorAuthClientCredentials = ConnectorAuthHandlerBase.extend({
    type: z.literal('client-credentials'),
    getCredentialsFromConnectionParameters: ConnectorMethodImplementationBase.optional(),
});
const ConnectorAuthProxy = ConnectorAuthHandlerBase.extend({
    type: z.literal('proxy'),
    proxyKey: z.string(),
});
const ConnectorAuthIntegrationAppToken = ConnectorAuthHandlerBase.extend({
    type: z.literal('integration-app-token'),
});
const ConnectorAuthMembraneToken = ConnectorAuthHandlerBase.extend({
    type: z.literal('membrane-token'),
});
const ConnectorAuthOption = z.union([
    ConnectorAuthOAuth2,
    ConnectorAuthOAuth1,
    ConnectorAuthClientCredentials,
    ConnectorAuthIntegrationAppToken,
    ConnectorAuthMembraneToken,
    ConnectorAuthProxy,
]);
const ConnectorAuthCommon = ConnectorAuthHandlerBase.extend({
    type: z.enum(CONNECTOR_AUTH_TYPES).optional(),
    getOAuthConfig: ConnectorMethodImplementation.optional(),
    getTokenData: ConnectorMethodImplementation.optional(),
    getCredentialsFromAccessTokenResponse: ConnectorMethodImplementation.optional(),
    getCredentialsFromRefreshTokenResponse: ConnectorMethodImplementation.optional(),
    getCredentialsFromConnectionParameters: ConnectorMethodImplementationBase.optional(),
    proxyKey: z.string().optional(),
    oAuthConfig: ConnectorMethodImplementation.optional(),
});
const ConnectorAuthWithType = ConnectorAuthOption.and(z.object({
    options: z.record(z.string(), ConnectorAuthOption).optional(),
}));
const ConnectorAuthWithOptions = ConnectorAuthCommon.and(z.object({
    options: z.record(z.string(), ConnectorAuthOption),
}));
const ConnectorAuth = z.union([ConnectorAuthWithType, ConnectorAuthWithOptions]);
const OAUTH_CONFIG_SCHEMA = {
    type: 'object',
    properties: {
        clientId: { type: 'string' },
        clientSecret: { type: 'string' },
        authorizeUri: {
            type: 'string',
            description: 'URL to redirect user to when starting the authentication process',
        },
        tokenUri: {
            type: 'string',
            description: 'URL used to exchnage code for access token',
        },
        scopes: { type: 'array', items: { type: 'string' } },
        clientAuthLocation: {
            type: 'string',
            enum: ['headers', 'body'],
            default: 'headers',
            description: 'Whether to send clientId and clientSecret in the request body or headers (different apps expect it in different places)',
        },
        noRefreshToken: {
            type: 'boolean',
            description: 'Do not expect refresh_token in the token response. Use this if application does not support refresh tokens.',
        },
        skipPkce: {
            type: 'boolean',
            description: 'Skip sending PKCE parameters in the token request. Some authentication processes error if PKCE is enabled.',
        },
        authorizationCodeParam: {
            type: 'string',
            default: 'code',
            description: 'Name of the redirect query parameter that carries the authorization code. Defaults to "code". Set this for providers that return the code under a non-standard parameter name (e.g. Amazon Selling Partner API uses "spapi_oauth_code").',
        },
        extra: {
            type: 'object',
            additionalProperties: true,
            description: 'Any additional parameters in the request query parameters that are not covered by the oAuth2 standard.',
        },
        skipClientAuthInBody: {
            type: 'boolean',
            description: '(Deprecated) Skip sending client credentials in the body of the token request',
        },
        skipClientAuthInHeader: {
            type: 'boolean',
            description: '(Deprecated) Skip sending client credentials in the body of the token request',
        },
    },
};
const OAUTH1_CONFIG_SCHEMA = {
    type: 'object',
    properties: {
        consumerKey: { type: 'string' },
        consumerSecret: { type: 'string' },
        requestTokenUri: { type: 'string' },
        authorizeUri: { type: 'string' },
        tokenUri: { type: 'string' },
        scopes: { type: 'array', items: { type: 'string' } },
        extra: {
            type: 'object',
            additionalProperties: true,
            description: 'Additional parameters in the request query parameters',
        },
    },
};
const REST_API_CLIENT_RESPONSE_HANDLERS_SCHEMA = {
    title: 'Respose Handlers',
    description: 'Define how to handle responses from the API',
    type: 'array',
    items: {
        type: 'object',
        properties: {
            match: {
                type: 'boolean',
                description: 'Whether this handler matches the response. If true, the handler will be used.',
            },
            isSuccess: {
                type: 'boolean',
                description: 'Whether to consider response successful. If yes - data will be returned. If no - error will be thrown',
            },
            data: {},
            error: {
                type: 'object',
                properties: {
                    message: { type: 'string' },
                    key: {
                        type: 'string',
                        enum: [
                            ConnectionErrorKey.ACCESS_TOKEN_EXPIRED,
                            ConnectionErrorKey.CONNECTION_IS_DEFUNCT,
                            ConnectionErrorKey.RATE_LIMIT_EXCEEDED,
                        ],
                    },
                    data: {},
                },
            },
        },
    },
};
const REST_API_CLIENT_SCHEMA$1 = {
    type: 'object',
    properties: {
        args: {
            type: 'object',
            properties: {
                baseUri: {
                    type: 'string',
                    description: 'This prefix will be added to all relative API requests.',
                },
                query: {
                    type: 'object',
                    additionalProperties: true,
                },
                headers: {
                    type: 'object',
                    additionalProperties: true,
                },
                auth: {
                    type: 'object',
                    description: 'Basic authentication',
                    properties: {
                        username: { type: 'string' },
                        password: { type: 'string' },
                    },
                },
                responseHandlers: REST_API_CLIENT_RESPONSE_HANDLERS_SCHEMA,
            },
        },
    },
};

const CONNECTOR_DATA_DIR = 'data';
const CONNECTOR_OPERATIONS_DIR = 'operations';
const CONNECTOR_DOCS_DIR = 'docs';
const CONNECTOR_GLOBAL_WEBHOOKS_DIR = 'global-webhooks';
const CONNECTOR_EVENTS_DIR = 'events';

function getEffectiveConnectorOption(connector, optionKey) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const option = (_a = connector.options) === null || _a === void 0 ? void 0 : _a[optionKey];
    if (!option) {
        if (optionKey === '') {
            return connector;
        }
        return undefined;
    }
    const connectorInputSchema = (_b = connector.inputSchema) !== null && _b !== void 0 ? _b : (_c = connector.ui) === null || _c === void 0 ? void 0 : _c.schema;
    const optionInputSchema = (_d = option === null || option === void 0 ? void 0 : option.inputSchema) !== null && _d !== void 0 ? _d : (_e = option === null || option === void 0 ? void 0 : option.ui) === null || _e === void 0 ? void 0 : _e.schema;
    return {
        ...connector,
        ...option,
        inputSchema: mergeSchemas([connectorInputSchema, optionInputSchema]),
        parametersSchema: mergeSchemas([connector.parametersSchema, option === null || option === void 0 ? void 0 : option.parametersSchema]),
        credentialsSchema: mergeSchemas([connector.credentialsSchema, option === null || option === void 0 ? void 0 : option.credentialsSchema]),
        makeApiClient: (_f = option === null || option === void 0 ? void 0 : option.makeApiClient) !== null && _f !== void 0 ? _f : connector.makeApiClient,
        test: (_g = option === null || option === void 0 ? void 0 : option.test) !== null && _g !== void 0 ? _g : connector.test,
        disconnect: (_h = option === null || option === void 0 ? void 0 : option.disconnect) !== null && _h !== void 0 ? _h : connector.disconnect,
    };
}
const getDefaultInputSchema = (authConfig) => {
    var _a, _b, _c;
    const inputSchema = (_a = authConfig === null || authConfig === void 0 ? void 0 : authConfig.inputSchema) !== null && _a !== void 0 ? _a : (_b = authConfig === null || authConfig === void 0 ? void 0 : authConfig.ui) === null || _b === void 0 ? void 0 : _b.schema;
    return {
        type: 'object',
        properties: {
            connectorParameters: (_c = authConfig === null || authConfig === void 0 ? void 0 : authConfig.parametersSchema) !== null && _c !== void 0 ? _c : {},
            ...(inputSchema && { connectionInput: inputSchema }),
        },
    };
};
const REST_API_CLIENT_SCHEMA = {
    type: 'object',
    properties: {
        args: {
            type: 'object',
            properties: {
                baseUri: {
                    type: 'string',
                    description: 'This prefix will be added to all relative API requests.',
                },
                query: {
                    type: 'object',
                    additionalProperties: true,
                },
                headers: {
                    type: 'object',
                    additionalProperties: true,
                },
                auth: {
                    type: 'object',
                    description: 'Basic authentication',
                    properties: {
                        username: { type: 'string' },
                        password: { type: 'string' },
                    },
                },
            },
        },
    },
};
const ConnectorFunctionSpecs = {
    getOAuthConfig: {
        authTypes: ['oauth2', 'oauth1'],
        supportedFunctionTypes: [FunctionType.mapping, FunctionType.javascript],
        getInputSchema: (authConfig) => {
            const schema = getDefaultInputSchema(authConfig);
            return {
                ...schema,
                properties: {
                    ...schema.properties,
                    redirectUri: { type: 'string' },
                    state: { type: 'string' },
                },
            };
        },
        getOutputSchema: (authConfig) => {
            return (authConfig === null || authConfig === void 0 ? void 0 : authConfig.type) === 'oauth1' ? OAUTH1_CONFIG_SCHEMA : OAUTH_CONFIG_SCHEMA;
        },
        isRequired: true,
    },
    getTokenData: {
        authTypes: ['oauth2'],
        supportedFunctionTypes: [FunctionType.mapping, FunctionType.javascript],
        getInputSchema: getDefaultInputSchema,
        getOutputSchema: (authConfig) => {
            var _a;
            return (_a = authConfig === null || authConfig === void 0 ? void 0 : authConfig.customCredentialsSchema) !== null && _a !== void 0 ? _a : authConfig === null || authConfig === void 0 ? void 0 : authConfig.credentialsSchema;
        },
        isRequired: false,
    },
    getCredentialsFromAccessTokenResponse: {
        authTypes: ['oauth2'],
        supportedFunctionTypes: [FunctionType.mapping, FunctionType.javascript],
        isRequired: false,
    },
    getCredentialsFromRefreshTokenResponse: {
        authTypes: ['oauth2'],
        supportedFunctionTypes: [FunctionType.mapping, FunctionType.javascript],
        isRequired: false,
    },
    getCredentialsFromConnectionParameters: {
        authTypes: ['client-credentials', 'oauth2'],
        getInputSchema: getDefaultInputSchema,
        getOutputSchema: (authConfig) => {
            return authConfig === null || authConfig === void 0 ? void 0 : authConfig.credentialsSchema;
        },
        supportedFunctionTypes: [FunctionType.mapping, FunctionType.javascript],
        isRequired: false,
    },
    refreshCredentials: {
        authTypes: ['oauth2', 'oauth1', 'client-credentials'],
        getInputSchema: getDefaultInputSchema,
        getOutputSchema: (authConfig) => {
            return authConfig === null || authConfig === void 0 ? void 0 : authConfig.credentialsSchema;
        },
        supportedFunctionTypes: [FunctionType.javascript],
        isRequired: false,
    },
    makeApiClient: {
        supportedFunctionTypes: [FunctionType.mapping, FunctionType.javascript],
        getInputSchema: (authConfig) => {
            var _a, _b, _c;
            return {
                type: 'object',
                properties: {
                    credentials: mergeSchemas([
                        ((_a = authConfig === null || authConfig === void 0 ? void 0 : authConfig.customCredentialsSchema) !== null && _a !== void 0 ? _a : authConfig === null || authConfig === void 0 ? void 0 : authConfig.credentialsSchema),
                        (_b = authConfig === null || authConfig === void 0 ? void 0 : authConfig.inputSchema) !== null && _b !== void 0 ? _b : (_c = authConfig === null || authConfig === void 0 ? void 0 : authConfig.ui) === null || _c === void 0 ? void 0 : _c.schema,
                    ]),
                },
            };
        },
        getOutputSchema: () => REST_API_CLIENT_SCHEMA,
        isRequired: true,
    },
    test: {
        supportedFunctionTypes: [FunctionType.operationMapping, FunctionType.restApiMapping, FunctionType.javascript],
        getOutputSchema: () => ({
            title: 'Success Criteria',
            description: 'If this value is truthy, the test is considered a success.',
            type: 'boolean',
        }),
        isRequired: true,
    },
    disconnect: {
        authTypes: ['oauth2', 'oauth1', 'client-credentials'],
        getInputSchema: getDefaultInputSchema,
        supportedFunctionTypes: [FunctionType.javascript],
        isRequired: false,
    },
};

const ConnectorOperationMethodImplementationTypes = [
    ConnectorMethodImplementationType.restApiMapping,
    ConnectorMethodImplementationType.javascript,
];

const DataRecordSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
    uri: z.string().optional(),
    iconUri: z.string().optional(),
    fields: z.record(z.string(), z.any()).optional(),
    udm: z.string().optional(),
    unifiedFields: z.record(z.string(), z.any()).optional(),
    rawFields: z.record(z.string(), z.any()).optional(),
    createdTime: z.string().optional(),
    updatedTime: z.string().optional(),
    deletedTime: z.string().optional(),
    createdById: z.string().optional(),
    updatedById: z.string().optional(),
});
var DataCollectionEventType;
(function (DataCollectionEventType) {
    DataCollectionEventType["CREATED"] = "created";
    DataCollectionEventType["UPDATED"] = "updated";
    DataCollectionEventType["DELETED"] = "deleted";
})(DataCollectionEventType || (DataCollectionEventType = {}));

const structuredClone$1 = (structuredClonePolyfill === null || structuredClonePolyfill === void 0 ? void 0 : structuredClonePolyfill.default) || structuredClonePolyfill;
function isNil(value) {
    return value == null;
}
function omitBy(obj, check) {
    obj = { ...obj };
    Object.entries(obj).forEach(([key, value]) => check(value) && delete obj[key]);
    return obj;
}
function isEmptyValue(value) {
    if (isNil(value)) {
        return true;
    }
    if (typeof value === 'symbol') {
        return true;
    }
    if (typeof value === 'boolean') {
        return false;
    }
    if (typeof value === 'number') {
        return isNaN(value);
    }
    if (typeof value === 'string') {
        return value.trim().length === 0;
    }
    const tag = value.toString();
    if (tag === '[object Map]' || tag === '[object Set]') {
        return !value.size;
    }
    return typeof value === 'object' && Object.keys(value).length === 0;
}
const simpleUniqueId = () => {
    return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        .toString(36)
        .padStart(11, '0')
        .substring(0, 11);
};

function getDataLocationMethodPath(locationKey, methodKey) {
    return `data/${locationKey}/${toKebabCase(methodKey)}`;
}
function dataLocationParametersMatch({ locationParameters, targetParameters, }) {
    targetParameters = typeof targetParameters === 'object' && targetParameters ? targetParameters : {};
    locationParameters = typeof locationParameters === 'object' && locationParameters ? locationParameters : {};
    for (const [key, value] of Object.entries(targetParameters)) {
        if (locationParameters[key] !== value) {
            return false;
        }
    }
    return true;
}
function parseDataLocationPath(path) {
    if (!path) {
        return undefined;
    }
    if (path.startsWith('/')) {
        path = path.slice(1);
    }
    if (!path.startsWith('data/')) {
        throw new Error(`Invalid data location path: ${path}. It should start with "data/"`);
    }
    path = path.slice(5);
    const [key, query] = path.split('?');
    const parameters = Object.fromEntries(new URLSearchParams(query).entries());
    return { key, parameters };
}
function makeDataLocationPath(pointer) {
    var _a;
    const normalizedPointer = normalizePointer(pointer);
    if (normalizedPointer === null || normalizedPointer === void 0 ? void 0 : normalizedPointer.key) {
        let path = `/data/${normalizedPointer.key}`;
        const parameters = omitBy((_a = normalizedPointer.parameters) !== null && _a !== void 0 ? _a : {}, isNil);
        if (Object.keys(parameters).length > 0) {
            path += `?${new URLSearchParams(parameters).toString()}`;
        }
        return path;
    }
    else {
        return undefined;
    }
}
function normalizePointer(pointer) {
    if (typeof pointer === 'string' && pointer.startsWith('/data/')) {
        return {
            key: pointer.slice(6),
        };
    }
    return pointer;
}
function makeDataLocationOperationPath(collectionPath, operation) {
    const [path, query] = (collectionPath !== null && collectionPath !== void 0 ? collectionPath : '').split('?');
    return urljoin(path, operation, query ? `?${query}` : '');
}
function isSameDataLocation(location1, location2) {
    if (location1.key !== location2.key) {
        return false;
    }
    const cleanParameters1 = location1.parameters && Object.keys(location1.parameters).length > 0 ? location1.parameters : undefined;
    const cleanParameters2 = location2.parameters && Object.keys(location2.parameters).length > 0 ? location2.parameters : undefined;
    if (!deepEqual(cleanParameters1, cleanParameters2)) {
        return false;
    }
    return true;
}
function isDataLocationMethodSupported(location, methodKey) {
    var _a;
    const method = (_a = location === null || location === void 0 ? void 0 : location.methods) === null || _a === void 0 ? void 0 : _a[methodKey];
    return method && method.implementationType !== ConnectorMethodImplementationType.notSupported;
}
function getDataCollectionCreateFields(spec) {
    if (!spec)
        return undefined;
    if (!spec.fieldsSchema)
        return undefined;
    if (!spec.create)
        return undefined;
    let schema = excludeReadOnlyFieldsFromSchema(spec.fieldsSchema);
    if (spec.create.fields) {
        schema = pickFieldsFromSchema(schema, spec.create.fields);
    }
    if (spec.create.excludedFields) {
        schema = excludeFieldsFromSchema(schema, spec.create.excludedFields);
    }
    return schema;
}
function getDataCollectionUpdateFields(spec) {
    if (!spec)
        return undefined;
    if (!spec.fieldsSchema)
        return undefined;
    if (!spec.update)
        return undefined;
    let schema = excludeReadOnlyFieldsFromSchema(spec.fieldsSchema);
    if (spec.update.fields) {
        schema = pickFieldsFromSchema(schema, spec.update.fields);
    }
    if (spec.update.excludedFields) {
        schema = excludeFieldsFromSchema(schema, spec.update.excludedFields);
    }
    return schema;
}
function getWritableFieldsSchema(spec) {
    if (!spec)
        return undefined;
    if (!spec.fieldsSchema)
        return undefined;
    return mergeSchemas([getDataCollectionCreateFields(spec), getDataCollectionUpdateFields(spec)]);
}
function getReferenceCollectionPointerForSchema(schema) {
    if ((schema === null || schema === void 0 ? void 0 : schema.referenceCollection) && schema.referenceCollection.key) {
        return schema.referenceCollection;
    }
    else if (schema === null || schema === void 0 ? void 0 : schema.referenceCollectionPath) {
        return parseDataLocationPath(schema.referenceCollectionPath);
    }
    else if (schema === null || schema === void 0 ? void 0 : schema.referenceCollectionUri) {
        return parseDataLocationPath(schema.referenceCollectionUri);
    }
    else {
        return undefined;
    }
}
function getReferenceCollectionPathForSchema(schema) {
    const pointer = getReferenceCollectionPointerForSchema(schema);
    return makeDataLocationPath(pointer);
}

const FIELDS_SCHEMA$G = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        title: { type: 'string' },
        description: { type: 'string' },
        type: { type: 'string' },
        durationSeconds: { type: 'number' },
        location: { type: 'string' },
        isRecurrent: { type: 'boolean' },
        participants: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    userId: { type: 'string' },
                    contactId: { type: 'string' },
                },
            },
        },
        status: { type: 'string' },
        contactId: {
            type: 'string',
            referenceUdm: 'contacts',
        },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        dealId: {
            type: 'string',
            referenceUdm: 'deals',
        },
        leadId: {
            type: 'string',
            referenceUdm: 'leads',
        },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        startTime: {
            type: 'string',
            format: 'date-time',
        },
        endTime: {
            type: 'string',
            format: 'date-time',
        },
        dueTime: {
            type: 'string',
            format: 'date-time',
        },
        createdTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
    },
};
const FallbackFieldsMap = {
    fromActivitiesToTasks: {
        subject: {
            $var: 'title',
        },
        content: {
            $var: 'description',
        },
    },
    fromTasksToActivities: {
        title: {
            $var: 'subject',
        },
        description: {
            $var: 'content',
        },
    },
    fromActivitiesToNotes: {
        content: {
            $var: 'description',
        },
    },
    fromNotesToActivities: {
        description: {
            $var: 'content',
        },
    },
    fromActivitiesToEmails: {
        subject: {
            $var: 'title',
        },
        body: {
            $var: 'description',
        },
    },
    fromEmailsToActivities: {
        title: {
            $var: 'subject',
        },
        description: {
            $var: 'body',
        },
    },
};
const udm$G = {
    singularName: 'activity',
    pluralName: 'activities',
    fieldsSchema: FIELDS_SCHEMA$G,
    list: {
        filterFields: ['type', 'companyId', 'contactId', 'dealId', 'leadId', 'ownerId'],
    },
    match: {
        fields: ['type', 'companyId', 'contactId', 'dealId', 'leadId', 'ownerId'],
    },
    populateFields,
    udmFallbacks: [
        {
            udm: 'tasks',
            importFields: FallbackFieldsMap.fromTasksToActivities,
            exportFields: FallbackFieldsMap.fromActivitiesToTasks,
        },
        {
            udm: 'meetings',
        },
        {
            udm: 'notes',
            importFields: FallbackFieldsMap.fromNotesToActivities,
            exportFields: FallbackFieldsMap.fromActivitiesToNotes,
        },
        {
            udm: 'emails',
            importFields: FallbackFieldsMap.fromEmailsToActivities,
            exportFields: FallbackFieldsMap.fromActivitiesToEmails,
        },
    ],
};
function populateFields(fields) {
    const result = JSON.parse(JSON.stringify(fields));
    if (!result.dueTime) {
        result.dueTime = result.endTime || result.startTime;
    }
    if (!result.startTime) {
        result.startTime = result.dueTime || result.endTime;
    }
    if (!result.endTime) {
        result.endTime = result.dueTime || result.startTime;
    }
    return result;
}

const FIELDS_SCHEMA$F = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        number: { type: 'string' },
        name: { type: 'string' },
        memo: { type: 'string' },
        status: { type: 'string' },
        paymentMethod: { type: 'string' },
        supplierId: { type: 'string', referenceUdm: 'suppliers' },
        currency: { type: 'string' },
        payments: {
            type: 'array',
            items: {
                type: 'string',
                referenceUdm: 'payments',
            },
        },
        lineItems: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    itemName: { type: 'string' },
                    description: { type: 'string' },
                    code: { type: 'string' },
                    type: { type: 'string' },
                    companyId: { type: 'string', referenceUdm: 'companies' },
                    ledgerAccountId: { type: 'string', referenceUdm: 'ledger-accounts' },
                    quantity: { type: 'number' },
                    unitPrice: { type: 'number' },
                    unitOfMeasure: { type: 'string' },
                    purchasePrice: { type: 'number' },
                    salesPrice: { type: 'number' },
                    taxable: { type: 'boolean' },
                    taxAmount: { type: 'number' },
                    totalAmount: { type: 'number' },
                },
            },
        },
        totalAmount: { type: 'number' },
        totalTax: { type: 'number' },
        exchangeRate: { type: 'number' },
        totalDiscount: { type: 'number' },
        subTotal: { type: 'number' },
        balance: { type: 'number' },
        ledgerAccountId: { type: 'string', referenceUdm: 'ledger-accounts' },
        issueDate: {
            type: 'string',
            format: 'date-time',
        },
        dueDate: {
            type: 'string',
            format: 'date-time',
        },
        paidOnDate: {
            type: 'string',
            format: 'date-time',
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$F = {
    singularName: 'bill',
    pluralName: 'bills',
    fieldsSchema: FIELDS_SCHEMA$F,
    list: {
        filterFields: ['name', 'number', 'status', 'supplierId'],
    },
    match: {
        fields: ['name', 'number', 'status', 'supplierId'],
    },
};

const FIELDS_SCHEMA$E = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        fullName: { type: 'string' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        primaryEmail: { type: 'string' },
        campaignId: {
            type: 'string',
            referenceUdm: 'campaigns',
        },
        contactId: {
            type: 'string',
            referenceUdm: 'contacts',
        },
        leadId: {
            type: 'string',
            referenceUdm: 'leads',
        },
        source: { type: 'string' },
        status: { type: 'string' },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        jobTitle: { type: 'string' },
        hasOptedOutOfEmail: { type: 'boolean' },
        createdTime: {
            type: 'string',
            format: 'date-time',
        },
        createdBy: {
            type: 'string',
            readOnly: true,
            referenceUdm: 'users',
        },
        updatedTime: {
            type: 'string',
            readOnly: true,
            format: 'date-time',
        },
        updatedBy: {
            type: 'string',
            readOnly: true,
            referenceUdm: 'users',
        },
    },
};
const udm$E = {
    singularName: 'campaign-member',
    pluralName: 'campaign-members',
    fieldsSchema: FIELDS_SCHEMA$E,
    list: {
        filterFields: [
            'primaryEmail',
            'fullName',
            'firstName',
            'lastName',
            'campaignId',
            'contactId',
            'leadId',
            'companyId',
        ],
    },
    match: {
        fields: ['primaryEmail', 'fullName', 'firstName', 'lastName', 'campaignId', 'contactId', 'leadId', 'companyId'],
    },
};

const FIELDS_SCHEMA$D = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: {
            type: 'string',
        },
        cost: {
            type: 'number',
        },
        description: {
            type: 'string',
        },
        source: {
            type: 'string',
        },
        stage: {
            type: 'string',
        },
        type: {
            type: 'string',
        },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        sentCount: {
            type: 'number',
            readOnly: true,
        },
        replyCount: {
            type: 'number',
            readOnly: true,
        },
        openCount: {
            type: 'number',
            readOnly: true,
        },
        clickCount: {
            type: 'number',
            readOnly: true,
        },
        startTime: {
            type: 'string',
            format: 'date-time',
        },
        endTime: {
            type: 'string',
            format: 'date-time',
        },
        createdTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        lastActivityTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
    },
};
const MODIFIABLE_FIELDS$2 = ['name', 'description', 'ownerId', 'startTime', 'endTime', 'cost', 'source', 'stage', 'type'];
const udm$D = {
    singularName: 'campaign',
    pluralName: 'campaigns',
    fieldsSchema: FIELDS_SCHEMA$D,
    categories: ['Marketing Automation', 'Sales', 'CRM', 'Ads'],
    list: {
        filterFields: ['name', 'source', 'stage', 'type', 'ownerId'],
    },
    find: {
        queryFields: ['name', 'source', 'stage', 'type', 'ownerId'],
    },
    match: {
        fields: ['name', 'source', 'stage', 'type', 'ownerId'],
    },
    create: {
        fields: MODIFIABLE_FIELDS$2,
    },
    update: {
        fields: MODIFIABLE_FIELDS$2,
    },
};

const AddressSchema = {
    type: 'object',
    properties: {
        type: { type: 'string' },
        full: { type: 'string' },
        street: { type: 'string' },
        city: { type: 'string' },
        state: { type: 'string' },
        country: { type: 'string' },
        zip: { type: 'string' },
    },
};
const PhoneEmailSchema = {
    type: 'object',
    properties: {
        value: { type: 'string' },
        type: { type: 'string' },
    },
};

const FIELDS_SCHEMA$C = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: {
            type: 'string',
        },
        websiteUrl: {
            type: 'string',
        },
        phones: {
            type: 'array',
            items: PhoneEmailSchema,
        },
        primaryPhone: {
            type: 'string',
        },
        description: {
            type: 'string',
        },
        currency: {
            type: 'string',
        },
        industry: {
            type: 'string',
        },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        primaryAddress: AddressSchema,
        addresses: {
            type: 'array',
            items: AddressSchema,
        },
        numberOfEmployees: {
            type: 'number',
        },
        createdTime: {
            type: 'string',
            format: 'date-time',
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        lastActivityTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
    },
};
const udm$C = {
    singularName: 'company',
    pluralName: 'companies',
    fieldsSchema: FIELDS_SCHEMA$C,
    list: {
        filterFields: ['ownerId', 'name', 'websiteUrl', 'industry'],
    },
    find: {
        queryFields: ['ownerId', 'name', 'websiteUrl', 'industry'],
    },
    match: {
        fields: ['ownerId', 'name', 'websiteUrl', 'industry'],
    },
    fieldFallbacks: {
        'primaryAddress.full': [
            {
                $concat: {
                    values: [
                        { $var: 'primaryAddress.street' },
                        { $var: 'primaryAddress.city' },
                        { $var: 'primaryAddress.state' },
                        { $var: 'primaryAddress.zip' },
                        { $var: 'primaryAddress.country' },
                    ],
                    delimiter: ', ',
                },
            },
        ],
    },
};

const FIELDS_SCHEMA$B = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        description: { type: 'string' },
        source: { type: 'string' },
        type: { type: 'string' },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        createdTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
    },
};
const MODIFIABLE_FIELDS$1 = ['name', 'description', 'ownerId', 'source', 'type'];
const udm$B = {
    singularName: 'contact-list',
    pluralName: 'contact-lists',
    fieldsSchema: FIELDS_SCHEMA$B,
    categories: ['Marketing Automation', 'Sales', 'CRM'],
    list: {
        filterFields: ['name', 'source', 'type', 'ownerId'],
    },
    match: {
        fields: ['name'],
    },
    create: {
        fields: MODIFIABLE_FIELDS$1,
    },
    update: {
        fields: MODIFIABLE_FIELDS$1,
    },
    udmFallbacks: [{ udm: 'campaigns' }],
};

const FIELDS_SCHEMA$A = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        fullName: { type: 'string' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        primaryEmail: { type: 'string' },
        emails: {
            type: 'array',
            items: PhoneEmailSchema,
        },
        primaryPhone: { type: 'string' },
        phones: {
            type: 'array',
            items: PhoneEmailSchema,
        },
        primaryAddress: AddressSchema,
        addresses: {
            type: 'array',
            items: AddressSchema,
        },
        stage: { type: 'string' },
        companyName: { type: 'string' },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        jobTitle: { type: 'string' },
        source: { type: 'string' },
        createdTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        lastActivityTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
    },
};
const FILTER_FIELDS$g = [
    'fullName',
    'firstName',
    'lastName',
    'primaryEmail',
    'primaryPhone',
    'companyId',
    'companyName',
    'jobTitle',
    'source',
    'stage',
    'ownerId',
];
const udm$A = {
    singularName: 'contact',
    pluralName: 'contacts',
    fieldsSchema: FIELDS_SCHEMA$A,
    list: {
        filterFields: FILTER_FIELDS$g,
    },
    match: {
        fields: ['fullName', 'primaryEmail', 'primaryPhone'],
    },
    fieldFallbacks: {
        fullName: [
            {
                $concat: {
                    values: [{ $var: 'firstName' }, { $var: 'lastName' }],
                    delimiter: ' ',
                },
            },
        ],
        firstName: [
            {
                $firstName: {
                    $var: 'fullName',
                },
            },
        ],
        lastName: [
            {
                $lastName: {
                    $var: 'fullName',
                },
            },
        ],
        primaryEmail: [
            {
                $var: 'emails[0].value',
            },
        ],
        emails: [
            [
                {
                    value: { $var: 'primaryEmail' },
                },
            ],
        ],
        primaryPhone: [
            {
                $var: 'phones[0].value',
            },
        ],
        phones: [
            [
                {
                    value: { $var: 'primaryPhone' },
                },
            ],
        ],
        primaryAddress: [
            {
                $var: 'addresses[0]',
            },
        ],
        addresses: [
            [
                {
                    value: { $var: 'primaryAddress' },
                },
            ],
        ],
        'primaryAddress.full': [
            {
                $concat: {
                    values: [
                        { $var: 'primaryAddress.street' },
                        { $var: 'primaryAddress.city' },
                        { $var: 'primaryAddress.state' },
                        { $var: 'primaryAddress.zip' },
                        { $var: 'primaryAddress.country' },
                    ],
                    delimiter: ', ',
                },
            },
        ],
    },
};

const FIELDS_SCHEMA$z = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        number: { type: 'string' },
        memo: { type: 'string' },
        status: { type: 'string' },
        customerId: { type: 'string', referenceUdm: 'customers' },
        currency: { type: 'string' },
        exchangeRate: { type: 'number' },
        payments: {
            type: 'array',
            items: {
                type: 'string',
                referenceUdm: 'payments',
            },
        },
        lineItems: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    itemName: { type: 'string' },
                    description: { type: 'string' },
                    quantity: { type: 'number' },
                    totalLineAmount: { type: 'number' },
                    companyId: { type: 'string', referenceUdm: 'companies' },
                    ledgerAccountId: { type: 'string', referenceUdm: 'ledger-accounts' },
                    unitPrice: { type: 'number' },
                    taxRate: { type: 'string', referenceUdm: 'tax-rates' },
                },
            },
        },
        allocations: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    type: {
                        type: 'string',
                        enum: ['invoice', 'order', 'expense', 'credit_memo', 'over_payment', 'pre_payment'],
                    },
                    amount: { type: 'number' },
                },
            },
        },
        totalAmount: { type: 'number' },
        totalTax: { type: 'number' },
        totalDiscount: { type: 'number' },
        subTotal: { type: 'number' },
        balance: { type: 'number' },
        ledgerAccountId: { type: 'string', referenceUdm: 'ledger-accounts' },
        issueDate: {
            type: 'string',
            format: 'date-time',
        },
        paidOnDate: {
            type: 'string',
            format: 'date-time',
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$z = {
    singularName: 'credit-note',
    pluralName: 'credit-notes',
    fieldsSchema: FIELDS_SCHEMA$z,
    categories: ['Accounting', 'Payments'],
    list: {
        filterFields: ['id', 'number', 'customerId', 'status', 'issueDate'],
    },
    match: {
        fields: ['id', 'number', 'customerId', 'status', 'issueDate'],
    },
};

const FIELDS_SCHEMA$y = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        code: { type: 'string' },
        description: { type: 'string' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        companyName: { type: 'string' },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        status: 'active',
        currency: { type: 'string' },
        addresses: {
            type: 'array',
            items: AddressSchema,
        },
        phones: {
            type: 'array',
            items: PhoneEmailSchema,
        },
        emails: {
            type: 'array',
            items: PhoneEmailSchema,
        },
        websiteUrl: {
            type: 'string',
        },
        notes: { type: 'string' },
        tax_number: { type: 'string' },
        orderIds: {
            type: 'array',
            items: {
                type: 'string',
                referenceUdm: 'orders',
            },
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$y = {
    singularName: 'customer',
    pluralName: 'customers',
    fieldsSchema: FIELDS_SCHEMA$y,
    categories: ['CRM', 'Sales', 'E-Commerce', 'Accounting', 'Payments'],
    list: {
        filterFields: ['code', 'companyId', 'companyName', 'firstName', 'lastName', 'name', 'status', 'websiteUrl'],
    },
    match: {
        fields: ['code', 'companyId', 'companyName', 'firstName', 'lastName', 'name', 'status', 'websiteUrl'],
    },
    udmFallbacks: [
        {
            udm: 'companies',
        },
    ],
};

const FIELDS_SCHEMA$x = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        quantity: { type: 'number' },
        price: { type: 'number' },
        dealId: {
            type: 'string',
            referenceUdm: 'deals',
        },
        productId: {
            type: 'string',
            referenceUdm: 'products',
        },
        isActive: { type: 'boolean' },
        createdTime: {
            type: 'string',
            format: 'date-time',
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
    },
};
const MODIFIABLE_FIELDS = ['name', 'quantity', 'price'];
const CREATABLE_FIELDS = ['name', 'quantity', 'price', 'dealId', 'productId'];
const udm$x = {
    singularName: 'deal-product',
    pluralName: 'deal-products',
    fieldsSchema: FIELDS_SCHEMA$x,
    list: {
        filterFields: ['dealId', 'productId', 'name'],
    },
    match: {
        fields: ['dealId', 'productId', 'name'],
    },
    find: {
        queryFields: ['dealId', 'productId', 'name'],
    },
    create: {
        fields: CREATABLE_FIELDS,
    },
    update: {
        fields: MODIFIABLE_FIELDS,
    },
};

const FIELDS_SCHEMA$w = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        state: { type: 'string', enum: ['open', 'won', 'lost'] },
    },
};
const udm$w = {
    singularName: 'deal-stage',
    pluralName: 'deal-stages',
    fieldsSchema: FIELDS_SCHEMA$w,
    categories: ['CRM', 'Sales'],
    list: {
        filterFields: ['name', 'state'],
    },
    match: {
        fields: ['name'],
    },
};

const FIELDS_SCHEMA$v = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        amount: { type: 'number' },
        currency: { type: 'string' },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        contactId: {
            type: 'string',
            referenceUdm: 'contacts',
        },
        campaignId: {
            type: 'string',
            referenceUdm: 'campaigns',
        },
        source: { type: 'string' },
        description: { type: 'string' },
        stage: { type: 'string' },
        stageId: { type: 'string' },
        status: {
            type: 'string',
            enum: ['open', 'won', 'lost'],
        },
        type: { type: 'string' },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        probability: { type: 'number' },
        closeTime: {
            type: 'string',
            format: 'date-time',
        },
        createdTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        lastActivityTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
    },
};
const udm$v = {
    singularName: 'deal',
    pluralName: 'deals',
    fieldsSchema: FIELDS_SCHEMA$v,
    categories: ['CRM', 'Sales'],
    list: {
        filterFields: ['companyId', 'name', 'stage', 'status', 'contactId', 'campaignId', 'source', 'type', 'ownerId'],
    },
    find: {
        queryFields: ['companyId', 'name', 'stage', 'status', 'contactId', 'campaignId', 'source', 'type', 'ownerId'],
    },
    match: {
        fields: ['companyId', 'name', 'stage', 'status', 'contactId', 'campaignId', 'source', 'type', 'ownerId'],
    },
};

const FIELDS_SCHEMA$u = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        uri: { type: 'string', readOnly: true },
        title: { type: 'string' },
        parentDocumentId: { type: 'string', referenceUdm: 'documents' },
        canHaveChildren: { type: 'boolean', readOnly: true },
        isDownloadable: { type: 'boolean', readOnly: true, default: true },
        text: { type: 'string' },
        ownerId: { type: 'string', referenceUdm: 'users' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$u = {
    singularName: 'document',
    pluralName: 'documents',
    fieldsSchema: FIELDS_SCHEMA$u,
    categories: ['Document Management'],
    list: {
        filterFields: ['parentDocumentId', 'ownerId'],
    },
};

const FIELDS_SCHEMA$t = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        itemType: { type: 'string', enum: ['file', 'folder'], allowCustom: true },
        name: { type: 'string' },
        mimeType: { type: 'string' },
        description: { type: 'string' },
        previewUri: { type: 'string', readOnly: true },
        size: { type: 'integer', readOnly: true },
        path: { type: 'string', readOnly: true },
        folderId: { type: 'string', referenceUdm: 'drive-items' },
        ownerId: { type: 'string', referenceUdm: 'users' },
        driveId: { type: 'string', referenceUdm: 'drives' },
        downloadUri: { type: 'string', readOnly: true },
        uri: { type: 'string', readOnly: true },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$t = {
    singularName: 'drive-item',
    pluralName: 'drive-items',
    fieldsSchema: FIELDS_SCHEMA$t,
    categories: ['File Storage', 'Communication', 'Document Management'],
    list: {
        filterFields: ['ownerId', 'folderId', 'driveId', 'mimeType', 'itemType'],
    },
};

const FIELDS_SCHEMA$s = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        description: { type: 'string' },
        ownerId: { type: 'string', referenceUdm: 'users' },
        uri: { type: 'string', readOnly: true },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$s = {
    singularName: 'drive',
    pluralName: 'drives',
    fieldsSchema: FIELDS_SCHEMA$s,
    categories: ['File Storage', 'Communication'],
    list: {
        filterFields: ['ownerId', 'name'],
    },
    match: {
        fields: ['ownerId', 'name'],
    },
};

const FIELDS_SCHEMA$r = {
    type: 'object',
    properties: {
        id: {
            type: 'string',
            readonly: true,
        },
        candidateId: {
            type: 'string',
            referenceUdm: 'jobCandidates',
        },
        race: { type: 'string' },
        gender: { type: 'string' },
        veteranStatus: { type: 'string' },
        disabilityStatus: { type: 'string' },
        submittedTime: { type: 'string', format: 'date-time', readOnly: true },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$f = ['candidateId', 'race', 'gender', 'veteranStatus', 'disabilityStatus'];
const udm$r = {
    singularName: 'eeoc',
    pluralName: 'eeocs',
    description: 'Represents the Equal Employment Opportunity Commission information for a candidate',
    fieldsSchema: FIELDS_SCHEMA$r,
    categories: ['ATS'],
    match: {
        fields: FILTER_FIELDS$f,
    },
    list: {
        filterFields: FILTER_FIELDS$f,
    },
};

const FIELDS_SCHEMA$q = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        threadId: { type: 'string', readOnly: true },
        to: {
            type: 'array',
            items: { type: 'string' },
        },
        cc: {
            type: 'array',
            items: { type: 'string' },
        },
        bcc: {
            type: 'array',
            items: { type: 'string' },
        },
        from: { type: 'string' },
        replyTo: { type: 'string' },
        subject: { type: 'string' },
        body: { type: 'string' },
        html_body: { type: 'string' },
        status: { type: 'string' },
        ownerId: { type: 'string', referenceUdm: 'users' },
        contactId: { type: 'string', referenceUdm: 'contacts' },
        leadId: { type: 'string', referenceUdm: 'leads' },
        companyId: { type: 'string', referenceUdm: 'companies' },
        dealId: { type: 'string', referenceUdm: 'deals' },
        attachments: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    type: { type: 'string' },
                    name: { type: 'string' },
                    url: { type: 'string' },
                    content: { type: 'string' },
                },
            },
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$q = {
    singularName: 'email',
    pluralName: 'emails',
    fieldsSchema: FIELDS_SCHEMA$q,
    categories: ['Communication', 'CRM', 'Sales', 'Customer Success', 'Marketing Automation'],
    list: {
        filterFields: ['subject', 'status', 'ownerId', 'contactId', 'leadId', 'companyId', 'dealId'],
    },
    match: {
        fields: ['subject', 'status', 'ownerId', 'contactId', 'leadId', 'companyId', 'dealId'],
    },
    udmFallbacks: [
        {
            udm: 'activities',
            importFields: FallbackFieldsMap.fromActivitiesToEmails,
            exportFields: FallbackFieldsMap.fromEmailsToActivities,
        },
    ],
};

const FIELDS_SCHEMA$p = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        fullName: { type: 'string' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        userName: { type: 'string' },
        preferredName: { type: 'string' },
        personalEmail: { type: 'string' },
        workEmail: { type: 'string' },
        mobileNumber: { type: 'string' },
        homeAddress: AddressSchema,
        workAddress: AddressSchema,
        employmentStatus: {
            type: 'string',
            enum: ['active', 'inactive', 'terminated', 'other'],
        },
        companyId: { type: 'string', referenceUdm: 'companies' },
        departmentId: { type: 'string' },
        startDate: { type: 'string', format: 'date-time' },
        terminationDate: { type: 'string', format: 'date-time' },
        gender: {
            type: 'string',
            enum: ['male', 'female', 'other', 'not_specified'],
            allowCustom: true,
        },
        ethnicity: { type: 'string' },
        birthday: { type: 'string', format: 'date-time' },
        photoUrl: { type: 'string' },
        timezone: { type: 'string' },
        title: { type: 'string' },
        employments: {
            type: 'array',
            properties: {
                id: { type: 'string' },
                title: { type: 'string' },
                role: { type: 'string' },
                type: { type: 'string' },
                compensationRate: { type: 'number' },
                paymentUnit: { type: 'string' },
                currency: { type: 'string' },
                department: { type: 'string' },
                division: { type: 'string' },
                location: AddressSchema,
                startDate: { type: 'string', format: 'date-time' },
                endDate: { type: 'string', format: 'date-time' },
            },
        },
        compensations: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    jobId: { type: 'string' },
                    rate: { type: 'number' },
                    paymentUnit: { type: 'string' },
                    currency: { type: 'string' },
                    paymentFrequency: { type: 'string' },
                    flsaStatus: { type: 'string' },
                    effectiveDate: { type: 'string', format: 'date-time' },
                },
            },
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$e = [
    'companyId',
    'ethnicity',
    'firstName',
    'fullName',
    'gender',
    'lastName',
    'mobileNumber',
    'personalEmail',
    'title',
    'userName',
    'workEmail',
];
const udm$p = {
    singularName: 'employee',
    pluralName: 'employees',
    fieldsSchema: FIELDS_SCHEMA$p,
    categories: ['HRIS', 'ATS'],
    match: {
        fields: FILTER_FIELDS$e,
    },
    list: {
        filterFields: FILTER_FIELDS$e,
    },
    fieldFallbacks: {
        fullName: [
            {
                $concat: {
                    values: [{ $var: 'firstName' }, { $var: 'lastName' }],
                    delimiter: ' ',
                },
            },
        ],
        firstName: [
            {
                $firstName: {
                    $var: 'fullName',
                },
            },
        ],
        lastName: [
            {
                $lastName: {
                    $var: 'fullName',
                },
            },
        ],
        'homeAddress.full': [
            {
                $concat: {
                    values: [
                        { $var: 'homeAddress.street' },
                        { $var: 'homeAddress.city' },
                        { $var: 'homeAddress.state' },
                        { $var: 'homeAddress.zip' },
                        { $var: 'homeAddress.country' },
                    ],
                    delimiter: ', ',
                },
            },
        ],
    },
    udmFallbacks: [
        {
            udm: 'users',
            importFields: {
                workEmail: {
                    $var: 'primaryEmail',
                },
                mobileNumber: {
                    $var: 'primaryPhone',
                },
                workAddress: {
                    $var: 'primaryAddress',
                },
            },
            exportFields: {
                primaryEmail: {
                    $var: 'workEmail',
                },
                primaryPhone: {
                    $var: 'mobileNumber',
                },
                primaryAddress: {
                    $var: 'workAddress',
                },
            },
        },
    ],
};

const FIELDS_SCHEMA$o = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        mimeType: { type: 'string' },
        name: { type: 'string' },
        description: { type: 'string' },
        previewUri: { type: 'string', readOnly: true },
        size: { type: 'integer', readOnly: true },
        path: { type: 'string', readOnly: true },
        folderId: { type: 'string', referenceUdm: 'folders' },
        ownerId: { type: 'string', referenceUdm: 'users' },
        driveId: { type: 'string', referenceUdm: 'drives' },
        downloadUri: { type: 'string', readOnly: true },
        uri: { type: 'string', readOnly: true },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$o = {
    singularName: 'file',
    pluralName: 'files',
    fieldsSchema: FIELDS_SCHEMA$o,
    categories: ['File Storage', 'Communication', 'Document Management'],
    list: {
        filterFields: ['ownerId', 'folderId', 'driveId', 'name', 'mimeType', 'size'],
    },
    match: {
        fields: ['ownerId', 'driveId', 'folderId', 'name', 'mimeType', 'size'],
    },
};

const FIELDS_SCHEMA$n = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        description: { type: 'string' },
        size: { type: 'integer', readOnly: true },
        path: { type: 'string', readOnly: true },
        parentFolderId: { type: 'string', referenceUdm: 'folders' },
        ownerId: { type: 'string', referenceUdm: 'users' },
        driveId: { type: 'string', referenceUdm: 'drives' },
        downloadUri: { type: 'string', readOnly: true },
        uri: { type: 'string', readOnly: true },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$n = {
    singularName: 'folder',
    pluralName: 'folders',
    fieldsSchema: FIELDS_SCHEMA$n,
    categories: ['File Storage', 'Communication', 'Document Management'],
    list: {
        filterFields: ['ownerId', 'parentFolderId', 'driveId', 'name', 'size'],
    },
    match: {
        fields: ['ownerId', 'parentFolderId', 'driveId', 'name', 'size'],
    },
};

const FIELDS_SCHEMA$m = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        number: { type: 'string' },
        name: { type: 'string' },
        memo: { type: 'string' },
        status: { type: 'string' },
        paymentMethod: { type: 'string' },
        customerId: { type: 'string', referenceUdm: 'customers' },
        currency: { type: 'string' },
        payments: {
            type: 'array',
            items: {
                type: 'string',
                referenceUdm: 'payments',
            },
        },
        lineItems: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    itemName: { type: 'string' },
                    description: { type: 'string' },
                    code: { type: 'string' },
                    type: { type: 'string' },
                    status: { type: 'string' },
                    companyId: { type: 'string', referenceUdm: 'companies' },
                    ledgerAccountId: { type: 'string', referenceUdm: 'ledger-accounts' },
                    quantity: { type: 'number' },
                    unitOfMeasure: { type: 'string' },
                    unitPrice: { type: 'number' },
                    purchasePrice: { type: 'number' },
                    salesPrice: { type: 'number' },
                    taxable: { type: 'boolean' },
                    taxAmount: { type: 'number' },
                    totalAmount: { type: 'number' },
                    inventoryDate: { type: 'string', format: 'date-time' },
                },
            },
        },
        totalAmount: { type: 'number' },
        totalTax: { type: 'number' },
        exchangeRate: { type: 'number' },
        totalDiscount: { type: 'number' },
        subTotal: { type: 'number' },
        balance: { type: 'number' },
        ledgerAccountId: { type: 'string', referenceUdm: 'ledger-accounts' },
        issueDate: {
            type: 'string',
            format: 'date-time',
        },
        dueDate: {
            type: 'string',
            format: 'date-time',
        },
        paidOnDate: {
            type: 'string',
            format: 'date-time',
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$m = {
    singularName: 'invoice',
    pluralName: 'invoices',
    fieldsSchema: FIELDS_SCHEMA$m,
    categories: ['Accounting', 'Payments'],
    list: {
        filterFields: ['number', 'name', 'status', 'customerId', 'paymentMethod', 'issueDate', 'dueDate', 'paidOnDate'],
    },
    match: {
        fields: ['number', 'name', 'status', 'customerId', 'paymentMethod', 'issueDate', 'dueDate', 'paidOnDate'],
    },
};

const FIELDS_SCHEMA$l = {
    type: 'object',
    properties: {
        id: {
            type: 'string',
            readonly: true,
        },
        candidateId: {
            type: 'string',
            referenceUdm: 'jobCandidates',
        },
        jobId: {
            type: 'string',
            referenceUdm: 'jobs',
        },
        appliedAt: {
            type: 'string',
            format: 'date-time',
        },
        status: { type: 'string' },
        currentStage: { type: 'string' },
        source: { type: 'string' },
        rejectedTime: {
            type: 'string',
            format: 'date-time',
        },
        rejectionReason: { type: 'string' },
        creditedTo: {
            type: 'string',
            referenceUdm: 'users',
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$d = ['candidateId', 'jobId', 'status', 'currentStage', 'source'];
const udm$l = {
    singularName: 'job-application',
    pluralName: 'job-applications',
    fieldsSchema: FIELDS_SCHEMA$l,
    categories: ['ATS'],
    match: {
        fields: FILTER_FIELDS$d,
    },
    list: {
        filterFields: FILTER_FIELDS$d,
    },
};

const FIELDS_SCHEMA$k = {
    type: 'object',
    properties: {
        id: { type: 'string', readonly: true },
        fullName: { type: 'string' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        company: { type: 'string' },
        title: { type: 'string' },
        addresses: { type: 'array', items: AddressSchema },
        phones: { type: 'array', items: PhoneEmailSchema },
        primaryEmail: { type: 'string' },
        primaryPhone: { type: 'string' },
        emails: { type: 'array', items: PhoneEmailSchema },
        urls: { type: 'array', items: PhoneEmailSchema },
        applicationIds: {
            type: 'array',
            items: {
                type: 'string',
                referenceUdm: 'jobApplications',
            },
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$c = ['firstName', 'lastName', 'fullName', 'title', 'company', 'primaryEmail', 'primaryPhone'];
const udm$k = {
    singularName: 'job-candidate',
    pluralName: 'job-candidates',
    fieldsSchema: FIELDS_SCHEMA$k,
    categories: ['ATS'],
    match: {
        fields: FILTER_FIELDS$c,
    },
    list: {
        filterFields: FILTER_FIELDS$c,
    },
    fieldFallbacks: {
        fullName: [
            {
                $concat: {
                    values: [{ $var: 'firstName' }, { $var: 'lastName' }],
                    delimiter: ' ',
                },
            },
        ],
        firstName: [
            {
                $firstName: {
                    $var: 'fullName',
                },
            },
        ],
        lastName: [
            {
                $lastName: {
                    $var: 'fullName',
                },
            },
        ],
        primaryEmail: [
            {
                $primaryEmail: {
                    $var: 'emails[0].value',
                },
            },
        ],
        primaryPhone: [
            {
                $primaryPhone: {
                    $var: 'phones[0].value',
                },
            },
        ],
    },
};

const FIELDS_SCHEMA$j = {
    type: 'object',
    properties: {
        id: {
            type: 'string',
            readonly: true,
        },
        candidateId: {
            type: 'string',
            referenceUdm: 'jobCandidates',
        },
        organizerId: { type: 'string', referenceUdm: 'users' },
        interviewerIds: {
            type: 'array',
            items: { type: 'string', referenceUdm: 'users' },
        },
        stage: { type: 'string' },
        status: { type: 'string' },
        location: { type: 'string' },
        startTime: { type: 'string', format: 'date-time' },
        endTime: { type: 'string', format: 'date-time' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$b = ['candidateId', 'organizerId', 'stage', 'status'];
const udm$j = {
    singularName: 'job-interview',
    pluralName: 'job-interviews',
    fieldsSchema: FIELDS_SCHEMA$j,
    categories: ['ATS'],
    match: {
        fields: FILTER_FIELDS$b,
    },
    list: {
        filterFields: FILTER_FIELDS$b,
    },
};

const FIELDS_SCHEMA$i = {
    type: 'object',
    properties: {
        id: {
            type: 'string',
            readonly: true,
        },
        jobApplicationId: {
            type: 'string',
            referenceUdm: 'job-applications',
        },
        status: { type: 'string' },
        sentTime: {
            type: 'string',
            format: 'date-time',
        },
        closedTime: {
            type: 'string',
            format: 'date-time',
        },
        startDate: {
            type: 'string',
            format: 'date',
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$a = ['jobApplicationId', 'status'];
const udm$i = {
    singularName: 'job-offer',
    pluralName: 'job-offers',
    fieldsSchema: FIELDS_SCHEMA$i,
    categories: ['ATS'],
    match: {
        fields: FILTER_FIELDS$a,
    },
    list: {
        filterFields: FILTER_FIELDS$a,
    },
};

const FIELDS_SCHEMA$h = {
    type: 'object',
    properties: {
        id: {
            type: 'string',
            readonly: true,
        },
        name: { type: 'string' },
        description: { type: 'string' },
        code: { type: 'string' },
        status: { type: 'string' },
        isConfidential: { type: 'boolean' },
        hiringManagersIds: {
            type: 'array',
            items: { type: 'string', referenceUdm: 'users' },
        },
        recruitersIds: {
            type: 'array',
            items: { type: 'string', referenceUdm: 'users' },
        },
        departmentsIds: { type: 'array', items: { type: 'string' } },
        officesIds: { type: 'array', items: { type: 'string' } },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$9 = ['name', 'code', 'status'];
const udm$h = {
    singularName: 'job',
    pluralName: 'jobs',
    fieldsSchema: FIELDS_SCHEMA$h,
    categories: ['ATS'],
    match: {
        fields: FILTER_FIELDS$9,
    },
    list: {
        filterFields: FILTER_FIELDS$9,
    },
};

const FIELDS_SCHEMA$g = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        number: { type: 'string' },
        title: { type: 'string' },
        memo: { type: 'string' },
        currency: { type: 'string' },
        companyId: { type: 'string', referenceUdm: 'companies' },
        lineItems: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    description: { type: 'string' },
                    type: { type: 'string', enum: ['debit', 'credit'] },
                    amount: { type: 'number' },
                    ledgerAccountId: { type: 'string', referenceUdm: 'ledger-accounts' },
                    companyId: { type: 'string', referenceUdm: 'companies' },
                    contactId: { type: 'string', referenceUdm: 'contacts' },
                    exchangeRate: { type: 'number' },
                },
            },
        },
        taxType: { type: 'string' },
        taxCode: { type: 'string' },
        ledgerAccountId: { type: 'string', referenceUdm: 'ledger-accounts' },
        payments: {
            type: 'array',
            items: { type: 'string', referenceUdm: 'payments' },
        },
        transactionDate: { type: 'string', format: 'date-time' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
    },
};
const udm$g = {
    singularName: 'journal-entry',
    pluralName: 'journal-entries',
    fieldsSchema: FIELDS_SCHEMA$g,
    categories: ['Accounting', 'Payments'],
    list: {
        filterFields: ['number', 'title', 'companyId', 'ledgerAccountId', 'transactionDate'],
    },
    match: {
        fields: ['number', 'title', 'companyId', 'ledgerAccountId', 'transactionDate'],
    },
};

const FIELDS_SCHEMA$f = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        fullName: { type: 'string' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        primaryEmail: { type: 'string' },
        emails: {
            type: 'array',
            items: PhoneEmailSchema,
        },
        primaryPhone: { type: 'string' },
        phones: {
            type: 'array',
            items: PhoneEmailSchema,
        },
        primaryAddress: AddressSchema,
        addresses: {
            type: 'array',
            items: AddressSchema,
        },
        stage: { type: 'string' },
        value: { type: 'number' },
        companyName: { type: 'string' },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        jobTitle: { type: 'string' },
        source: { type: 'string' },
        createdTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        lastActivityTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
    },
};
const FILTER_FIELDS$8 = [
    'fullName',
    'firstName',
    'lastName',
    'primaryEmail',
    'primaryPhone',
    'companyId',
    'companyName',
    'jobTitle',
    'source',
    'stage',
    'value',
    'ownerId',
];
const udm$f = {
    singularName: 'lead',
    pluralName: 'leads',
    fieldsSchema: FIELDS_SCHEMA$f,
    categories: ['CRM', 'Sales', 'Marketing Automation'],
    find: {
        queryFields: FILTER_FIELDS$8,
    },
    list: {
        filterFields: FILTER_FIELDS$8,
    },
    match: {
        fields: FILTER_FIELDS$8,
    },
    udmFallbacks: [{ udm: 'contacts' }],
    fieldFallbacks: {
        fullName: [
            {
                $concat: {
                    values: [{ $var: 'firstName' }, { $var: 'lastName' }],
                    delimiter: ' ',
                },
            },
        ],
        firstName: [
            {
                $firstName: {
                    $var: 'fullName',
                },
            },
        ],
        lastName: [
            {
                $lastName: {
                    $var: 'fullName',
                },
            },
        ],
        primaryEmail: [
            {
                $var: 'emails[0].value',
            },
        ],
        emails: [
            [
                {
                    value: { $var: 'primaryEmail' },
                },
            ],
        ],
        primaryPhone: [
            {
                $var: 'phones[0].value',
            },
        ],
        phones: [
            [
                {
                    value: { $var: 'primaryPhone' },
                },
            ],
        ],
        primaryAddress: [
            {
                $var: 'addresses[0]',
            },
        ],
        addresses: [
            [
                {
                    value: { $var: 'primaryAddress' },
                },
            ],
        ],
        'primaryAddress.full': [
            {
                $concat: {
                    values: [
                        { $var: 'primaryAddress.street' },
                        { $var: 'primaryAddress.city' },
                        { $var: 'primaryAddress.state' },
                        { $var: 'primaryAddress.zip' },
                        { $var: 'primaryAddress.country' },
                    ],
                    delimiter: ', ',
                },
            },
        ],
    },
};

const FIELDS_SCHEMA$e = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        code: { type: 'string' },
        description: { type: 'string' },
        classification: {
            type: 'string',
            enum: ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'],
        },
        type: { type: 'string' },
        status: {
            type: 'string',
            enum: ['Active', 'Inactive', 'Archived'],
        },
        currentBalance: { type: 'number' },
        currency: { type: 'string' },
        taxRateId: {
            type: 'string',
            referenceUdm: 'tax-rates',
        },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$e = {
    singularName: 'ledger-account',
    pluralName: 'ledger-accounts',
    fieldsSchema: FIELDS_SCHEMA$e,
    categories: ['Accounting'],
    list: {
        filterFields: ['name', 'code', 'type', 'status', 'classification', 'companyId'],
    },
    match: {
        fields: ['name', 'code', 'type', 'status', 'classification', 'companyId'],
    },
};

const FIELDS_SCHEMA$d = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        title: {
            type: 'string',
        },
        description: {
            type: 'string',
        },
        durationSeconds: {
            type: 'number',
        },
        location: {
            type: 'string',
        },
        isRecurrent: {
            type: 'boolean',
        },
        participants: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    userId: {
                        type: 'string',
                        referenceUdm: 'users',
                    },
                    contactId: {
                        type: 'string',
                        referenceUdm: 'contacts',
                    },
                    leadId: {
                        type: 'string',
                        referenceUdm: 'leads',
                    },
                },
            },
        },
        status: {
            type: 'string',
        },
        contactId: {
            type: 'string',
            referenceUdm: 'contacts',
        },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        dealId: {
            type: 'string',
            referenceUdm: 'deals',
        },
        leadId: {
            type: 'string',
            referenceUdm: 'leads',
        },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        startTime: {
            type: 'string',
            format: 'date-time',
        },
        endTime: {
            type: 'string',
            format: 'date-time',
        },
        createdTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
    },
};
const udm$d = {
    singularName: 'meeting',
    pluralName: 'meetings',
    fieldsSchema: FIELDS_SCHEMA$d,
    categories: ['CRM', 'Sales', 'Marketing Automation', 'Communication', 'Project Management', 'Customer Success'],
    list: {
        filterFields: ['title', 'companyId', 'contactId', 'dealId', 'leadId', 'ownerId', 'status'],
    },
    match: {
        fields: ['title', 'companyId', 'contactId', 'dealId', 'leadId', 'ownerId', 'status'],
    },
    udmFallbacks: [
        {
            udm: 'activities',
        },
    ],
    fieldFallbacks: {
        endTime: [
            {
                $var: 'startTime',
            },
        ],
        startTime: [
            {
                $var: 'endTime',
            },
        ],
    },
};

const FIELDS_SCHEMA$c = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        subject: { type: 'string' },
        channelId: { type: 'string' },
        threadId: { type: 'string' },
        text: { type: 'string' },
        ownerId: { type: 'string', referenceUdm: 'users' },
        status: { type: 'string' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$c = {
    singularName: 'message',
    pluralName: 'messages',
    fieldsSchema: FIELDS_SCHEMA$c,
    categories: ['Communication'],
};

const FIELDS_SCHEMA$b = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        title: { type: 'string' },
        content: { type: 'string' },
        status: { type: 'string' },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        contactId: {
            type: 'string',
            referenceUdm: 'contacts',
        },
        leadId: {
            type: 'string',
            referenceUdm: 'leads',
        },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        dealId: {
            type: 'string',
            referenceUdm: 'deals',
        },
        createdTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
    },
};
const udm$b = {
    singularName: 'note',
    pluralName: 'notes',
    fieldsSchema: FIELDS_SCHEMA$b,
    categories: [
        'CRM',
        'Sales',
        'Marketing Automation',
        'Communication',
        'Project Management',
        'Customer Success',
        'Ticketing',
        'ATS',
        'HRIS',
    ],
    list: {
        filterFields: ['companyId', 'contactId', 'dealId', 'leadId', 'ownerId', 'status', 'title'],
    },
    match: {
        fields: ['companyId', 'contactId', 'dealId', 'leadId', 'ownerId', 'status', 'title'],
    },
    udmFallbacks: [
        {
            udm: 'activities',
            importFields: FallbackFieldsMap.fromActivitiesToNotes,
            exportFields: FallbackFieldsMap.fromNotesToActivities,
        },
    ],
};

const FIELDS_SCHEMA$a = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        notes: { type: 'string' },
        status: { type: 'string', enum: ['open', 'closed'] },
        customerId: { type: 'string', referenceUdm: 'customers' },
        currency: { type: 'string' },
        totalAmount: { type: 'number' },
        paymentMethod: { type: 'string' },
        paymentStatus: { type: 'string' },
        fullFillmentStatus: { type: 'string' },
        billingAddress: AddressSchema,
        shippingAddress: AddressSchema,
        lineItems: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    name: { type: 'string' },
                    productId: { type: 'string', referenceUdm: 'products' },
                    sku: { type: 'string' },
                    quantity: { type: 'number' },
                    unitPrice: { type: 'number' },
                    totalAmount: { type: 'number' },
                },
            },
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$7 = ['name', 'status', 'customerId', 'currency', 'paymentMethod', 'paymentStatus'];
const udm$a = {
    singularName: 'order',
    pluralName: 'orders',
    fieldsSchema: FIELDS_SCHEMA$a,
    categories: ['CRM', 'Sales', 'E-Commerce', 'Accounting', 'Payments'],
    list: {
        filterFields: FILTER_FIELDS$7,
    },
    find: {
        queryFields: FILTER_FIELDS$7,
    },
    match: {
        fields: FILTER_FIELDS$7,
    },
};

const FIELDS_SCHEMA$9 = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        status: { type: 'string' },
        type: { type: 'string' },
        notes: { type: 'string' },
        currency: { type: 'string' },
        exchangeRate: { type: 'string' },
        totalAmount: { type: 'number' },
        supplierId: { type: 'string', referenceUdm: 'suppliers' },
        customerId: { type: 'string', referenceUdm: 'customers' },
        ledgerAccountId: { type: 'string', referenceUdm: 'ledger-accounts' },
        paymentMethod: { type: 'string' },
        transactionDate: { type: 'string', format: 'date-time' },
        isReconciled: { type: 'boolean' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$9 = {
    singularName: 'payment',
    pluralName: 'payments',
    fieldsSchema: FIELDS_SCHEMA$9,
    categories: ['Accounting', 'Payments'],
    list: {
        filterFields: ['customerId', 'type', 'status', 'supplierId', 'ledgerAccountId', 'paymentMethod', 'isReconciled'],
    },
    match: {
        fields: ['customerId', 'type', 'status', 'supplierId', 'ledgerAccountId', 'paymentMethod', 'isReconciled'],
    },
};

const FIELDS_SCHEMA$8 = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        employeeId: { type: 'string', referenceUdm: 'employees' },
        companyId: { type: 'string', referenceUdm: 'companies' },
        runState: {
            type: 'string',
            enum: ['PAID', 'DRAFT', 'APPROVED', 'CLOSED'],
            allowCustom: true,
        },
        runType: {
            type: 'string',
            enum: ['REGULAR', 'OFF_CYCLE', 'UNSCHEDULED', 'CORRECTION', 'TERMINATION', 'SIGN_ON_BONUS'],
            allowCustom: true,
        },
        isProcessed: { type: 'boolean' },
        processedTime: { type: 'string', format: 'date-time' },
        checkTime: { type: 'string', format: 'date-time' },
        startTime: { type: 'string', format: 'date-time' },
        endTime: { type: 'string', format: 'date-time' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$6 = ['employeeId', 'companyId', 'runState', 'runType', 'isProcessed'];
const udm$8 = {
    singularName: 'payroll',
    pluralName: 'payrolls',
    fieldsSchema: FIELDS_SCHEMA$8,
    categories: ['HRIS'],
    match: {
        fields: FILTER_FIELDS$6,
    },
    list: {
        filterFields: FILTER_FIELDS$6,
    },
};

const FIELDS_SCHEMA$7 = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        description: { type: 'string' },
        code: { type: 'string' },
        type: { type: 'string' },
        weight: { type: 'number' },
        isActive: { type: 'boolean' },
        price: { type: 'number' },
        status: { type: 'string' },
        options: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    name: { type: 'string' },
                    values: { type: 'array', items: { type: 'string' } },
                },
            },
        },
        variants: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    name: { type: 'string' },
                    price: { type: 'number' },
                    sku: { type: 'string' },
                    weight: { type: 'number' },
                },
            },
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$5 = ['name', 'code', 'type', 'weight', 'isActive', 'price', 'status'];
const udm$7 = {
    singularName: 'product',
    pluralName: 'products',
    fieldsSchema: FIELDS_SCHEMA$7,
    categories: ['E-Commerce', 'Accounting', 'Sales', 'CRM', 'Customer Success', 'Payments'],
    list: {
        filterFields: FILTER_FIELDS$5,
    },
    match: {
        fields: FILTER_FIELDS$5,
    },
};

const FIELDS_SCHEMA$6 = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        description: { type: 'string' },
        type: { type: 'string' },
        parentId: { type: 'string', referenceUdm: 'projects' },
        ownerId: { type: 'string', referenceUdm: 'users' },
        startTime: { type: 'string', format: 'date-time' },
        dueTime: { type: 'string', format: 'date-time' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$6 = {
    singularName: 'project',
    pluralName: 'projects',
    fieldsSchema: FIELDS_SCHEMA$6,
    categories: ['Project Management'],
    find: {
        queryFields: ['name', 'description', 'type', 'ownerId'],
    },
    list: {
        filterFields: ['name', 'description', 'type', 'ownerId'],
    },
    match: {
        fields: ['name', 'description', 'type', 'ownerId'],
    },
};

const FIELDS_SCHEMA$5 = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        code: { type: 'string' },
        description: { type: 'string' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        companyName: { type: 'string' },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        status: { type: 'string' },
        currency: { type: 'string' },
        addresses: {
            type: 'array',
            items: AddressSchema,
        },
        phones: {
            type: 'array',
            items: PhoneEmailSchema,
        },
        emails: {
            type: 'array',
            items: PhoneEmailSchema,
        },
        websiteUrl: {
            type: 'string',
        },
        notes: { type: 'string' },
        taxNumber: { type: 'string' },
        orderIds: {
            type: 'array',
            items: {
                type: 'string',
                referenceUdm: 'orders',
            },
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$4 = [
    'companyName',
    'firstName',
    'lastName',
    'name',
    'code',
    'status',
    'currency',
    'companyId',
    'websiteUrl',
    'taxNumber',
];
const udm$5 = {
    singularName: 'supplier',
    pluralName: 'suppliers',
    fieldsSchema: FIELDS_SCHEMA$5,
    categories: ['CRM', 'Sales', 'E-Commerce', 'Accounting', 'Payments'],
    list: {
        filterFields: FILTER_FIELDS$4,
    },
    match: {
        fields: FILTER_FIELDS$4,
    },
    udmFallbacks: [
        {
            udm: 'companies',
        },
    ],
};

const FIELDS_SCHEMA$4 = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        subject: { type: 'string' },
        content: { type: 'string' },
        status: { type: 'string' },
        type: { type: 'string' },
        priority: { type: 'string' },
        ownerId: {
            type: 'string',
            referenceUdm: 'users',
        },
        assigneeId: {
            type: 'string',
            referenceUdm: 'users',
        },
        followerIds: {
            type: 'array',
            items: { type: 'string', referenceUdm: 'users' },
        },
        parentTaskId: {
            type: 'string',
            referenceUdm: 'tasks',
        },
        projectId: {
            type: 'string',
            referenceUdm: 'projects',
        },
        tags: {
            type: 'array',
            items: { type: 'string' },
        },
        contactId: {
            type: 'string',
            referenceUdm: 'contacts',
        },
        leadId: {
            type: 'string',
            referenceUdm: 'leads',
        },
        companyId: {
            type: 'string',
            referenceUdm: 'companies',
        },
        dealId: {
            type: 'string',
            referenceUdm: 'deals',
        },
        dueTime: {
            type: 'string',
            format: 'date-time',
        },
        startTime: {
            type: 'string',
            format: 'date-time',
        },
        endTime: {
            type: 'string',
            format: 'date-time',
        },
        createdTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        createdBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
        updatedTime: {
            type: 'string',
            format: 'date-time',
            readOnly: true,
        },
        updatedBy: {
            type: 'string',
            referenceUdm: 'users',
            readOnly: true,
        },
    },
};
const FILTER_FIELDS$3 = [
    'companyId',
    'contactId',
    'dealId',
    'leadId',
    'ownerId',
    'status',
    'type',
    'priority',
    'assigneeId',
    'parentTaskId',
    'projectId',
];
const udm$4 = {
    singularName: 'task',
    pluralName: 'tasks',
    fieldsSchema: FIELDS_SCHEMA$4,
    categories: ['Project Management', 'CRM', 'Sales', 'Customer Success', 'Ticketing'],
    list: {
        filterFields: FILTER_FIELDS$3,
    },
    match: {
        fields: FILTER_FIELDS$3,
    },
    udmFallbacks: [
        {
            udm: 'activities',
            importFields: FallbackFieldsMap.fromActivitiesToTasks,
            exportFields: FallbackFieldsMap.fromTasksToActivities,
        },
    ],
    fieldFallbacks: {
        endTime: [
            {
                $var: 'startTime',
            },
        ],
        startTime: [
            {
                $var: 'endTime',
            },
        ],
        assigneeId: [
            {
                $var: 'ownerId',
            },
        ],
        ownerId: [
            {
                $var: 'assigneeId',
            },
        ],
    },
};

const FIELDS_SCHEMA$3 = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        name: { type: 'string' },
        code: { type: 'string' },
        description: { type: 'string' },
        effectiveTaxRate: { type: 'number' },
        totalTaxRate: { type: 'number' },
        type: { type: 'string' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const udm$3 = {
    singularName: 'tax-rate',
    pluralName: 'tax-rates',
    fieldsSchema: FIELDS_SCHEMA$3,
    categories: ['Accounting', 'Payments'],
    list: {
        filterFields: ['name', 'code', 'type', 'effectiveTaxRate', 'totalTaxRate'],
    },
    match: {
        fields: ['name', 'code', 'type', 'effectiveTaxRate', 'totalTaxRate'],
    },
};

const FIELDS_SCHEMA$2 = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        employeeId: { type: 'string', referenceUdm: 'employees' },
        balanceHours: { type: 'number' },
        usedHours: { type: 'number' },
        policyType: {
            type: 'string',
            enum: ['VACATION', 'SICK', 'PERSONAL', 'JURY_DUTY', 'VOLUNTEER', 'BEREAVEMENT'],
            allowCustom: true,
        },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$2 = ['employeeId', 'approverId', 'status', 'requestType', 'startTime', 'endTime'];
const udm$2 = {
    singularName: 'timeoff-balance',
    pluralName: 'timeoff-balances',
    fieldsSchema: FIELDS_SCHEMA$2,
    categories: ['HRIS'],
    match: {
        fields: FILTER_FIELDS$2,
    },
    list: {
        filterFields: FILTER_FIELDS$2,
    },
};

const FIELDS_SCHEMA$1 = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        employeeId: { type: 'string', referenceUdm: 'employees' },
        approverId: { type: 'string', referenceUdm: 'employees' },
        status: {
            type: 'string',
            enum: ['REQUESTED', 'APPROVED', 'DECLINED', 'CANCELLED'],
            allowCustom: true,
        },
        employeeNote: { type: 'string' },
        units: {
            type: 'string',
            enum: ['DAYS', 'HOURS'],
            allowCustom: true,
        },
        amount: { type: 'number' },
        requestType: {
            type: 'string',
            enum: ['VACATION', 'SICK', 'PERSONAL', 'JURY_DUTY', 'VOLUNTEER', 'BEREAVEMENT'],
            allowCustom: true,
        },
        startTime: { type: 'string', format: 'date-time' },
        endTime: { type: 'string', format: 'date-time' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS$1 = ['employeeId', 'approverId', 'status', 'requestType', 'startTime', 'endTime'];
const udm$1 = {
    singularName: 'timeoff-request',
    pluralName: 'timeoff-requests',
    fieldsSchema: FIELDS_SCHEMA$1,
    categories: ['HRIS'],
    match: {
        fields: FILTER_FIELDS$1,
    },
    list: {
        filterFields: FILTER_FIELDS$1,
    },
};

const FIELDS_SCHEMA = {
    type: 'object',
    properties: {
        id: { type: 'string', readOnly: true },
        title: { type: 'string' },
        fullName: { type: 'string' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        userName: { type: 'string' },
        primaryEmail: { type: 'string' },
        emails: { type: 'array', items: PhoneEmailSchema },
        primaryPhone: { type: 'string' },
        phones: { type: 'array', items: PhoneEmailSchema },
        primaryAddress: AddressSchema,
        addresses: {
            type: 'array',
            items: AddressSchema,
        },
        companyName: { type: 'string' },
        companyId: { type: 'string', referenceUdm: 'companies' },
        imageUrl: { type: 'string' },
        timezone: { type: 'string' },
        isActive: { type: 'boolean' },
        roleId: { type: 'string' },
        createdTime: { type: 'string', format: 'date-time', readOnly: true },
        createdBy: { type: 'string', referenceUdm: 'users', readOnly: true },
        updatedTime: { type: 'string', format: 'date-time', readOnly: true },
        updatedBy: { type: 'string', referenceUdm: 'users', readOnly: true },
    },
};
const FILTER_FIELDS = [
    'companyId',
    'companyName',
    'userName',
    'fullName',
    'firstName',
    'lastName',
    'primaryEmail',
    'primaryPhone',
    'roleId',
    'title',
    'isActive',
];
const udm = {
    singularName: 'user',
    pluralName: 'users',
    fieldsSchema: FIELDS_SCHEMA,
    match: {
        fields: FILTER_FIELDS,
    },
    find: {
        queryFields: FILTER_FIELDS,
    },
    list: {
        filterFields: FILTER_FIELDS,
    },
    fieldFallbacks: {
        fullName: [
            {
                $concat: {
                    values: [{ $var: 'firstName' }, { $var: 'lastName' }],
                    delimiter: ' ',
                },
            },
        ],
        firstName: [
            {
                $firstName: {
                    $var: 'fullName',
                },
            },
        ],
        lastName: [
            {
                $lastName: {
                    $var: 'fullName',
                },
            },
        ],
        primaryEmail: [
            {
                $var: 'emails[0].value',
            },
        ],
        emails: [
            [
                {
                    value: { $var: 'primaryEmail' },
                },
            ],
        ],
        primaryPhone: [
            {
                $var: 'phones[0].value',
            },
        ],
        phones: [
            [
                {
                    value: { $var: 'primaryPhone' },
                },
            ],
        ],
        primaryAddress: [
            {
                $var: 'addresses[0]',
            },
        ],
        addresses: [
            [
                {
                    value: { $var: 'primaryAddress' },
                },
            ],
        ],
        'primaryAddress.full': [
            {
                $concat: {
                    values: [
                        { $var: 'primaryAddress.street' },
                        { $var: 'primaryAddress.city' },
                        { $var: 'primaryAddress.state' },
                        { $var: 'primaryAddress.zip' },
                        { $var: 'primaryAddress.country' },
                    ],
                    delimiter: ', ',
                },
            },
        ],
    },
};

var UDM;
(function (UDM) {
    UDM["ACTIVITIES"] = "activities";
    UDM["BILLS"] = "bills";
    UDM["CAMPAIGN_MEMBERS"] = "campaign-members";
    UDM["CAMPAIGNS"] = "campaigns";
    UDM["COMPANIES"] = "companies";
    UDM["CONTACTS"] = "contacts";
    UDM["CONTACT_LISTS"] = "contact-lists";
    UDM["CREDIT_NOTES"] = "credit-notes";
    UDM["CUSTOMERS"] = "customers";
    UDM["DEAL_PRODUCTS"] = "deal-products";
    UDM["DEALS"] = "deals";
    UDM["DEAL_STAGES"] = "deal-stages";
    UDM["DOCUMENTS"] = "documents";
    UDM["DRIVES"] = "drives";
    UDM["DRIVE_ITEMS"] = "drive-items";
    UDM["EEOCS"] = "eeocs";
    UDM["EMAILS"] = "emails";
    UDM["EMPLOYEES"] = "employees";
    UDM["FILES"] = "files";
    UDM["FOLDERS"] = "folders";
    UDM["INVOICES"] = "invoices";
    UDM["JOB_APPLICATIONS"] = "job-applications";
    UDM["JOB_CANDIDATES"] = "job-candidates";
    UDM["JOB_INTERVIEWS"] = "job-interviews";
    UDM["JOB_OFFERS"] = "job-offers";
    UDM["JOBS"] = "jobs";
    UDM["JOURNAL_ENTRIES"] = "journal-entries";
    UDM["LEADS"] = "leads";
    UDM["LEDGER_ACCOUNTS"] = "ledger-accounts";
    UDM["MEETINGS"] = "meetings";
    UDM["MESSAGES"] = "messages";
    UDM["NOTES"] = "notes";
    UDM["ORDERS"] = "orders";
    UDM["PAYMENTS"] = "payments";
    UDM["PAYROLLS"] = "payrolls";
    UDM["PRODUCTS"] = "products";
    UDM["PROJECTS"] = "projects";
    UDM["SUPPLIERS"] = "suppliers";
    UDM["TASKS"] = "tasks";
    UDM["TAX_RATES"] = "tax-rates";
    UDM["USERS"] = "users";
    UDM["TIMEOFF_BALANCES"] = "timeoff-balances";
    UDM["TIMEOFF_REQUESTS"] = "timeoff-requests";
})(UDM || (UDM = {}));
const UNIFIED_DATA_MODELS = {
    [UDM.ACTIVITIES]: udm$G,
    [UDM.BILLS]: udm$F,
    [UDM.CAMPAIGN_MEMBERS]: udm$E,
    [UDM.CAMPAIGNS]: udm$D,
    [UDM.COMPANIES]: udm$C,
    [UDM.CONTACTS]: udm$A,
    [UDM.CONTACT_LISTS]: udm$B,
    [UDM.CREDIT_NOTES]: udm$z,
    [UDM.CUSTOMERS]: udm$y,
    [UDM.DEAL_PRODUCTS]: udm$x,
    [UDM.DEALS]: udm$v,
    [UDM.DEAL_STAGES]: udm$w,
    [UDM.DOCUMENTS]: udm$u,
    [UDM.DRIVES]: udm$s,
    [UDM.DRIVE_ITEMS]: udm$t,
    [UDM.EEOCS]: udm$r,
    [UDM.EMAILS]: udm$q,
    [UDM.EMPLOYEES]: udm$p,
    [UDM.FILES]: udm$o,
    [UDM.FOLDERS]: udm$n,
    [UDM.INVOICES]: udm$m,
    [UDM.JOB_APPLICATIONS]: udm$l,
    [UDM.JOB_CANDIDATES]: udm$k,
    [UDM.JOB_INTERVIEWS]: udm$j,
    [UDM.JOB_OFFERS]: udm$i,
    [UDM.JOBS]: udm$h,
    [UDM.JOURNAL_ENTRIES]: udm$g,
    [UDM.LEADS]: udm$f,
    [UDM.LEDGER_ACCOUNTS]: udm$e,
    [UDM.MEETINGS]: udm$d,
    [UDM.MESSAGES]: udm$c,
    [UDM.NOTES]: udm$b,
    [UDM.ORDERS]: udm$a,
    [UDM.PAYMENTS]: udm$9,
    [UDM.PAYROLLS]: udm$8,
    [UDM.PRODUCTS]: udm$7,
    [UDM.PROJECTS]: udm$6,
    [UDM.SUPPLIERS]: udm$5,
    [UDM.TASKS]: udm$4,
    [UDM.TAX_RATES]: udm$3,
    [UDM.USERS]: udm,
    [UDM.TIMEOFF_BALANCES]: udm$2,
    [UDM.TIMEOFF_REQUESTS]: udm$1,
};
function findUdmRootLocation(spec, udm, useFallback = true) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    if (!spec) {
        return undefined;
    }
    if (!udm) {
        return (_c = parseDataLocationPath((_b = (_a = spec.data) === null || _a === void 0 ? void 0 : _a.rootLocation) === null || _b === void 0 ? void 0 : _b.path)) !== null && _c !== void 0 ? _c : parseDataLocationPath((_e = (_d = spec.data) === null || _d === void 0 ? void 0 : _d.root) === null || _e === void 0 ? void 0 : _e.path);
    }
    const udmSpec = UNIFIED_DATA_MODELS[udm];
    if (!udmSpec) {
        throw new BadRequestError(`Unknown UDM: ${udm}`);
    }
    if ((_g = (_f = spec.udm) === null || _f === void 0 ? void 0 : _f[udm]) === null || _g === void 0 ? void 0 : _g.rootDirectory) {
        return (_j = (_h = spec.udm) === null || _h === void 0 ? void 0 : _h[udm]) === null || _j === void 0 ? void 0 : _j.rootDirectory;
    }
    else if ((_l = (_k = spec === null || spec === void 0 ? void 0 : spec.data) === null || _k === void 0 ? void 0 : _k.udm) === null || _l === void 0 ? void 0 : _l[udm]) {
        if (spec.data.udm[udm].rootLocation) {
            return parseDataLocationPath(spec.data.udm[udm].rootLocation.path);
        }
    }
    else if ((_m = spec.data) === null || _m === void 0 ? void 0 : _m[udm]) {
        return parseDataLocationPath((_o = spec.data[udm]) === null || _o === void 0 ? void 0 : _o.path);
    }
    if (useFallback) {
        for (const fallback of (_p = udmSpec.udmFallbacks) !== null && _p !== void 0 ? _p : []) {
            const pointer = findUdmRootLocation(spec, fallback.udm, false);
            if (pointer) {
                return pointer;
            }
        }
    }
    return (_s = parseDataLocationPath((_r = (_q = spec.data) === null || _q === void 0 ? void 0 : _q.rootLocation) === null || _r === void 0 ? void 0 : _r.path)) !== null && _s !== void 0 ? _s : parseDataLocationPath((_u = (_t = spec.data) === null || _t === void 0 ? void 0 : _t.root) === null || _u === void 0 ? void 0 : _u.path);
}
function findUdmDefaultCollection(spec, udm, useFallback = true) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    if (!spec || !udm) {
        return undefined;
    }
    if ((_b = (_a = spec.udm) === null || _a === void 0 ? void 0 : _a[udm]) === null || _b === void 0 ? void 0 : _b.defaultCollection) {
        return (_d = (_c = spec.udm) === null || _c === void 0 ? void 0 : _c[udm]) === null || _d === void 0 ? void 0 : _d.defaultCollection;
    }
    if ((_h = (_g = (_f = (_e = spec.udm) === null || _e === void 0 ? void 0 : _e[udm]) === null || _f === void 0 ? void 0 : _f.collectionMappings) === null || _g === void 0 ? void 0 : _g[0]) === null || _h === void 0 ? void 0 : _h.key) {
        return {
            key: spec.udm[udm].collectionMappings[0].key,
        };
    }
    const udmSpec = UNIFIED_DATA_MODELS[udm];
    if (!udmSpec) {
        throw new BadRequestError(`Unknown UDM: ${udm}`);
    }
    if (useFallback) {
        for (const fallback of (_j = udmSpec.udmFallbacks) !== null && _j !== void 0 ? _j : []) {
            const pointer = findUdmDefaultCollection(spec, fallback.udm, false);
            if (pointer) {
                return pointer;
            }
        }
    }
    return undefined;
}
function addUdmFallbackFields(fields, udm) {
    if (!udm) {
        return fields;
    }
    const udmSpec = UNIFIED_DATA_MODELS[udm];
    if (!udmSpec) {
        throw new BadRequestError(`Unknown UDM: ${udm}`);
    }
    if (!udmSpec.fieldFallbacks) {
        return fields;
    }
    let result = structuredClone$1(fields);
    for (const fieldKeyWithFallbacks of Object.keys(udmSpec.fieldFallbacks)) {
        const currentValue = getValueAtLocator(fields, fieldKeyWithFallbacks);
        if (currentValue !== undefined)
            continue;
        for (const fallbackValue of udmSpec.fieldFallbacks[fieldKeyWithFallbacks]) {
            const varFormulaLocators = getVariableLocators(fallbackValue);
            const varLocators = [];
            for (const varFormulaLocator of varFormulaLocators) {
                const formula = getFormula$1(getValueAtLocator(fallbackValue, varFormulaLocator));
                varLocators.push(formula.locator);
            }
            const fallbackValueHasDependencies = varLocators.some((locator) => getValueAtLocator(fields, locator) !== undefined);
            if (fallbackValueHasDependencies) {
                result = setValueAtLocator(result, fieldKeyWithFallbacks, transformVars(fallbackValue, fields));
                break;
            }
        }
    }
    return result;
}

const DATA_RECORD_SCHEMA = {
    type: 'object',
    properties: {
        id: {
            type: 'string',
        },
        name: {
            type: 'string',
        },
        uri: {
            type: 'string',
            format: 'url',
        },
        createdTime: {
            type: 'string',
            format: 'datetime',
        },
        updatedTime: {
            type: 'string',
            format: 'datetime',
        },
        deletedTime: {
            type: 'string',
            format: 'datetime',
        },
        createdById: {
            type: 'string',
        },
        updatedById: {
            type: 'string',
        },
    },
};
function makeDataRecordSchema({ fieldsSchema, udm, rawFieldsSchema, }) {
    var _a, _b, _c;
    const schema = structuredClone$1(DATA_RECORD_SCHEMA);
    schema.properties = {
        ...((_a = schema.properties) !== null && _a !== void 0 ? _a : {}),
        fields: fieldsSchema !== null && fieldsSchema !== void 0 ? fieldsSchema : { type: 'object' },
    };
    if (udm) {
        const unifiedModel = UNIFIED_DATA_MODELS[udm];
        if (unifiedModel === null || unifiedModel === void 0 ? void 0 : unifiedModel.fieldsSchema) {
            schema.properties = {
                ...((_b = schema.properties) !== null && _b !== void 0 ? _b : {}),
                unifiedFields: unifiedModel.fieldsSchema,
            };
        }
    }
    if (rawFieldsSchema) {
        schema.properties = {
            ...((_c = schema.properties) !== null && _c !== void 0 ? _c : {}),
            rawFields: rawFieldsSchema,
        };
    }
    return schema;
}

const DataLocationMethodImplementationTypes = [
    ConnectorMethodImplementationType.operationMapping,
    ConnectorMethodImplementationType.restApiMapping,
    ConnectorMethodImplementationType.graphqlApiMapping,
    ConnectorMethodImplementationType.javascript,
    ConnectorMethodImplementationType.notSupported,
];

const DataCollectionMethodCreate = {
    name: 'Create',
    description: 'Create a new data record in the collection',
    getConfigSchema: ({ collectionSpec }) => {
        var _a, _b;
        const fields = Object.keys((_b = (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) === null || _a === void 0 ? void 0 : _a.properties) !== null && _b !== void 0 ? _b : {}).map(locatorToField);
        return {
            type: 'object',
            properties: {
                fields: {
                    type: 'array',
                    items: { type: 'string', enum: fields },
                },
                excludedFields: {
                    type: 'array',
                    items: { type: 'string', enum: fields },
                },
            },
        };
    },
    getInputSchema: ({ collectionSpec, method, credentialsSchema }) => {
        var _a;
        let fieldsSchema = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema;
        if (method === null || method === void 0 ? void 0 : method.fields) {
            fieldsSchema = pickFieldsFromSchema(fieldsSchema, method.fields);
        }
        if (method === null || method === void 0 ? void 0 : method.excludedFields) {
            fieldsSchema = excludeFieldsFromSchema(fieldsSchema, method.excludedFields);
        }
        return {
            type: 'object',
            properties: nonEmptyObjectProperties({
                fields: (_a = transformVariablesWith(fieldsSchema, (locator) => `fields.${locatorToField(locator)}`)) !== null && _a !== void 0 ? _a : {
                    type: 'object',
                },
                credentials: credentialsSchema,
            }),
        };
    },
    getOutputSchema: (_) => ({
        type: 'object',
        properties: {
            id: { type: 'string' },
        },
    }),
    supportedImplementationTypes: DataLocationMethodImplementationTypes,
};

const DataCollectionMethodDelete = {
    name: 'Delete',
    description: 'Delete a data record from the collection by id',
    getInputSchema: ({ collectionHandler, parameters, credentialsSchema }) => {
        return {
            type: 'object',
            properties: nonEmptyObjectProperties({
                id: {
                    type: 'string',
                    referenceCollection: { key: collectionHandler === null || collectionHandler === void 0 ? void 0 : collectionHandler.key, parameters },
                },
                credentials: credentialsSchema,
            }),
        };
    },
    getOutputSchema: () => null,
    supportedImplementationTypes: DataLocationMethodImplementationTypes,
};

const DataCollectionMethodFind = {
    isDeprecated: true,
    name: 'Find',
    description: 'Find a list of data records in the collection',
    getConfigSchema: ({ collectionSpec }) => {
        var _a, _b;
        const fields = Object.keys((_b = (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) === null || _a === void 0 ? void 0 : _a.properties) !== null && _b !== void 0 ? _b : {}).map(locatorToField);
        return {
            type: 'object',
            properties: {
                queryFields: {
                    type: 'array',
                    items: { type: 'string', enum: fields },
                },
            },
        };
    },
    getInputSchema: ({ collectionSpec, method, credentialsSchema }) => {
        const queryFieldsSchema = pickFieldsFromSchema(collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema, method === null || method === void 0 ? void 0 : method.queryFields);
        const schema = {
            type: 'object',
            properties: nonEmptyObjectProperties({
                cursor: { type: 'string' },
                credentials: credentialsSchema,
            }),
        };
        if (queryFieldsSchema === null || queryFieldsSchema === void 0 ? void 0 : queryFieldsSchema.properties) {
            schema.properties = {
                ...schema.properties,
                query: transformVariablesWith(queryFieldsSchema, (locator) => `query.${locatorToField(locator)}`),
            };
        }
        return schema;
    },
    getOutputSchema: ({ collectionSpec }) => {
        var _a;
        return ({
            type: 'object',
            properties: {
                records: {
                    type: 'array',
                    items: (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) !== null && _a !== void 0 ? _a : { type: 'object' },
                },
                cursor: {
                    type: 'string',
                },
            },
        });
    },
    supportedImplementationTypes: DataLocationMethodImplementationTypes,
};

const DataCollectionMethodFindById = {
    name: 'Find by ID',
    description: 'Find a single data record in the collection by its id',
    getInputSchema: ({ collectionHandler, parameters, credentialsSchema }) => ({
        type: 'object',
        properties: nonEmptyObjectProperties({
            id: {
                type: 'string',
                referenceCollection: { key: collectionHandler === null || collectionHandler === void 0 ? void 0 : collectionHandler.key, parameters },
            },
            credentials: credentialsSchema,
        }),
    }),
    getOutputSchema: ({ collectionSpec }) => {
        var _a;
        return ({
            type: 'object',
            properties: {
                record: (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) !== null && _a !== void 0 ? _a : { type: 'object' },
            },
        });
    },
    supportedImplementationTypes: DataLocationMethodImplementationTypes,
};

const DataCollectionMethodList = {
    name: 'List',
    description: 'Return all data records from the collection',
    getConfigSchema: ({ collectionSpec }) => {
        var _a, _b;
        const fields = Object.keys((_b = (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) === null || _a === void 0 ? void 0 : _a.properties) !== null && _b !== void 0 ? _b : {}).map(locatorToField);
        return {
            type: 'object',
            properties: {
                filterFields: {
                    type: 'array',
                    items: { type: 'string', enum: fields },
                },
            },
        };
    },
    getInputSchema: ({ method, collectionSpec, credentialsSchema }) => {
        const fieldsSchema = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema;
        let filterSchema;
        if (method === null || method === void 0 ? void 0 : method.filterFields) {
            filterSchema = pickFieldsFromSchema(fieldsSchema, method.filterFields);
        }
        return {
            type: 'object',
            properties: nonEmptyObjectProperties({
                filter: filterSchema,
                cursor: {
                    type: 'string',
                    description: 'Cursor returned in response to the previous "list" request',
                },
                credentials: credentialsSchema,
            }),
        };
    },
    getOutputSchema: ({ collectionSpec }) => {
        var _a;
        return ({
            type: 'object',
            properties: {
                records: {
                    type: 'array',
                    items: (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) !== null && _a !== void 0 ? _a : { type: 'object' },
                },
                cursor: {
                    type: 'string',
                    description: 'Cursor to request the next page of results',
                },
            },
        });
    },
    supportedImplementationTypes: DataLocationMethodImplementationTypes,
};

const DataCollectionMethodMatch = {
    name: 'Match',
    description: 'Find a single matching record in a data collection by a set of fields',
    getConfigSchema: ({ collectionSpec }) => {
        var _a, _b;
        const fields = Object.keys((_b = (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) === null || _a === void 0 ? void 0 : _a.properties) !== null && _b !== void 0 ? _b : {}).map(locatorToField);
        return {
            type: 'object',
            properties: {
                fields: {
                    type: 'array',
                    items: { type: 'string', enum: fields },
                },
            },
        };
    },
    getInputSchema: ({ collectionSpec, method, credentialsSchema }) => {
        const fieldsSchema = pickFieldsFromSchema(collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema, method === null || method === void 0 ? void 0 : method.fields);
        return {
            type: 'object',
            properties: nonEmptyObjectProperties({
                query: transformVariablesWith(fieldsSchema !== null && fieldsSchema !== void 0 ? fieldsSchema : {}, (locator) => `query.${locatorToField(locator)}`),
                credentials: credentialsSchema,
            }),
        };
    },
    getOutputSchema: ({ collectionSpec }) => {
        var _a;
        return ({
            type: 'object',
            properties: {
                record: (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) !== null && _a !== void 0 ? _a : { type: 'object' },
            },
        });
    },
    supportedImplementationTypes: DataLocationMethodImplementationTypes,
};

const DataCollectionMethodSearch = {
    name: 'Search',
    description: 'Find data records in a collection by a string query',
    getInputSchema: ({ credentialsSchema }) => ({
        type: 'object',
        properties: nonEmptyObjectProperties({
            query: { type: 'string' },
            cursor: { type: 'string' },
            credentials: credentialsSchema,
        }),
    }),
    getOutputSchema: ({ collectionSpec }) => {
        var _a;
        return ({
            type: 'object',
            properties: {
                records: {
                    type: 'array',
                    items: (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) !== null && _a !== void 0 ? _a : { type: 'object' },
                },
                cursor: { type: 'string' },
            },
        });
    },
    supportedImplementationTypes: DataLocationMethodImplementationTypes,
};

const DataCollectionMethodUpdate = {
    name: 'Update',
    description: 'Update one data record in the collection by its id',
    getConfigSchema: ({ collectionSpec }) => {
        var _a, _b;
        const fields = Object.keys((_b = (_a = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) === null || _a === void 0 ? void 0 : _a.properties) !== null && _b !== void 0 ? _b : {}).map(locatorToField);
        return {
            type: 'object',
            properties: {
                fields: {
                    type: 'array',
                    items: { type: 'string', enum: fields },
                },
                excludedFields: {
                    type: 'array',
                    items: { type: 'string', enum: fields },
                },
            },
        };
    },
    getInputSchema: ({ collectionSpec, collectionHandler, credentialsSchema, method, parameters }) => {
        var _a;
        let fieldsSchema = collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema;
        if (method === null || method === void 0 ? void 0 : method.fields) {
            fieldsSchema = pickFieldsFromSchema(fieldsSchema, method.fields);
        }
        if (method === null || method === void 0 ? void 0 : method.excludedFields) {
            fieldsSchema = excludeFieldsFromSchema(fieldsSchema, method.excludedFields);
        }
        return {
            type: 'object',
            properties: nonEmptyObjectProperties({
                id: {
                    type: 'string',
                    referenceCollection: { key: collectionHandler === null || collectionHandler === void 0 ? void 0 : collectionHandler.key, parameters },
                },
                fields: (_a = transformVariablesWith(fieldsSchema, (locator) => `fields.${locatorToField(locator)}`)) !== null && _a !== void 0 ? _a : {
                    type: 'object',
                },
                credentials: credentialsSchema,
            }),
        };
    },
    getOutputSchema: (_) => ({
        type: 'object',
        properties: {
            id: { type: 'string' },
        },
    }),
    supportedImplementationTypes: DataLocationMethodImplementationTypes,
};

const GetEventConfig = {
    fileKey: 'connector-event-get-event-config',
    getInputSchema: ({ collection }) => ({
        type: 'object',
        properties: {
            parameters: collection.parametersSchema,
        },
    }),
    getOutputSchema: (_) => ({
        type: 'object',
        properties: {
            eventKey: {
                type: 'string',
            },
            eventParameters: {
                type: 'object',
                additionalProperties: true,
            },
        },
    }),
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.mapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const ExtractEventsMethod = {
    fileKey: 'connector-event-extract-events',
    getInputSchema: (_) => ({
        type: 'object',
        properties: {
            events: {
                type: 'array',
            },
        },
    }),
    getOutputSchema: ({ collection }) => ({
        type: 'object',
        properties: {
            events: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        record: collection.fieldsSchema,
                    },
                },
            },
        },
    }),
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.mapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const ConnectorEventImplementationType$1 = {
    methods: {
        getEventConfig: GetEventConfig,
        extractEvents: ExtractEventsMethod,
    },
};

const SubscribeMethod = {
    fileKey: 'custom-pull-subscribe',
    getInputSchema: ({ collection }) => ({
        type: 'object',
        properties: {
            parameters: collection.parametersSchema,
        },
    }),
    getOutputSchema: (_) => ({}),
    supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
};
const CollectEventsMethod = {
    fileKey: 'custom-pull-collect-events',
    getInputSchema: ({ collection }) => ({
        type: 'object',
        properties: {
            parameters: collection.parametersSchema,
        },
    }),
    getOutputSchema: ({ collection }) => ({
        type: 'object',
        properties: {
            events: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        record: collection.fieldsSchema,
                    },
                },
            },
            state: {},
        },
    }),
    supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
};
const CustomPullEventImplementationType = {
    methods: {
        subscribe: SubscribeMethod,
        collectEvents: CollectEventsMethod,
    },
};

const FullScanEventImplementationType = {
    methods: {},
};

const PullLatestRecordsMethod = {
    fileKey: 'pull-latest-records',
    getInputSchema: ({ collection }) => ({
        type: 'object',
        properties: {
            parameters: collection.parametersSchema,
            cursor: { type: 'string' },
        },
    }),
    getOutputSchema: ({ collection }) => ({
        type: 'object',
        properties: {
            records: {
                type: 'array',
                items: collection.fieldsSchema,
            },
            cursor: {
                type: 'string',
            },
        },
    }),
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.restApiMapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const PullLatestRecordsEventImplementationType = {
    methods: {
        pullLatestRecords: PullLatestRecordsMethod,
    },
};

const WebhookSubscribeMethod = {
    fileKey: 'webhook-subscribe',
    getInputSchema: ({ collection }) => ({
        type: 'object',
        properties: {
            parameters: collection.parametersSchema,
            webhookUri: { type: 'string' },
        },
    }),
    getOutputSchema: ({ event }) => {
        var _a;
        return ({
            type: 'object',
            properties: {
                state: (_a = event.stateSchema) !== null && _a !== void 0 ? _a : { type: 'object' },
                nextRefreshTimestamp: { type: 'number' },
            },
            required: ['state'],
        });
    },
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.restApiMapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const WebhookUnsubscribeMethod = {
    fileKey: 'webhook-unsubscribe',
    getInputSchema: ({ collection, event }) => {
        var _a;
        return ({
            type: 'object',
            properties: {
                parameters: collection.parametersSchema,
                state: (_a = event.stateSchema) !== null && _a !== void 0 ? _a : { type: 'object' },
            },
        });
    },
    getOutputSchema: () => null,
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.restApiMapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const WebhookHandleMethod = {
    fileKey: 'webhook-handle',
    getInputSchema: ({ collection, event }) => ({
        type: 'object',
        properties: {
            query: event.querySchema,
            data: event.dataSchema,
            parameters: collection.parametersSchema,
            state: event.stateSchema,
        },
    }),
    getOutputSchema: ({ collection, event }) => {
        var _a, _b;
        return ({
            type: 'object',
            properties: {
                events: {
                    type: 'array',
                    items: {
                        type: 'object',
                        properties: {
                            type: {
                                type: 'string',
                                enum: Object.values(DataCollectionEventType),
                            },
                            record: (_a = collection.fieldsSchema) !== null && _a !== void 0 ? _a : { type: 'object' },
                        },
                    },
                },
                response: {
                    type: 'object',
                    properties: {
                        headers: {
                            type: 'object',
                        },
                        data: {},
                    },
                },
                state: (_b = event.stateSchema) !== null && _b !== void 0 ? _b : { type: 'object' },
            },
        });
    },
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.mapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const WebhookRefreshMethod = {
    fileKey: 'webhook-refresh',
    getInputSchema: ({ collection, event }) => ({
        type: 'object',
        properties: {
            parameters: collection.parametersSchema,
            state: event.stateSchema,
        },
    }),
    getOutputSchema: ({ event }) => {
        var _a;
        return ({
            type: 'object',
            properties: {
                state: (_a = event.stateSchema) !== null && _a !== void 0 ? _a : { type: 'object' },
                nextRefreshTimestamp: { type: 'number' },
            },
        });
    },
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.restApiMapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const WebhookEventImplementationType = {
    methods: {
        subscribe: WebhookSubscribeMethod,
        unsubscribe: WebhookUnsubscribeMethod,
        handle: WebhookHandleMethod,
        refresh: WebhookRefreshMethod,
    },
};

const ConnectorDataCollectionEventImplementationType = {
    webhook: WebhookEventImplementationType,
    'pull-latest-records': PullLatestRecordsEventImplementationType,
    'custom-pull': CustomPullEventImplementationType,
    'connector-event': ConnectorEventImplementationType$1,
    'full-scan': FullScanEventImplementationType,
};
function getEventMethodFileKey(eventType, eventImplementationType, methodKey) {
    const methodType = ConnectorDataCollectionEventImplementationType[eventImplementationType].methods[methodKey];
    if (!methodType) {
        return undefined;
    }
    return `events-${eventType}-${methodType.fileKey}`;
}
function getAllEventMethodFilePaths(eventType, eventImplementationType, methodKey) {
    return [
        eventType === 'all' ? undefined : getEventMethodFileKey(eventType, eventImplementationType, methodKey),
        getLegacyEventMethodFielPath(eventType, eventImplementationType, methodKey),
    ].filter(Boolean);
}
function getLegacyEventMethodFielPath(eventType, eventImplementationType, methodKey) {
    const prefix = eventType === 'all' ? 'events' : eventType;
    switch (eventImplementationType) {
        case 'webhook':
            switch (methodKey) {
                case 'handle':
                    return `${prefix}-webhook-handle`;
                case 'subscribe':
                    return `${prefix}-webhook-subscribe`;
                case 'unsubscribe':
                    return `${prefix}-webhook-unsubscribe`;
                default:
                    return null;
            }
        case 'pull-latest-records':
            switch (methodKey) {
                case 'pullLatestRecords':
                    return `${prefix}-pull-latest-records`;
                default:
                    return null;
            }
        case 'custom-pull':
            switch (methodKey) {
                case 'subscribe':
                    return `${prefix}-custom-pull-subscribe`;
                case 'collectEvents':
                    return `${prefix}-custom-pull-collect-events`;
                default:
                    return null;
            }
        default:
            return null;
    }
}

const DataCollectionCustomSpecMethod = {
    supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
};
const DataCollectionCustomFieldsSchemaMethod = {
    fileKey: 'custom-fields-schema',
    supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
};
const DataCollectionRecordFromApiMethod = {
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.mapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const DataCollectionRecordFromFieldsMethod = {
    getInputSchema: ({ collectionSpec }) => ({
        type: 'object',
        properties: {
            ...((collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) && { fields: collectionSpec.fieldsSchema }),
            ...((collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.parametersSchema) && { parameters: collectionSpec.parametersSchema }),
        },
    }),
    getOutputSchema: () => DATA_RECORD_SCHEMA,
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.mapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const DataCollectionRecordFromApiMappingMethod = {
    fileKey: 'record-from-api',
    supportedImplementationTypes: [ConnectorMethodImplementationType.mapping],
};
const DataCollectionRecordFromApiCodeMethod = {
    fileKey: 'record-from-api',
    supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
};
const DataCollectionFieldsToApiMappingMethod = {
    fileKey: 'fields-to-api',
    supportedImplementationTypes: [ConnectorMethodImplementationType.mapping],
};
const DataCollectionFieldsToApiCodeMethod = {
    fileKey: 'fields-to-api',
    supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
};
const DataCollectionFieldsFromApiMethod = {
    name: 'Fields From API',
    description: 'Transform fields coming from API into fieldsSchema',
    getInputSchema: () => {
        return {
            type: 'object',
            properties: {
                fields: {
                    type: 'object',
                    additionalProperties: true,
                },
            },
        };
    },
    getOutputSchema: ({ collectionSpec }) => (collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) || { type: 'object' },
    supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
};
const DataCollectionFieldsToApiMethod = {
    name: 'Fields To API',
    description: "Transform fieldsSchema into format accepted by the application's API",
    getInputSchema: ({ collectionSpec }) => ({
        type: 'object',
        properties: {
            ...((collectionSpec === null || collectionSpec === void 0 ? void 0 : collectionSpec.fieldsSchema) && { fields: collectionSpec.fieldsSchema }),
        },
    }),
    getOutputSchema: () => {
        return {
            type: 'object',
            additionalProperties: true,
        };
    },
    supportedImplementationTypes: [ConnectorMethodImplementationType.javascript],
};
const DataCollectionRecordToApiMethod = {
    supportedImplementationTypes: [
        ConnectorMethodImplementationType.mapping,
        ConnectorMethodImplementationType.javascript,
    ],
};
const DataLocationTypeCollection = {
    spec: DataCollectionCustomSpecMethod,
    recordFromFields: DataCollectionRecordFromFieldsMethod,
    customFieldsSchema: DataCollectionCustomFieldsSchemaMethod,
    fieldsFromApi: DataCollectionFieldsFromApiMethod,
    fieldsToApi: DataCollectionFieldsToApiMethod,
    methods: {
        list: DataCollectionMethodList,
        findById: DataCollectionMethodFindById,
        match: DataCollectionMethodMatch,
        search: DataCollectionMethodSearch,
        create: DataCollectionMethodCreate,
        update: DataCollectionMethodUpdate,
        delete: DataCollectionMethodDelete,
        find: DataCollectionMethodFind,
    },
    recordToApi: DataCollectionRecordToApiMethod,
    recordFromApi: DataCollectionRecordFromApiMethod,
    recordFromApiMapping: DataCollectionRecordFromApiMappingMethod,
    recordFromApiCode: DataCollectionRecordFromApiCodeMethod,
    fieldsToApiMapping: DataCollectionFieldsToApiMappingMethod,
    fieldsToApiCode: DataCollectionFieldsToApiCodeMethod,
};
const ConnectorDataCollectionMethodKeys = Object.keys(DataLocationTypeCollection.methods);

const ApiRequestSpec = z.object({
    path: z.unknown(),
    method: z.unknown(),
});
const DataCollectionMethodSpec = z.object({
    apiRequests: z.array(ApiRequestSpec).optional(),
});
const DataCollectionListSpec = DataCollectionMethodSpec.extend({
    filterFields: z.array(z.string()).optional(),
});
const DataCollectionSearchSpec = DataCollectionMethodSpec.extend({});
const DataCollectionMatchSpec = DataCollectionMethodSpec.extend({
    fields: z.array(z.string()).optional(),
});
const DataCollectionFindByIdSpec = DataCollectionMethodSpec.extend({});
const DataCollectionCreateSpec = DataCollectionMethodSpec.extend({
    fields: z.array(z.string()).optional(),
    requiredFields: z.array(z.string()).optional(),
    excludedFields: z.array(z.string()).optional(),
});
const DataCollectionUpdateSpec = DataCollectionMethodSpec.extend({
    fields: z.array(z.string()).optional(),
    excludedFields: z.array(z.string()).optional(),
});
const DataCollectionDeleteSpec = DataCollectionMethodSpec.extend({});
const DataCollectionFindSpec = DataCollectionMethodSpec.extend({
    queryFields: z.array(z.string()).optional(),
});
const DataCollectionEventTypeSpec = z.object({
    type: z.enum(['push', 'pull']),
    isFullScan: z.boolean().optional(),
    isIdOnly: z.boolean().optional(),
});
const DataCollectionEventsSpec = z
    .object({
    created: DataCollectionEventTypeSpec.optional(),
    updated: DataCollectionEventTypeSpec.optional(),
    deleted: DataCollectionEventTypeSpec.optional(),
    all: DataCollectionEventTypeSpec.optional(),
})
    .catchall(DataCollectionEventTypeSpec);
const DataCollectionUdmSpec = z.object({
    fields: z.array(z.string()).optional(),
    extract: z.record(z.string(), z.any()).optional(),
    parse: z.record(z.string(), z.any()).optional(),
});
const DataCollectionUdmsSpec = z.record(z.string(), DataCollectionUdmSpec);
const DataCollectionSpec = z.object({
    type: z.literal('collection'),
    key: z.string().optional(),
    name: z.string(),
    parametersSchema: DataSchema.optional(),
    fieldsSchema: DataSchema.optional(),
    list: DataCollectionListSpec.optional(),
    search: DataCollectionSearchSpec.optional(),
    match: DataCollectionMatchSpec.optional(),
    findById: DataCollectionFindByIdSpec.optional(),
    create: DataCollectionCreateSpec.optional(),
    update: DataCollectionUpdateSpec.optional(),
    delete: DataCollectionDeleteSpec.optional(),
    events: DataCollectionEventsSpec.optional(),
    customFields: z.boolean().optional(),
    udm: DataCollectionUdmsSpec.optional(),
    find: DataCollectionFindSpec.optional(),
});
const DataLocationPointer = z.object({
    key: z.string(),
    parameters: z.record(z.string(), z.any()).optional(),
});
const DataCollectionMethodRequest = z.object({
    parameters: z.record(z.string(), z.any()).optional(),
});
const DataCollectionFindRequest = z.object({
    query: z.any().optional(),
    cursor: z.string().optional(),
});
const DataCollectionFindResponse = z.object({
    records: z.array(DataRecordSchema),
    cursor: z.string().optional(),
});
const DataCollectionListRequest = DataCollectionMethodRequest.extend({
    filter: z.any().optional(),
    unifiedFilter: z.any().optional(),
    cursor: z.string().optional(),
});
const DataCollectionListResponseDrilldown = z.object({
    parameters: z.record(z.string(), z.any()).optional(),
    filter: z.record(z.string(), z.any()).optional(),
});
const DataCollectionListResponse = z.object({
    records: z.array(DataRecordSchema),
    drilldowns: z.array(DataCollectionListResponseDrilldown).optional(),
    cursor: z.string().optional(),
});
const DataCollectionSearchRequest = DataCollectionMethodRequest.extend({
    query: z.string(),
    cursor: z.string().optional(),
});
const DataCollectionSearchResponse = z.object({
    records: z.array(DataRecordSchema),
    cursor: z.string().optional(),
});
const DataCollectionFindByIdRequest = DataCollectionMethodRequest.extend({
    id: z.string(),
    udm: z.string().optional(),
});
const DataCollectionFindByIdResponse = z.object({
    record: DataRecordSchema,
});
const DataCollectionMatchRequest = DataCollectionMethodRequest.extend({
    query: z.record(z.string(), z.any()),
});
const DataCollectionMatchResponse = z.object({
    record: DataRecordSchema.optional(),
});
const DataCollectionCreateRequest = DataCollectionMethodRequest.extend({
    fields: z.record(z.string(), z.any()),
});
const DataCollectionCreateResponse = z.object({
    id: z.string(),
});
const DataCollectionUpdateRequest = DataCollectionMethodRequest.extend({
    id: z.string(),
    fields: z.record(z.string(), z.any()),
});
const DataCollectionUpdateResponse = z.object({
    id: z.string(),
});
const DataCollectionDeleteRequest = DataCollectionMethodRequest.extend({
    id: z.string(),
});
const DataCollectionDeleteResponse = z.undefined();
const ConnectorDataLocationTypes = {
    collection: DataLocationTypeCollection,
};

const ConnectorUdmListItem = z.object({
    key: z.string(),
});
const ConnectorUdmCollectionMapping = DataLocationPointer.extend({
    fieldsToCollection: z.any().optional(),
    fieldsFromCollection: z.any().optional(),
});
const ConnectorUdmSpec = z.object({
    defaultCollection: DataLocationPointer.optional(),
    rootDirectory: DataLocationPointer.optional(),
    collectionMappings: z.array(ConnectorUdmCollectionMapping),
});
function findUdmCollectionMapping(udm, collectionKey, parameters) {
    if (!Array.isArray(udm === null || udm === void 0 ? void 0 : udm.collectionMappings)) {
        return undefined;
    }
    return udm.collectionMappings.find((mapping) => mapping.key === collectionKey &&
        dataLocationParametersMatch({
            targetParameters: mapping.parameters,
            locationParameters: parameters,
        }));
}

var ConnectorEventImplementationType;
(function (ConnectorEventImplementationType) {
    ConnectorEventImplementationType["GlobalWebhook"] = "global-webhook";
    ConnectorEventImplementationType["Webhook"] = "webhook";
})(ConnectorEventImplementationType || (ConnectorEventImplementationType = {}));
const ConnectorEventHandlerMethods = {
    [ConnectorEventImplementationType.GlobalWebhook]: {
        getEventSelector: {
            supportedImplementationTypes: [
                ConnectorMethodImplementationType.mapping,
                ConnectorMethodImplementationType.javascript,
            ],
            isRequired: true,
        },
    },
    [ConnectorEventImplementationType.Webhook]: {
        subscribe: {
            supportedImplementationTypes: [
                ConnectorMethodImplementationType.restApiMapping,
                ConnectorMethodImplementationType.javascript,
            ],
            isRequired: true,
        },
        unsubscribe: {
            supportedImplementationTypes: [
                ConnectorMethodImplementationType.restApiMapping,
                ConnectorMethodImplementationType.javascript,
            ],
            isRequired: true,
        },
        handle: {
            supportedImplementationTypes: [
                ConnectorMethodImplementationType.mapping,
                ConnectorMethodImplementationType.javascript,
            ],
            isRequired: true,
        },
        refresh: {
            supportedImplementationTypes: [
                ConnectorMethodImplementationType.restApiMapping,
                ConnectorMethodImplementationType.javascript,
            ],
            isRequired: false,
        },
    },
};

const ConnectorAuthSuccessRecord = z.object({
    id: z.string(),
    connectorId: z.string(),
    connectorVersion: z.string(),
    authOptionKey: z.string().optional(),
    connectorParameters: z.any().optional(),
    connectionInput: z.any().optional(),
    credentials: z.any().optional(),
    logs: z.array(z.any()),
    createdAt: z.string(),
});

const CONNECTOR_VERSION_DEVELOPMENT = 'dev';
const CONNECTOR_VERSION_LATEST = 'latest';
const CONNECTOR_CATEGORIES = [
    'Accounting',
    'Ads',
    'Analytics',
    'ATS',
    'Communication',
    'CRM',
    'Customer Success',
    'Document Management',
    'E-Commerce',
    'E-Signature',
    'ERP',
    'File Storage',
    'HRIS',
    'Legal',
    'Marketing Automation',
    'Payments',
    'Project Management',
    'Sales',
    'Ticketing',
];
const ConnectorStatusValues = ['production', 'beta', 'planned'];
const ConnectorUiSpec = z.object({
    schema: DataSchema.optional(),
    description: z.string().optional(),
    helpUri: z.string().optional(),
    test: z.boolean().optional(),
});
const ConnectorSpec = z.object({
    key: z.string().optional(),
    name: z.string().optional(),
    logoUri: z.string().url().optional(),
    api: z
        .object({
        type: z.enum(['openapi', 'graphql']),
    })
        .optional(),
    ui: ConnectorUiSpec.optional().describe('[INTERNAL] Deprecated: Use inputSchema instead'),
    inputSchema: DataSchema.optional(),
    auth: ConnectorAuth.optional(),
    parametersSchema: DataSchema.optional(),
    udms: z.array(z.string()).optional(),
    udm: z.record(z.string(), z.any()).optional(),
    operations: z.array(z.any()).optional(),
    test: ConnectorMethodImplementation.optional(),
    hasReadme: z.boolean().optional(),
    externalAppId: z.string().optional(),
    appUuid: z.string().optional().describe('[INTERNAL] Deprecated: resolved to externalAppId'),
    knowledgeBaseUuid: z.string().optional(),
    workspaceId: z.string().optional(),
    data: z.any().optional(),
});
var ConnectorStatus;
(function (ConnectorStatus) {
    ConnectorStatus["production"] = "production";
    ConnectorStatus["beta"] = "beta";
    ConnectorStatus["planned"] = "planned";
})(ConnectorStatus || (ConnectorStatus = {}));
const ConnectorAuthWithFunctions = z.object({
    type: z
        .enum(['integration-app-token', 'membrane-token', 'oauth2', 'oauth1', 'client-credentials', 'proxy'])
        .optional()
        .describe('Authentication protocol used by the connector.'),
    credentialsSchema: DataSchema.optional().describe('JSON Schema describing the credentials produced by the auth flow (e.g. `access_token`, `refresh_token`). Usually inferred from the auth type; override only when the connector stores custom credential fields.'),
    customCredentialsSchema: DataSchema.optional().describe('Additional JSON Schema for workspace-level overrides of credentials (e.g. BYO-OAuth clientId/clientSecret).'),
    inputSchema: DataSchema.optional().describe('JSON Schema for end-user input collected during connection (e.g. an API key or subdomain the user types in).'),
    parametersSchema: DataSchema.optional().describe('JSON Schema for connector-level parameters configured per integration (e.g. region, account ID).'),
    getCredentialsFromConnectionParameters: GenericFunctionDefinition.optional().describe('Function that derives auth credentials from connection parameters. Use for static-key auth (api-key, basic-auth) where the user-provided input IS the credential.'),
    refreshCredentials: GenericFunctionDefinition.optional().describe('Function that refreshes expired credentials. Implement for non-standard OAuth refresh flows or when additional credentials need to be refreshed alongside the OAuth token.'),
    makeApiClient: GenericFunctionDefinition.optional().describe('Function that configures the HTTP client used by operations, actions and data sources. Typically a `mapping` type with `baseUri` and auth header wiring.'),
    getOAuthConfig: GenericFunctionDefinition.optional().describe('OAuth2: function returning `{ authorizeUri, tokenUri, scopes, noRefreshToken? }` — the authorize/token endpoint URLs and requested scopes.'),
    getTokenData: GenericFunctionDefinition.optional().describe('OAuth2: function invoked with the raw token response to extract token metadata. Rarely needed.'),
    getCredentialsFromAccessTokenResponse: GenericFunctionDefinition.optional().describe('OAuth2: function that transforms the token endpoint response into the final credentials object. Required for multi-tenant APIs (Microsoft) or APIs that require post-OAuth bootstrap calls.'),
    getCredentialsFromRefreshTokenResponse: GenericFunctionDefinition.optional().describe('OAuth2: function that transforms the refresh token endpoint response into the refreshed credentials. Required when refresh responses differ from initial-token responses.'),
    test: GenericFunctionDefinition.optional().describe('Function that verifies the connection by making a lightweight authenticated API call (typically a `rest-api-mapping` against a `/me`-style endpoint).'),
    disconnect: GenericFunctionDefinition.optional().describe('Function that revokes credentials at the provider when the connection is removed. Implement only if the provider exposes a revoke endpoint.'),
    proxyKey: z
        .string()
        .optional()
        .describe('For `type: "proxy"` — identifies which proxy configuration the workspace should use.'),
    ui: z
        .object({
        schema: DataSchema.optional(),
        description: z.string().optional(),
        helpUri: z.string().optional(),
    })
        .optional()
        .describe('[INTERNAL] Deprecated: Use inputSchema instead'),
});
const WriteableConnectorOption = ConnectorAuthWithFunctions.extend({
    title: z.string().optional(),
    description: z.string().optional(),
    enabled: z.any().optional(),
}).omit({
    credentialsSchema: true,
});
const ConnectorOption = WriteableConnectorOption.extend({
    credentialsSchema: DataSchema.optional(),
});
const GeneratedConnectorOption = z.object({
    key: z.string().optional(),
    type: z.enum(CONNECTOR_AUTH_TYPES),
    title: z.string().optional(),
    description: z.string().optional(),
    inputSchema: DataSchema.optional(),
    connectorParametersSchema: DataSchema.optional(),
    oAuthCallbackUri: z.string().optional(),
});
const ConnectorOptions = z.record(z.string(), ConnectorOption);
const WritableConnectorVersionData = ConnectorAuthWithFunctions.extend({
    parametersSchema: DataSchema.optional(),
    options: z
        .record(z.string(), WriteableConnectorOption)
        .optional()
        .describe('Map of auth option key to auth configuration, for connectors that support multiple auth methods. Mutually exclusive with the top-level auth fields: use either the top-level fields (single-auth) OR `options` (multi-auth), not both.'),
}).omit({
    credentialsSchema: true,
});
const ConnectorVersionData = z
    .object({
    baseUri: z.string().optional(),
    revision: z.string().optional(),
    dataCollectionsCount: z.number().optional(),
    operationsCount: z.number().optional(),
    eventsCount: z.number().optional(),
    udms: z.array(z.string()).optional(),
    hasTest: z.boolean().optional(),
    hasAuthTest: z.boolean().optional(),
    hasOperations: z.boolean().optional(),
    hasData: z.boolean().optional(),
    isReadOnly: z.boolean().optional(),
    version: z.string(),
    credentialsSchema: DataSchema.optional(),
})
    .extend(WritableConnectorVersionData.shape)
    .extend({
    options: ConnectorOptions.optional(),
});
const ConnectorVersion = ConnectorVersionData.extend({
    id: z.string(),
    connectorId: z.string(),
    changelog: z.string(),
});
const WriteableConnectorFields = z
    .object({
    key: z.string().optional(),
    name: z.string().optional(),
    logoUri: z.string().optional(),
    externalAppId: z.string().optional(),
    externalAppUuid: z.string().optional(),
    appUuid: z.string().optional().describe('[INTERNAL] Deprecated: resolved to externalAppId'),
    categories: z.array(z.string()).optional(),
})
    .extend(WritableConnectorVersionData.shape);
const ConnectorExportProperties = WriteableConnectorFields.extend({
    uuid: z.string(),
})
    .omit({ externalAppId: true })
    .required({ key: true });
const ConnectorVersionExportProperties = WritableConnectorVersionData.extend({
    version: z.string().min(1),
});
const UpdateConnectorRequest = WriteableConnectorFields;
const CreateConnectorRequest = WriteableConnectorFields.extend({
    name: z.string(),
    uuid: z.string().optional(),
});
const BaseConnector = z.object({
    id: z.string(),
    key: z.string(),
    uuid: z.string(),
    externalAppId: z.string().optional(),
    name: z.string(),
    logoUri: z.string(),
    appUri: z
        .string()
        .optional()
        .describe('Site URL of the linked external app, denormalized onto public connector listings.'),
    categories: z.array(z.string()).optional(),
    isPublic: z.boolean().optional(),
    popularity: z.number().optional(),
    tenantId: z.string().optional(),
    archivedAt: z.string().optional().describe('When set, the connector is archived (soft-deleted).'),
});
const Connector = z.object({}).extend(BaseConnector.shape).extend(ConnectorVersionData.shape);
const MinimalConnector = z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    logoUri: z.string(),
    appUri: z
        .string()
        .optional()
        .describe('Site URL of the linked external app, denormalized onto public connector listings.'),
    isPublic: z.boolean().optional(),
    tenantId: z.string().optional(),
});

const DEFAULT_PULL_UPDATES_INTERVAL_SECONDS = 60 * 5;
const DEFAULT_FULL_SYNC_INTERVAL_SECONDS = 60 * 60 * 3;
const MIN_FULL_SYNC_INTERVAL_SECONDS = 10 * 60;
const MIN_PULL_UPDATES_INTERVAL_SECONDS = 1 * 60;
const DataSourceSpecificProperties = z.object({
    connectionId: z.string().optional(),
    instanceKey: z.string().optional(),
    dataSourceId: z.string().optional(),
    universalDataSourceId: z.string().optional(),
    udm: z.string().optional(),
    pullUpdatesIntervalSeconds: z.number().optional(),
    fullSyncIntervalSeconds: z.number().optional(),
    collectionKey: z.string().optional(),
    collectionParameters: z.any().optional(),
});
const DataSourceEditableProperties = BaseIntegrationLevelMembraneInterfaceEditableProperties.omit({
    parentId: true,
}).extend(DataSourceSpecificProperties.shape);
const BackwardCompatibleDataSourceEditableProperties = DataSourceEditableProperties.extend({
    path: z.string().optional().meta({ deprecated: true }),
    defaultPath: z.string().optional().meta({ deprecated: true }),
});
const DataSourceExportProperties = BaseIntegrationLevelMembraneInterfaceExportProperties.extend(DataSourceSpecificProperties.shape)
    .extend({
    path: z.string().optional().meta({ deprecated: true }),
    defaultPath: z.string().optional().meta({ deprecated: true }),
})
    .omit({
    connectionId: true,
    dataSourceId: true,
    universalDataSourceId: true,
});
const DataSourceReadOnlyProperties = BaseIntegrationLevelMembraneInterfaceReadOnlyProperties.extend({
    ...TenantLayerElement.partial().shape,
    dataSourceRevision: z.string().optional(),
    universalDataSourceRevision: z.string().optional(),
    defaultCollectionKey: z.string().optional(),
    defaultCollectionParameters: z.any().optional(),
    collectionSpec: DataCollectionSpec.optional(),
    isCustomized: z.boolean().optional(),
    errors: z.array(ErrorDataSchema).optional(),
});
const BaseDataSource = BaseMembraneInterface.merge(DataSourceEditableProperties).merge(DataSourceReadOnlyProperties);
const BaseDataSourceInstance = BaseDataSource;
const DataSourceUnitConfig = z.object({
    key: z.any().optional(),
    collectionKey: z.any().optional(),
    collectionParameters: z.any().optional(),
    udm: z.any().optional(),
    pullUpdatesIntervalSeconds: z.number().optional(),
    fullSyncIntervalSeconds: z.number().optional(),
    path: z.any().optional(),
    defaultPath: z.any().optional(),
});

var ExternalEventType;
(function (ExternalEventType) {
    ExternalEventType["DataRecordCreated"] = "data-record-created";
    ExternalEventType["DataRecordUpdated"] = "data-record-updated";
    ExternalEventType["DataRecordDeleted"] = "data-record-deleted";
    ExternalEventType["ConnectorEvent"] = "connector-event";
})(ExternalEventType || (ExternalEventType = {}));
var IncomingWebhooksState;
(function (IncomingWebhooksState) {
    IncomingWebhooksState["SUCCESS"] = "success";
    IncomingWebhooksState["ERROR"] = "error";
})(IncomingWebhooksState || (IncomingWebhooksState = {}));
const ExternalEventUnitConfig = z.object({
    type: z.enum(ExternalEventType),
    dataSource: DataSourceUnitConfig.optional(),
    eventKey: z.string().optional(),
    eventParameters: z.any().optional(),
});
const BaseExternalEvent = z.object({
    config: ExternalEventUnitConfig.optional(),
});
function externalEventTypeToDataCollectionEventType(type) {
    switch (type) {
        case ExternalEventType.DataRecordCreated:
            return DataCollectionEventType.CREATED;
        case ExternalEventType.DataRecordUpdated:
            return DataCollectionEventType.UPDATED;
        case ExternalEventType.DataRecordDeleted:
            return DataCollectionEventType.DELETED;
        default:
            throw new InternalError(`Cannot convert ExternalEventType "${type}" to DataCollectionEventType`);
    }
}
function dataCollectionEventTypeToExternalEventType(type) {
    switch (type) {
        case DataCollectionEventType.CREATED:
            return ExternalEventType.DataRecordCreated;
        case DataCollectionEventType.UPDATED:
            return ExternalEventType.DataRecordUpdated;
        case DataCollectionEventType.DELETED:
            return ExternalEventType.DataRecordDeleted;
        default:
            throw new InternalError(`Cannot convert DataCollectionEventType "${type}" to ExternalEventType`);
    }
}

var FlowNodeRunStatus;
(function (FlowNodeRunStatus) {
    FlowNodeRunStatus["COMPLETED"] = "completed";
    FlowNodeRunStatus["FAILED"] = "failed";
    FlowNodeRunStatus["SKIPPED"] = "skipped";
})(FlowNodeRunStatus || (FlowNodeRunStatus = {}));
const UpstreamFlowNodeRunSchema = z.object({
    nodeKey: z.string(),
    runId: z.string(),
    outputId: z.string(),
});
const DownstreamFlowNodeRunSchema = z.object({
    runId: z.string(),
    nodeKey: z.string(),
});
const FlowNodeRunParametersSchema = z.object({
    id: z.string(),
    upstreamRuns: z.array(UpstreamFlowNodeRunSchema),
    input: z.any(),
});
const FlowNodeRunOutputSchema = z.object({
    id: z.string(),
    data: z.string(),
    downstreamRuns: z.array(DownstreamFlowNodeRunSchema),
});
const FlowNodeRunResultSchema = z.object({
    status: z.enum(FlowNodeRunStatus),
    logs: z.array(z.any()),
    outputs: z.array(FlowNodeRunOutputSchema),
    errors: z.array(ErrorDataSchema),
});
const FlowNodeRunRecordSchema = FlowNodeRunParametersSchema.merge(FlowNodeRunResultSchema);
const FlowNodeRunOutputWithoutDownstreamRunsSchema = z.object({
    id: z.string(),
    data: z.string(),
});
const FlowNodeRunOutputMetadataSchema = z.object({
    id: z.string(),
    downstreamRuns: z.array(DownstreamFlowNodeRunSchema),
});
const FlowNodeRunRecordWithoutOutputsDataSchema = z.object({
    id: z.string(),
    upstreamRuns: z.array(UpstreamFlowNodeRunSchema),
    input: z.any(),
    status: z.enum(FlowNodeRunStatus),
    logs: z.array(z.any()),
    outputs: z.array(FlowNodeRunOutputMetadataSchema),
    errors: z.array(ErrorDataSchema),
});

var FlowRunNodeState;
(function (FlowRunNodeState) {
    FlowRunNodeState["RUNNING"] = "running";
    FlowRunNodeState["COMPLETED"] = "completed";
    FlowRunNodeState["STOPPED"] = "stopped";
    FlowRunNodeState["FAILED"] = "failed";
})(FlowRunNodeState || (FlowRunNodeState = {}));
var FlowRunState;
(function (FlowRunState) {
    FlowRunState["QUEUED"] = "queued";
    FlowRunState["RUNNING"] = "running";
    FlowRunState["COMPLETED"] = "completed";
    FlowRunState["STOPPED"] = "stopped";
    FlowRunState["FAILED"] = "failed";
})(FlowRunState || (FlowRunState = {}));
var FlowRunLaunchedByTrigger;
(function (FlowRunLaunchedByTrigger) {
    FlowRunLaunchedByTrigger["ApiTrigger"] = "api-trigger";
    FlowRunLaunchedByTrigger["ScheduleTrigger"] = "schedule-trigger";
    FlowRunLaunchedByTrigger["AppEventTrigger"] = "app-event-trigger";
    FlowRunLaunchedByTrigger["DataSourceTrigger"] = "data-source-trigger";
    FlowRunLaunchedByTrigger["ExternalEvent"] = "external-event";
})(FlowRunLaunchedByTrigger || (FlowRunLaunchedByTrigger = {}));
const FlowRunLaunchedBy = z.object({
    type: z.enum(FlowRunLaunchedByTrigger),
    ids: z.array(z.string()).optional(),
});
const FlowRunNode = z.object({
    state: z.enum(FlowRunNodeState),
    runs: z.number().optional(),
    erroredRuns: z.number().optional(),
    outputs: z.number().optional(),
});
const BaseFlowRun = ActivityLogRecord.extend({
    name: z.string().optional(),
    connectionFlowId: z.string(),
    integrationFlowId: z.string().optional(),
    universalFlowId: z.string().optional(),
    flowInstanceId: z.string().optional().describe('[INTERNAL] Deprecated: Use connectionFlowId instead'),
    flowId: z.string().optional().describe('[INTERNAL] Deprecated: Use integrationFlowId instead'),
    startNodeKey: z.string(),
    input: z.any().optional(),
    state: z.enum(FlowRunState),
    startTime: z.string(),
    endTime: z.string().optional(),
    errors: z.array(ErrorDataSchema).optional(),
    nodes: z.record(z.string(), FlowRunNode).optional(),
    launchedBy: FlowRunLaunchedBy.optional(),
    meta: z.record(z.string(), z.any()).optional(),
});

var FlowNodeState;
(function (FlowNodeState) {
    FlowNodeState["SETUP_FAILED"] = "SETUP_FAILED";
    FlowNodeState["READY"] = "READY";
})(FlowNodeState || (FlowNodeState = {}));
const FlowNodeLink = z.object({
    key: z.string().optional(),
    filter: z.any().optional(),
    name: z.string().optional(),
});
const FlowNode = z.object({
    type: z.string().optional(),
    version: z.number().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    config: z.any().optional(),
    concurrency: z.number().optional(),
    onError: z.enum(['stop', 'continue']).optional(),
    ui: z.any().optional(),
    inputSchema: z.any().optional(),
    outputSchema: z.any().optional(),
    outputExample: z.any().optional(),
    links: z.array(FlowNodeLink).optional(),
    isCustomized: z.boolean().optional(),
    state: z.enum(FlowNodeState).optional(),
    errors: z.array(ErrorDataSchema).optional(),
    userConfig: z.any().optional(),
    testInput: z.any().optional(),
    dependencies: z.array(z.any()).optional(),
});
class FlowNodeSpec {
    constructor(args) {
        Object.assign(this, args);
    }
}

var ApiRequestToExternalApp = new FlowNodeSpec({
    name: 'API Request to External App',
    description: 'Send an arbitrary API request to the external application',
    requiresIntegration: true,
});

var ApiRequestToYourApp = new FlowNodeSpec({
    name: 'API Request to Your App',
    description: 'Send an API request to my application',
});

var ApiTrigger = new FlowNodeSpec({
    name: 'API Trigger',
    description: 'Launch flow via API',
    isTrigger: true,
});

var AppEventTrigger = new FlowNodeSpec({
    name: 'Internal Event Trigger',
    description: 'Trigger flow when a matching Internal Event is received',
    isTrigger: true,
});

var connectorEventTrigger = new FlowNodeSpec({
    name: 'Connector Event Trigger',
    description: "Trigger flow on one of the events defined by the external app's connector.",
    isTrigger: true,
    requiresIntegration: true,
    direction: FieldMappingDirection.IMPORT,
});

var CreateDataLink = new FlowNodeSpec({
    name: 'Create Data Link',
    description: 'Link records between your app and external app',
});

var CreateDataRecord = new FlowNodeSpec({
    name: 'Create Data Record',
    description: 'Create a new data record in a collection',
    requiresDataSpec: true,
    direction: FieldMappingDirection.EXPORT,
});

var CustomHttpRequest = new FlowNodeSpec({
    name: 'Custom HTTP Request',
    description: 'Send an API request',
});

var DataRecordCreatedTrigger = new FlowNodeSpec({
    name: 'Data Record Created Trigger',
    description: 'Launch flow when a data record is created in a remote data collection',
    isTrigger: true,
    requiresDataSpec: true,
    direction: FieldMappingDirection.IMPORT,
});

var DataRecordDeletedTrigger = new FlowNodeSpec({
    name: 'Data Record Deleted Trigger',
    description: 'Launch flow when a data record is deleted in a remote data collection',
    isTrigger: true,
    requiresDataSpec: true,
    direction: FieldMappingDirection.IMPORT,
});

var DataRecordUpdatedTrigger = new FlowNodeSpec({
    name: 'Data Record Updated Trigger',
    description: 'Launch flow when a data record is updated in a remote data collection',
    isTrigger: true,
    requiresDataSpec: true,
    direction: FieldMappingDirection.IMPORT,
});

var DeleteDataLink = new FlowNodeSpec({
    name: 'Delete Data Link',
    description: 'Unlink records between your app and external app',
});

var DeleteDataRecord = new FlowNodeSpec({
    name: 'Delete Data Record',
    description: 'Delete a new data record from a collection using its id',
    requiresDataSpec: true,
    direction: FieldMappingDirection.EXPORT,
});

var Filter = new FlowNodeSpec({
    name: 'Filter',
    description: 'Filter records that go forward through the flow',
});

var FindDataLink = new FlowNodeSpec({
    name: 'Find Data Link',
    description: 'Find a link between records in your app and external app',
});

var FindDataRecordById = new FlowNodeSpec({
    name: 'Find Data Record By Id',
    description: 'Find a single data record by id',
    requiresDataSpec: true,
    direction: FieldMappingDirection.IMPORT,
});

var FindDataRecords = new FlowNodeSpec({
    name: 'Find Data Records',
    description: 'Find records in a data collection by their field values',
    isDeprecated: true,
    requiresDataSpec: true,
    direction: FieldMappingDirection.IMPORT,
});

var FindOrCreateDataRecord = new FlowNodeSpec({
    name: 'Find or Create Data Record',
    description: 'Find a matching data record or create a new one if nothing found.',
    requiresDataSpec: true,
    direction: FieldMappingDirection.EXPORT,
});

var ForEach = new FlowNodeSpec({
    name: 'For Each',
    description: 'Execute one or more steps for each item on a list.',
});

var ForEachV2 = new FlowNodeSpec({
    name: 'For Each',
    description: 'Execute one or more steps for each item on a list.',
    getSubFlowRootNodeKey: (node) => { var _a; return (_a = node.config) === null || _a === void 0 ? void 0 : _a.rootNodeKey; },
});

var IntegrationSpecificOperation = new FlowNodeSpec({
    name: 'Integration-specific Operation',
    description: 'Node that executes operation specific to an integration flow is created for.',
    requiresIntegration: true,
});

var ListDataRecords = new FlowNodeSpec({
    name: 'List Data Records',
    description: 'List all records from a data collection with an optional filter',
    requiresDataSpec: true,
    direction: FieldMappingDirection.IMPORT,
});

var LookupDataRecord = new FlowNodeSpec({
    name: 'Lookup Data Record',
    description: 'Find a one data record by its fields',
    requiresDataSpec: true,
    direction: FieldMappingDirection.IMPORT,
});

var RunAction = new FlowNodeSpec({
    name: 'Run Action',
    description: 'Run prebuilt action.',
});

var RunJavascript = new FlowNodeSpec({
    name: 'Run JavaScript',
    description: 'Run custom JavaScript code',
});

var ScheduleTrigger = new FlowNodeSpec({
    name: 'Schedule Trigger',
    description: 'Run flow on schedule',
    isTrigger: true,
});

var SearchDataRecords = new FlowNodeSpec({
    name: 'Search Data Records',
    description: 'Search records by query.',
    direction: FieldMappingDirection.IMPORT,
});

var TransformData = new FlowNodeSpec({
    name: 'Transform Data',
    description: 'Create a data structure of any shape by combining and transforming inputs',
    requiresDataSpec: false,
});

var UpdateDataRecord = new FlowNodeSpec({
    name: 'Update Data Record',
    description: 'Update a new data record in a collection using its id',
    requiresDataSpec: true,
    direction: FieldMappingDirection.EXPORT,
});

var FlowNodeType;
(function (FlowNodeType) {
    FlowNodeType["ApiTrigger"] = "api-trigger";
    FlowNodeType["ScheduleTrigger"] = "schedule-trigger";
    FlowNodeType["AppEventTrigger"] = "app-event-trigger";
    FlowNodeType["DataRecordCreatedTrigger"] = "data-record-created-trigger";
    FlowNodeType["DataRecordUpdatedTrigger"] = "data-record-updated-trigger";
    FlowNodeType["DataRecordDeletedTrigger"] = "data-record-deleted-trigger";
    FlowNodeType["ConnectorEventTrigger"] = "connector-event-trigger";
    FlowNodeType["SearchDataRecords"] = "search-data-records";
    FlowNodeType["ListDataRecords"] = "list-data-records";
    FlowNodeType["FindOrCreateDataRecord"] = "find-or-create-data-record";
    FlowNodeType["LookupDataRecord"] = "lookup-data-record";
    FlowNodeType["FindDataRecordById"] = "find-data-record-by-id";
    FlowNodeType["CreateDataRecord"] = "create-data-record";
    FlowNodeType["UpdateDataRecord"] = "update-data-record";
    FlowNodeType["DeleteDataRecord"] = "delete-data-record";
    FlowNodeType["CreateDataLink"] = "create-data-link";
    FlowNodeType["FindDataLink"] = "find-data-link";
    FlowNodeType["DeleteDataLink"] = "delete-data-link";
    FlowNodeType["TransformData"] = "transform-data";
    FlowNodeType["Filter"] = "filter";
    FlowNodeType["ForEachV2"] = "for-each-v2";
    FlowNodeType["RunJavascript"] = "run-javascript";
    FlowNodeType["IntegrationSpecificOperation"] = "integration-specific-operation";
    FlowNodeType["ApiRequestToYourApp"] = "api-request-to-your-app";
    FlowNodeType["ApiRequestToExernalApp"] = "api-request-to-external-app";
    FlowNodeType["CustomHttpRequest"] = "custom-http-request";
    FlowNodeType["RunAction"] = "run-action";
    FlowNodeType["ApiRequest"] = "api-request";
    FlowNodeType["CustomApiRequest"] = "custom-api-request";
    FlowNodeType["HttpRequest"] = "http-request";
    FlowNodeType["ForEach"] = "for-each";
    FlowNodeType["FindDataRecords"] = "find-data-records";
})(FlowNodeType || (FlowNodeType = {}));
const FLOW_NODE_SPECS = {
    [FlowNodeType.ApiTrigger]: ApiTrigger,
    [FlowNodeType.ScheduleTrigger]: ScheduleTrigger,
    [FlowNodeType.AppEventTrigger]: AppEventTrigger,
    [FlowNodeType.ConnectorEventTrigger]: connectorEventTrigger,
    [FlowNodeType.DataRecordCreatedTrigger]: DataRecordCreatedTrigger,
    [FlowNodeType.DataRecordUpdatedTrigger]: DataRecordUpdatedTrigger,
    [FlowNodeType.DataRecordDeletedTrigger]: DataRecordDeletedTrigger,
    [FlowNodeType.SearchDataRecords]: SearchDataRecords,
    [FlowNodeType.ListDataRecords]: ListDataRecords,
    [FlowNodeType.LookupDataRecord]: LookupDataRecord,
    [FlowNodeType.FindOrCreateDataRecord]: FindOrCreateDataRecord,
    [FlowNodeType.FindDataRecords]: {
        ...FindDataRecords,
        isDeprecated: true,
    },
    [FlowNodeType.FindDataRecordById]: FindDataRecordById,
    [FlowNodeType.CreateDataRecord]: CreateDataRecord,
    [FlowNodeType.UpdateDataRecord]: UpdateDataRecord,
    [FlowNodeType.DeleteDataRecord]: DeleteDataRecord,
    [FlowNodeType.TransformData]: TransformData,
    [FlowNodeType.Filter]: Filter,
    [FlowNodeType.CreateDataLink]: CreateDataLink,
    [FlowNodeType.FindDataLink]: FindDataLink,
    [FlowNodeType.DeleteDataLink]: DeleteDataLink,
    [FlowNodeType.ForEach]: {
        ...ForEach,
        isDeprecated: true,
    },
    [FlowNodeType.ForEachV2]: ForEachV2,
    [FlowNodeType.RunJavascript]: RunJavascript,
    [FlowNodeType.IntegrationSpecificOperation]: IntegrationSpecificOperation,
    [FlowNodeType.ApiRequestToYourApp]: ApiRequestToYourApp,
    [FlowNodeType.HttpRequest]: {
        ...ApiRequestToYourApp,
        isDeprecated: true,
    },
    [FlowNodeType.ApiRequestToExernalApp]: ApiRequestToExternalApp,
    [FlowNodeType.CustomApiRequest]: {
        ...ApiRequestToExternalApp,
        isDeprecated: true,
    },
    [FlowNodeType.CustomHttpRequest]: CustomHttpRequest,
    [FlowNodeType.ApiRequest]: {
        ...CustomHttpRequest,
        isDeprecated: true,
    },
    [FlowNodeType.RunAction]: RunAction,
};

const FlowSpecificProperties = z.object({
    connectionId: z.string().optional(),
    instanceKey: z.string().optional(),
    flowId: z.string().optional(),
    universalFlowId: z.string().optional(),
    parametersSchema: DataSchema.optional(),
    parameters: z.any().optional(),
    enabled: z.boolean().optional(),
    nodes: z.record(z.string(), FlowNode).optional(),
    autoCreateInstances: z.boolean().optional(),
});
const FlowEditableProperties = BaseIntegrationLevelMembraneInterfaceEditableProperties.omit({
    parentId: true,
}).extend(FlowSpecificProperties.shape);
const FlowExportProperties = BaseIntegrationLevelMembraneInterfaceExportProperties.extend(FlowSpecificProperties.shape).omit({
    connectionId: true,
    flowId: true,
    universalFlowId: true,
});
const FlowReadOnlyProperties = BaseIntegrationLevelMembraneInterfaceReadOnlyProperties.extend({
    ...TenantLayerElement.partial().shape,
    flowRevision: z.string().optional(),
    universalFlowRevision: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    state: z.enum(WorkspaceElementState).optional(),
    customized: z
        .object({
        name: z.boolean().optional(),
        nodes: z.boolean().optional(),
    })
        .optional(),
    dependencies: z.array(z.any()).optional(),
});
const BaseFlow = BaseMembraneInterface.merge(FlowEditableProperties).merge(FlowReadOnlyProperties);
const BaseFlowInstance = BaseFlow;
const flowNodeLinkSchema = z.object({
    key: z.string().optional(),
    filter: z.any().optional(),
    name: z.string().optional(),
});
const flowInstanceNodeCreateSchema = z.object({
    name: z.string().optional(),
    type: z.string().optional(),
    userConfig: z.any().optional(),
    config: z.any().optional(),
    onError: z.enum(['stop', 'continue']).optional(),
    concurrency: z.number().optional(),
    ui: z.any().optional(),
    links: z.array(flowNodeLinkSchema).optional(),
    isCustomized: z.boolean().optional(),
});
const flowInstanceNodeUpdateSchema = z.object({
    name: z.string().optional(),
    type: z.string().optional(),
    userConfig: z.any().optional(),
    config: z.any().optional(),
    onError: z.enum(['stop', 'continue']).optional(),
    links: z.array(flowNodeLinkSchema).optional(),
    concurrency: z.number().optional(),
    isCustomized: z.boolean().optional(),
});
const createFlowInstanceSchema = z.object({
    name: z.string().optional(),
    userId: z.string().optional(),
    flowId: z.string().optional(),
    connectionId: z.string().optional(),
    integrationId: z.string().optional(),
    instanceKey: z.string().optional(),
    parameters: z.any().optional(),
    nodes: z.record(z.string(), flowInstanceNodeCreateSchema).optional(),
    enabled: z.boolean().optional(),
    customized: z
        .object({
        name: z.boolean().optional(),
        nodes: z.boolean().optional(),
    })
        .optional(),
});
const updateFlowInstanceSchema = z.object({
    name: z.string().optional(),
    enabled: z.boolean().optional(),
    parameters: z.any().optional(),
    nodes: z.record(z.string(), flowInstanceNodeUpdateSchema).optional(),
    archivedAt: z.string().optional(),
    customized: z
        .object({
        name: z.boolean().optional(),
        nodes: z.boolean().optional(),
    })
        .optional(),
});

const IntegrationOptionConfig = z.object({
    disabled: z.boolean().optional(),
    parameters: z.record(z.string(), z.any()).optional(),
});
const IntegrationOptions = z.record(z.string(), IntegrationOptionConfig);
const BaseIntegration = BaseMembraneInterface.extend({
    logoUri: z.string(),
    connectorId: z.string().optional(),
    connectorVersion: z.string().optional(),
    oAuthCallbackUri: z.string().optional(),
    parameters: z.any().optional(),
    archivedAt: z.string().optional(),
    hasMissingParameters: z.boolean().optional(),
    hasDocumentation: z.boolean().optional(),
    hasOperations: z.boolean().optional(),
    operationsCount: z.number().optional(),
    hasData: z.boolean().optional(),
    dataCollectionsCount: z.number().optional(),
    hasEvents: z.boolean().optional(),
    eventsCount: z.number().optional(),
    hasGlobalWebhooks: z.boolean().optional(),
    hasUdm: z.boolean().optional(),
    isTest: z.boolean().optional(),
    externalAppId: z.string().optional(),
    appUuid: z.string().optional().describe('[INTERNAL] Deprecated: Use externalAppId instead'),
    isDeactivated: z.boolean().optional(),
    authType: z.enum(CONNECTOR_AUTH_TYPES).optional(),
    optionsConfig: IntegrationOptions.optional(),
});
const IntegrationEditableProperties = BaseMembraneInterfaceEditableProperties.extend({
    logoUri: z.string().optional(),
    externalAppId: z
        .string()
        .optional()
        .describe("External app id; alternative to connectorId — resolves to the app's default connector."),
    externalAppUuid: z.string().optional(),
    appUuid: z.string().optional().describe('[INTERNAL] Deprecated: resolved to externalAppId'),
    oAuthCallbackUri: z.url().or(z.literal('')).optional(),
    logoBase64: z.string().optional(),
    connectorVersion: z.string().optional(),
    connectorId: z.string().optional().describe('ID of the connector this integration uses.'),
    connectorUuid: z.string().optional(),
    connectorKey: z
        .string()
        .optional()
        .describe('Key of the connector; alternative to connectorId. Resolved to connectorId by the API.'),
    parameters: z
        .record(z.string(), z.any())
        .optional()
        .describe('Connector-specific parameters (e.g. OAuth { clientId, clientSecret, scopes }).'),
    optionsConfig: IntegrationOptions.optional(),
});
const IntegrationExportProperties = IntegrationEditableProperties.omit({
    connectorId: true,
    externalAppId: true,
    parameters: true,
}).required({ uuid: true, key: true });
const AppliedToIntegrations = (elementSchema) => z.array(z.object({
    element: elementSchema,
    integration: BaseIntegration,
}));

const PACKAGE_VERSION_DEVELOPMENT = 'dev';
const PACKAGE_VERSION_LATEST = 'latest';
const PackageElement = z.object({
    id: z.string(),
    type: z.enum(WorkspaceElementType),
    element: z.any().optional(),
});
const PackageElementExport = z.object({
    uuid: z.string(),
    type: z.enum(WorkspaceElementType),
    element: z.any().optional(),
});
const PackageEditableProperties = BaseIntegrationLevelMembraneInterfaceEditableProperties.extend({
    scenarioTemplateId: z.string().optional(),
    elements: z.array(PackageElement).optional(),
});
const PackageExportProperties = BaseIntegrationLevelMembraneInterfaceExportProperties.extend({
    elements: z.array(PackageElementExport).optional(),
});
const PackageCalculatedProperties = z.object({
    key: z.string(),
    name: z.string(),
    isCustomized: z.boolean().optional(),
    isPublic: z.boolean().optional(),
});
const BasePackage = BaseMembraneInterface.merge(PackageEditableProperties).merge(PackageCalculatedProperties);
const WritablePackageVersionData = z.object({
    elements: z.array(PackageElement).optional(),
});
const PackageVersionData = WritablePackageVersionData.extend({
    version: z.string().optional(),
    changelog: z.string().optional(),
    parentPackageVersion: z.string().optional(),
});
const PackageVersionListItem = z.object({
    id: z.string(),
    version: z.string(),
    changelog: z.string().optional(),
    parentPackageVersion: z.string().optional(),
});

var ScreenType;
(function (ScreenType) {
    ScreenType["Integration"] = "integration";
})(ScreenType || (ScreenType = {}));
var ScreenBlockType;
(function (ScreenBlockType) {
    ScreenBlockType["Flow"] = "flow";
    ScreenBlockType["FieldMapping"] = "field-mapping";
    ScreenBlockType["DataSource"] = "data-source";
    ScreenBlockType["Action"] = "action";
})(ScreenBlockType || (ScreenBlockType = {}));
const ScreenBlock = z
    .object({
    type: z.enum(ScreenBlockType),
})
    .and(z.record(z.string(), z.any()));
const BaseScreen = BaseWorkspaceElement.extend({
    type: z.enum(ScreenType),
    key: z.string().optional(),
    blocks: z.array(ScreenBlock),
});

const BaseCustomer = z.object({
    id: z.string(),
    name: z.string(),
    meta: z.record(z.string(), z.any()).optional(),
    internalId: z.string(),
    fields: z.record(z.string(), z.any()).optional(),
    credentials: z.any().optional(),
    lastActiveAt: z.string().optional(),
    isTest: z.boolean().optional(),
    isBillable: z.boolean().optional(),
    isActive: z.boolean().optional(),
    aiCreditsRolling30DayLimit: z.number().min(0).nullable().optional(),
    createdAt: z.string().optional(),
    archivedAt: z.string().optional(),
});

var DataLinkDirection;
(function (DataLinkDirection) {
    DataLinkDirection["IMPORT"] = "import";
    DataLinkDirection["EXPORT"] = "export";
    DataLinkDirection["BOTH"] = "both";
})(DataLinkDirection || (DataLinkDirection = {}));
const DataLinkTableSpecificProperties = z.object({
    connectionId: z.string().optional(),
    instanceKey: z.string().optional(),
    dataLinkTableId: z.string().optional(),
});
const DataLinkTableEditableProperties = BaseMembraneInterfaceEditableProperties.extend(DataLinkTableSpecificProperties.shape);
const DataLinkTableExportProperties = BaseExportProperties.extend(DataLinkTableSpecificProperties.shape).omit({
    connectionId: true,
    dataLinkTableId: true,
});
const DataLinkTableReadOnlyProperties = BaseMembraneInterfaceReadOnlyProperties.extend({
    ...TenantLayerElement.partial().shape,
    integrationId: z.string().optional(),
    archivedAt: z.string().optional(),
    isCustomized: z.boolean().optional(),
});
const BaseDataLinkTable = BaseMembraneInterface.merge(DataLinkTableEditableProperties).merge(DataLinkTableReadOnlyProperties);
const BaseDataLinkTableInstance = BaseDataLinkTable;
const DataLink = z.object({
    id: z.string(),
    dataLinkTableInstanceId: z.string(),
    externalRecordId: z.string(),
    appRecordId: z.string(),
    direction: z.enum(DataLinkDirection),
});
const DataLinkTableConfig = z.object({
    key: z.string(),
});

const BaseAppEventType = BaseMembraneInterface.extend({
    revision: z.string(),
    archivedAt: z.string().optional(),
    webhookKey: z.string(),
    subscribeRequest: HttpRequestSpec.optional(),
    example: z.any().optional(),
    schema: DataSchema.optional(),
    globalWebhookUri: z.string(),
    tenantIdFormula: z.any().optional(),
    userIdFormula: z.any().optional(),
});
const AppEventTypeEditableProperties = BaseMembraneInterfaceEditableProperties.extend({
    schema: DataSchema.optional(),
    example: z.any().optional(),
    tenantIdFormula: z.any().optional(),
    userIdFormula: z.any().optional(),
    subscribeRequest: HttpRequestSpec.optional(),
});
const AppEventTypeExportProperties = BaseExportProperties.extend({});

const BaseAppEventSubscription = BaseMembraneInterface.extend({
    ...TenantLayerElement.shape,
    revision: z.string(),
    appEventTypeId: z.string(),
    instanceKey: z.string().optional(),
    isSubscribed: z.boolean(),
    schema: DataSchema.optional(),
    webhookUri: z.string(),
    subscriptionRequest: HttpRequestSpec,
    subscriptionResponse: z.any().optional(),
    archivedAt: z.string().optional(),
});

const AppDataSchemaSpecificProperties = z.object({
    instanceKey: z.string().optional(),
    appDataSchemaId: z.string().optional(),
    schema: z.any().optional(),
    code: z.string().optional(),
});
const AppDataSchemaEditableProperties = BaseMembraneInterfaceEditableProperties.extend(AppDataSchemaSpecificProperties.shape);
const AppDataSchemaExportProperties = BaseExportProperties.extend(AppDataSchemaSpecificProperties.shape).omit({
    appDataSchemaId: true,
});
const AppDataSchemaReadOnlyProperties = BaseMembraneInterfaceReadOnlyProperties.extend({
    ...TenantLayerElement.partial().shape,
    appDataSchemaRevision: z.string().optional(),
    archivedAt: z.string().optional(),
    isCustomized: z.boolean().optional(),
    error: ErrorDataSchema.optional(),
});
const BaseAppDataSchema = BaseMembraneInterface.merge(AppDataSchemaEditableProperties).merge(AppDataSchemaReadOnlyProperties);
const BaseAppDataSchemaInstance = BaseAppDataSchema;

var ExternalEventSubscriptionStatus;
(function (ExternalEventSubscriptionStatus) {
    ExternalEventSubscriptionStatus["Subscribed"] = "subscribed";
    ExternalEventSubscriptionStatus["Unsubscribed"] = "unsubscribed";
    ExternalEventSubscriptionStatus["Error"] = "error";
})(ExternalEventSubscriptionStatus || (ExternalEventSubscriptionStatus = {}));
var ExternalEventSubscriptionType;
(function (ExternalEventSubscriptionType) {
    ExternalEventSubscriptionType["DataRecordCreated"] = "data-record-created";
    ExternalEventSubscriptionType["DataRecordUpdated"] = "data-record-updated";
    ExternalEventSubscriptionType["DataRecordDeleted"] = "data-record-deleted";
    ExternalEventSubscriptionType["ConnectorEvent"] = "connector-event";
})(ExternalEventSubscriptionType || (ExternalEventSubscriptionType = {}));
const ExternalEventSubscriptionConfig = z.object({
    type: z.enum(ExternalEventSubscriptionType),
    dataSource: DataSourceUnitConfig.optional(),
    eventKey: z.string().optional(),
    eventParameters: z.any().optional(),
});
const BaseExternalEventSubscription = TenantLayerElement.extend({
    id: z.string(),
    name: z.string().optional(),
    connectionId: z.string(),
    integrationId: z.string(),
    externalEventTypeId: z.string().optional(),
    config: ExternalEventSubscriptionConfig.optional(),
    status: z.enum(ExternalEventSubscriptionStatus).optional(),
    error: ErrorDataSchema.optional(),
    isRealTime: z.boolean().optional(),
    requiresPull: z.boolean().optional(),
    requiresFullSync: z.boolean().optional(),
    createdAt: z.string().optional(),
    archivedAt: z.string().optional(),
    isDeactivated: z.boolean().optional(),
    stateData: z.any().optional(),
    nextPullEventsTimestamp: z.number().optional(),
    consecutiveEmptyPulls: z.number().optional(),
    effectivePullIntervalSeconds: z.number().optional(),
    pullUpdatesIntervalSeconds: z.number().optional(),
    fullSyncIntervalSeconds: z.number().optional(),
    nextRefreshTimestamp: z.number().optional(),
    globalWebhookKey: z.string().optional(),
    globalWebhookEventSelector: z.string().optional(),
    incomingWebhookUri: z.string().optional(),
    webhookUri: z.string().optional(),
});

var ExternalEventLogStatus;
(function (ExternalEventLogStatus) {
    ExternalEventLogStatus["CREATED"] = "created";
    ExternalEventLogStatus["ERROR"] = "error";
    ExternalEventLogStatus["SUCCESS"] = "success";
    ExternalEventLogStatus["SKIPPED"] = "skipped";
})(ExternalEventLogStatus || (ExternalEventLogStatus = {}));
var ExternalEventLogFlowTriggerSkipReasonCode;
(function (ExternalEventLogFlowTriggerSkipReasonCode) {
    ExternalEventLogFlowTriggerSkipReasonCode["AllDependentFlowInstancesDisabled"] = "all-dependent-flow-instances-disabled";
    ExternalEventLogFlowTriggerSkipReasonCode["DependentFlowInstancesNotLaunchable"] = "dependent-flow-instances-not-launchable";
})(ExternalEventLogFlowTriggerSkipReasonCode || (ExternalEventLogFlowTriggerSkipReasonCode = {}));
const ExternalEventLogFlowTriggerSkipReason = z.object({
    code: z.enum(ExternalEventLogFlowTriggerSkipReasonCode),
    message: z.string(),
    dependentFlowInstanceIds: z.array(z.string()),
});
const BaseExternalEventLogRecord = ActivityLogRecord.extend({
    name: z.string().optional(),
    externalEventSubscriptionId: z.string(),
    integrationId: z.string(),
    connectionId: z.string(),
    externalEventTypeId: z.string().optional(),
    payload: z.any().optional(),
    launchedFlowRunIds: z.array(z.string()).optional(),
    status: z.enum(ExternalEventLogStatus),
    error: ErrorDataSchema.optional(),
    flowTriggerSkipReason: ExternalEventLogFlowTriggerSkipReason.optional(),
    createdAt: z.string(),
});

var ExternalEventPullStatus;
(function (ExternalEventPullStatus) {
    ExternalEventPullStatus["RUNNING"] = "running";
    ExternalEventPullStatus["SUCCESS"] = "success";
    ExternalEventPullStatus["ERROR"] = "error";
    ExternalEventPullStatus["QUEUED"] = "queued";
})(ExternalEventPullStatus || (ExternalEventPullStatus = {}));
const BaseExternalEventPull = ActivityLogRecord.extend({
    name: z.string().optional(),
    externalEventSubscriptionId: z.string(),
    integrationId: z.string(),
    connectionId: z.string(),
    startDatetime: z.string(),
    endDatetime: z.string(),
    isFullScan: z.boolean().optional(),
    status: z.enum(ExternalEventPullStatus),
    collectedEventIds: z.array(z.string()),
    telemetryUri: z
        .string()
        .optional()
        .describe('Storage key of the per-pull diagnostic telemetry artifact (what this pull observed and why it did or did not emit events). Read via GET /external-event-pulls/{id}/telemetry.'),
    error: ErrorDataSchema.optional(),
});
const ExternalEventPullTelemetry = z.object({
    pullId: z.string(),
    subscriptionId: z.string(),
    connectionId: z.string().optional(),
    strategy: z.enum(['full-scan', 'pull-latest-records', 'custom-pull', 'skipped', 'unknown']),
    eventType: z.string().optional(),
    collectionKey: z.string().optional(),
    startedAt: z.string(),
    finishedAt: z.string(),
    emittedEventCount: z.number(),
    emittedEvents: z.array(z.object({ type: z.string(), recordId: z.string().optional() })),
    notes: z.array(z.string()),
    fullScan: z
        .object({
        totalRecords: z.number(),
        totalPages: z.number(),
        previousSnapshotId: z.string().optional(),
        previousSnapshotRecordCount: z.number().optional(),
        previousSnapshotMissing: z.boolean(),
        newSnapshotId: z.string().optional(),
        isBaseline: z.boolean(),
    })
        .optional(),
    pullLatest: z
        .object({
        stateBefore: z.object({ id: z.string().optional(), datetime: z.string().optional() }).optional(),
        stateAfter: z.object({ id: z.string().optional(), datetime: z.string().optional() }).optional(),
        pagesScanned: z.number(),
        scannedRecords: z.array(z.object({ id: z.string(), datetime: z.string().optional() })),
        newRecordIds: z.array(z.string()),
        isBaseline: z.boolean(),
    })
        .optional(),
});

var ExternalEventTypeImplementationType;
(function (ExternalEventTypeImplementationType) {
    ExternalEventTypeImplementationType["Webhook"] = "webhook";
})(ExternalEventTypeImplementationType || (ExternalEventTypeImplementationType = {}));
const EXTERNAL_EVENT_TYPE_FUNCTION_KEYS = ['subscribe', 'unsubscribe', 'handle', 'refresh'];
const ExternalEventTypeCommonFields = {
    schema: DataSchema.optional(),
    parametersSchema: DataSchema.optional(),
    example: z.any().optional(),
    type: z.nativeEnum(ExternalEventTypeImplementationType).optional(),
    subscribe: GenericFunctionDefinition.optional(),
    unsubscribe: GenericFunctionDefinition.optional(),
    handle: GenericFunctionDefinition.optional(),
    refresh: GenericFunctionDefinition.optional(),
};
const ExternalEventTypeEditableProperties = BaseIntegrationLevelMembraneInterfaceEditableProperties.extend({
    ...ExternalEventTypeCommonFields,
});
const ExternalEventTypeExportProperties = BaseIntegrationLevelMembraneInterfaceExportProperties.extend({
    ...ExternalEventTypeCommonFields,
});
const BaseExternalEventType = BaseIntegrationLevelMembraneInterface.extend({
    ...ExternalEventTypeCommonFields,
});

var ActionRunLogStatus;
(function (ActionRunLogStatus) {
    ActionRunLogStatus["SUCCESS"] = "success";
    ActionRunLogStatus["ERROR"] = "error";
})(ActionRunLogStatus || (ActionRunLogStatus = {}));
const BaseActionRunLogRecord = ActivityLogRecord.extend({
    name: z.string(),
    universalActionId: z.string().optional(),
    integrationActionId: z.string(),
    connectionActionId: z.string().optional(),
    actionId: z.string().optional().describe('[INTERNAL] Deprecated: Use integrationActionId instead'),
    actionInstanceId: z.string().optional().describe('[INTERNAL] Deprecated: Use connectionActionId instead'),
    integrationId: z.string(),
    connectionId: z
        .string()
        .describe('[DEPRECATED] Deprecated: Read from action.connectionId or action.connectionKey instead'),
    input: z.any().optional().describe('[DEPRECATED] Deprecated: Read from action.input instead'),
    output: z.any().optional(),
    status: z.enum(ActionRunLogStatus),
    createdAt: z.string(),
    completedAt: z.string().optional(),
    error: ErrorDataSchema.optional(),
    duration: z.number().optional(),
    meta: z.record(z.string(), z.any()).optional(),
    action: ActionSnapshot.optional().describe('Complete /act-body snapshot of what ran — sufficient to replay the run by POSTing back to /act.'),
});

const ConnectedProductType = z.enum(['ai-agent', 'app']);
const ConnectedProductAudience = z.enum(['personal', 'customers']);
const AiAgentAudience = ConnectedProductAudience;
const AiAgentType = z.enum([
    'claude-code',
    'codex',
    'openclaw',
    'cursor',
    'copilot',
    'windsurf',
    'claude',
    'chatgpt',
    'custom',
]);
const AiAgentParameters = z
    .object({
    agentType: z.string().optional(),
})
    .passthrough();
const BaseConnectedProduct = z.object({
    id: z.string(),
    name: z.string(),
    key: z.string(),
    type: ConnectedProductType,
    audience: ConnectedProductAudience.optional(),
    parameters: AiAgentParameters.optional(),
    isConnected: z.boolean().optional(),
    lastApiRequestDate: z.string().optional(),
    createdAt: z.string().optional(),
    archivedAt: z.string().optional(),
});

const BaseConnectionAttemptLog = ActivityLogRecord.extend({
    connectorId: z.string().optional(),
    connectorVersion: z.string().optional(),
    authOptionKey: z.string().optional(),
    connectionRequestId: z.string().optional(),
    status: z.enum(['success', 'error']),
    error: ErrorDataSchema.optional(),
    createdAt: z.string(),
});

const ActApiDispatch = z.object({
    method: z.enum(HttpRequestMethod).describe('HTTP method to use.'),
    path: z.string().optional().describe("Path appended to the connection's base URL, e.g. `/v2/users/me`. Required."),
    body: z.any().optional().describe('Request body. For JSON APIs, pass a plain object.'),
    headers: z.record(z.string(), z.string()).optional().describe('Additional HTTP headers.'),
    query: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
        .optional()
        .describe('Query-string parameters.'),
    url: z.string().optional().describe('[INTERNAL] Alias for `path`. Normalized to `path` on the server.'),
    data: z.any().optional().describe('[INTERNAL] Alias for `body`. Normalized to `body` on the server.'),
});
const ActRequest = z
    .object({
    id: z.string().optional().describe('ID of the action to run.'),
    key: z
        .string()
        .optional()
        .describe('Key of the action to run. If the same key exists across multiple integrations, also pass `integrationId`, `integrationKey`, or `connectionId` so we know which one you mean.'),
    api: ActApiDispatch.optional().describe("Inline HTTP request spec, sent through the resolved connection's auth layer and base URL. Pair with `connectionId`, `connectionKey`, `integrationId`, or `integrationKey` to pick the connection."),
    code: z
        .string()
        .optional()
        .describe('Inline JavaScript executed in a sandbox. The module must export a function that receives `{ input, membrane, ... }`. Its return value becomes the action output.'),
    integrationId: z
        .string()
        .optional()
        .describe("ID of the integration to run the action on. When `key` is provided, looks up the action within this integration. When no connection is specified, runs against this integration's default connection."),
    integrationKey: z
        .string()
        .optional()
        .describe("Key of the integration to run the action on. When `key` is provided, looks up the action within this integration. When no connection is specified, runs against this integration's default connection."),
    connectionId: z
        .string()
        .optional()
        .describe('ID of the connection to run the action against. If a connection-level version of the action exists on this connection, that version is used; otherwise the integration-level or universal action is used.'),
    connectionKey: z
        .string()
        .optional()
        .describe('Key of the connection to run the action against. Use when you have a stable key for the connection instead of an ID.'),
    input: z.any().optional().describe('Input passed to the action, matching its input schema.'),
    meta: z.record(z.string(), z.any()).optional().describe('Arbitrary metadata stored on the action run log.'),
})
    .strict();

const ActionApiResponse = BaseAction.extend({
    integration: BaseIntegration.optional(),
    user: BaseCustomer.optional(),
    parent: BaseAction.optional(),
    appliedToIntegrations: AppliedToIntegrations(BaseAction).optional(),
    defaultOutputSchema: DataSchema.optional(),
    transformedOutputSchema: DataSchema.optional(),
    outputSchema: DataSchema.optional(),
    dependencies: z.array(z.any()).optional(),
});
const FindActionsQuery = FindIntegrationLevelMembraneInterfaceQuery.extend({
    isUniversal: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Is Universal',
        isFlag: true,
    }),
    externalAppId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'External App',
        referenceElementType: WorkspaceElementType.App,
    }),
    layer: MembraneElementLayer.optional(),
    parentId: z
        .string()
        .optional()
        .meta({ hidden: true }),
    universalParentId: z
        .string()
        .optional()
        .meta({ hidden: true }),
    intent: z
        .string()
        .max(200)
        .optional()
        .meta({ hidden: true }),
});
const CreateActionRequest = ActionEditableProperties.merge(IntentProperties);
const UpdateActionRequest = CreateActionRequest.partial();
const CreateActionInstanceRequest = CreateActionRequest;
const UpdateActionInstanceRequest = UpdateActionRequest;
const RunActionRequest = z.object({
    input: z.any().optional().describe('Input data for the action, matching the action inputSchema'),
    meta: z.record(z.string(), z.any()).optional(),
});
const ActionRunResponse = z.object({
    output: z.any().optional(),
    actionRunId: z.string().optional(),
});
const ActionInstanceApiResponse = ActionApiResponse;
const ListActionInstancesForConnectionQuery = FindActionsQuery;
const FindActionInstancesQuery = FindActionsQuery;

const ActionRunLogRecordApiResponse = BaseActionRunLogRecord.extend({
    actionInstance: BaseAction.optional(),
    integration: BaseIntegration.optional(),
    connection: BaseConnection.optional(),
    user: BaseCustomer.optional(),
});
const FindActionRunLogsQuery = PaginationQuery.extend({
    actionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Action',
        referenceElementType: WorkspaceElementType.Action,
        useWorkspaceSearch: true,
    }),
    universalActionId: z.string().optional().meta({ hidden: true }),
    integrationActionId: z.string().optional().meta({ hidden: true }),
    connectionActionId: z.string().optional().meta({ hidden: true }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    status: z
        .enum(ActionRunLogStatus)
        .optional()
        .meta({
        filterTitle: 'Status',
    }),
    externalAppId: z.string().optional().meta({
        hidden: true,
    }),
    from: z
        .string()
        .optional()
        .meta({
        filterTitle: 'From',
        isDatetime: true,
    }),
    to: z
        .string()
        .optional()
        .meta({
        filterTitle: 'To',
        isDatetime: true,
    }),
});
const FindActionRunLogsResponse = createPaginationResponseSchema(ActionRunLogRecordApiResponse);

const FindConnectionsQuery = PaginationQuery.merge(SearchQuery)
    .merge(IncludeArchivedQuery)
    .extend({
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    integrationId: z
        .string()
        .optional()
        .describe('Filter by integration ID')
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    connectorId: z
        .string()
        .optional()
        .describe('Filter by connector ID')
        .meta({
        filterTitle: 'Connector',
    }),
    externalAppId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'External App',
        referenceElementType: WorkspaceElementType.App,
    }),
    connected: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Connected',
        isFlag: true,
    }),
    disconnected: zodBooleanCoercion()
        .optional()
        .describe('[DEPRECATED] Use `connected` instead.')
        .meta({
        filterTitle: 'Disconnected',
        isFlag: true,
        hidden: true,
    }),
    isTest: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Is Test',
        isFlag: true,
    }),
    userId: z
        .string()
        .optional()
        .meta({ hidden: true }),
    connectorVersion: z
        .string()
        .optional()
        .meta({ hidden: true }),
    integrationKey: z
        .string()
        .optional()
        .meta({ hidden: true }),
    includeSecrets: zodBooleanCoercion()
        .optional()
        .meta({ hidden: true }),
});
const EnsureConnectionRequest = z.object({
    appUrl: z
        .string()
        .min(1)
        .max(2000)
        .refine((v) => {
        try {
            const url = /^https?:\/\//i.test(v) ? v : `https://${v}`;
            const { hostname } = new URL(url);
            if (!hostname || !hostname.includes('.'))
                return false;
            const stripped = hostname.replace(/^www\./i, '');
            if (/^(\d{1,3}\.){3}\d{1,3}$/.test(stripped))
                return false;
            return true;
        }
        catch (_a) {
            return false;
        }
    }, { message: 'Must be a valid URL or domain (e.g. "https://slack.com" or "slack.com")' })
        .describe('URL or domain of the app to connect to (e.g. "https://slack.com" or "slack.com"). Will be normalized to a domain.'),
    name: z
        .string()
        .optional()
        .describe('Custom name for the connection. Only used when a new connection is created; ignored if an existing one is returned.'),
});
const CreateConnectionRequest = ConnectionEditableProperties.merge(IntentProperties);
const UpdateConnectionRequest = ConnectionEditableProperties.merge(IntentProperties);
const ConnectionExportProperties = ConnectionEditableProperties.omit({
    credentials: true,
    connectorParameters: true,
    input: true,
    integrationId: true,
    integrationKey: true,
    connectorId: true,
    connectorKey: true,
    connectorVersion: true,
    externalAppId: true,
});
const ConnectionTestResponse = z.object({
    success: z.boolean(),
});
const ConnectionProxyRequest = z.object({
    path: z.string(),
    method: z.string().toUpperCase().pipe(z.enum(HttpRequestMethod)),
    pathParameters: z.record(z.string(), z.string()).optional(),
    data: z.any().optional(),
    query: z.record(z.string(), z.string()).optional(),
    headers: z.record(z.string(), z.string()).optional(),
});
const ConnectionOperationRunInput = z.object({
    input: z.any().optional(),
});
const ConnectionOperationRunResponse = z.object({
    executionLogs: z.array(z.any()).optional(),
    output: z.any().optional(),
});
class FindConnectionsResponse extends PaginationResponse {
}
const ConnectionSelector = z.object({
    id: z.string().optional(),
    integrationKey: z.string().optional(),
    integrationId: z.string().optional(),
    connectionId: z.string().optional(),
    connectionKey: z.string().optional(),
});
const ConnectionApiResponse = BaseConnection.extend({
    user: BaseCustomer.optional(),
    integration: BaseIntegration.optional(),
    connectorParameters: z.unknown().optional(),
    input: z.unknown().optional(),
});
const ConnectionApiResponseWithSecrets = ConnectionApiResponse.extend({
    credentials: z.unknown().optional(),
});
const ConnectionMessagePayload = z.discriminatedUnion('type', [
    z.object({
        source: z.string(),
        requestId: z.string(),
        type: z.literal('newConnectionCreated'),
        connection: ConnectionApiResponse,
    }),
    z.object({
        source: z.string(),
        requestId: z.string(),
        type: z.literal('newConnectionFailure'),
        error: z.string(),
        errorData: ErrorDataSchema.optional(),
        connectionId: z.string().optional(),
    }),
]);
const ConnectionEntityMessagePayload = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('connected'),
        connectionId: z.string(),
        timestamp: z.string(),
        message: z.string().optional(),
    }),
    z.object({
        type: z.literal('connectionPatched'),
        connectionId: z.string(),
    }),
]);
const GenerateOptionsRequest = z.object({
    integrationId: z.string().optional(),
    integrationKey: z.string().optional(),
    connectorId: z.string().optional(),
    connectorKey: z.string().optional(),
    connectionId: z.string().optional(),
    connectorParameters: z.any().optional(),
    connectorVersion: z.string().optional(),
});
const ConnectPayload = z.object({
    type: ConnectionType.optional(),
    requestId: z.string().optional(),
    integrationKey: z.string().optional(),
    integrationId: z.string().optional(),
    connectorId: z.string().optional(),
    connectorKey: z.string().optional(),
    connectorVersion: z.string().optional(),
    externalAppId: z.string().optional(),
    externalAppKey: z.string().optional(),
    connectionId: z.string().optional(),
    connectionKey: z.string().min(1).max(100).optional(),
    name: z.string().optional(),
    input: z.record(z.string(), z.unknown()).optional(),
    connectionParameters: z.any().optional().describe('[INTERNAL] Deprecated: Use input instead'),
    connectorParameters: z.record(z.string(), z.unknown()).optional(),
    authOptionKey: z.string().optional(),
    allowMultipleConnections: z.boolean().optional(),
    redirectUri: z.string().optional(),
    customState: z.string().optional(),
    connectionRequestId: z.string().optional(),
    intent: z.string().optional(),
});
const ConnectUIOptions = ConnectPayload.omit({ input: true, redirectUri: true }).extend({
    theme: z.enum(['light', 'dark', 'auto']).optional(),
});

const ListPublicConnectorsQuery = PaginationQuery.merge(IncludeArchivedQuery).extend({
    search: z.string().optional(),
});
const ListConnectorsQuery = z.object({
    limit: z.coerce.number().optional(),
    cursor: z.string().optional(),
    workspaceId: z.string().optional().describe('[INTERNAL] Legacy field for backward compatibility'),
    search: z.string().optional(),
    appUuid: z.string().optional(),
    externalAppId: z.string().optional(),
    includeArchived: zodBooleanCoercion().optional(),
});

const DataSourceApiResponse = BaseDataSource.extend({
    integration: BaseIntegration.optional(),
    appliedToIntegrations: AppliedToIntegrations(BaseDataSource).optional(),
    user: BaseCustomer.optional(),
    connection: BaseConnection.optional(),
    dataSource: BaseDataSource.optional(),
    path: z.string().optional(),
    defaultPath: z.string().optional(),
});
const DataSourceInstanceApiResponse = DataSourceApiResponse;
const FindDataSourcesQuery = FindIntegrationLevelMembraneInterfaceQuery.omit({ parentId: true }).extend({
    dataSourceId: z.string().optional(),
    universalDataSourceId: z.string().optional(),
    externalAppId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'External App',
        referenceElementType: WorkspaceElementType.App,
    }),
});
const FindDataSourceInstancesQuery = FindDataSourcesQuery;
const CreateDataSourceRequest = DataSourceEditableProperties;
const UpdateDataSourceRequest = CreateDataSourceRequest.extend({}).partial();
const CreateDataSourceInstanceRequest = z.object({
    dataSourceId: z.string(),
    connectionId: z.string(),
    path: z.string().optional(),
});
const UpdateDataSourceInstanceRequest = z.object({
    path: z.string().optional(),
    collectionKey: z.string().optional(),
    collectionParameters: z.any().optional(),
    pullUpdatesIntervalSeconds: z.number().optional(),
    fullSyncIntervalSeconds: z.number().optional(),
    subscribedTo: z
        .object({
        created: z.boolean().optional(),
        updated: z.boolean().optional(),
        deleted: z.boolean().optional(),
    })
        .optional(),
});
const FindDataSourceEventsQuery = z
    .object({
    userId: z.string().optional(),
    udm: z.string().optional(),
    dataSourceInstanceId: z.string().optional(),
    startDatetime: z.string().optional(),
    endDatetime: z.string().optional(),
})
    .merge(PaginationQuery);
const FindDataSourceSyncsQuery = z
    .object({
    userId: z.string().optional(),
    dataSourceId: z.string().optional(),
    dataSourceInstanceId: z.string().optional(),
    integrationId: z.string().optional(),
    connectionId: z.string().optional(),
    startedAfter: z.string().optional(),
})
    .merge(PaginationQuery);
const ListDataSourceInstancesForConnectionQuery = z
    .object({
    dataSourceId: z.string().optional(),
})
    .merge(PaginationQuery);
const FindDataSourceInstanceSyncsQuery = ListDataSourceInstancesForConnectionQuery.extend({
    userId: z.string().optional(),
    connectionId: z.string().optional(),
    integrationId: z.string().optional(),
    dataSourceInstanceId: z.string().optional(),
});

const ListExternalAppsQuery = PaginationQuery.merge(IncludeArchivedQuery).extend({
    search: z.string().optional(),
    category: z
        .string()
        .optional()
        .meta({ hidden: true }),
    isConnected: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Is Connected',
        isFlag: true,
    }),
});
const CreateExternalAppRequest = z.object({
    key: z.string().min(1).describe('Machine key (e.g., "attio"). Unique per workspace.'),
    name: z.string().min(1).describe('Human-readable app name (e.g., "Attio").'),
    appUri: z.url().optional().describe('App\'s main URL (e.g., "https://attio.com").'),
    logoUri: z.url().optional(),
    categories: z.array(z.string()).optional().describe('[INTERNAL] Categorization tags used by the Universe catalog.'),
    defaultConnectorId: z
        .string()
        .optional()
        .describe('[INTERNAL] Connector ID surfaced as the default for this app in the Universe catalog.'),
    basePackageId: z.string().optional().describe('[INTERNAL] Base package ID this app is derived from.'),
    hasFreeTestAccount: z.boolean().nullable().optional().describe('[INTERNAL] Whether the app has a free test account.'),
    freeTestAccountGuide: z.string().optional().describe('[INTERNAL] Guide for obtaining a free test account.'),
    freeTestAccountGuideVerifiedAt: z.iso
        .datetime()
        .nullable()
        .optional()
        .describe('[INTERNAL] When the free test account guide was last verified.'),
    tenantId: z.string().optional(),
});
const UpdateExternalAppRequest = CreateExternalAppRequest.partial();

const ExternalEvent = BaseExternalEvent.extend({
    appliedToIntegrations: AppliedToIntegrations(BaseExternalEvent).optional(),
});

const FlowApiResponse = BaseFlow.extend({
    integration: BaseIntegration.optional(),
    appliedToIntegrations: AppliedToIntegrations(BaseFlow).optional(),
    user: BaseCustomer.optional(),
    connection: BaseConnection.optional(),
    flow: BaseFlow.optional(),
});
const FlowInstanceApiResponse = FlowApiResponse;
const FindFlowsQuery = FindIntegrationLevelMembraneInterfaceQuery.omit({ parentId: true }).extend({
    flowId: z.string().optional(),
    universalFlowId: z.string().optional(),
    externalAppId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'External App',
        referenceElementType: WorkspaceElementType.App,
    }),
});
const CreateFlowRequest = FlowEditableProperties;
const UpdateFlowRequest = CreateFlowRequest.extend({}).partial();
const ListFlowInstancesForConnectionQuery = z
    .object({
    flowId: z.string().optional(),
    flowKey: z.string().optional(),
    enabled: z.boolean().optional(),
})
    .merge(CommonInstancesListQuery);
const FindFlowInstancesQuery = ListFlowInstancesForConnectionQuery.merge(CommonIntegrationOrConnectionQuery);
const ResetFlowInstanceOptions = z.object({
    name: z.boolean().optional(),
    nodes: z.record(z.string(), z.boolean()).optional(),
    allNodes: z.boolean().optional(),
});
const RunFlowApiRequest = z.object({
    nodeKey: z.string().optional(),
    input: z.any().optional(),
    meta: z.record(z.string(), z.any()).optional(),
});
const CreateFlowNodeRequest = z.object({
    type: z.string(),
    config: z.any().optional(),
    ui: z.any().optional(),
    links: z.array(FlowNodeLink).optional(),
});

const CreateFlowRunRequest = z.object({
    flowInstanceId: z.string(),
    startNodeKey: z.string().optional(),
    nodeKey: z.string().optional().meta({ deprecated: true }),
    input: z.any().optional(),
    meta: z.record(z.string(), z.any()).optional(),
});
const FindFlowRunsQuery = PaginationQuery.extend({
    flowId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Flow',
        referenceElementType: WorkspaceElementType.Flow,
        useWorkspaceSearch: true,
    }),
    universalFlowId: z.string().optional().meta({ hidden: true }),
    integrationFlowId: z.string().optional().meta({ hidden: true }),
    connectionFlowId: z.string().optional().meta({ hidden: true }),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    state: z
        .enum(FlowRunState)
        .optional()
        .meta({
        filterTitle: 'Status',
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    externalAppId: z.string().optional().meta({
        hidden: true,
    }),
    from: z
        .string()
        .optional()
        .meta({
        filterTitle: 'From',
        isDatetime: true,
    }),
    to: z
        .string()
        .optional()
        .meta({
        filterTitle: 'To',
        isDatetime: true,
    }),
});
const FlowRunLaunchedByApi = z.object({
    type: z.enum(FlowRunLaunchedByTrigger),
    ids: z.array(z.string()).optional(),
});
const FlowRunApiResponse = BaseFlowRun.extend({
    flowInstance: BaseFlow.optional(),
    connection: BaseConnection.optional(),
    integration: BaseIntegration.optional(),
    user: BaseCustomer.optional(),
});
const FindFlowRunsResponse = z.object({
    items: z.array(FlowRunApiResponse),
    cursor: z.string().optional(),
});

const CreateIntegrationRequest = IntegrationEditableProperties.merge(IntentProperties);
const UpdateIntegrationRequest = IntegrationEditableProperties.partial();
const FindIntegrationsQuery = z
    .object({
    appUuid: z.string().optional(),
    connectors: z.coerce
        .number()
        .optional()
        .describe('Override auto-generate integrations from connectors: 1 to enable, 0 to disable'),
    externalApps: z.coerce
        .number()
        .optional()
        .describe('Override auto-generate integrations from external apps: 1 to enable, 0 to disable'),
    websearch: z.coerce
        .number()
        .optional()
        .describe('Override auto-generate integrations from web search: 1 to enable, 0 to disable'),
    all: z.coerce
        .number()
        .optional()
        .describe('Enable all auto-generation sources (connectors, external apps, web search) regardless of workspace settings or per-source flags'),
})
    .merge(PaginationQuery)
    .merge(SearchQuery)
    .merge(IncludeArchivedQuery);
const IntegrationAuthUi = z.object({
    schema: z.any().optional(),
    helpUri: z.string().optional(),
});
const IntegrationAuthOption = z.object({
    key: z.string(),
    type: z.enum(CONNECTOR_AUTH_TYPES),
    title: z.string().optional(),
    description: z.string().optional(),
    inputSchema: DataSchema.optional(),
    ui: IntegrationAuthUi.optional().describe('[INTERNAL] Deprecated: Use inputSchema instead'),
});
const IntegrationApiResponse = BaseIntegration.extend({
    connection: BaseConnection.optional(),
    spec: z.any().optional(),
    authOptions: z
        .array(IntegrationAuthOption)
        .optional()
        .describe('[INTERNAL] Deprecated: Use POST /connectors/:id/generate-options instead'),
    id: z.string().optional(),
    logoUri: z.string().optional(),
    appUri: z.string().optional(),
    isConnected: z.boolean().optional(),
});
const IntegrationListItem = IntegrationApiResponse;

const PackageElementApi = z.object({
    id: z.string(),
    type: z.enum(WorkspaceElementType),
    element: z.any().optional(),
});
const FindPackagesQuery = FindIntegrationLevelMembraneInterfaceQuery.extend({
    externalAppId: z.string().optional(),
});
const CreatePackageRequest = PackageEditableProperties;
const UpdatePackageRequest = CreatePackageRequest.extend({}).partial();
const PackageApiResponse = BasePackage.merge(PackageVersionData).extend({
    appliedToIntegrations: AppliedToIntegrations(BasePackage).optional(),
});
const ListPublicPackagesQuery = PaginationQuery.extend({
    search: z.string().optional(),
});

const ScreenSelector = z.object({
    id: z.string().optional(),
    type: z.enum(ScreenType).optional(),
    key: z.string().optional(),
    autoCreate: zodBooleanCoercion().optional(),
    connectionId: z.string().optional(),
});
const UpdateScreenRequest = z.object({
    name: z.string().optional(),
    meta: z.record(z.string(), z.any()).optional(),
    blocks: z.array(ScreenBlock).optional(),
    key: z.string().optional(),
});
const CreateScreenRequest = UpdateScreenRequest.extend({
    type: z.enum(ScreenType),
});
const FindScreensQuery = PaginationQuery.extend({
    type: z.enum(ScreenType).optional(),
    key: z.string().optional(),
});
const ScreenApiResponse = BaseScreen;

const CheckpointApiResponse = z.object({
    id: z.string(),
    elementType: z.enum(WorkspaceElementType),
    elementId: z.string(),
    checkpointNumber: z.number(),
    snapshot: z.record(z.string(), z.unknown()),
    createdAt: z.string(),
    asyncRequestId: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
const CheckpointDiffOperationSchema = z.object({
    op: z.enum(['add', 'remove', 'replace']),
    path: z.string().describe('Dot/bracket path, e.g. name, nodes[0].id'),
    value: z.unknown().optional(),
    oldValue: z.unknown().optional(),
});
const CheckpointDiffResponseSchema = z.array(CheckpointDiffOperationSchema);

const CreateCustomerRequest = z.object({
    name: z.string(),
    internalId: z.string().optional(),
    fields: z.any().optional(),
    credentials: z.any().optional(),
    aiCreditsRolling30DayLimit: z.number().min(0).nullable().optional(),
});
const UpdateCustomerRequest = z.object({
    name: z.string().optional(),
    internalId: z.string().optional(),
    fields: z.any().optional(),
    credentials: z.any().optional(),
    aiCreditsRolling30DayLimit: z.number().min(0).nullable().optional(),
});
const FindCustomersQuery = z
    .object({
    isTest: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Is Test',
        isFlag: true,
    }),
    isActive: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Is Active',
        isFlag: true,
    }),
})
    .merge(PaginationQuery)
    .merge(SearchQuery)
    .merge(IncludeArchivedQuery);
const CustomerSelector = z.object({ id: z.string() });
const CustomerApiResponse = BaseCustomer;
const TenantSelfResponse = BaseCustomer.extend({
    workspaceKey: z.string().optional(),
});

const FieldMappingApiResponse = BaseFieldMapping.extend({
    integration: BaseIntegration.optional(),
    appliedToIntegrations: AppliedToIntegrations(BaseFieldMapping).optional(),
    user: BaseCustomer.optional(),
    connection: BaseConnection.optional(),
    fieldMapping: BaseFieldMapping.optional(),
});
const FieldMappingInstanceApiResponse = FieldMappingApiResponse;
const FindFieldMappingsQuery = FindIntegrationLevelMembraneInterfaceQuery.omit({ parentId: true }).extend({
    fieldMappingId: z.string().optional(),
    universalFieldMappingId: z.string().optional(),
    externalAppId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'External App',
        referenceElementType: WorkspaceElementType.App,
    }),
});
const FindFieldMappingInstancesQuery = FindFieldMappingsQuery;
const CreateFieldMappingRequest = FieldMappingEditableProperties;
const CreateFieldMappingInstanceRequest = CreateFieldMappingRequest;
const UpdateFieldMappingRequest = CreateFieldMappingRequest.extend({}).partial();
const UpdateFieldMappingInstanceRequest = UpdateFieldMappingRequest;
const RunFieldMappingRequest = z.object({
    direction: z.enum(FieldMappingDirection).optional(),
    input: z.any().optional(),
});
const RunFieldMappingResponse = z.object({
    output: z.any().optional(),
});

const DataLinkTableLayer = z.enum(['universal', 'connection']);
const FindDataLinkTablesQuery = z.object({
    ...CommonListElementsQuery.shape,
    layer: DataLinkTableLayer.optional().meta({
        filterTitle: 'Layer',
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    dataLinkTableId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Data Link Table',
        referenceElementType: WorkspaceElementType.DataLinkTable,
    }),
    instanceKey: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Instance Key',
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    integrationKey: z.string().optional().meta({
        hidden: true,
    }),
});
const CreateDataLinkTableRequest = DataLinkTableEditableProperties;
const UpdateDataLinkTableRequest = CreateDataLinkTableRequest.partial();
const DataLinkTableApiResponse = BaseDataLinkTable.extend({
    user: z.any().optional(),
    connection: z.any().optional(),
    integration: z.any().optional(),
    dataLinkTable: z.any().optional(),
});

const FindDataLinkTableInstancesQuery = PaginationQuery.extend({
    id: z.string().optional(),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    dataLinkTableId: z.string().optional(),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    instanceKey: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Instance Key',
    }),
}).merge(IncludeArchivedQuery);
const FindDataLinksInTableQuery = PaginationQuery.extend({
    direction: z.enum(DataLinkDirection).optional(),
    appRecordId: z.string().optional(),
    externalRecordId: z.string().optional(),
});
const FindDataLinksQuery = PaginationQuery.extend({
    dataLinkTableInstanceId: z.string(),
    externalRecordId: z.string().optional(),
    appRecordId: z.string().optional(),
    direction: z.enum(DataLinkDirection).optional(),
});
const FindDataLinksResponse = createPaginationResponseSchema(DataLink);
const FindDataLinkTableInstanceLinksQuery = ConnectionSelector.extend({
    id: z.string().optional(),
    instanceKey: z.string().optional(),
    dataLinkTableId: z.string().optional(),
    dataLinkTableKey: z.string().optional(),
    autoCreate: zodBooleanCoercion().optional(),
    direction: z.enum(DataLinkDirection).optional(),
    appRecordId: z.string().optional(),
    externalRecordId: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(1000).optional(),
    cursor: z.string().optional(),
});
const DataLinkTableInstanceApiResponse = BaseDataLinkTable.extend({
    dataLinkTable: BaseDataLinkTable.optional(),
    connection: BaseConnection.optional(),
    integration: BaseIntegration.optional(),
    user: BaseCustomer.optional(),
});

const FindAppEventTypesQuery = CommonFindElementsQuery.extend({});
const FindAppEventsQuery = PaginationQuery.merge(SearchQuery).merge(IncludeArchivedQuery).extend({
    id: z.string().optional(),
    userId: z.string().optional(),
    appEventTypeId: z.string().optional(),
    appEventSubscriptionId: z.string().optional(),
    startDatetime: zodIsoDatetime().optional(),
    endDatetime: zodIsoDatetime().optional(),
    instanceKey: z.string().optional(),
});
const AppEventTypeApiResponse = BaseAppEventType;

const FindAppEventSubscriptionsQuery = PaginationQuery.merge(IncludeArchivedQuery).extend({
    id: z.string().optional(),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    appEventTypeId: z.string().optional(),
    isSubscribed: zodBooleanCoercion().optional(),
    instanceKey: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Instance Key',
    }),
});
const AppEventSubscriptionApiResponse = BaseAppEventSubscription.extend({
    appEventType: BaseAppEventType.optional(),
    user: BaseCustomer.optional(),
});

const FindAppDataSchemasQuery = CommonListElementsQuery.extend({
    isUniversal: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Is Universal',
    }),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    appDataSchemaId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'App Data Schema',
        referenceElementType: WorkspaceElementType.AppDataSchema,
    }),
    instanceKey: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Instance Key',
    }),
});
const AppDataSchemaApiResponse = BaseAppDataSchema.extend({
    user: z.any().optional(),
    appDataSchema: z.any().optional(),
});

const FindAppDataSchemaInstancesQuery = PaginationQuery.extend({
    id: z.string().optional(),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    appDataSchemaId: z.string().optional(),
    instanceKey: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Instance Key',
    }),
}).merge(IncludeArchivedQuery);
const AppDataSchemaInstanceApiResponse = BaseAppDataSchema.extend({
    user: BaseCustomer.optional(),
    appDataSchema: BaseAppDataSchema.optional(),
});

const ListExternalEventLogRecordsQuery = PaginationQuery.extend({
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    externalEventSubscriptionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Event Subscription',
        referenceElementType: WorkspaceElementType.ExternalEventSubscription,
    }),
    externalEventTypeId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'External Event Type',
        referenceElementType: WorkspaceElementType.ExternalEventType,
    }),
    status: z
        .enum(ExternalEventLogStatus)
        .optional()
        .meta({
        filterTitle: 'Status',
    }),
    externalAppId: z.string().optional().meta({
        hidden: true,
    }),
    from: z
        .string()
        .optional()
        .meta({
        filterTitle: 'From',
        isDatetime: true,
    }),
    to: z
        .string()
        .optional()
        .meta({
        filterTitle: 'To',
        isDatetime: true,
    }),
});
const ExternalEventLogRecordApiResponse = BaseExternalEventLogRecord.extend({
    user: BaseCustomer.optional(),
    externalEventSubscription: BaseExternalEventSubscription.optional(),
    integration: BaseIntegration.optional(),
    connection: BaseConnection.optional(),
});

const ListExternalEventPullsQuery = CommonListElementsQuery.extend({
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    externalEventSubscriptionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Event Subscription',
        referenceElementType: WorkspaceElementType.ExternalEventSubscription,
    }),
    status: z
        .enum(ExternalEventPullStatus)
        .optional()
        .meta({
        filterTitle: 'Status',
    }),
});
const FindExternalEventPullsQuery = PaginationQuery.extend({
    externalEventSubscriptionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Event Subscription',
        referenceElementType: WorkspaceElementType.ExternalEventSubscription,
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    status: z
        .enum(ExternalEventPullStatus)
        .optional()
        .meta({
        filterTitle: 'Status',
    }),
    externalAppId: z.string().optional().meta({
        hidden: true,
    }),
    from: z
        .string()
        .optional()
        .meta({
        filterTitle: 'From',
        isDatetime: true,
    }),
    to: z
        .string()
        .optional()
        .meta({
        filterTitle: 'To',
        isDatetime: true,
    }),
});
const ExternalEventPullApiResponse = BaseExternalEventPull.extend({
    user: BaseCustomer.optional(),
    externalEventSubscription: BaseExternalEventSubscription.optional(),
    integration: BaseIntegration.optional(),
    connection: BaseConnection.optional(),
});

const ListExternalEventSubscriptionsQuery = PaginationQuery.merge(IncludeArchivedQuery).extend({
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    externalAppId: z.string().optional().meta({
        hidden: true,
    }),
    externalEventTypeId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'External Event Type',
        referenceElementType: WorkspaceElementType.ExternalEventType,
    }),
    status: z
        .enum(ExternalEventSubscriptionStatus)
        .optional()
        .meta({
        filterTitle: 'Status',
    }),
});
const CreateExternalEventSubscriptionRequest = z.object({
    connectionId: z.string(),
    externalEventTypeId: z.string(),
});
const ExternalEventSubscriptionApiResponse = BaseExternalEventSubscription.extend({
    user: BaseCustomer.optional(),
    connection: BaseConnection.optional(),
    integration: BaseIntegration.optional(),
});
const PatchExternalEventSubscriptionRequest = z.object({
    webhookUri: z.url().or(z.literal('')).optional(),
});

const CreateExternalEventTypeRequest = ExternalEventTypeEditableProperties.extend({
    name: z.string(),
});
const UpdateExternalEventTypeRequest = ExternalEventTypeEditableProperties;
const FindExternalEventTypesQuery = FindIntegrationLevelMembraneInterfaceQuery.extend({
    isUniversal: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Is Universal',
        isFlag: true,
    }),
    externalAppId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'External App',
        referenceElementType: WorkspaceElementType.App,
    }),
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Tenant',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    parentId: z
        .string()
        .optional()
        .meta({ hidden: true }),
    universalParentId: z
        .string()
        .optional()
        .meta({ hidden: true }),
});
const ExternalEventTypeApiResponse = BaseExternalEventType.extend({
    appliedToIntegrations: AppliedToIntegrations(BaseExternalEventType).optional(),
});

const ListAppEventLogRecordsQuery = CommonListElementsQuery.extend({
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    userId: z.string().optional().meta({
        hidden: true,
    }),
    appEventTypeId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Event Type',
        referenceElementType: WorkspaceElementType.AppEventType,
    }),
    from: z
        .string()
        .optional()
        .meta({
        filterTitle: 'From',
        isDatetime: true,
    }),
    to: z
        .string()
        .optional()
        .meta({
        filterTitle: 'To',
        isDatetime: true,
    }),
});
const AppEventLogRecordApiResponse = z.object({
    id: z.string(),
    name: z.string().optional(),
    userId: z.string(),
    user: BaseCustomer.optional(),
    appEventTypeId: z.string(),
    appEventType: BaseAppEventType.optional(),
    appEventSubscriptionId: z.string(),
    appEventSubscription: BaseAppEventSubscription.optional(),
    event: z.any(),
    datetime: z.string(),
    launchedFlowRunIds: z.array(z.string()),
});

const ListExternalApiLogsQuery = PaginationQuery.extend({
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    isSuccess: zodBooleanCoercion()
        .optional()
        .meta({
        filterTitle: 'Status',
    }),
    externalAppId: z.string().optional().meta({
        hidden: true,
    }),
    from: z
        .string()
        .optional()
        .meta({
        filterTitle: 'From',
        isDatetime: true,
    }),
    to: z
        .string()
        .optional()
        .meta({
        filterTitle: 'To',
        isDatetime: true,
    }),
    sortOrder: z.enum(['asc', 'desc']).optional().meta({
        hidden: true,
    }),
});
const ExternalApiLogApiResponse = z.object({
    id: z.string(),
    workspaceId: z.string(),
    customerId: z.string(),
    integrationId: z.string(),
    connectionId: z.string(),
    externalAppId: z.string().optional(),
    statusCode: z.number(),
    isSuccess: z.boolean(),
    s3Uri: z.string(),
    createdAt: z.string(),
    requestUrl: z.string(),
    duration: z.number(),
    customer: BaseCustomer.optional(),
    connection: BaseConnection.optional(),
    integration: BaseIntegration.optional(),
});

const ListIncomingWebhooksQuery = PaginationQuery.extend({
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    subscriptionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Subscription',
        referenceElementType: WorkspaceElementType.ExternalEventSubscription,
    }),
    status: z
        .enum(IncomingWebhooksState)
        .optional()
        .meta({
        filterTitle: 'Status',
    }),
    externalAppId: z.string().optional().meta({
        hidden: true,
    }),
    from: z
        .string()
        .optional()
        .meta({
        filterTitle: 'From',
        isDatetime: true,
    }),
    to: z
        .string()
        .optional()
        .meta({
        filterTitle: 'To',
        isDatetime: true,
    }),
});
const IncomingWebhookApiResponse = z.object({
    id: z.string(),
    name: z.string().optional(),
    workspaceId: z.string(),
    customerId: z.string(),
    integrationId: z.string(),
    connectionId: z.string(),
    externalAppId: z.string().optional(),
    subscriptionId: z.string(),
    userId: z.string(),
    uuid: z.string(),
    status: z.enum(IncomingWebhooksState),
    collectedEventIds: z.array(z.string()),
    createdAt: z.string(),
    customer: BaseCustomer.optional(),
    connection: BaseConnection.optional(),
    subscription: BaseExternalEventSubscription.optional(),
});

const CreateConnectedProductRequest = z.object({
    name: z.string(),
    key: z.string().optional(),
    type: ConnectedProductType,
    audience: ConnectedProductAudience.optional(),
    parameters: AiAgentParameters.optional(),
});
const UpdateConnectedProductRequest = z.object({
    name: z.string().optional(),
    key: z.string().optional(),
    type: ConnectedProductType.optional(),
    audience: ConnectedProductAudience.optional(),
    parameters: AiAgentParameters.optional(),
});
const FindConnectedProductsQuery = z
    .object({
    type: ConnectedProductType.optional(),
    audience: ConnectedProductAudience.optional(),
})
    .merge(PaginationQuery)
    .merge(SearchQuery)
    .merge(IncludeArchivedQuery);
const ConnectedProductApiResponse = BaseConnectedProduct;

const ConnectionAttemptLogApiResponse = BaseConnectionAttemptLog.extend({
    customer: BaseCustomer.optional(),
    connection: BaseConnection.optional(),
    integration: BaseIntegration.optional(),
});
const FindConnectionAttemptLogsQuery = PaginationQuery.extend({
    tenantId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Customer',
        referenceElementType: WorkspaceElementType.Customer,
    }),
    integrationId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Integration',
        referenceElementType: WorkspaceElementType.Integration,
    }),
    connectionId: z
        .string()
        .optional()
        .meta({
        filterTitle: 'Connection',
        referenceElementType: WorkspaceElementType.Connection,
    }),
    status: z
        .enum(['success', 'error'])
        .optional()
        .meta({
        filterTitle: 'Status',
    }),
    from: z
        .string()
        .optional()
        .meta({
        filterTitle: 'From',
        isDatetime: true,
    }),
    to: z
        .string()
        .optional()
        .meta({
        filterTitle: 'To',
        isDatetime: true,
    }),
});
const FindConnectionAttemptLogsResponse = createPaginationResponseSchema(ConnectionAttemptLogApiResponse);

class DependencyError extends MembraneError {
    constructor(arg, workspaceElementReference) {
        super(arg);
        this.data.type = ErrorType.DEPENDENCY_ERROR;
        this.data.data = { workspaceElementReference };
    }
}

function getFlowNode(flow, nodeKey) {
    var _a;
    return (_a = flow.nodes) === null || _a === void 0 ? void 0 : _a[nodeKey];
}
function getFlowNodeTitle(flow, nodeKey) {
    var _a, _b, _c, _d, _e;
    const node = getFlowNode(flow, nodeKey);
    if (!node) {
        return undefined;
    }
    if (!node.type) {
        return undefined;
    }
    return (_e = (_c = (_b = (_a = node.ui) === null || _a === void 0 ? void 0 : _a.title) !== null && _b !== void 0 ? _b : node.name) !== null && _c !== void 0 ? _c : (_d = FLOW_NODE_SPECS[node.type]) === null || _d === void 0 ? void 0 : _d.name) !== null && _e !== void 0 ? _e : toHeaderCase(nodeKey);
}
function getFlowNodeDescription(flow, nodeKey) {
    var _a;
    const node = getFlowNode(flow, nodeKey);
    return (_a = node === null || node === void 0 ? void 0 : node.ui) === null || _a === void 0 ? void 0 : _a.description;
}
function getFlowNodeSpec(node) {
    if (!node) {
        throw new Error(`getFlowNodeSpec called without a node`);
    }
    if (!node.type) {
        throw new Error(`getFlowNodeSpec called on a node without a type`);
    }
    const spec = FLOW_NODE_SPECS[node.type];
    if (!spec) {
        throw new Error(`Unknow flow node type: ${node.type}`);
    }
    return spec;
}
function getFlowInstanceNodeDependency({ dependencies }, type) {
    return dependencies === null || dependencies === void 0 ? void 0 : dependencies.find((dep) => dep.type === type);
}
function getRootNodeKeys({ nodes }) {
    var _a;
    const allNodeKeys = Object.keys(nodes !== null && nodes !== void 0 ? nodes : {});
    const allLinkKeys = Object.values(nodes !== null && nodes !== void 0 ? nodes : {})
        .flatMap((n) => { var _a, _b; return (_b = (_a = n.links) === null || _a === void 0 ? void 0 : _a.map((l) => l.key)) !== null && _b !== void 0 ? _b : []; })
        .filter(Boolean);
    const allSubFlowRootNodeKeys = [];
    for (const nodeKey of allNodeKeys) {
        const node = nodes === null || nodes === void 0 ? void 0 : nodes[nodeKey];
        if (!node || !node.type)
            continue;
        if ((_a = FLOW_NODE_SPECS[node.type]) === null || _a === void 0 ? void 0 : _a.getSubFlowRootNodeKey) {
            const subFlowRootNodeKey = FLOW_NODE_SPECS[node.type].getSubFlowRootNodeKey(node);
            if (subFlowRootNodeKey) {
                allSubFlowRootNodeKeys.push(subFlowRootNodeKey);
            }
        }
    }
    const allNonRootNodeKeys = allLinkKeys.concat(allSubFlowRootNodeKeys);
    return allNodeKeys.filter((k) => !allNonRootNodeKeys.includes(k));
}
function getChildNodeKeys(flow, nodeKey) {
    const node = getFlowNode(flow, nodeKey);
    const nodeLinks = (node === null || node === void 0 ? void 0 : node.links) || [];
    return nodeLinks.map((nodeLink) => nodeLink.key).filter((k) => { var _a; return !!k && !!((_a = flow.nodes) === null || _a === void 0 ? void 0 : _a[k]); });
}
function getDownstreamNodeKeys(flow, nodeKey) {
    const linkedKeys = getChildNodeKeys(flow, nodeKey);
    return linkedKeys.concat(linkedKeys.map((k) => getChildNodeKeys(flow, k)).flat());
}
function getParentNodeKeys(flow, nodeKey) {
    const linkedNodeKeys = Object.keys(flow.nodes || {}).filter((key) => {
        var _a;
        const node = getFlowNode(flow, key);
        return (_a = node === null || node === void 0 ? void 0 : node.links) === null || _a === void 0 ? void 0 : _a.some((link) => link.key === nodeKey);
    });
    const subFlowParentNodeKeys = Object.keys(flow.nodes || {}).filter((key) => {
        var _a;
        const node = getFlowNode(flow, key);
        return (node === null || node === void 0 ? void 0 : node.type) && ((_a = FLOW_NODE_SPECS[node.type]) === null || _a === void 0 ? void 0 : _a.getSubFlowRootNodeKey)
            ? FLOW_NODE_SPECS[node.type].getSubFlowRootNodeKey(node) === nodeKey
            : false;
    });
    return Array.from(new Set(linkedNodeKeys.concat(subFlowParentNodeKeys)));
}
function getUpstreamNodeKeys(flow, nodeKey) {
    const parentKeys = getParentNodeKeys(flow, nodeKey);
    return parentKeys.concat(parentKeys.map((n) => getUpstreamNodeKeys(flow, n)).flat());
}
function hasCycles(nodes) {
    if (!nodes) {
        return false;
    }
    let hasCycles = false;
    const allVisited = new Set();
    function visitNode(nodeKey, visited) {
        var _a;
        if (!nodeKey)
            return visited;
        if (visited.includes(nodeKey)) {
            hasCycles = true;
            return visited;
        }
        const node = nodes === null || nodes === void 0 ? void 0 : nodes[nodeKey];
        if (!node)
            return visited;
        visited = [...visited, nodeKey];
        allVisited.add(nodeKey);
        if (node.type && ((_a = FLOW_NODE_SPECS[node.type]) === null || _a === void 0 ? void 0 : _a.getSubFlowRootNodeKey)) {
            visited = [...visited, ...visitNode(FLOW_NODE_SPECS[node.type].getSubFlowRootNodeKey(node), visited)];
        }
        if (node && node.links) {
            let visitedSubNodes = [];
            for (const link of node.links) {
                if (link.key) {
                    visitedSubNodes = [...visitedSubNodes, ...visitNode(link.key, visited)];
                }
            }
            visited = [...visited, ...Array.from(new Set(visitedSubNodes))];
        }
        return visited;
    }
    const rootNodeKeys = getRootNodeKeys({ nodes });
    for (const nodeKey of rootNodeKeys) {
        visitNode(nodeKey, []);
    }
    if (allVisited.size !== Object.keys(nodes).length) {
        hasCycles = true;
    }
    return hasCycles;
}

function getSchemaDescription(schema) {
    const json = z.toJSONSchema(schema);
    return json.description;
}

function addUndefinedWriteableProperties(value, schema) {
    if (!(schema instanceof z.ZodObject)) {
        return value;
    }
    const allProperties = Object.keys(schema.shape);
    for (const key of allProperties) {
        if (!(key in value)) {
            value[key] = undefined;
        }
    }
    return value;
}
function lenientParseWithSchema(data, schema) {
    if (data === undefined || data === null) {
        return data;
    }
    const def = schema._def;
    if (!def) {
        return data;
    }
    const typeName = def.typeName || def.type;
    if (typeName === 'ZodOptional' || typeName === 'optional') {
        if (data === undefined)
            return undefined;
        return lenientParseWithSchema(data, def.innerType);
    }
    if (typeName === 'ZodNullable' || typeName === 'nullable') {
        if (data === null)
            return null;
        return lenientParseWithSchema(data, def.innerType);
    }
    if (typeName === 'ZodObject' || typeName === 'object') {
        if (typeof data !== 'object' || Array.isArray(data)) {
            return data;
        }
        const shape = schema.shape;
        const result = {};
        if (shape) {
            for (const key in shape) {
                if (key in data) {
                    result[key] = lenientParseWithSchema(data[key], shape[key]);
                }
            }
        }
        const catchall = def.catchall;
        if (catchall) {
            const catchallDef = catchall._def;
            if ((catchallDef === null || catchallDef === void 0 ? void 0 : catchallDef.typeName) === 'ZodNever' || (catchallDef === null || catchallDef === void 0 ? void 0 : catchallDef.type) === 'never') ;
            else if ((catchallDef === null || catchallDef === void 0 ? void 0 : catchallDef.typeName) === 'ZodAny' || (catchallDef === null || catchallDef === void 0 ? void 0 : catchallDef.type) === 'unknown' || (catchallDef === null || catchallDef === void 0 ? void 0 : catchallDef.type) === 'any') {
                for (const key in data) {
                    if (!(key in result)) {
                        result[key] = data[key];
                    }
                }
            }
            else {
                for (const key in data) {
                    if (shape && !(key in shape)) {
                        result[key] = data[key];
                    }
                }
            }
        }
        return result;
    }
    if (typeName === 'ZodArray' || typeName === 'array') {
        if (!Array.isArray(data)) {
            return data;
        }
        const elementSchema = def.element || def.type;
        if (!elementSchema) {
            return data;
        }
        return data.map((item) => lenientParseWithSchema(item, elementSchema));
    }
    if (typeName === 'ZodRecord' || typeName === 'record') {
        if (typeof data !== 'object' || data === null || Array.isArray(data)) {
            return data;
        }
        const valueSchema = def.valueType;
        if (!valueSchema) {
            return data;
        }
        const result = {};
        for (const key in data) {
            result[key] = lenientParseWithSchema(data[key], valueSchema);
        }
        return result;
    }
    return data;
}

function setEditablePropertiesForWorkspaceElement(currentElement, newEditableFields, exportSchema) {
    const parsedNewFields = lenientParseWithSchema(newEditableFields, exportSchema);
    const normalizedFields = addUndefinedWriteableProperties(parsedNewFields, exportSchema);
    return {
        ...currentElement,
        ...normalizedFields,
    };
}

const REFERENCE_ELEMENT_TYPE_SELF = 'self';
function getFilterFieldMeta(meta) {
    if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
        return meta;
    }
    return undefined;
}

const AI_GATEWAY_SUPPORTED_MODELS = [
    { id: 'anthropic/claude-sonnet-4.6', name: 'Claude Sonnet 4.6' },
    { id: 'anthropic/claude-opus-4.6', name: 'Claude Opus 4.6' },
    { id: 'anthropic/claude-sonnet-4.5', name: 'Claude Sonnet 4.5' },
    { id: 'anthropic/claude-opus-4.5', name: 'Claude Opus 4.5' },
    { id: 'anthropic/claude-haiku-4.5', name: 'Claude Haiku 4.5' },
    { id: 'openai/gpt-5.2', name: 'GPT-5.2' },
    { id: 'openai/gpt-5-nano', name: 'GPT-5 Nano' },
    { id: 'google/gemini-3-pro-preview', name: 'Gemini 3 Pro' },
    { id: 'moonshotai/kimi-k2.5', name: 'Kimi K2.5' },
];
const AI_GATEWAY_SUPPORTED_MODEL_IDS = AI_GATEWAY_SUPPORTED_MODELS.map((model) => model.id);
if (AI_GATEWAY_SUPPORTED_MODEL_IDS.length === 0) {
    throw new Error('AI_GATEWAY_SUPPORTED_MODEL_IDS must not be empty');
}
const AIGatewaySupportedModelIdSchema = z.enum(AI_GATEWAY_SUPPORTED_MODEL_IDS);
const DEFAULT_AI_GATEWAY_MODEL_ID = 'anthropic/claude-sonnet-4.6';
const AI_GATEWAY_PROVIDER_MODELS = Object.fromEntries(AI_GATEWAY_SUPPORTED_MODELS.map((model) => [model.id, { name: model.name }]));
const aiGatewaySupportedModelIdSet = new Set(AI_GATEWAY_SUPPORTED_MODEL_IDS);
function isAIGatewaySupportedModelId(modelId) {
    return aiGatewaySupportedModelIdSet.has(modelId);
}

var AgentSessionStatus;
(function (AgentSessionStatus) {
    AgentSessionStatus["QUEUED"] = "queued";
    AgentSessionStatus["STARTING"] = "starting";
    AgentSessionStatus["RUNNING"] = "running";
    AgentSessionStatus["COMPLETED"] = "completed";
    AgentSessionStatus["FAILED"] = "failed";
    AgentSessionStatus["CANCELLED"] = "cancelled";
})(AgentSessionStatus || (AgentSessionStatus = {}));
var AgentSessionState;
(function (AgentSessionState) {
    AgentSessionState["BUSY"] = "busy";
    AgentSessionState["IDLE"] = "idle";
})(AgentSessionState || (AgentSessionState = {}));
var AgentName;
(function (AgentName) {
    AgentName["MEMBRANE"] = "membrane";
    AgentName["MEMBRANE_CORE"] = "membrane-core";
    AgentName["SELF_INTEGRATING"] = "self-integrating";
    AgentName["CONNECTION_BUILDING"] = "connection-building";
    AgentName["ACTION_BUILDING"] = "action-building";
    AgentName["UNIVERSE"] = "universe";
})(AgentName || (AgentName = {}));
const MEMBRANE_SESSION_ID_HEADER = 'X-Membrane-Session-Id';
const AgentSession = z.object({
    ...TenantLayerElement.partial().shape,
    id: z.string(),
    workspaceId: z.string(),
    orgWorkspaceId: z.string().optional(),
    workspaceElementType: z.enum(WorkspaceElementType).optional(),
    workspaceElementId: z.string().optional(),
    type: z.string(),
    status: z.enum(AgentSessionStatus),
    prompt: z.string(),
    error: ErrorDataSchema.optional(),
    lastActivityAt: z.iso
        .datetime()
        .describe('Last meaningful session progress timestamp used for inactivity timeout checks.'),
    title: z.string().optional(),
    summary: z.string().optional(),
    cost: z.number().optional(),
    state: z.enum(AgentSessionState).default(AgentSessionState.BUSY),
    hasWorker: z.boolean(),
    isExternal: z.boolean().optional(),
    agentName: z.enum(AgentName).optional(),
    agentHarnessId: z.string().optional(),
    agentHarnessSnapshotId: z.string().optional(),
    output: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
const AgentSessionAttachment = z.object({
    title: z.string(),
    data: z.unknown(),
});
const CreateAgentSession = z.object({
    workspaceElementType: z.enum(WorkspaceElementType).optional(),
    workspaceElementId: z.string().min(1).optional(),
    prompt: z.string().min(1),
    testCustomerId: z.string().optional(),
    isExternal: z.boolean().optional(),
    modelId: AIGatewaySupportedModelIdSchema.optional(),
    agentName: z.enum(AgentName).optional(),
    agentHarnessId: z.string().min(1).optional(),
    promptAttachments: z.array(AgentSessionAttachment).optional(),
});
const AgentSessionInputSchema = z.object({
    input: z.string().min(1),
    synthetic: z.boolean().optional(),
    attachments: z.array(AgentSessionAttachment).optional(),
});
const PatchAgentSessionSchema = z.object({
    state: z.enum(AgentSessionState).optional(),
    lastActivityAt: zodDateCoercion()
        .optional()
        .describe('Last meaningful session progress timestamp used for inactivity timeout checks.'),
    title: z.string().optional(),
    summary: z.string().optional(),
    error: ErrorDataSchema.optional(),
    opencodeSessionUuid: z.string().optional(),
    output: z.record(z.string(), z.unknown()).optional(),
    status: z.enum(AgentSessionStatus).optional(),
});

const SYSTEM_FIELDS = ['updatedAt', 'createdAt', 'revision', '__v', '_id', 'id'];
function jsonPointerToDotPath(pointer) {
    if (!pointer || pointer === '/')
        return '';
    const segments = pointer
        .split('/')
        .slice(1)
        .map((seg) => seg.replace(/~1/g, '/').replace(/~0/g, '~'));
    return segments.map((seg, i) => (/^\d+$/.test(seg) ? `[${seg}]` : i === 0 ? seg : `.${seg}`)).join('');
}
function unwrapWrappers(schema) {
    const def = schema._def;
    if (!def)
        return schema;
    const typeName = def.typeName || def.type;
    if (typeName === 'ZodOptional' || typeName === 'ZodNullable' || typeName === 'ZodDefault') {
        return def.innerType ? unwrapWrappers(def.innerType) : schema;
    }
    return schema;
}
function getEditablePathsFromSchema(schema, prefix = '') {
    const result = new Set();
    const unwrapped = unwrapWrappers(schema);
    if (unwrapped instanceof z.ZodObject) {
        const shape = unwrapped.shape;
        for (const key of Object.keys(shape)) {
            const fullPath = prefix ? `${prefix}.${key}` : key;
            result.add(fullPath);
            const subUnwrapped = unwrapWrappers(shape[key]);
            if (subUnwrapped instanceof z.ZodObject) {
                getEditablePathsFromSchema(shape[key], fullPath).forEach((p) => result.add(p));
            }
        }
        return result;
    }
    if (prefix)
        result.add(prefix);
    return result;
}
function getEditablePathsForElementType$1(specs, elementType) {
    const spec = specs[elementType];
    const schema = spec === null || spec === void 0 ? void 0 : spec.editablePropertiesSchema;
    if (!schema)
        return null;
    return getEditablePathsFromSchema(schema);
}
function isPathUserEditable$1(specs, elementType, path) {
    const editablePaths = getEditablePathsForElementType$1(specs, elementType);
    if (editablePaths !== null) {
        return [...editablePaths].some((editablePath) => {
            if (path === editablePath)
                return true;
            if (path.startsWith(editablePath + '.'))
                return true;
            if (path.startsWith(editablePath + '['))
                return true;
            return false;
        });
    }
    const isSystemField = SYSTEM_FIELDS.some((sysField) => path === sysField || path.endsWith(`.${sysField}`));
    return !isSystemField;
}

const WorkspaceElementSpecs = {
    [WorkspaceElementType.Customer]: {
        type: WorkspaceElementType.Customer,
        apiPath: 'customers',
        name: 'Customer',
        namePlural: 'Customers',
        relatedIntegrationLayerElements: [
            WorkspaceElementType.Connection,
            WorkspaceElementType.Action,
            WorkspaceElementType.Flow,
            WorkspaceElementType.ExternalEventSubscription,
            WorkspaceElementType.AppEventSubscription,
            WorkspaceElementType.DataSource,
            WorkspaceElementType.FieldMapping,
            WorkspaceElementType.AppDataSchemaInstance,
            WorkspaceElementType.DataLinkTableInstance,
        ],
        relatedActivityLogElements: [
            WorkspaceElementType.FlowRun,
            WorkspaceElementType.ActionRunLogRecord,
            WorkspaceElementType.ExternalApiLogRecord,
            WorkspaceElementType.IncomingWebhookLogRecord,
            WorkspaceElementType.ExternalEventLogRecord,
            WorkspaceElementType.ExternalEventPull,
            WorkspaceElementType.AppEventLogRecord,
        ],
    },
    [WorkspaceElementType.App]: {
        type: WorkspaceElementType.App,
        apiPath: 'external-apps',
        name: 'App',
        namePlural: 'Apps',
        hasKey: true,
        hasPublicLayer: true,
        remoteRepositories: true,
        relatedIntegrationLayerElements: [
            WorkspaceElementType.Connection,
            WorkspaceElementType.Action,
            WorkspaceElementType.Flow,
            WorkspaceElementType.DataSource,
            WorkspaceElementType.FieldMapping,
        ],
        relatedActivityLogElements: [
            WorkspaceElementType.FlowRun,
            WorkspaceElementType.ActionRunLogRecord,
            WorkspaceElementType.ExternalApiLogRecord,
            WorkspaceElementType.IncomingWebhookLogRecord,
            WorkspaceElementType.ExternalEventLogRecord,
            WorkspaceElementType.ExternalEventPull,
        ],
    },
    [WorkspaceElementType.Integration]: {
        type: WorkspaceElementType.Integration,
        apiPath: 'integrations',
        collectionName: 'integrations',
        name: 'Integration',
        namePlural: 'Integrations',
        editablePropertiesSchema: IntegrationEditableProperties,
        exportPropertiesSchema: IntegrationExportProperties,
        hasKey: true,
        isAgentic: true,
        agentName: AgentName.CONNECTION_BUILDING,
        relatedIntegrationLayerElements: [
            WorkspaceElementType.Connection,
            WorkspaceElementType.Package,
            WorkspaceElementType.Action,
            WorkspaceElementType.Flow,
            WorkspaceElementType.ExternalEventSubscription,
            WorkspaceElementType.AppEventSubscription,
            WorkspaceElementType.DataSource,
            WorkspaceElementType.FieldMapping,
            WorkspaceElementType.AppDataSchemaInstance,
            WorkspaceElementType.DataLinkTableInstance,
        ],
        relatedActivityLogElements: [
            WorkspaceElementType.FlowRun,
            WorkspaceElementType.ActionRunLogRecord,
            WorkspaceElementType.ExternalApiLogRecord,
            WorkspaceElementType.IncomingWebhookLogRecord,
            WorkspaceElementType.ExternalEventLogRecord,
            WorkspaceElementType.ExternalEventPull,
            WorkspaceElementType.AppEventLogRecord,
        ],
    },
    [WorkspaceElementType.Connector]: {
        type: WorkspaceElementType.Connector,
        apiPath: 'connectors',
        name: 'Connector',
        namePlural: 'Connectors',
        exportPropertiesSchema: ConnectorExportProperties,
        hasKey: true,
        hasPublicLayer: true,
        remoteRepositories: true,
    },
    [WorkspaceElementType.Connection]: {
        type: WorkspaceElementType.Connection,
        apiPath: 'connections',
        name: 'Connection',
        namePlural: 'Connections',
        exportPropertiesSchema: ConnectionExportProperties,
        hasKey: false,
        hasUuid: false,
        isAgentic: true,
        agentName: AgentName.CONNECTION_BUILDING,
        statsKey: 'connections',
        relatedIntegrationLayerElements: [
            WorkspaceElementType.Action,
            WorkspaceElementType.Flow,
            WorkspaceElementType.ExternalEventSubscription,
            WorkspaceElementType.AppEventSubscription,
            WorkspaceElementType.DataSource,
            WorkspaceElementType.FieldMapping,
            WorkspaceElementType.AppDataSchemaInstance,
            WorkspaceElementType.DataLinkTableInstance,
        ],
        relatedActivityLogElements: [
            WorkspaceElementType.FlowRun,
            WorkspaceElementType.ActionRunLogRecord,
            WorkspaceElementType.ExternalApiLogRecord,
            WorkspaceElementType.IncomingWebhookLogRecord,
            WorkspaceElementType.ExternalEventLogRecord,
            WorkspaceElementType.ExternalEventPull,
            WorkspaceElementType.AppEventLogRecord,
        ],
    },
    [WorkspaceElementType.Package]: {
        type: WorkspaceElementType.Package,
        apiPath: 'packages',
        collectionName: 'integration-packages',
        name: 'Package',
        namePlural: 'Packages',
        editablePropertiesSchema: PackageEditableProperties,
        exportPropertiesSchema: PackageExportProperties,
        apiResponseSchema: PackageApiResponse,
        findQuerySchema: FindPackagesQuery,
        isMembraneInterface: true,
        isIntegrationLevel: true,
        hasKey: true,
        hasParentChildRelationship: true,
        hasPublicLayer: true,
        isProOnly: true,
        statsKey: 'packages',
    },
    [WorkspaceElementType.ActionInstance]: {
        type: WorkspaceElementType.ActionInstance,
        apiPath: 'action-instances',
        collectionName: 'action-instances',
        name: 'Action Instance',
        namePlural: 'Action Instances',
        parentFieldKey: 'parentId',
        exportPropertiesSchema: ActionExportProperties,
        isMembraneInterface: true,
    },
    [WorkspaceElementType.Action]: {
        type: WorkspaceElementType.Action,
        apiPath: 'actions',
        collectionName: 'actions',
        name: 'Action',
        namePlural: 'Actions',
        parentFieldKey: 'parentId',
        editablePropertiesSchema: ActionEditableProperties,
        backwardCompatibleEditablePropertiesSchema: CreateActionRequest,
        exportPropertiesSchema: ActionExportProperties,
        apiResponseSchema: ActionApiResponse,
        findQuerySchema: FindActionsQuery,
        isMembraneInterface: true,
        isIntegrationLevel: true,
        canBeAddedToPackage: true,
        hasParentChildRelationship: true,
        hasKey: true,
        hasPublicLayer: true,
        remoteRepositories: true,
        isAgentic: true,
        agentName: AgentName.ACTION_BUILDING,
        statsKey: 'actions',
    },
    [WorkspaceElementType.ActionRunLogRecord]: {
        type: WorkspaceElementType.ActionRunLogRecord,
        apiPath: 'action-run-logs',
        name: 'Action Run Log Record',
        namePlural: 'Action Run Log Records',
        statsKey: 'actionRunLogs',
    },
    [WorkspaceElementType.Flow]: {
        type: WorkspaceElementType.Flow,
        apiPath: 'flows',
        collectionName: 'flows',
        name: 'Flow',
        namePlural: 'Flows',
        parentFieldKey: 'universalFlowId',
        editablePropertiesSchema: FlowEditableProperties,
        exportPropertiesSchema: FlowExportProperties,
        apiResponseSchema: FlowApiResponse,
        findQuerySchema: FindFlowsQuery,
        isMembraneInterface: true,
        isIntegrationLevel: true,
        canBeAddedToPackage: true,
        hasParentChildRelationship: true,
        hasKey: true,
        isProOnly: true,
        statsKey: 'flows',
    },
    [WorkspaceElementType.FlowInstance]: {
        type: WorkspaceElementType.FlowInstance,
        apiPath: 'flow-instances',
        collectionName: 'flow-instances',
        createSchema: createFlowInstanceSchema,
        updateSchema: updateFlowInstanceSchema,
        name: 'Flow',
        namePlural: 'Flows',
        parentFieldKey: 'flowId',
        universalParentFieldKey: 'universalFlowId',
        exportPropertiesSchema: FlowExportProperties,
        isMembraneInterface: true,
        isProOnly: true,
    },
    [WorkspaceElementType.FlowRun]: {
        type: WorkspaceElementType.FlowRun,
        apiPath: 'flow-runs',
        name: 'Flow run',
        namePlural: 'Flow runs',
        isProOnly: true,
        statsKey: 'flowRuns',
    },
    [WorkspaceElementType.FieldMapping]: {
        type: WorkspaceElementType.FieldMapping,
        apiPath: 'field-mappings',
        collectionName: 'field-mappings',
        name: 'Field Mapping',
        namePlural: 'Field Mappings',
        parentFieldKey: 'universalFieldMappingId',
        editablePropertiesSchema: FieldMappingEditableProperties,
        exportPropertiesSchema: FieldMappingExportProperties,
        apiResponseSchema: FieldMappingApiResponse,
        findQuerySchema: FindFieldMappingsQuery,
        isMembraneInterface: true,
        isIntegrationLevel: true,
        canBeAddedToPackage: true,
        hasParentChildRelationship: true,
        hasKey: true,
        isProOnly: true,
        statsKey: 'fieldMappings',
    },
    [WorkspaceElementType.FieldMappingInstance]: {
        type: WorkspaceElementType.FieldMappingInstance,
        apiPath: 'field-mapping-instances',
        collectionName: 'field-mapping-instances',
        name: 'Field Mapping Instance',
        namePlural: 'Field Mapping Instances',
        parentFieldKey: 'fieldMappingId',
        universalParentFieldKey: 'universalFieldMappingId',
        exportPropertiesSchema: FieldMappingExportProperties,
        isMembraneInterface: true,
        isProOnly: true,
    },
    [WorkspaceElementType.DataSource]: {
        type: WorkspaceElementType.DataSource,
        apiPath: 'data-sources',
        collectionName: 'data-sources',
        name: 'Data Source',
        namePlural: 'Data Sources',
        parentFieldKey: 'universalDataSourceId',
        editablePropertiesSchema: DataSourceEditableProperties,
        backwardCompatibleEditablePropertiesSchema: BackwardCompatibleDataSourceEditableProperties,
        exportPropertiesSchema: DataSourceExportProperties,
        apiResponseSchema: DataSourceApiResponse,
        findQuerySchema: FindDataSourcesQuery,
        isMembraneInterface: true,
        isIntegrationLevel: true,
        canBeAddedToPackage: true,
        hasParentChildRelationship: true,
        hasKey: true,
        isProOnly: true,
        statsKey: 'dataSources',
    },
    [WorkspaceElementType.DataSourceInstance]: {
        type: WorkspaceElementType.DataSourceInstance,
        apiPath: 'data-source-instances',
        collectionName: 'data-source-instances',
        name: 'Data Source Instance',
        namePlural: 'Data Source Instances',
        parentFieldKey: 'dataSourceId',
        universalParentFieldKey: 'universalDataSourceId',
        exportPropertiesSchema: DataSourceExportProperties,
        isMembraneInterface: true,
        isProOnly: true,
    },
    [WorkspaceElementType.DataLinkTable]: {
        type: WorkspaceElementType.DataLinkTable,
        apiPath: 'data-link-tables',
        collectionName: 'data-link-tables',
        name: 'Data Link Table',
        namePlural: 'Data Link Tables',
        editablePropertiesSchema: DataLinkTableEditableProperties,
        exportPropertiesSchema: DataLinkTableExportProperties,
        apiResponseSchema: DataLinkTableApiResponse,
        findQuerySchema: FindDataLinkTablesQuery,
        isMembraneInterface: true,
        canBeAddedToPackage: true,
        hasKey: true,
        isProOnly: true,
    },
    [WorkspaceElementType.DataLinkTableInstance]: {
        type: WorkspaceElementType.DataLinkTableInstance,
        apiPath: 'data-link-table-instances',
        collectionName: 'data-link-table-instances',
        name: 'Data Link Table Instance',
        namePlural: 'Data Link Table Instances',
        parentFieldKey: 'dataLinkTableId',
        isMembraneInterface: true,
        isProOnly: true,
        statsKey: 'dataLinkTableInstances',
    },
    [WorkspaceElementType.AppEventType]: {
        type: WorkspaceElementType.AppEventType,
        apiPath: 'app-event-types',
        name: 'Internal Event Type',
        namePlural: 'Internal Event Types',
        isMembraneInterface: true,
        exportPropertiesSchema: AppEventTypeExportProperties,
        editablePropertiesSchema: AppEventTypeEditableProperties,
        canBeAddedToPackage: true,
        hasKey: true,
        isProOnly: true,
    },
    [WorkspaceElementType.AppEventSubscription]: {
        type: WorkspaceElementType.AppEventSubscription,
        apiPath: 'app-event-subscriptions',
        collectionName: 'app-event-subscriptions',
        name: 'Internal Event Subscription',
        namePlural: 'Internal Event Subscriptions',
        isMembraneInterface: true,
        isProOnly: true,
        statsKey: 'appEventSubscriptions',
    },
    [WorkspaceElementType.AppEventLogRecord]: {
        type: WorkspaceElementType.AppEventLogRecord,
        apiPath: 'app-events',
        name: 'Internal Event Log Record',
        namePlural: 'Internal Event Log Records',
        isProOnly: true,
        statsKey: 'appEvents',
    },
    [WorkspaceElementType.AppDataSchema]: {
        type: WorkspaceElementType.AppDataSchema,
        apiPath: 'app-data-schemas',
        collectionName: 'app-data-schemas',
        name: 'App Data Schema',
        namePlural: 'App Data Schemas',
        isMembraneInterface: true,
        exportPropertiesSchema: AppDataSchemaExportProperties,
        editablePropertiesSchema: AppDataSchemaEditableProperties,
        canBeAddedToPackage: true,
        hasKey: true,
        isProOnly: true,
    },
    [WorkspaceElementType.AppDataSchemaInstance]: {
        type: WorkspaceElementType.AppDataSchemaInstance,
        apiPath: 'app-data-schema-instances',
        collectionName: 'app-data-schema-instances',
        name: 'App Data Schema Instance',
        namePlural: 'App Data Schema Instances',
        parentFieldKey: 'appDataSchemaId',
        isMembraneInterface: true,
        isProOnly: true,
        statsKey: 'appDataSchemaInstances',
    },
    [WorkspaceElementType.ExternalEventSubscription]: {
        type: WorkspaceElementType.ExternalEventSubscription,
        apiPath: 'external-event-subscriptions',
        collectionName: 'external-event-subscriptions',
        name: 'External Event Subscription',
        namePlural: 'External Event Subscriptions',
        isMembraneInterface: true,
        isProOnly: true,
        statsKey: 'externalEventSubscriptions',
    },
    [WorkspaceElementType.ExternalEventLogRecord]: {
        type: WorkspaceElementType.ExternalEventLogRecord,
        apiPath: 'external-event-log-records',
        name: 'External Event Log Record',
        namePlural: 'External Event Log Records',
        isProOnly: true,
        statsKey: 'externalEvents',
    },
    [WorkspaceElementType.ExternalEventPull]: {
        type: WorkspaceElementType.ExternalEventPull,
        apiPath: 'external-event-pulls',
        name: 'External Event Pull',
        namePlural: 'External Event Pulls',
        isProOnly: true,
        statsKey: 'externalEventPulls',
    },
    [WorkspaceElementType.ExternalEventType]: {
        type: WorkspaceElementType.ExternalEventType,
        apiPath: 'external-event-types',
        collectionName: 'external-event-types',
        name: 'External Event Type',
        namePlural: 'External Event Types',
        isMembraneInterface: true,
        isIntegrationLevel: true,
        hasParentChildRelationship: true,
        parentFieldKey: 'parentId',
        apiResponseSchema: ExternalEventTypeApiResponse,
        findQuerySchema: FindExternalEventTypesQuery,
        editablePropertiesSchema: ExternalEventTypeEditableProperties,
        exportPropertiesSchema: ExternalEventTypeExportProperties,
        hasKey: true,
        hasUuid: true,
        statsKey: 'externalEventTypes',
    },
    [WorkspaceElementType.ExternalApiLogRecord]: {
        type: WorkspaceElementType.ExternalApiLogRecord,
        apiPath: 'external-api-logs',
        name: 'External API Log Record',
        namePlural: 'External API Log Records',
        statsKey: 'externalApiLogs',
    },
    [WorkspaceElementType.IncomingWebhookLogRecord]: {
        type: WorkspaceElementType.IncomingWebhookLogRecord,
        apiPath: 'incoming-webhooks',
        name: 'Incoming Webhook Log Record',
        namePlural: 'Incoming Webhook Log Records',
        statsKey: 'externalWebhookLogs',
    },
    [WorkspaceElementType.Screen]: {
        type: WorkspaceElementType.Screen,
        apiPath: 'screens',
        name: 'Screen',
        namePlural: 'Screens',
        isMembraneInterface: true,
        hasKey: true,
        isProOnly: true,
    },
    [WorkspaceElementType.ConnectedProduct]: {
        type: WorkspaceElementType.ConnectedProduct,
        apiPath: 'connected-products',
        name: 'Connected Product',
        namePlural: 'Connected Products',
        hasKey: true,
    },
    [WorkspaceElementType.AgentHarness]: {
        type: WorkspaceElementType.AgentHarness,
        apiPath: 'agent/harness',
        collectionName: 'agent-harnesses',
        name: 'Agent Harness',
        namePlural: 'Agent Harnesses',
        hasKey: true,
    },
    [WorkspaceElementType.AgentSkill]: {
        type: WorkspaceElementType.AgentSkill,
        apiPath: 'agent/skills',
        collectionName: 'agent-skills',
        name: 'Agent Skill',
        namePlural: 'Agent Skills',
        hasKey: true,
    },
    [WorkspaceElementType.AgentInstruction]: {
        type: WorkspaceElementType.AgentInstruction,
        apiPath: 'agent/instructions',
        collectionName: 'agent-instructions',
        name: 'Agent Instruction',
        namePlural: 'Agent Instructions',
        hasKey: true,
    },
};
function getEditablePathsForElementType(elementType) {
    return getEditablePathsForElementType$1(WorkspaceElementSpecs, elementType);
}
function isPathUserEditable(elementType, path) {
    return isPathUserEditable$1(WorkspaceElementSpecs, elementType, path);
}

const MEMBRANE_ELEMENT_CONFIG_FILE_NAME = 'spec.yaml';
function getMembraneElementPath(elementType, elementKey, integrationKey) {
    const elementTypePath = WorkspaceElementSpecs[elementType].apiPath;
    if (!integrationKey) {
        return `${elementTypePath}/${elementKey}/${MEMBRANE_ELEMENT_CONFIG_FILE_NAME}`;
    }
    const integrationTypePath = WorkspaceElementSpecs[WorkspaceElementType.Integration].apiPath;
    return `${integrationTypePath}/${integrationKey}/${elementTypePath}/${elementKey}/${MEMBRANE_ELEMENT_CONFIG_FILE_NAME}`;
}
function parseMembraneElementPath(relativePath) {
    const normalizedPath = relativePath.split(/[\\/]/).join('/');
    const typeByApiPath = new Map();
    for (const spec of Object.values(WorkspaceElementSpecs)) {
        typeByApiPath.set(spec.apiPath, spec.type);
    }
    const elementTypes = Object.values(WorkspaceElementSpecs)
        .map((spec) => spec.apiPath)
        .join('|');
    const universalPattern = new RegExp(`^(?<elementType>${elementTypes})/(?<elementKey>[^/]+)/${MEMBRANE_ELEMENT_CONFIG_FILE_NAME}$`);
    const universalMatch = normalizedPath.match(universalPattern);
    if (universalMatch === null || universalMatch === void 0 ? void 0 : universalMatch.groups) {
        const { elementType: elementTypePath, elementKey } = universalMatch.groups;
        const elementType = typeByApiPath.get(elementTypePath);
        if (elementType) {
            return { type: elementType, key: elementKey };
        }
    }
    const integrationLevelElementTypes = Object.values(WorkspaceElementSpecs)
        .filter((spec) => spec.isIntegrationLevel)
        .map((spec) => spec.apiPath)
        .join('|');
    const integrationLevelPattern = new RegExp(`^integrations/(?<integrationKey>[^/]+)/(?<elementType>${integrationLevelElementTypes})/(?<elementKey>[^/]+)/${MEMBRANE_ELEMENT_CONFIG_FILE_NAME}$`);
    const integrationLevelMatch = normalizedPath.match(integrationLevelPattern);
    if (integrationLevelMatch === null || integrationLevelMatch === void 0 ? void 0 : integrationLevelMatch.groups) {
        const { integrationKey, elementType: elementTypePath, elementKey } = integrationLevelMatch.groups;
        const elementType = typeByApiPath.get(elementTypePath);
        if (elementType) {
            return { type: elementType, key: elementKey, integrationKey };
        }
    }
    return undefined;
}
function getConnectorSpecPath(connectorUuid) {
    const connectorsPath = WorkspaceElementSpecs[WorkspaceElementType.Connector].apiPath;
    return `${connectorsPath}/${connectorUuid}/${connectorUuid}.yml`;
}
function getConnectorVersionPath(connectorUuid, version) {
    const connectorsPath = WorkspaceElementSpecs[WorkspaceElementType.Connector].apiPath;
    const versionDir = version === CONNECTOR_VERSION_DEVELOPMENT ? 'development' : version;
    return `${connectorsPath}/${connectorUuid}/${versionDir}/src.zip`;
}
function getConnectorVersionSpecPath(connectorUuid, version) {
    const connectorsPath = WorkspaceElementSpecs[WorkspaceElementType.Connector].apiPath;
    const versionDir = version === CONNECTOR_VERSION_DEVELOPMENT ? 'development' : version;
    return `${connectorsPath}/${connectorUuid}/${versionDir}/spec.yml`;
}

class SelfAccessor {
    constructor(client) {
        this.client = client;
    }
    async get() {
        return this.client.get('self');
    }
    async patch(data) {
        return this.client.patch('self', data);
    }
}

const PlatformUserPermissionsSchema = z.object({
    isEngineAdmin: z.boolean(),
    isPlatformAdmin: z.boolean(),
    platformUserId: z.string().optional(),
});
const OrgPermissionsSchema = z.object({
    orgId: z.string(),
    role: z.enum(['admin', 'member']).optional(),
    canRead: z.boolean(),
    canWrite: z.boolean(),
});
const WorkspacePermissionsSchema = z.object({
    workspaceId: z.string().optional(),
    isWorkspaceManager: z.boolean(),
    isReadOnly: z.boolean().default(false),
    scopes: z.array(z.string()),
});
const TenantPermissionsSchema = z.object({
    tenantId: z.string().optional(),
    workspaceId: z.string().optional(),
    canRead: z.boolean(),
    canWrite: z.boolean(),
});
const AuthContextPermissionsSchema = z.object({
    platformUser: PlatformUserPermissionsSchema,
    org: OrgPermissionsSchema.optional(),
    workspace: WorkspacePermissionsSchema.optional(),
    tenant: TenantPermissionsSchema.optional(),
});

const REDACTED = '[REDACTED]';
const VISIBLE_EDGE_LENGTH = 4;
const MIN_PARTIALLY_VISIBLE_LENGTH = 16;
const FULLY_REDACTED_KEY_PARTS = ['secret', 'password', 'privatekey'];
const PARTIALLY_REDACTED_KEY_PARTS = [
    'authorization',
    'cookie',
    'apikey',
    'authkey',
    'accesskey',
    'accesstoken',
    'refreshtoken',
    'idtoken',
    'authtoken',
    'apitoken',
    'sessiontoken',
    'privatetoken',
    'developertoken',
    'logincustomerid',
    'linkedcustomerid',
];
const PARTIALLY_REDACTED_KEYS = ['token', 'jwt'];
const AUTH_SCHEME_PATTERN = /^(bearer|basic|token|digest)\s+/i;
function getRedaction(key) {
    const normalizedKey = key.toLowerCase().replace(/[-_]/g, '');
    if (FULLY_REDACTED_KEY_PARTS.some((part) => normalizedKey.includes(part)))
        return 'full';
    if (PARTIALLY_REDACTED_KEYS.includes(normalizedKey) ||
        PARTIALLY_REDACTED_KEY_PARTS.some((part) => normalizedKey.includes(part))) {
        return 'partial';
    }
    return undefined;
}
function isSensitiveKey(key) {
    return getRedaction(key) !== undefined;
}
function isPlainObject(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}
function redactString(value, redaction) {
    var _a, _b;
    const scheme = (_b = (_a = value.match(AUTH_SCHEME_PATTERN)) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '';
    const secret = value.slice(scheme.length);
    const isBasicAuth = scheme.trim().toLowerCase() === 'basic';
    if (redaction === 'full' || isBasicAuth || secret.length < MIN_PARTIALLY_VISIBLE_LENGTH) {
        return `${scheme}${REDACTED}`;
    }
    return `${scheme}${secret.slice(0, VISIBLE_EDGE_LENGTH)}${REDACTED}${secret.slice(-VISIBLE_EDGE_LENGTH)}`;
}
function redactValue(value, redaction) {
    if (typeof value === 'string')
        return redactString(value, redaction);
    if (Array.isArray(value))
        return value.map((item) => redactValue(item, redaction));
    if (isPlainObject(value)) {
        return Object.fromEntries(Object.entries(value).map(([key, entry]) => {
            var _a;
            return [
                key,
                redactValue(entry, redaction === 'full' ? 'full' : ((_a = getRedaction(key)) !== null && _a !== void 0 ? _a : redaction)),
            ];
        }));
    }
    if (value === null || value === undefined || typeof value === 'boolean')
        return value;
    return REDACTED;
}
function redactSecrets(value) {
    if (Array.isArray(value)) {
        return value.map(redactSecrets);
    }
    if (isPlainObject(value)) {
        return Object.fromEntries(Object.entries(value).map(([key, entry]) => {
            const redaction = getRedaction(key);
            return [key, redaction ? redactValue(entry, redaction) : redactSecrets(entry)];
        }));
    }
    return value;
}
function redactAllValues(value) {
    return redactValue(value, 'partial');
}

var LogRecordType;
(function (LogRecordType) {
    LogRecordType["MSG"] = "message";
    LogRecordType["TEXT"] = "text";
    LogRecordType["ERR"] = "error";
    LogRecordType["DATA"] = "data";
    LogRecordType["COMP"] = "compare";
    LogRecordType["API"] = "api-request";
})(LogRecordType || (LogRecordType = {}));

function isStream(obj) {
    return (obj !== null &&
        typeof obj === 'object' &&
        typeof obj.pipe === 'function' &&
        (typeof obj.readable === 'boolean' || typeof obj.writable === 'boolean'));
}
async function streamToString(stream) {
    return new Promise((resolve, reject) => {
        let result = '';
        if (stream.closed) {
            resolve('');
        }
        stream.on('data', (chunk) => {
            result += chunk.toString();
        });
        stream.on('end', () => {
            resolve(result);
        });
        stream.on('error', (err) => {
            reject(err);
        });
    });
}
function isBlob(value) {
    var _a;
    if (value === null || typeof value !== 'object') {
        return false;
    }
    if (typeof Blob !== 'undefined' && value instanceof Blob) {
        return true;
    }
    const obj = value;
    const hasRequiredProps = typeof obj.size === 'number' && typeof obj.type === 'string' && typeof obj.slice === 'function';
    if (!hasRequiredProps) {
        return false;
    }
    const hasBlobMethod = typeof obj.stream === 'function' || typeof obj.text === 'function' || typeof obj.arrayBuffer === 'function';
    if (hasBlobMethod) {
        return true;
    }
    const constructorName = (_a = obj.constructor) === null || _a === void 0 ? void 0 : _a.name;
    return constructorName === 'Blob' || constructorName === 'File';
}
function truncateData(data, depth = 0) {
    const maxArrayItems = 500;
    const maxObjectKeys = 500;
    const maxStringLength = 500;
    const maxDepth = 15;
    if (depth > maxDepth) {
        return '<max depth reached>';
    }
    if (!data) {
        return data;
    }
    else if (isStream(data)) {
        return '<stream>';
    }
    else if (Array.isArray(data)) {
        const result = data.slice(0, maxArrayItems).map((item) => truncateData(item, depth + 1));
        if (data.length > maxArrayItems) {
            result.push({
                '...moreItems': data.length - maxArrayItems,
            });
        }
        return result;
    }
    else if (typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) {
        return data.toString('utf-8').slice(0, maxStringLength);
    }
    else if (isBlob(data)) {
        return `<Blob: size=${data.size}, type=${data.type}>`;
    }
    else if (typeof data === 'object') {
        const keys = Object.keys(data);
        const result = {};
        for (const key of keys.slice(0, maxObjectKeys)) {
            result[key] = truncateData(data[key], depth + 1);
        }
        if (keys.length > maxObjectKeys) {
            result['...moreKeys'] = keys.slice(maxObjectKeys).join(', ').slice(0, maxStringLength);
        }
        return result;
    }
    else if (typeof data === 'string' && data.length > maxStringLength) {
        return `${data.slice(0, maxStringLength)}...`;
    }
    else {
        return data;
    }
}

const ERROR_CLASS_BY_TYPE = {
    [ErrorType.BAD_REQUEST]: BadRequestError,
    [ErrorType.CONNECTION]: ConnectionError,
    [ErrorType.CONFIGURATION]: ConfigurationError,
    [ErrorType.DEPENDENCY_ERROR]: DependencyError,
    [ErrorType.FLOW_RUN]: FlowRunError,
    [ErrorType.FLOW_INSTANCE_SETUP]: FlowInstanceSetupError,
    [ErrorType.INTERNAL]: InternalError,
    [ErrorType.CONCURRENCY]: ConcurrencyError,
    [ErrorType.ACTION_RUN]: ActionRunError,
    [ErrorType.ACTION_INSTANCE_SETUP]: ActionInstanceSetupError,
    [ErrorType.UNIT_RUN]: UnitRunError,
    [ErrorType.CUSTOM_CODE]: CustomCodeError,
    [ErrorType.INSUFFICIENT_CREDIT]: InsufficientCreditsError,
    [ErrorType.OPENROUTER_NOT_CONFIGURED]: OpenRouterNotConfiguredError,
};
function getErrorFromData(data) {
    if (typeof data === 'object' &&
        data !== null &&
        Object.values(ErrorType).includes(data.type) &&
        data.type in ERROR_CLASS_BY_TYPE) {
        return new ERROR_CLASS_BY_TYPE[data.type](data);
    }
    return null;
}
function extractMembraneErrorData(error) {
    var _a, _b, _c, _d, _e;
    const errorMessage = error.message || ((_a = error.toString) === null || _a === void 0 ? void 0 : _a.call(error));
    const data = error.isMembraneError
        ? error.data
        : new ErrorData({
            message: errorMessage,
            type: ErrorType.INTERNAL,
        });
    if (!data.message) {
        data.message = errorMessage;
    }
    const isAxiosError = (error === null || error === void 0 ? void 0 : error.response) && (error === null || error === void 0 ? void 0 : error.config);
    if (!data.data && isAxiosError) {
        let responseData;
        if (error.response) {
            if (isStream(error.response.data)) {
                responseData = streamToString(error.response.data);
            }
            else if (error.response.data instanceof ArrayBuffer) {
                responseData = Buffer.from(error.response.data).toString('utf-8');
            }
            else {
                responseData = error.response.data;
            }
        }
        data.data = {
            request: error.config
                ? {
                    url: (_b = error.config) === null || _b === void 0 ? void 0 : _b.url,
                    method: (_c = error.config) === null || _c === void 0 ? void 0 : _c.method,
                }
                : undefined,
            response: {
                data: responseData,
                status: (_d = error.response) === null || _d === void 0 ? void 0 : _d.status,
                headers: (_e = error.response) === null || _e === void 0 ? void 0 : _e.headers,
            },
        };
    }
    if (error instanceof ZodError) {
        data.message = fromError(error).toString();
    }
    return data;
}

class DataForm {
    constructor(args) {
        Object.assign(this, args);
    }
    getFields() {
        var _a;
        switch (this.schema.type) {
            case 'object':
                return Object.entries((_a = this.schema.properties) !== null && _a !== void 0 ? _a : {}).map(([key, schema]) => this.makeDataField(key, schema));
            case 'array':
                return [];
            default:
                return [this.makeDataField(null, this.schema)];
        }
    }
    getFieldValueOptions(field) {
        var _a, _b, _c;
        const options = [];
        const defaultValue = (_a = field.schema) === null || _a === void 0 ? void 0 : _a.default;
        if (defaultValue !== undefined) {
            options.push({
                name: valueToString(field.schema.default, this.variablesSchema),
                value: field.schema.default,
            });
        }
        if (((_b = this.variablesSchema) === null || _b === void 0 ? void 0 : _b.type) === 'object') {
            for (const locator of Object.keys((_c = this.variablesSchema.properties) !== null && _c !== void 0 ? _c : {})) {
                const formula = new DataBuilderFormulaVar(locator);
                const value = formula.toObject();
                if (!deepEqual(value, defaultValue)) {
                    options.push({
                        name: formula.toString(this.variablesSchema),
                        value,
                    });
                }
            }
        }
        options.forEach((option) => {
            option.selected = deepEqual(option.value, field.value);
        });
        return options;
    }
    setFieldValue(field, value) {
        return setValueAtLocator(this.value, field.locator, value);
    }
    makeDataField(key, schema) {
        var _a;
        return {
            name: (_a = schema === null || schema === void 0 ? void 0 : schema.title) !== null && _a !== void 0 ? _a : toHeaderCase(key),
            locator: key,
            schema,
            value: getValueAtLocator(this.value, key),
        };
    }
}

function parseDate(value) {
    let timestamp;
    if (typeof value === 'number') {
        timestamp = value;
    }
    else if (typeof value === 'string') {
        timestamp = Date.parse(value);
        if (isNaN(timestamp)) {
            timestamp = undefined;
        }
        if (!timestamp && Number(value) > 1000000000) {
            timestamp = Number(value);
        }
    }
    if (timestamp) {
        return timestampToDate(timestamp);
    }
    else {
        return undefined;
    }
}
function timestampToDate(value) {
    if (Math.round(value).toString().length < 13) {
        return new Date(value * 1000);
    }
    else {
        return new Date(value);
    }
}

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
function isBusinessDay(date) {
    const dayOfWeek = date.getDay();
    return dayOfWeek >= 1 && dayOfWeek <= 5;
}
function getBusinessDaysBetween(startDate, endDate) {
    const businessDays = new Set();
    if (endDate < startDate) {
        return businessDays;
    }
    const startTime = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endTime = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const dayCount = Math.floor((endTime - startTime) / ONE_DAY_IN_MS) + 1;
    for (let i = 0; i < dayCount; i++) {
        const currentTime = startTime + i * ONE_DAY_IN_MS;
        const currentDate = new Date(currentTime);
        if (isBusinessDay(currentDate)) {
            businessDays.add(currentDate.toISOString().split('T')[0]);
        }
    }
    return businessDays;
}

const StatsFilterQuery = z.object({
    tenantId: z.string().optional(),
    integrationId: z.string().optional(),
    connectionId: z.string().optional(),
    externalAppId: z.string().optional(),
});
const ActivityStatsQuery = StatsFilterQuery.extend({
    startDatetime: zodIsoDatetime({ offset: true }),
    elementType: z.enum(WorkspaceElementType).optional(),
});
const IntegrationLayerStatsQuery = StatsFilterQuery.extend({
    elementType: z.enum(WorkspaceElementType).optional(),
});
const ActionRunsStatsQuery = z.object({
    actionId: z.string(),
    startDatetime: zodIsoDatetime({ offset: true }),
});
const FlowRunsStatsQuery = z.object({
    flowId: z.string(),
    startDatetime: zodIsoDatetime({ offset: true }),
});
const WORKSPACE_ELEMENT_COLLECTIONS = [
    'integrations',
    'integration-packages',
    'actions',
    'flows',
    'external-event-subscriptions',
    'app-event-subscriptions',
    'data-sources',
    'field-mappings',
    'app-data-schemas',
    'data-link-tables',
];
const WORKSPACE_ELEMENTS_STATS_COLLECTIONS = WORKSPACE_ELEMENT_COLLECTIONS;
const WORKSPACE_DATABASE_RECORD_COLLECTIONS = [
    'data-links',
    ...[
        WorkspaceElementType.FlowInstance,
        WorkspaceElementType.ActionInstance,
        WorkspaceElementType.DataSourceInstance,
        WorkspaceElementType.FieldMappingInstance,
        WorkspaceElementType.DataLinkTableInstance,
        WorkspaceElementType.AppDataSchemaInstance,
    ]
        .map((type) => WorkspaceElementSpecs[type].collectionName)
        .filter((c) => c != null),
];
const WorkspaceElementsStatsSchema = z.object({
    total: z.number(),
    breakdown: z.object(Object.fromEntries(WORKSPACE_ELEMENT_COLLECTIONS.map((collectionName) => [collectionName, z.number()]))),
});

var IntegrationElementLevel;
(function (IntegrationElementLevel) {
    IntegrationElementLevel["UNIVERSAL"] = "universal";
    IntegrationElementLevel["CONNECTOR"] = "connector";
    IntegrationElementLevel["CONNECTION"] = "connection";
})(IntegrationElementLevel || (IntegrationElementLevel = {}));
var IntegrationElementType;
(function (IntegrationElementType) {
    IntegrationElementType["APP_DATA_SCHEMA"] = "app-data-schema";
    IntegrationElementType["APP_EVENT_TYPE"] = "app-event-type";
    IntegrationElementType["DATA_SOURCE"] = "data-source";
    IntegrationElementType["SCHEMA"] = "schema";
    IntegrationElementType["FIELD_MAPPING"] = "field-mapping";
    IntegrationElementType["FLOW"] = "flow";
    IntegrationElementType["INTEGRATION"] = "integration";
    IntegrationElementType["ACTION"] = "action";
    IntegrationElementType["DATA_LINK_TABLE"] = "data-link-table";
    IntegrationElementType["EXTERNAL_EVENT"] = "external-event";
})(IntegrationElementType || (IntegrationElementType = {}));

var UsageType;
(function (UsageType) {
    UsageType["FLOW_RUNS_DURATION"] = "flow-runs-duration";
    UsageType["FLOW_RUNS_LOG_NUMBER"] = "flow-runs-log-number";
    UsageType["FLOW_RUNS_LOG_SIZE"] = "flow-runs-log-size";
    UsageType["ACTION_RUNS_NUMBER"] = "action-runs-number";
    UsageType["API_LOGS_NUMBER"] = "api-logs-number";
    UsageType["API_LOGS_SIZE"] = "api-logs-size";
    UsageType["WEBHOOK_LOGS_NUMBER"] = "webhook-logs-number";
    UsageType["WEBHOOK_LOGS_SIZE"] = "webhook-logs-size";
    UsageType["EVENT_LOGS_NUMBER"] = "event-logs-number";
    UsageType["EVENT_LOGS_SIZE"] = "event-logs-size";
    UsageType["EVENT_LOG_PROCESS_DURATION"] = "event-log-process-duration";
    UsageType["EXTERNAL_EVENTS_PULL_DURATION"] = "external-events-pull-duration";
    UsageType["EXTERNAL_EVENT_PULL_LOG_NUMBER"] = "external-event-pull-log-number";
    UsageType["EXTERNAL_EVENT_PULL_LOG_SIZE"] = "external-event-pull-log-size";
    UsageType["EXTERNAL_EVENT_WEBHOOK_REFRESH_DURATION"] = "external-event-webhook-refresh-duration";
    UsageType["APP_EVENTS_LOG_NUMBER"] = "app-events-log-number";
    UsageType["APP_EVENTS_LOG_SIZE"] = "app-events-log-size";
    UsageType["FILES_UPLOAD_NUMBER"] = "files-upload-number";
    UsageType["FILES_UPLOAD_SIZE"] = "files-upload-size";
    UsageType["CUSTOM_CODE_RUNTIME_DURATION"] = "custom-code-runtime-duration";
    UsageType["INSTANT_TASKS_DURATION"] = "instant-tasks-duration";
    UsageType["MEMBRANE_AGENT"] = "membrane-agent";
    UsageType["AI_GATEWAY"] = "ai-gateway";
})(UsageType || (UsageType = {}));

function getNodeInputSchema(flow, nodeKey) {
    const schema = {
        type: 'object',
        properties: {},
    };
    getUpstreamNodeKeys(flow, nodeKey).forEach((prevNodeKey) => {
        const prevNode = flow.nodes[prevNodeKey];
        if (prevNode.outputSchema) {
            const prevNodeSchema = JSON.parse(JSON.stringify(prevNode.outputSchema));
            if (!prevNodeSchema.title) {
                prevNodeSchema.title = prevNode.name;
            }
            schema.properties[prevNodeKey] = prevNodeSchema;
        }
    });
    return schema;
}
function getFlowNodeConfigTimeVariablesSchema({ parametersSchema, userSchema, }) {
    return {
        type: 'object',
        properties: {
            flowInstance: getFlowInstanceVariableSchema({ parametersSchema }),
            user: userSchema,
        },
    };
}
function getActionRunTimeVariablesSchema({ inputSchema, userSchema }) {
    return {
        type: 'object',
        properties: {
            input: inputSchema,
            user: userSchema,
            actionInstance: getActionInstanceVariableSchema(),
            connection: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    name: { type: 'string' },
                },
            },
            integration: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    name: { type: 'string' },
                },
            },
        },
    };
}
function getActionInstanceVariableSchema() {
    return {
        type: 'object',
        properties: {
            id: { type: 'string ' },
            instanceKey: { type: 'string' },
        },
    };
}
function getFlowNodeRunTimeVariablesSchema({ parametersSchema, inputSchema, userSchema, }) {
    return {
        type: 'object',
        properties: {
            input: inputSchema,
            user: userSchema,
            flowRun: getFlowRunVariableSchema(),
            flowInstance: getFlowInstanceVariableSchema({ parametersSchema }),
            connection: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    name: { type: 'string' },
                },
            },
            integration: {
                type: 'object',
                properties: {
                    id: { type: 'string' },
                    key: { type: 'string' },
                    name: { type: 'string' },
                },
            },
        },
    };
}
function getFlowInstanceVariableSchema({ parametersSchema }) {
    return {
        type: 'object',
        properties: {
            id: { type: 'string ' },
            instanceKey: { type: 'string' },
            flowId: { type: 'string' },
            parameters: parametersSchema,
        },
    };
}
function getFlowRunVariableSchema() {
    return {
        type: 'object',
        properties: {
            id: { type: 'string ' },
        },
    };
}

var AlertStatus;
(function (AlertStatus) {
    AlertStatus["ONGOING"] = "ongoing";
    AlertStatus["RESOLVED"] = "resolved";
})(AlertStatus || (AlertStatus = {}));
var AlertSeverity;
(function (AlertSeverity) {
    AlertSeverity["CRITICAL"] = "critical";
    AlertSeverity["WARNING"] = "warning";
})(AlertSeverity || (AlertSeverity = {}));
var AlertType;
(function (AlertType) {
    AlertType["fileUploadsMbPerHour"] = "fileUploadsMbPerHour";
    AlertType["apiRequestsPerSecond"] = "apiRequestsPerSecond";
    AlertType["apiRequestsPerHour"] = "apiRequestsPerHour";
    AlertType["webhookRequestsPerSecond"] = "webhookRequestsPerSecond";
    AlertType["webhookRequestsPerHour"] = "webhookRequestsPerHour";
    AlertType["workspaceElementCreationsPerSecond"] = "workspaceElementCreationsPerSecond";
    AlertType["workspaceElementCreationsPerHour"] = "workspaceElementCreationsPerHour";
    AlertType["workspaceElementSearchIndexingPerMinute"] = "workspaceElementSearchIndexingPerMinute";
    AlertType["externalEventsPerCustomerPerDay"] = "externalEventsPerCustomerPerDay";
    AlertType["apiRequestsPerCustomerPerSecond"] = "apiRequestsPerCustomerPerSecond";
    AlertType["apiRequestsPerCustomerPerHour"] = "apiRequestsPerCustomerPerHour";
    AlertType["webhookRequestsPerCustomerPerSecond"] = "webhookRequestsPerCustomerPerSecond";
    AlertType["webhookRequestsPerCustomerPerHour"] = "webhookRequestsPerCustomerPerHour";
    AlertType["parallelApiRequestsPerCustomer"] = "parallelApiRequestsPerCustomer";
    AlertType["engineCreditsExhaustionProjected"] = "engineCreditsExhaustionProjected";
    AlertType["engineCreditsExhaustionActual"] = "engineCreditsExhaustionActual";
    AlertType["aiCreditsDebtAccumulated"] = "aiCreditsDebtAccumulated";
    AlertType["totalNumberOfDatabaseEntitiesPerCustomer"] = "totalNumberOfDatabaseEntitiesPerCustomer";
    AlertType["totalNumberOfCustomers"] = "totalNumberOfCustomers";
    AlertType["totalNumberOfConnections"] = "totalNumberOfConnections";
    AlertType["totalNumberOfWorkspaceElements"] = "totalNumberOfWorkspaceElements";
    AlertType["totalNumberOfWorkspaceDatabaseRecords"] = "totalNumberOfWorkspaceDatabaseRecords";
    AlertType["instantTasksQueueSize"] = "instantTasksQueueSize";
    AlertType["queuedTasksQueueSize"] = "queuedTasksQueueSize";
    AlertType["flowRunsQueueSizePerConnection"] = "flowRunsQueueSizePerConnection";
    AlertType["eventsProcessingQueueSizePerConnection"] = "eventsProcessingQueueSizePerConnection";
    AlertType["parallelApiRequests"] = "parallelApiRequests";
    AlertType["testAlert"] = "testAlert";
})(AlertType || (AlertType = {}));
var AlertCategory;
(function (AlertCategory) {
    AlertCategory["RATE_LIMIT"] = "rateLimit";
    AlertCategory["CUSTOMER_RATE_LIMIT"] = "customerRateLimit";
    AlertCategory["USAGE"] = "usage";
    AlertCategory["WORKSPACE_SIZE"] = "workspaceSize";
    AlertCategory["TEST"] = "test";
})(AlertCategory || (AlertCategory = {}));
const ALERT_TYPE_CATEGORIES = {
    [AlertType.fileUploadsMbPerHour]: AlertCategory.RATE_LIMIT,
    [AlertType.apiRequestsPerSecond]: AlertCategory.RATE_LIMIT,
    [AlertType.apiRequestsPerHour]: AlertCategory.RATE_LIMIT,
    [AlertType.webhookRequestsPerSecond]: AlertCategory.RATE_LIMIT,
    [AlertType.webhookRequestsPerHour]: AlertCategory.RATE_LIMIT,
    [AlertType.workspaceElementCreationsPerSecond]: AlertCategory.RATE_LIMIT,
    [AlertType.workspaceElementCreationsPerHour]: AlertCategory.RATE_LIMIT,
    [AlertType.workspaceElementSearchIndexingPerMinute]: AlertCategory.RATE_LIMIT,
    [AlertType.externalEventsPerCustomerPerDay]: AlertCategory.RATE_LIMIT,
    [AlertType.apiRequestsPerCustomerPerSecond]: AlertCategory.CUSTOMER_RATE_LIMIT,
    [AlertType.apiRequestsPerCustomerPerHour]: AlertCategory.CUSTOMER_RATE_LIMIT,
    [AlertType.webhookRequestsPerCustomerPerSecond]: AlertCategory.CUSTOMER_RATE_LIMIT,
    [AlertType.webhookRequestsPerCustomerPerHour]: AlertCategory.CUSTOMER_RATE_LIMIT,
    [AlertType.parallelApiRequestsPerCustomer]: AlertCategory.CUSTOMER_RATE_LIMIT,
    [AlertType.engineCreditsExhaustionProjected]: AlertCategory.USAGE,
    [AlertType.engineCreditsExhaustionActual]: AlertCategory.USAGE,
    [AlertType.aiCreditsDebtAccumulated]: AlertCategory.USAGE,
    [AlertType.totalNumberOfDatabaseEntitiesPerCustomer]: AlertCategory.WORKSPACE_SIZE,
    [AlertType.testAlert]: AlertCategory.TEST,
    [AlertType.totalNumberOfCustomers]: AlertCategory.WORKSPACE_SIZE,
    [AlertType.totalNumberOfConnections]: AlertCategory.WORKSPACE_SIZE,
    [AlertType.totalNumberOfWorkspaceElements]: AlertCategory.WORKSPACE_SIZE,
    [AlertType.totalNumberOfWorkspaceDatabaseRecords]: AlertCategory.WORKSPACE_SIZE,
    [AlertType.instantTasksQueueSize]: AlertCategory.WORKSPACE_SIZE,
    [AlertType.queuedTasksQueueSize]: AlertCategory.WORKSPACE_SIZE,
    [AlertType.flowRunsQueueSizePerConnection]: AlertCategory.WORKSPACE_SIZE,
    [AlertType.eventsProcessingQueueSizePerConnection]: AlertCategory.WORKSPACE_SIZE,
    [AlertType.parallelApiRequests]: AlertCategory.RATE_LIMIT,
};
const Alert = z.object({
    id: z.string(),
    key: z.string(),
    description: z.string(),
    status: z.enum(Object.values(AlertStatus)),
    type: z.enum(Object.values(AlertType)),
    workspaceId: z.string().optional(),
    orgId: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    lastSeenAt: z.string(),
    resolvedAt: z.string().optional(),
    customerId: z.string().optional(),
    severity: z.enum(Object.values(AlertSeverity)),
});
const AlertSchema = Alert;
const ALERT_DELIVERY_METHODS = ['internal', 'webhook', 'email'];
const AlertTypeDeliverySettingsSchema = z.object({
    internal: z.literal(true),
    webhook: z
        .object({
        enabled: z.boolean(),
    })
        .optional(),
    email: z
        .object({
        enabled: z.boolean(),
    })
        .optional(),
});
const DEFAULT_ALERT_TYPE_DELIVERY_SETTINGS = {
    internal: true,
    webhook: {
        enabled: false,
    },
    email: {
        enabled: false,
    },
};
const AlertDeliverySettingsSchema = z.object({
    alertTypes: z.record(z.enum(Object.values(AlertType)), AlertTypeDeliverySettingsSchema.optional()).optional(),
});
const DEFAULT_ALERT_DELIVERY_SETTINGS = {
    alertTypes: undefined,
};
const FindAlertsQuery = PaginationQuery.extend({
    id: z.string().optional(),
    orgId: z.string().optional(),
    workspaceId: z.string().optional(),
    contextKey: z.string().optional(),
    status: z.enum(Object.values(AlertStatus)).optional(),
    type: z.enum(Object.values(AlertType)).optional(),
    createdAt: z.string().optional(),
});
const CreateAlert = z.object({
    key: z.string().min(1),
    description: z.string().min(1),
    status: z.literal(AlertStatus.ONGOING),
    type: z.enum(Object.values(AlertType)),
    workspaceId: z.string().min(1).optional(),
    orgId: z.string().min(1),
    lastSeenAt: z.string(),
    severity: z.enum(Object.values(AlertSeverity)),
    resolvedAt: z.string().nullable().optional(),
    contextKey: z.string().optional(),
});

function isValidAlertType(value) {
    return Object.values(AlertType).includes(value);
}
function getAlertTypeDisplayName(alertType) {
    const names = {
        [AlertType.fileUploadsMbPerHour]: 'File Uploads MB Per Hour',
        [AlertType.apiRequestsPerSecond]: 'API Requests Per Second',
        [AlertType.apiRequestsPerHour]: 'API Requests Per Hour',
        [AlertType.webhookRequestsPerSecond]: 'Webhook Requests Per Second',
        [AlertType.webhookRequestsPerHour]: 'Webhook Requests Per Hour',
        [AlertType.workspaceElementCreationsPerSecond]: 'Workspace Element Creations Per Second',
        [AlertType.workspaceElementCreationsPerHour]: 'Workspace Element Creations Per Hour',
        [AlertType.workspaceElementSearchIndexingPerMinute]: 'Workspace Element Search Indexing Per Minute',
        [AlertType.externalEventsPerCustomerPerDay]: 'External Events Per Customer Per Day',
        [AlertType.apiRequestsPerCustomerPerSecond]: 'API Requests Per Customer Per Second',
        [AlertType.apiRequestsPerCustomerPerHour]: 'API Requests Per Customer Per Hour',
        [AlertType.webhookRequestsPerCustomerPerSecond]: 'Webhook Requests Per Customer Per Second',
        [AlertType.webhookRequestsPerCustomerPerHour]: 'Webhook Requests Per Customer Per Hour',
        [AlertType.parallelApiRequestsPerCustomer]: 'Parallel API Requests Per Customer',
        [AlertType.engineCreditsExhaustionProjected]: 'Engine Credits Exhaustion Projected',
        [AlertType.engineCreditsExhaustionActual]: 'Engine Credits Exhaustion Actual',
        [AlertType.aiCreditsDebtAccumulated]: 'AI Credits Debt Accumulated',
        [AlertType.totalNumberOfDatabaseEntitiesPerCustomer]: 'Database Entities Per Customer',
        [AlertType.testAlert]: 'Test Alert',
        [AlertType.totalNumberOfCustomers]: 'Total Number of Customers',
        [AlertType.totalNumberOfConnections]: 'Total Number of Connections',
        [AlertType.totalNumberOfWorkspaceElements]: 'Total Number of Workspace Elements',
        [AlertType.totalNumberOfWorkspaceDatabaseRecords]: 'Total Number of Workspace Data Records',
        [AlertType.instantTasksQueueSize]: 'Instant Tasks Queue Size',
        [AlertType.queuedTasksQueueSize]: 'Queued Tasks Queue Size',
        [AlertType.flowRunsQueueSizePerConnection]: 'Flow Runs Queue Size Per Connection',
        [AlertType.eventsProcessingQueueSizePerConnection]: 'Events Processing Queue Size Per Connection',
        [AlertType.parallelApiRequests]: 'Parallel API Requests',
    };
    return names[alertType] || alertType;
}
function getAlertDeliveryMethodLabel(method) {
    const labels = {
        internal: 'Internal Delivery',
        webhook: 'Webhook Delivery',
        email: 'Email Delivery',
    };
    return labels[method] || method;
}
function isDeliveryMethodEnabled(settings, method) {
    var _a;
    if (method === 'internal') {
        return true;
    }
    if (((_a = settings[method]) !== null && _a !== void 0 ? _a : DEFAULT_ALERT_TYPE_DELIVERY_SETTINGS[method]).enabled) {
        return true;
    }
    return false;
}
function getAlertCategoryDisplayName(category) {
    const names = {
        [AlertCategory.RATE_LIMIT]: 'Rate Limit Alerts',
        [AlertCategory.CUSTOMER_RATE_LIMIT]: 'Customer Rate Limit Alerts',
        [AlertCategory.USAGE]: 'Usage Alerts',
        [AlertCategory.TEST]: 'Test',
        [AlertCategory.WORKSPACE_SIZE]: 'Workspace Size Alerts',
    };
    return names[category] || category;
}
function getAlertTypesByCategory(category) {
    return Object.entries(ALERT_TYPE_CATEGORIES)
        .filter(([, alertCategory]) => alertCategory === category)
        .map(([alertType]) => alertType)
        .filter(isValidAlertType);
}

var WorkspaceType;
(function (WorkspaceType) {
    WorkspaceType["PRODUCTION"] = "production";
    WorkspaceType["DEVELOPMENT"] = "development";
})(WorkspaceType || (WorkspaceType = {}));
var WorkspaceNotificationType;
(function (WorkspaceNotificationType) {
    WorkspaceNotificationType["WorkspaceUpdate"] = "workspace-update";
    WorkspaceNotificationType["CopilotActivity"] = "copilot-activity";
    WorkspaceNotificationType["LocalClientUpdate"] = "local-client-update";
})(WorkspaceNotificationType || (WorkspaceNotificationType = {}));
var ParallelExecutionLimits;
(function (ParallelExecutionLimits) {
    ParallelExecutionLimits["ParallelEventPulls"] = "parallelEventPulls";
    ParallelExecutionLimits["ParallelIncrementalEventPullsPerConnection"] = "parallelIncrementalEventPullsPerConnection";
    ParallelExecutionLimits["ParallelFullSyncEventPullsPerConnection"] = "parallelFullSyncEventPullsPerConnection";
    ParallelExecutionLimits["ParallelFlowRuns"] = "parallelFlowRuns";
    ParallelExecutionLimits["ParallelFlowRunsPerConnection"] = "parallelFlowRunsPerConnection";
    ParallelExecutionLimits["ParallelApiRequests"] = "parallelApiRequests";
    ParallelExecutionLimits["ParallelSseRequests"] = "parallelSseRequests";
    ParallelExecutionLimits["ParallelBackgroundJobs"] = "parallelBackgroundJobs";
    ParallelExecutionLimits["ParallelEventProcessingJobs"] = "parallelEventProcessingJobs";
    ParallelExecutionLimits["ParallelEventProcessingJobsPerConnection"] = "parallelEventProcessingJobsPerConnection";
    ParallelExecutionLimits["ParallelAgentSessions"] = "parallelAgentSessions";
    ParallelExecutionLimits["ParallelInstantTasksActiveJobs"] = "parallelInstantTasksActiveJobs";
    ParallelExecutionLimits["ParallelCustomCodeRuns"] = "parallelCustomCodeRuns";
})(ParallelExecutionLimits || (ParallelExecutionLimits = {}));
var RateLimits;
(function (RateLimits) {
    RateLimits["fileUploadsMbPerHour"] = "fileUploadsMbPerHour";
    RateLimits["ApiRequestsPerSecond"] = "apiRequestsPerSecond";
    RateLimits["ApiRequestsPerHour"] = "apiRequestsPerHour";
    RateLimits["WebhookRequestsPerSecond"] = "webhookRequestsPerSecond";
    RateLimits["WebhookRequestsPerHour"] = "webhookRequestsPerHour";
    RateLimits["WorkspaceElementCreationsPerSecond"] = "workspaceElementCreationsPerSecond";
    RateLimits["WorkspaceElementCreationsPerHour"] = "workspaceElementCreationsPerHour";
    RateLimits["WorkspaceElementSearchIndexingPerMinute"] = "workspaceElementSearchIndexingPerMinute";
    RateLimits["ExternalEventsPerCustomerPerDay"] = "externalEventsPerCustomerPerDay";
    RateLimits["McpOAuthAuthorizationsPerHour"] = "mcpOAuthAuthorizationsPerHour";
    RateLimits["McpOAuthTokensPerHour"] = "mcpOAuthTokensPerHour";
})(RateLimits || (RateLimits = {}));
var WorkspaceSizeLimits;
(function (WorkspaceSizeLimits) {
    WorkspaceSizeLimits["TotalNumberOfCustomers"] = "totalNumberOfCustomers";
    WorkspaceSizeLimits["TotalNumberOfConnections"] = "totalNumberOfConnections";
    WorkspaceSizeLimits["TotalNumberOfWorkspaceElements"] = "totalNumberOfWorkspaceElements";
    WorkspaceSizeLimits["TotalNumberOfWorkspaceDatabaseRecords"] = "totalNumberOfWorkspaceDatabaseRecords";
    WorkspaceSizeLimits["InstantTasksQueueSize"] = "instantTasksQueueSize";
    WorkspaceSizeLimits["QueuedTasksQueueSize"] = "queuedTasksQueueSize";
    WorkspaceSizeLimits["FlowRunsQueueSizePerConnection"] = "flowRunsQueueSizePerConnection";
    WorkspaceSizeLimits["EventsProcessingQueueSizePerConnection"] = "eventsProcessingQueueSizePerConnection";
})(WorkspaceSizeLimits || (WorkspaceSizeLimits = {}));
var CustomerLimits;
(function (CustomerLimits) {
    CustomerLimits["ParallelApiRequestsPerCustomer"] = "parallelApiRequestsPerCustomer";
    CustomerLimits["ParallelBackgroundJobsPerCustomer"] = "parallelBackgroundJobsPerCustomer";
    CustomerLimits["ParallelCustomCodeRunsPerCustomer"] = "parallelCustomCodeRunsPerCustomer";
    CustomerLimits["ApiRequestsPerCustomerPerSecond"] = "apiRequestsPerCustomerPerSecond";
    CustomerLimits["ApiRequestsPerCustomerPerHour"] = "apiRequestsPerCustomerPerHour";
    CustomerLimits["WebhookRequestsPerCustomerPerSecond"] = "webhookRequestsPerCustomerPerSecond";
    CustomerLimits["WebhookRequestsPerCustomerPerHour"] = "webhookRequestsPerCustomerPerHour";
    CustomerLimits["TotalNumberOfDatabaseEntitiesPerCustomer"] = "totalNumberOfDatabaseEntitiesPerCustomer";
})(CustomerLimits || (CustomerLimits = {}));
var WorkspaceAiCreditLimits;
(function (WorkspaceAiCreditLimits) {
    WorkspaceAiCreditLimits["DefaultTenantAiCreditsRolling30DayLimit"] = "defaultTenantAiCreditsRolling30DayLimit";
})(WorkspaceAiCreditLimits || (WorkspaceAiCreditLimits = {}));
var EventPullLimits;
(function (EventPullLimits) {
    EventPullLimits["MinPullUpdatesIntervalSeconds"] = "minPullUpdatesIntervalSeconds";
})(EventPullLimits || (EventPullLimits = {}));
var LimitUnits;
(function (LimitUnits) {
    LimitUnits["Number"] = "number";
    LimitUnits["Msec"] = "msec";
    LimitUnits["Mb"] = "Mb";
})(LimitUnits || (LimitUnits = {}));
const WorkspaceLimit = z$1.object({
    value: z$1.number().optional(),
    defaultValue: z$1.number().optional(),
    unit: z$1.nativeEnum(LimitUnits),
});
const WorkspaceLimitsSchema = z$1.object({
    parallelEventPulls: WorkspaceLimit.optional(),
    parallelIncrementalEventPullsPerConnection: WorkspaceLimit.optional(),
    parallelFullSyncEventPullsPerConnection: WorkspaceLimit.optional(),
    parallelFlowRuns: WorkspaceLimit.optional(),
    parallelFlowRunsPerConnection: WorkspaceLimit.optional(),
    parallelApiRequests: WorkspaceLimit.optional(),
    parallelSseRequests: WorkspaceLimit.optional(),
    parallelBackgroundJobs: WorkspaceLimit.optional(),
    parallelEventProcessingJobs: WorkspaceLimit.optional(),
    parallelEventProcessingJobsPerConnection: WorkspaceLimit.optional(),
    parallelInstantTasksActiveJobs: WorkspaceLimit.optional(),
    parallelAgentSessions: WorkspaceLimit.optional(),
    parallelCustomCodeRuns: WorkspaceLimit.optional(),
    ParallelWriteDatabaseRequests: WorkspaceLimit.optional(),
    fileUploadsMbPerHour: WorkspaceLimit.optional(),
    apiRequestsPerSecond: WorkspaceLimit.optional(),
    apiRequestsPerHour: WorkspaceLimit.optional(),
    webhookRequestsPerSecond: WorkspaceLimit.optional(),
    webhookRequestsPerHour: WorkspaceLimit.optional(),
    workspaceElementCreationsPerSecond: WorkspaceLimit.optional(),
    workspaceElementCreationsPerHour: WorkspaceLimit.optional(),
    workspaceElementSearchIndexingPerMinute: WorkspaceLimit.optional(),
    mcpOAuthAuthorizationsPerHour: WorkspaceLimit.optional(),
    mcpOAuthTokensPerHour: WorkspaceLimit.optional(),
    totalNumberOfCustomers: WorkspaceLimit.optional(),
    totalNumberOfConnections: WorkspaceLimit.optional(),
    totalNumberOfWorkspaceElements: WorkspaceLimit.optional(),
    totalNumberOfWorkspaceDatabaseRecords: WorkspaceLimit.optional(),
    flowRunsQueueSizePerConnection: WorkspaceLimit.optional(),
    instantTasksQueueSize: WorkspaceLimit.optional(),
    QueuedTasksQueueSize: WorkspaceLimit.optional(),
    parallelApiRequestsPerCustomer: WorkspaceLimit.optional(),
    parallelBackgroundJobsPerCustomer: WorkspaceLimit.optional(),
    parallelCustomCodeRunsPerCustomer: WorkspaceLimit.optional(),
    totalNumberOfDatabaseEntitiesPerCustomer: WorkspaceLimit.optional(),
    apiRequestsPerCustomerPerSecond: WorkspaceLimit.optional(),
    apiRequestsPerCustomerPerHour: WorkspaceLimit.optional(),
    webhookRequestsPerCustomerPerSecond: WorkspaceLimit.optional(),
    webhookRequestsPerCustomerPerHour: WorkspaceLimit.optional(),
    externalEventsPerCustomerPerDay: WorkspaceLimit.optional(),
    [WorkspaceAiCreditLimits.DefaultTenantAiCreditsRolling30DayLimit]: WorkspaceLimit.optional(),
    [EventPullLimits.MinPullUpdatesIntervalSeconds]: WorkspaceLimit.optional(),
});
const WorkspaceSettingsSchema = z$1.object({
    enableApiLogs: z$1.boolean().optional(),
    keepApiLogsForEmptyPulls: z$1
        .boolean()
        .optional()
        .describe('Keep API logs of event pulls that found no events. By default only a small sample of them is kept.'),
    enableWebhookLogs: z$1.boolean().optional(),
    enableActionRunLogs: z$1.boolean().optional(),
    disableSecretKeyAuth: z$1.boolean().optional(),
    useMembraneUniverse: z$1.boolean().optional(),
    useRemoteRepository: z$1.boolean().optional(),
    autoGenerateIntegrationsFromConnectors: z$1.boolean().optional(),
    autoGenerateIntegrationsFromExternalApps: z$1.boolean().optional(),
    autoGenerateIntegrationsFromWebSearch: z$1.boolean().optional(),
});
const EngineWorkspaceSettingsSchema = WorkspaceSettingsSchema;
const WorkspacePublicKey = z$1.object({
    name: z$1.string(),
    publicKey: z$1.string(),
});
const Workspace = z$1.object({
    id: z$1.string(),
    key: z$1.string(),
    logoUri: z$1.string().optional(),
    userFieldsSchema: z$1.any().optional().describe('[INTERNAL] User fields schema'),
    apiBaseUri: z$1.string().optional().describe('[INTERNAL] Deprecated: API base URI'),
    webhookUri: z$1.string().optional(),
    publicKey: z$1.string().optional().describe('[INTERNAL] Deprecated: use publicKeys instead'),
    publicKeys: z$1.array(WorkspacePublicKey).optional(),
    enabledWebhookEvents: z$1.array(z$1.string()).optional(),
    auth: z$1.any().optional().describe('[INTERNAL] Deprecated: use connector auth instead'),
    credentialsSchema: z$1.any().optional().describe('[INTERNAL] Deprecated: use connector credentials schema instead'),
    apiClient: z$1.any().optional().describe('[INTERNAL] Deprecated: use connector API client instead'),
    apiRequestHeaders: z$1.record(z$1.string(), z$1.any()).optional().describe('[INTERNAL] Deprecated: API request headers'),
    apiRequestQuery: z$1.record(z$1.string(), z$1.any()).optional().describe('[INTERNAL] Deprecated: API request query'),
    isOnPrem: z$1.boolean().optional().describe('[INTERNAL] Whether workspace runs on-premises'),
    connectorBaseUri: z$1.string().optional().describe('[INTERNAL] Connector base URI'),
    connectorRevision: z$1.string().optional().describe('[INTERNAL] Connector revision'),
    featureFlags: z$1.array(z$1.string()).optional().describe('[INTERNAL] Feature flags'),
    limits: WorkspaceLimitsSchema.optional(),
    areLimitsEnforced: z$1
        .boolean()
        .optional()
        .describe('[INTERNAL] Whether this deployment enforces the limits below, or only stores them'),
    settings: WorkspaceSettingsSchema.optional(),
    alertDeliverySettings: AlertDeliverySettingsSchema.optional(),
    type: z$1.nativeEnum(WorkspaceType).optional(),
    jwksUri: z$1.string().nullable().optional(),
    isTrial: z$1.boolean().optional().describe('[INTERNAL] Whether workspace is on a trial plan'),
    isThrottled: z$1.boolean().optional().describe('[INTERNAL] Whether workspace is throttled'),
    isDisabled: z$1.boolean().optional().describe('[INTERNAL] Whether workspace is disabled'),
    isBackgroundJobsDisabled: z$1.boolean().optional().describe('[INTERNAL] Whether background jobs are disabled'),
    isReadOnly: z$1.boolean().optional(),
    lastExternalApiRequestDate: z$1.string().nullable().optional(),
});
const AppSchema = Workspace;

const CreateWorkspaceRequest = z.object({
    workspaceKey: z.string().describe('Unique key identifying the workspace'),
    workspaceSecret: z.string().describe('Secret key for workspace authentication'),
    logoUri: z.string().optional().describe('URI for the workspace logo'),
    settings: WorkspaceSettingsSchema.optional().describe('Initial workspace settings'),
});
const UpdateWorkspaceRequest = z.object({
    name: z.string().optional().describe('Display name of the workspace'),
    secret: z.string().optional().describe('Secret key for workspace authentication'),
    logoUri: z.string().optional().describe('URI for the workspace logo'),
    auth: z.any().optional().describe('[INTERNAL] Deprecated: use connector auth instead'),
    credentialsSchema: z.any().optional().describe('[INTERNAL] Deprecated: use connector credentials schema instead'),
    apiClient: z.any().optional().describe('[INTERNAL] Deprecated: use connector API client instead'),
    publicKeys: z.array(WorkspacePublicKey).optional().describe('Public keys for JWT verification'),
    userFieldsSchema: z.any().optional().describe('[INTERNAL] User fields schema'),
    webhookUri: z.string().optional().describe('Webhook endpoint URI'),
    enabledWebhookEvents: z.array(z.string()).optional().describe('List of webhook event types to deliver'),
    apiBaseUri: z.string().optional().describe('[INTERNAL] Deprecated: API base URI'),
    apiRequestHeaders: z.record(z.string(), z.any()).optional().describe('[INTERNAL] Deprecated: API request headers'),
    apiRequestQuery: z.record(z.string(), z.any()).optional().describe('[INTERNAL] Deprecated: API request query'),
    archivedAt: zodDateCoercion().optional().describe('[INTERNAL] Archive timestamp'),
    settings: WorkspaceSettingsSchema.optional().describe('Workspace settings'),
    alertDeliverySettings: AlertDeliverySettingsSchema.optional().describe('Alert delivery configuration'),
    type: z.nativeEnum(WorkspaceType).optional().describe('Workspace type (production or development)'),
    jwksUri: z.string().nullable().optional().describe('JWKS URI for JWT verification'),
    featureFlags: z.array(z.string()).optional().describe('[INTERNAL] Feature flags'),
    isReadOnly: z.boolean().optional().describe('[INTERNAL] Whether the workspace is in read-only mode'),
});
const UpdateWorkspaceLimitsRequest = WorkspaceLimitsSchema.partial();
const GenerateWorkspaceAccessTokenRequest = z.object({
    isAdmin: z.boolean().optional().describe('Whether to generate an admin token'),
    tenantId: z.string().optional().describe('Customer ID to scope the token to'),
    tenantKey: z.string().optional().describe('Customer key to scope the token to (resolved to tenantId)'),
    name: z.string().optional().describe('Name for the generated token'),
    fields: z.record(z.string(), z.any()).optional().describe('Custom fields to include in the token'),
    connectedProductId: z.string().optional().describe('[INTERNAL] Connected product ID'),
    expiresIn: z
        .number()
        .int()
        .positive()
        .max(3600 * 24 * 90)
        .optional()
        .describe('[INTERNAL] Token lifetime in seconds (default: 1 hour, max: 90 days)'),
});
const GenerateWorkspaceAccessTokenResponse = z.object({
    token: z.string().describe('The generated access token'),
});
const GetWorkspaceQuery = IncludeArchivedQuery;
const MembraneCredentials = z.object({
    apiUri: z.string().describe('Uri for Membrane API'),
    workspaceKey: z.string().describe('Workspace key'),
    workspaceSecret: z.string().optional().describe('Workspace secret for signing tokens (never expose in browser code)'),
    tenantKey: z.string().optional().describe('Tenant key for the current test tenant'),
    notes: z.string().optional().describe('Usage notes'),
});

const PARALLEL_EXECUTION_LIMITS = [
    'parallelEventPulls',
    'parallelIncrementalEventPullsPerConnection',
    'parallelFullSyncEventPullsPerConnection',
    'parallelFlowRuns',
    'parallelApiRequests',
    'parallelSseRequests',
    'parallelBackgroundJobs',
    'parallelEventProcessingJobs',
    'parallelEventProcessingJobsPerConnection',
    'parallelAgentSessions',
    'parallelCustomCodeRuns',
];
const RATE_LIMITS = [
    'fileUploadsMbPerHour',
    'apiRequestsPerSecond',
    'apiRequestsPerHour',
    'webhookRequestsPerSecond',
    'webhookRequestsPerHour',
    'workspaceElementCreationsPerHour',
    'workspaceElementCreationsPerSecond',
];
const WORKSPACE_SIZE_LIMITS = [
    'totalNumberOfCustomers',
    'totalNumberOfConnections',
    'totalNumberOfWorkspaceElements',
    'totalNumberOfWorkspaceDatabaseRecords',
    'flowRunsQueueSizePerConnection',
];
const EDITABLE_LIMITS = [
    'parallelApiRequestsPerCustomer',
    'parallelBackgroundJobsPerCustomer',
    'parallelCustomCodeRunsPerCustomer',
    'apiRequestsPerCustomerPerSecond',
    'apiRequestsPerCustomerPerHour',
    'externalEventsPerCustomerPerDay',
    'webhookRequestsPerCustomerPerSecond',
    'webhookRequestsPerCustomerPerHour',
    'totalNumberOfDatabaseEntitiesPerCustomer',
    'parallelFlowRunsPerConnection',
];

var WorkspaceElementChangeType;
(function (WorkspaceElementChangeType) {
    WorkspaceElementChangeType["CREATE"] = "create";
    WorkspaceElementChangeType["UPDATE"] = "update";
    WorkspaceElementChangeType["DELETE"] = "delete";
})(WorkspaceElementChangeType || (WorkspaceElementChangeType = {}));
function compareWorkspaceExports(baseExport, targetExport, options = { includeDeletions: true }) {
    const changes = {};
    const comparison = {
        [WorkspaceElementChangeType.CREATE]: new Set(),
        [WorkspaceElementChangeType.UPDATE]: new Set(),
        [WorkspaceElementChangeType.DELETE]: new Set(),
    };
    const sourceUuidByTargetUuid = new Map();
    const diffs = [];
    const integrationMap = buildIntegrationsMap([
        ...(baseExport[WorkspaceElementType.Integration] || []),
        ...(targetExport[WorkspaceElementType.Integration] || []),
    ]);
    const baseMap = new Map();
    const targetMap = new Map();
    const allSelectors = new Set();
    const allElementTypes = new Set([...Object.keys(baseExport), ...Object.keys(targetExport)]);
    for (const elementType of allElementTypes) {
        const type = elementType;
        const baseElements = baseExport[type] || [];
        for (const element of baseElements) {
            const intIdentifier = getIntegrationIdentifier(element, integrationMap);
            const selector = getElementSelector(type, element.key, intIdentifier);
            baseMap.set(selector, { type, element });
            allSelectors.add(selector);
        }
        const targetElements = targetExport[type] || [];
        for (const element of targetElements) {
            const intIdentifier = getIntegrationIdentifier(element, integrationMap);
            const selector = getElementSelector(type, element.key, intIdentifier);
            targetMap.set(selector, { type, element });
            allSelectors.add(selector);
        }
    }
    const baseSelectorByUuid = new Map();
    for (const [selector, base] of baseMap) {
        if (base.element.uuid)
            baseSelectorByUuid.set(base.element.uuid, selector);
    }
    const pairs = [];
    const uuidPairedBaseSelectors = new Set();
    const uuidPairedTargetSelectors = new Set();
    for (const [selector, target] of targetMap) {
        const uuid = target.element.uuid;
        if (!uuid)
            continue;
        const baseSelector = baseSelectorByUuid.get(uuid);
        if (baseSelector === undefined || uuidPairedBaseSelectors.has(baseSelector))
            continue;
        const base = baseMap.get(baseSelector);
        if (base.type !== target.type)
            continue;
        pairs.push({ base, target });
        uuidPairedBaseSelectors.add(baseSelector);
        uuidPairedTargetSelectors.add(selector);
    }
    for (const selector of allSelectors) {
        const base = baseMap.get(selector);
        const target = targetMap.get(selector);
        const baseFree = base !== undefined && !uuidPairedBaseSelectors.has(selector);
        const targetFree = target !== undefined && !uuidPairedTargetSelectors.has(selector);
        if (baseFree && targetFree) {
            pairs.push({ base, target });
        }
        else if (baseFree) {
            pairs.push({ base });
        }
        else if (targetFree) {
            pairs.push({ target });
        }
    }
    for (const { base, target } of pairs) {
        if (base && target && base.element.uuid !== target.element.uuid) {
            sourceUuidByTargetUuid.set(target.element.uuid, base.element.uuid);
        }
        const change = compareElementExports(base === null || base === void 0 ? void 0 : base.element, target === null || target === void 0 ? void 0 : target.element);
        if (options.includeDeletions === false && (change === null || change === void 0 ? void 0 : change.type) === WorkspaceElementChangeType.DELETE) {
            continue;
        }
        if (!change) {
            continue;
        }
        const subject = target || base;
        const integrationKey = getIntegrationIdentifier(subject.element, integrationMap);
        const path = getMembraneElementPath(subject.type, subject.element.key, integrationKey);
        changes[change.uuid] = { elementType: subject.type, path };
        comparison[change.type].add(change.uuid);
        if (options.includeDiff) {
            const basePath = base
                ? getMembraneElementPath(base.type, base.element.key, getIntegrationIdentifier(base.element, integrationMap))
                : path;
            const baseYaml = base ? yaml.dump(base.element, { sortKeys: true }) : '';
            const targetYaml = target ? yaml.dump(target.element, { sortKeys: true }) : '';
            diffs.push(createTwoFilesPatch(`a/${basePath}`, `b/${path}`, baseYaml, targetYaml));
        }
    }
    const result = { comparison, changes, sourceUuidByTargetUuid };
    if (options.includeDiff) {
        result.diff = diffs.join('\n').trim();
    }
    return result;
}
function compareElementExports(baseElement, targetElement) {
    if (baseElement && !targetElement) {
        return {
            type: WorkspaceElementChangeType.DELETE,
            uuid: baseElement.uuid,
        };
    }
    if (!baseElement && targetElement) {
        return {
            type: WorkspaceElementChangeType.CREATE,
            uuid: targetElement.uuid,
        };
    }
    if (baseElement && targetElement && !deepEqual(baseElement, targetElement)) {
        return {
            type: WorkspaceElementChangeType.UPDATE,
            uuid: targetElement.uuid,
        };
    }
    return undefined;
}
function emptyComparisonResult() {
    return {
        comparison: {
            [WorkspaceElementChangeType.CREATE]: new Set(),
            [WorkspaceElementChangeType.UPDATE]: new Set(),
            [WorkspaceElementChangeType.DELETE]: new Set(),
        },
        changes: {},
        sourceUuidByTargetUuid: new Map(),
    };
}
function mergeComparisons(dest, src) {
    for (const changeType of Object.values(WorkspaceElementChangeType)) {
        for (const uuid of src.comparison[changeType])
            dest.comparison[changeType].add(uuid);
    }
    Object.assign(dest.changes, src.changes);
    for (const [k, v] of src.sourceUuidByTargetUuid)
        dest.sourceUuidByTargetUuid.set(k, v);
    if (src.diff)
        dest.diff = dest.diff ? `${dest.diff}\n${src.diff}` : src.diff;
    return dest;
}
function compareElementMaps(baseElements, targetElements, type, options = { includeDeletions: true }) {
    const changes = {};
    const comparison = {
        [WorkspaceElementChangeType.CREATE]: new Set(),
        [WorkspaceElementChangeType.UPDATE]: new Set(),
        [WorkspaceElementChangeType.DELETE]: new Set(),
    };
    const sourceUuidByTargetUuid = new Map();
    const diffs = [];
    const allPaths = new Set([...baseElements.keys(), ...targetElements.keys()]);
    for (const path of allPaths) {
        const base = baseElements.get(path);
        const target = targetElements.get(path);
        if (base && target && base.uuid !== target.uuid) {
            sourceUuidByTargetUuid.set(target.uuid, base.uuid);
        }
        const change = compareElementExports(base, target);
        if (options.includeDeletions === false && (change === null || change === void 0 ? void 0 : change.type) === WorkspaceElementChangeType.DELETE)
            continue;
        if (!change)
            continue;
        changes[change.uuid] = { elementType: type, path };
        comparison[change.type].add(change.uuid);
        if (options.includeDiff) {
            const baseYaml = base ? yaml.dump(base, { sortKeys: true }) : '';
            const targetYaml = target ? yaml.dump(target, { sortKeys: true }) : '';
            diffs.push(createTwoFilesPatch(`a/${path}`, `b/${path}`, baseYaml, targetYaml));
        }
    }
    const result = { comparison, changes, sourceUuidByTargetUuid };
    if (options.includeDiff) {
        result.diff = diffs.join('\n').trim();
    }
    return result;
}
function getIntegrationIdentifier(element, integrationMap) {
    let integrationIdentifier;
    if (element.integrationUuid) {
        integrationIdentifier = integrationMap.get(element.integrationUuid);
    }
    if (element.integrationId) {
        integrationIdentifier = integrationMap.get(element.integrationId);
    }
    return integrationIdentifier || element.integrationUuid || element.integrationId;
}
function getElementSelector(type, key, integrationIdentifier) {
    if (integrationIdentifier) {
        return `${type}:${key}:${integrationIdentifier}`;
    }
    return `${type}:${key}`;
}
function buildIntegrationsMap(integrations) {
    const map = new Map();
    for (const integration of integrations) {
        if (integration.uuid && integration.key) {
            map.set(integration.uuid, integration.key);
            continue;
        }
        if (integration.id && integration.key) {
            map.set(integration.id.toString(), integration.key);
        }
    }
    return map;
}

const IneligibilityReasonSchema = z.enum(['disposable', 'blockedDomain']);
const PlatformUser = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    trialRequested: z.boolean(),
    isEligible: z.boolean().optional(),
    ineligibilityReason: IneligibilityReasonSchema.optional(),
    emailVerified: z.boolean().optional(),
});
const FullPlatformUser = z.object({
    id: z.string(),
    auth0Id: z.string().optional(),
    email: z.string(),
    name: z.string(),
    defaultAccountId: z.string().optional(),
    defaultWorkspaceId: z.string().optional(),
    isAdmin: z.boolean().optional(),
    trialRequested: z.boolean(),
    pat: z.string().optional(),
    isEligible: z.boolean().optional(),
    ineligibilityReason: IneligibilityReasonSchema.optional(),
    emailVerified: z.boolean().optional(),
    isCompanyEmail: z.boolean().optional(),
});

var OrgLimitsType;
(function (OrgLimitsType) {
    OrgLimitsType["NUMBER_OF_WORKSPACES"] = "numberOfWorkspaces";
    OrgLimitsType["TODAY_USAGE"] = "todayUsage";
    OrgLimitsType["LAST_THIRTY_DAY_USAGE"] = "lastThirtyDayUsage";
    OrgLimitsType["MEMBRANE_EXPERT_CREDITS_CAP"] = "membraneExpertCreditsCapPerMonth";
    OrgLimitsType["DEFAULT_TENANT_AI_CREDITS_ROLLING_30_DAY_LIMIT"] = "defaultTenantAiCreditsRolling30DayLimit";
})(OrgLimitsType || (OrgLimitsType = {}));
var OrgPlan;
(function (OrgPlan) {
    OrgPlan["Core"] = "core";
    OrgPlan["Business"] = "business";
    OrgPlan["Enterprise"] = "enterprise";
})(OrgPlan || (OrgPlan = {}));
var OrgUserRole;
(function (OrgUserRole) {
    OrgUserRole["Admin"] = "admin";
    OrgUserRole["Member"] = "member";
    OrgUserRole["ReadOnly"] = "read-only";
})(OrgUserRole || (OrgUserRole = {}));
var UserWorkspaceRole;
(function (UserWorkspaceRole) {
    UserWorkspaceRole["Admin"] = "admin";
    UserWorkspaceRole["Member"] = "member";
    UserWorkspaceRole["ReadOnly"] = "read-only";
})(UserWorkspaceRole || (UserWorkspaceRole = {}));
var OrgUserStatus;
(function (OrgUserStatus) {
    OrgUserStatus["Invited"] = "invited";
    OrgUserStatus["Active"] = "active";
})(OrgUserStatus || (OrgUserStatus = {}));
const UserWorkspaceAccess = z.object({
    workspaceId: z.string(),
    role: z.enum(UserWorkspaceRole),
});
const OrgLimits = z.object({
    numberOfWorkspaces: z.number().optional(),
    todayUsage: z.number().optional(),
    lastThirtyDayUsage: z.number().optional(),
    membraneAgentLastThirtyDaysUsage: z.number().optional(),
    membraneExpertCreditsCapPerMonth: z.number().nullable().optional(),
    defaultTenantAiCreditsRolling30DayLimit: z
        .number()
        .min(0)
        .nullable()
        .optional()
        .describe('[INTERNAL] Default AI credits rolling 30-day limit per tenant'),
});
const MembraneAgentKey = z
    .object({
    key: z.string().optional(),
    expiresAt: z.string().optional(),
})
    .optional();
const Org = z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    plan: z.enum(OrgPlan).optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    trialEndDate: z.string().optional(),
    lastTrialExtensionDate: z.string().optional(),
    limits: OrgLimits.optional(),
    thirtyDayTotalCredits: z.number().optional(),
    lastThirtyDayUsagePercent: z.number().optional(),
    todayUsagePercent: z.number().optional(),
    domains: z.array(z.string()).optional(),
    featureFlags: z.array(z.string()).optional(),
    freeAiCredits: z.number().optional(),
    paidAiCredits: z.number().optional(),
    freeAiMonthlyCredits: z.number().nullable().optional(),
    stripeCustomerId: z.string().optional(),
    autoChargeEnabled: z.boolean().optional(),
    autoChargeThreshold: z.number().optional(),
    autoChargePurchaseAmount: z.number().optional(),
    autoChargeMonthlyLimit: z.number().optional(),
    freeEngineCredits: z.number().optional(),
    paidEngineCredits: z.number().optional(),
    freeEngineMonthlyCredits: z.number().nullable().optional(),
    engineAutoChargeEnabled: z.boolean().optional(),
    engineAutoChargeThreshold: z.number().optional(),
    engineAutoChargePurchaseAmount: z.number().optional(),
    engineAutoChargeMonthlyLimit: z.number().optional(),
    currentUserCanLeave: z
        .boolean()
        .optional()
        .describe('[INTERNAL] Whether the current user can leave this org (false if sole admin)'),
});
const EngineCreditsProjectionResponse = z.object({
    averageDailyUsage: z.number(),
    daysRemainingInMonth: z.number(),
    totalBudget: z.number(),
    budgetBreakdown: z.object({
        currentCredits: z.number(),
        freeMonthlyRefill: z.number(),
        autoPurchaseBudget: z.number(),
    }),
    dailyProjections: z.array(z.object({
        date: z.string(),
        projectedUsage: z.number(),
        projectedBalance: z.number(),
    })),
    daysUntilExhaustion: z.number().nullable(),
    willRunOut: z.boolean(),
});
const WorkspaceEditableFields = z.object({
    limits: WorkspaceLimitsSchema.optional(),
    settings: WorkspaceSettingsSchema.optional(),
    publicKeys: z.array(WorkspacePublicKey).optional(),
    userFieldsSchema: z.any().optional(),
    webhookUri: z.string().optional(),
    enabledWebhookEvents: z.array(z.string()).optional(),
    alertDeliverySettings: AlertDeliverySettingsSchema.optional(),
    jwksUri: z.string().nullable().optional(),
    type: z.enum(WorkspaceType).optional().describe('Workspace type (production or development)'),
    isReadOnly: z.boolean().optional().describe('Whether the workspace is in read-only mode'),
});
const OrgWorkspace = z
    .object({
    id: z.string(),
    name: z.string(),
    orgId: z.string(),
    apiBaseUri: z.string().optional().describe('[INTERNAL] API base URI'),
    key: z.string().describe('Unique workspace key'),
    secret: z.string().describe('[INTERNAL] Workspace secret'),
    createdAt: zodDateCoercion(),
    updatedAt: zodDateCoercion(),
    engineAccessToken: z.string().optional().describe('[INTERNAL] Engine access token'),
    trialEndDate: z.string().optional().describe('[INTERNAL] Trial end date'),
    featureFlags: z.array(z.string()).optional().describe('[INTERNAL] Feature flags'),
    logoUri: z.string().optional().describe('URI for the workspace logo'),
    membraneInstanceId: z.string().optional().describe('[INTERNAL] Membrane instance ID'),
    operatorLimits: WorkspaceLimitsSchema.optional().describe('[INTERNAL] Operator-set limit overrides stored on the hub. Mirrored to the instance via PUT /workspaces/:key/limits.'),
    operatorLimitsSyncedAt: zodDateCoercion()
        .optional()
        .describe('[INTERNAL] Last time operatorLimits were successfully mirrored to the owning instance.'),
    operatorLimitsSyncError: z
        .string()
        .optional()
        .describe('[INTERNAL] Last error message from a failed mirror attempt; cleared after a successful sync.'),
})
    .merge(WorkspaceEditableFields);
const OrgWorkspaceUpdateRequest = z
    .object({
    name: z.string().min(1).optional(),
    logoUri: z.string().optional(),
})
    .merge(WorkspaceEditableFields);
const WorkspaceOnboardingData = z
    .object({
    productSetup: z
        .object({
        type: ConnectedProductType.optional(),
        agentType: z.string().optional(),
        name: z.string().optional(),
        connectedProductId: z.string().optional(),
    })
        .optional(),
    connections: z
        .object({
        apps: z.array(z.object({
            externalAppId: z.string().optional(),
            name: z.string().optional(),
            url: z.string().optional(),
            status: z.enum(['pending', 'connected', 'skipped']).optional(),
        })),
    })
        .optional(),
})
    .passthrough();
const OrgWorkspaceUser = z.object({
    id: z.string(),
    workspaceId: z.string(),
    testCustomerId: z.string(),
    userId: z.string(),
    role: z.string(),
    onboardingData: WorkspaceOnboardingData.optional().describe('[INTERNAL]'),
});
const PatchUserWorkspaceSettings = z.object({
    onboardingData: WorkspaceOnboardingData.optional().describe('[INTERNAL]'),
});
const CreateOrgRequest = z.object({
    name: z.string(),
    website: z.string().optional(),
    logoUri: z.string().optional(),
    integrationApps: z.string().optional(),
    firstIntegrationUseCase: z.string().optional(),
    referralSource: z.string().optional(),
    useCase: z.string().max(1024).optional(),
    builder: z.array(z.enum(['me', 'team'])).optional(),
    stripeCustomerId: z.string().optional(),
    signupSource: z.string().optional(),
});
const UpdateOrgRequest = z.object({
    name: z.string().min(1).optional(),
    domains: z.array(z.string()).optional(),
});
const BaseOrgUser = z.object({
    id: z.string(),
    orgId: z.string(),
    userId: z.string(),
    role: z.string(),
    status: z.string(),
    workspaces: z.array(z.string()).optional(),
    workspacesWithRoles: z.array(UserWorkspaceAccess).optional(),
});
const FullOrgUser = BaseOrgUser.extend({
    user: FullPlatformUser,
    org: Org,
    workspaces: z.array(OrgWorkspace).or(z.array(z.string())).optional(),
});
const CreateOrgUserRequest = z.object({
    email: z.string(),
    workspaces: z.array(z.string()).optional(),
    workspacesWithRoles: z.array(UserWorkspaceAccess).optional(),
});
const UpdateOrgUserRequest = z.object({
    role: z.enum(OrgUserRole).optional(),
    status: z.enum(OrgUserStatus).optional(),
    workspaces: z.array(z.string()).optional(),
    workspacesWithRoles: z.array(UserWorkspaceAccess).optional(),
});
const OrgInvitation = z.object({
    id: z.string(),
    orgId: z.string(),
    org: Org,
    name: z.string(),
    workspaces: z.array(z.string()).optional(),
    uuid: z.string(),
    email: z.string().optional(),
});
const UpdateOrgInvitationRequest = z.object({
    name: z.string().optional(),
    workspaces: z.array(z.string()).optional(),
});
const CreateOrgInvitationRequest = UpdateOrgInvitationRequest.extend({
    email: z.string().optional(),
});
const FindOrgsQuery = z.object({
    search: z.string().optional(),
    myOrgs: zodBooleanCoercion().optional(),
});
const FindOrgWorkspacesQuery = z.object({
    search: z.string().optional(),
    cursor: z.coerce.number().optional(),
    limit: z.coerce.number().optional(),
});
const ListOrgWorkspacesQuery = FindOrgWorkspacesQuery.extend({
    orgId: z.string().optional().describe('Filter by organization ID'),
});
const ListOrgWorkspacesResponse = z.object({
    items: z.array(OrgWorkspace),
    cursor: z.string().optional(),
});
const CreateOrgWorkspaceRequest = z.object({
    name: z.string().min(1).optional().describe('Name of the workspace'),
    logoUri: z.string().optional().describe('URI for the workspace logo'),
    orgId: z.string().describe('Organization ID to create the workspace in'),
    membraneInstanceId: z.string().optional().describe('[INTERNAL] Membrane instance ID'),
});
const RotateOrgWorkspaceSecretResponse = z.object({
    secret: z.string().describe('The new workspace secret'),
});
const GenerateOrgAccessTokenRequest = z.object({
    expiresIn: z
        .number()
        .int()
        .positive()
        .max(3600 * 24 * 90)
        .optional()
        .describe('Token lifetime in seconds (default: 30 days, max: 90 days)'),
    name: z.string().optional().describe('Name identifying the token holder in logs (carried in the token subject)'),
});
const GenerateOrgAccessTokenResponse = z.object({
    token: z.string().describe('The generated read-only organization access token'),
});
const AccountResponse = z.object({
    user: FullPlatformUser.optional(),
    workspace: OrgWorkspace.optional(),
    hasWorkspaces: z.boolean().optional(),
    workspaceUser: OrgWorkspaceUser.extend({
        testCustomerId: z.string(),
    }).optional(),
    engineTestUser: BaseCustomer.optional(),
    testCustomer: BaseCustomer.optional(),
    engineApp: AppSchema.optional(),
    superAdminToken: z.string().optional().nullable(),
    orgs: z.array(Org).optional(),
    permissions: z.array(z.string()).optional(),
    authContextPermissions: AuthContextPermissionsSchema.optional(),
    featureFlags: z.any().optional(),
    message: z.string().optional(),
    error: z.string().optional(),
});

var WebhookTypeEnum;
(function (WebhookTypeEnum) {
    WebhookTypeEnum["USER_INVITED_TO_ORG"] = "user-invited-to-org";
    WebhookTypeEnum["ORG_ACCESS_REQUESTED"] = "org-access-requested";
    WebhookTypeEnum["ORG_CREATED"] = "org-created";
    WebhookTypeEnum["TASK_CREATED"] = "task-created";
    WebhookTypeEnum["TASK_UPDATED"] = "task-updated";
    WebhookTypeEnum["TASK_ACTIVITY_CREATED"] = "task-activity-created";
    WebhookTypeEnum["CONNECTOR_VERSION_PUBLISHED"] = "connector-version-published";
    WebhookTypeEnum["AGENT_SESSION_FINISHED"] = "agent-session-finished";
    WebhookTypeEnum["SEND_ALERT_EMAIL"] = "send-alert-email";
    WebhookTypeEnum["PUBLIC_PACKAGE_ACTION_RUN_FAILED"] = "public-package-action-run-failed";
    WebhookTypeEnum["BETTER_AUTH_SIGNUP_VERIFY"] = "better-auth-signup-verify";
    WebhookTypeEnum["BETTER_AUTH_RESET_PASSWORD"] = "better-auth-reset-password";
})(WebhookTypeEnum || (WebhookTypeEnum = {}));

const IntegrationSpecificElementSelector = z$1.object({
    id: z$1.string().optional(),
    key: z$1.string().optional(),
    integrationId: z$1.string().optional(),
    integrationKey: z$1.string().optional(),
});
class ElementAccessor {
    constructor(options) {
        this.options = options;
    }
    getUniqueIdentifier() {
        return this.getPath();
    }
    async get() {
        return this.options.client.get(this.getPath());
    }
    async put(data) {
        return this.options.client.put(this.getPath(), data);
    }
    async patch(data) {
        return this.options.client.patch(this.getPath(), data);
    }
    async archive() {
        await this.options.client.delete(this.getPath());
    }
    async restore() {
        return this.options.client.post(this.getPath('restore'));
    }
    getPath(operation = '') {
        const { path, selector } = this.options;
        if (typeof selector === 'string') {
            return `${path}s/${selector}${operation ? '/' + operation : ''}`;
        }
        const query = qs.stringify(selector);
        return `${path}${operation ? '/' + operation : ''}?${query}`;
    }
}
class ElementListAccessor {
    constructor(client, path) {
        this.client = client;
        this.path = path;
    }
    async find(query) {
        return this.client.get(this.path, query);
    }
    async findAll(query) {
        let allItems = [];
        let currentCursor = '0';
        while (true) {
            const { items, cursor } = await this.client.get(this.path, {
                ...query,
                limit: 1000,
                cursor: currentCursor,
            });
            allItems = allItems.concat(items);
            if (!cursor) {
                break;
            }
            else {
                currentCursor = cursor;
            }
        }
        return allItems;
    }
    async create(data) {
        return this.client.post(this.path, data);
    }
}
class ElementInstanceListAccessor {
    constructor(client, path) {
        this.client = client;
        this.path = path;
    }
    async find(query) {
        return this.client.get(this.path, query);
    }
}
class ElementInstanceAccessor {
    constructor(options) {
        this.options = options;
    }
    getUniqueIdentifier() {
        return this.getPath();
    }
    async get() {
        return this.options.client.get(this.getPath());
    }
    async getId() {
        if (!this.id) {
            if (this.options.selector && typeof this.options.selector === 'string') {
                this.id = this.options.selector;
            }
            else {
                const element = await this.get();
                if (!element) {
                    throw new Error(`Element not found using selector ${JSON.stringify(this.options.selector)}`);
                }
                this.id = element.id;
            }
        }
        return this.id;
    }
    async create(data) {
        if (this.id) {
            throw new Error('Cannot create: element already exists');
        }
        const element = await this.options.client.post(this.getPath(), data);
        this.id = element.id;
        return element;
    }
    async put(data) {
        return this.options.client.put(this.getPath(), data);
    }
    async patch(data) {
        return this.options.client.patch(this.getPath(), data);
    }
    async archive() {
        return this.options.client.delete(this.getPath());
    }
    async delete() {
        return this.options.client.delete(this.getPath());
    }
    async restore() {
        return this.options.client.post(this.getPath('restore'));
    }
    getPath(operation = '') {
        return this.options.instancePath + (operation ? `/${operation}` : '') + '?' + qs.stringify(this.getSelector());
    }
    getSelector() {
        return typeof this.options.selector === 'string'
            ? {
                id: this.options.selector,
            }
            : this.options.selector;
    }
}

const warned = new Set();
function warnDeprecatedOnce(key, message) {
    if (warned.has(key))
        return;
    warned.add(key);
    console.warn(`[Membrane deprecation] ${message}`);
}

class ConnectionLevelElementAccessor {
    constructor(client, connectionSelector, selector, elementKey, query) {
        this.client = client;
        this.connectionSelector = connectionSelector;
        this.selector = selector;
        this.elementKey = elementKey;
        this.query = query;
        this.endpoint = `/connections/${this.connectionSelector}/${elementKey}/${selector}`;
    }
    uri(path = '', query = {}) {
        return this.endpoint + path + '?' + qs.stringify({ ...this.query, ...query });
    }
    getUniqueIdentifier() {
        return this.endpoint;
    }
    async get(query = {}) {
        return this.client.get(this.uri('', query));
    }
    async create() {
        return this.client.put(this.uri());
    }
    async patch(data) {
        return this.client.patch(this.uri(), data);
    }
    async put(data) {
        return this.client.put(this.uri(), data);
    }
    async archive() {
        return this.client.delete(this.uri());
    }
    async reset() {
        return this.client.post(this.uri('/reset'));
    }
    async setup() {
        return this.client.post(this.uri('/setup'));
    }
}
class ConnectionLevelElementsListAccessor {
    constructor(client, connectionSelector, elementKey) {
        this.client = client;
        this.connectionSelector = connectionSelector;
        this.elementKey = elementKey;
    }
    async list(query) {
        const endpoint = `/connections/${this.connectionSelector}/${this.elementKey}?${qs.stringify(query !== null && query !== void 0 ? query : {})}`;
        return this.client.get(endpoint);
    }
}

class IntegrationLevelElementAccessor {
    constructor(client, integrationSelector, elementSelector, elementKey) {
        this.client = client;
        this.integrationSelector = integrationSelector;
        this.elementSelector = elementSelector;
        this.elementKey = elementKey;
        this.endpoint = `/integrations/${this.integrationSelector}/${elementKey}/${elementSelector}`;
    }
    getUniqueIdentifier() {
        return this.endpoint;
    }
    async get() {
        return this.client.get(this.endpoint);
    }
    async patch(data) {
        return this.client.patch(this.endpoint, data);
    }
    async put(data) {
        return this.client.put(this.endpoint, data);
    }
    async archive() {
        return this.client.delete(this.endpoint);
    }
    async reset() {
        return this.client.post(this.endpoint + '/reset');
    }
}
class IntegrationLevelElementsListAccessor {
    constructor(client, integrationSelector, elementKey) {
        this.client = client;
        this.integrationSelector = integrationSelector;
        this.elementKey = elementKey;
        this.endpoint = `/integrations/${integrationSelector}/${elementKey}`;
    }
    async list(query) {
        return this.client.get(this.endpoint, query !== null && query !== void 0 ? query : {});
    }
    async create(data) {
        return this.client.post(this.endpoint, data);
    }
}

class ActionsAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'actions');
    }
}
class ActionAccessor extends ElementAccessor {
    constructor(client, selector) {
        super({
            client,
            selector,
            path: 'action',
        });
    }
    async reset() {
        return await this.options.client.post(this.getPath('reset'));
    }
    async run(input, options) {
        warnDeprecatedOnce('ActionAccessor.run', 'ActionAccessor.run() is deprecated. Use POST /act with { action: { id } } or { integrationKey, key } instead.');
        const queryParams = options
            ? {
                integrationKey: options.integrationKey,
                connectionId: options.connectionId,
                connectionKey: options.connectionKey,
            }
            : {};
        return this.options.client.post(this.getPath('run'), { input }, { params: queryParams });
    }
}
class IntegrationLevelActionAccessor extends IntegrationLevelElementAccessor {
    constructor(client, integrationSelector, actionSelector) {
        super(client, integrationSelector, actionSelector, 'actions');
        this.client = client;
        this.integrationSelector = integrationSelector;
        this.actionSelector = actionSelector;
    }
}
class IntegrationLevelActionsListAccessor extends IntegrationLevelElementsListAccessor {
    constructor(client, integrationSelector) {
        super(client, integrationSelector, 'actions');
        this.client = client;
        this.integrationSelector = integrationSelector;
    }
}
class ConnectionLevelActionAccessor extends ConnectionLevelElementAccessor {
    constructor(client, connectionSelector, actionSelector, query) {
        super(client, connectionSelector, actionSelector, 'actions', query);
        this.client = client;
        this.connectionSelector = connectionSelector;
        this.actionSelector = actionSelector;
        this.query = query;
    }
    async run(input) {
        warnDeprecatedOnce('ConnectionLevelActionAccessor.run', 'ConnectionLevelActionAccessor.run() is deprecated. Use POST /act with { action: { id } } or { integrationKey, key } instead.');
        return this.client.post(this.uri('/run'), input);
    }
}
class ConnectionLevelActionsAccessor extends ConnectionLevelElementsListAccessor {
    constructor(client, connectionSelector) {
        super(client, connectionSelector, 'actions');
        this.client = client;
        this.connectionSelector = connectionSelector;
    }
}

const POPUP_ELEMENT_ID = '__integration-app-popup';
const CONTAINER_ELEMENT_ID = '__integration-app-container';
const CONTAINER_ELEMENT_CLASS = '__integration-app-container';
const CONTAINER_ELEMENT_CLASS__LOADER = '__integration-app-container__loader';
const BODY_CLASS = '__integration-app-open';
const STYLES_ELEMENT_ID = '__integration-app-embed-styles';
const TIMEOUT_MESSAGE_ELEMENT_ID = '__integration-app-timeout-message';
const LOADER_SVG_ELEMENT_ID = '__integration-app-loader-svg';
const SLOW_CONNECTION_TIMEOUT = 5000;
const CONNECTION_FAILED_TIMEOUT = 30000;
const HANDSHAKE_AFTER_LOAD_TIMEOUT = 3000;
const IFRAME_SHOW_LOADER_MINIMAL_TIME = 500;
const IFRAME_LAYOUT_SHIFT_TIME = 300;

function injectStyles(mountTargetSelector) {
    const hasCustomMountTarget = Boolean(mountTargetSelector && document.querySelector(mountTargetSelector));
    const styles = [
        `.${CONTAINER_ELEMENT_CLASS}:empty { display: none; }`,
        hasCustomMountTarget
            ? `.${CONTAINER_ELEMENT_CLASS} {
          inset: 0;
          background: rgba(0, 0, 0, 0);
          transition: background 0.2s ease-out;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          height: 100%;
        }`
            : `.${CONTAINER_ELEMENT_CLASS} {
          position: fixed;
          inset: 0;
          z-index: 2147483647;
          display: flex;
          justify-content: center;
          align-items: center;
          pointer-events: auto;
          background: rgba(0, 0, 0, 0);
          transition: background 0.2s ease-out;
        }`,
        `.${CONTAINER_ELEMENT_CLASS}.${CONTAINER_ELEMENT_CLASS__LOADER} {
      background: transparent;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }`,
        `.${CONTAINER_ELEMENT_CLASS} iframe {
      ${hasCustomMountTarget ? '' : 'position: absolute; inset: 0;'}
      width: 100%;
      height: 100%;
      border-width: 0;
      color-scheme: light;
    }`,
        `.${CONTAINER_ELEMENT_CLASS}.${CONTAINER_ELEMENT_CLASS__LOADER} iframe {
      visibility: hidden;
    }`,
        !hasCustomMountTarget ? `body.${BODY_CLASS} { overflow: hidden; }` : '',
        `#${LOADER_SVG_ELEMENT_ID} {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 4rem;
      height: 4rem;
      color: black;
    }`,
        `#${TIMEOUT_MESSAGE_ELEMENT_ID} {
      position: absolute;
      top: calc(50% + 3rem);
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      font-family: system-ui, -apple-system, sans-serif;
    }`,
        `#${TIMEOUT_MESSAGE_ELEMENT_ID} p {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 500;
      color: #52525b;
      text-align: center;
    }`,
        `#${TIMEOUT_MESSAGE_ELEMENT_ID}.error p {
      color: #dc2626;
    }`,
        `#${TIMEOUT_MESSAGE_ELEMENT_ID} button {
      padding: 0.5rem 1rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: white;
      background: #18181b;
      border: none;
      border-radius: 0.375rem;
      cursor: pointer;
      transition: background 0.15s;
    }`,
        `#${TIMEOUT_MESSAGE_ELEMENT_ID} button:hover {
      background: #27272a;
    }`,
    ];
    let styleElement = document.getElementById(STYLES_ELEMENT_ID);
    if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = STYLES_ELEMENT_ID;
        styleElement.type = 'text/css';
        document.head.appendChild(styleElement);
    }
    styleElement.textContent = styles.join('\n');
}
function removeInjectedStyles() {
    var _a;
    (_a = document.getElementById(STYLES_ELEMENT_ID)) === null || _a === void 0 ? void 0 : _a.remove();
}

function getContainer() {
    return document.getElementById(CONTAINER_ELEMENT_ID);
}
function createContainer() {
    const container = document.createElement('div');
    container.id = CONTAINER_ELEMENT_ID;
    container.classList.add(CONTAINER_ELEMENT_CLASS);
    return container;
}
function appendToContainer(el) {
    var _a;
    (_a = getContainer()) === null || _a === void 0 ? void 0 : _a.appendChild(el);
}
function deleteContainer() {
    var _a;
    (_a = getContainer()) === null || _a === void 0 ? void 0 : _a.remove();
    removeInjectedStyles();
}
function handleIframeUnmount(iframe) {
    const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
            for (const removedNode of mutation.removedNodes) {
                if (removedNode instanceof HTMLElement && removedNode.id === POPUP_ELEMENT_ID) {
                    observer.disconnect();
                    deleteContainer();
                    return;
                }
            }
        }
    });
    const parent = iframe.parentNode;
    if (parent) {
        observer.observe(parent, { childList: true });
    }
    else {
        console.warn(`iframe with ID "${POPUP_ELEMENT_ID}" has no parent node`);
    }
}

let loaderStartTime = 0;
let loaderTimeoutId;
function createLoaderSvg() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = LOADER_SVG_ELEMENT_ID;
    svg.setAttribute('viewBox', '0 0 120 120');
    svg.setAttribute('fill', 'currentColor');
    svg.innerHTML = `
    <defs>
      <mask id="__membrane-mask-2">
        <rect x="0" y="0" width="120" height="120" fill="#fff" />
        <path
          d="M43.48 22.8008C43.48 21.8386 44.5223 21.2378 45.3549 21.72L89.7526 47.4331C90.2346 47.7123 90.5314 48.2271 90.5314 48.7841V120H88.6565H44.103C44.103 120 43.48 119.721 43.48 119.377V22.8008Z"
          fill="#000"
        >
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0; 0 -12; 0 0"
            dur="1.1s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
            begin="0.15s"
          />
        </path>
      </mask>
      <mask id="__membrane-mask-1">
        <rect x="0" y="0" width="120" height="120" fill="#fff" />
        <path
          d="M23.5 36.4509C23.5 35.4886 24.5423 34.8878 25.3749 35.3701L69.7726 61.0832C70.2546 61.3623 70.5514 61.8771 70.5514 62.4341L70.55 120H68.6764H37.5H23.5V36.4509Z"
          fill="#000"
        >
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0; 0 -12; 0 0"
            dur="1.1s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
            begin="0s"
          />
        </path>
      </mask>
    </defs>
    <g mask="url(#__membrane-mask-2)">
      <path
        d="M56.45 13.1708C56.45 12.2086 57.4923 11.6078 58.3249 12.09L102.723 37.8031C103.205 38.0823 103.501 38.5971 103.501 39.1541V90.5875C103.501 91.5497 102.459 92.1505 101.626 91.6683L57.073 65.865C56.6874 65.6417 56.45 65.2298 56.45 64.7842V13.1708Z"
        fill="currentColor"
      >
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0 0; 0 -12; 0 0"
          dur="1.1s"
          repeatCount="indefinite"
          calcMode="spline"
          keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
          begin="0.3s"
        />
      </path>
    </g>
    <g mask="url(#__membrane-mask-1)">
      <path
        d="M36.48 26.8008C36.48 25.8386 37.5223 25.2378 38.3549 25.72L82.7526 51.4331C83.2346 51.7123 83.5314 52.2271 83.5314 52.7841V104.218C83.5314 105.18 82.4891 105.781 81.6565 105.298L37.103 79.495C36.7174 79.2717 36.48 78.8598 36.48 78.4142V26.8008Z"
        fill="currentColor"
      >
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0 0; 0 -12; 0 0"
          dur="1.1s"
          repeatCount="indefinite"
          calcMode="spline"
          keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
          begin="0.15s"
        />
      </path>
    </g>
    <path
      d="M16.5 40.4509C16.5 39.4886 17.5423 38.8878 18.3749 39.3701L62.7726 65.0832C63.2546 65.3623 63.5514 65.8771 63.5514 66.4341V117.868C63.5514 118.83 62.5091 119.431 61.6764 118.948L17.123 93.145C16.7374 92.9217 16.5 92.5098 16.5 92.0642V40.4509Z"
      fill="currentColor"
    >
      <animateTransform
        attributeName="transform"
        type="translate"
        values="0 0; 0 -12; 0 0"
        dur="1.1s"
        repeatCount="indefinite"
        calcMode="spline"
        keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
        begin="0s"
      />
    </path>
  `;
    return svg;
}
function showIframeLoader() {
    const container = getContainer();
    container === null || container === void 0 ? void 0 : container.classList.add(CONTAINER_ELEMENT_CLASS__LOADER);
    if (container && !document.getElementById(LOADER_SVG_ELEMENT_ID)) {
        container.appendChild(createLoaderSvg());
    }
    loaderStartTime = Date.now();
}
function hideIframeLoader(waitLayoutShift = IFRAME_LAYOUT_SHIFT_TIME) {
    if (loaderTimeoutId) {
        clearTimeout(loaderTimeoutId);
    }
    const delta = Date.now() - loaderStartTime;
    if (delta >= IFRAME_SHOW_LOADER_MINIMAL_TIME) {
        setTimeout(() => {
            var _a;
            const container = getContainer();
            container === null || container === void 0 ? void 0 : container.classList.remove(CONTAINER_ELEMENT_CLASS__LOADER);
            (_a = document.getElementById(LOADER_SVG_ELEMENT_ID)) === null || _a === void 0 ? void 0 : _a.remove();
            loaderStartTime = 0;
        }, waitLayoutShift);
    }
    else {
        const adjustedWait = delta >= IFRAME_LAYOUT_SHIFT_TIME ? 0 : IFRAME_LAYOUT_SHIFT_TIME - delta;
        const timeoutTime = IFRAME_SHOW_LOADER_MINIMAL_TIME - delta;
        loaderTimeoutId = setTimeout(() => {
            hideIframeLoader(adjustedWait);
        }, timeoutTime);
    }
}

function removeTimeoutMessage() {
    var _a;
    (_a = document.getElementById(TIMEOUT_MESSAGE_ELEMENT_ID)) === null || _a === void 0 ? void 0 : _a.remove();
}
function showTimeoutMessage(message, onClose, isError = false) {
    removeTimeoutMessage();
    const container = getContainer();
    if (!container)
        return;
    const messageEl = document.createElement('div');
    messageEl.id = TIMEOUT_MESSAGE_ELEMENT_ID;
    if (isError) {
        messageEl.classList.add('error');
    }
    const textEl = document.createElement('p');
    textEl.textContent = message;
    messageEl.appendChild(textEl);
    const button = document.createElement('button');
    button.textContent = 'Close';
    button.addEventListener('click', onClose);
    messageEl.appendChild(button);
    container.appendChild(messageEl);
}

function setupIframeConnection({ iframe, callbacks, childOrigin, onComplete, close }) {
    let slowTimeoutId = null;
    let failedTimeoutId = null;
    let handshakeTimeoutId = null;
    let handshakeReceived = false;
    function clearTimeouts() {
        if (slowTimeoutId)
            clearTimeout(slowTimeoutId);
        if (failedTimeoutId)
            clearTimeout(failedTimeoutId);
        if (handshakeTimeoutId)
            clearTimeout(handshakeTimeoutId);
        slowTimeoutId = null;
        failedTimeoutId = null;
        handshakeTimeoutId = null;
        removeTimeoutMessage();
    }
    function complete(callback) {
        clearTimeouts();
        close(callback);
        onComplete();
    }
    function handleError() {
        clearTimeouts();
        showTimeoutMessage('Unable to connect', () => complete(callbacks.onClose), true);
    }
    iframe.onerror = handleError;
    iframe.onload = () => {
        if (!handshakeReceived) {
            handshakeTimeoutId = setTimeout(() => {
                if (!handshakeReceived)
                    handleError();
            }, HANDSHAKE_AFTER_LOAD_TIMEOUT);
        }
    };
    slowTimeoutId = setTimeout(() => {
        showTimeoutMessage('Taking longer than expected', () => complete(callbacks.onClose));
    }, SLOW_CONNECTION_TIMEOUT);
    failedTimeoutId = setTimeout(() => {
        showTimeoutMessage('Unable to connect', () => complete(callbacks.onClose), true);
    }, CONNECTION_FAILED_TIMEOUT);
    const connection = connectToChild({
        iframe,
        ...(childOrigin && { childOrigin }),
        methods: {
            ...callbacks,
            handshake: (...args) => {
                var _a;
                handshakeReceived = true;
                clearTimeouts();
                hideIframeLoader();
                (_a = callbacks === null || callbacks === void 0 ? void 0 : callbacks.handshake) === null || _a === void 0 ? void 0 : _a.call(callbacks, ...args);
            },
            close: () => complete(callbacks.onClose),
        },
    });
    connection.promise.catch(handleError);
}

function init(mountTargetSelector) {
    var _a;
    injectStyles(mountTargetSelector);
    if (getContainer())
        return;
    const container = createContainer();
    if (mountTargetSelector && document.querySelector(mountTargetSelector)) {
        (_a = document.querySelector(mountTargetSelector)) === null || _a === void 0 ? void 0 : _a.appendChild(container);
    }
    else {
        document.body.appendChild(container);
    }
}
function close(callback) {
    document.body.classList.remove(BODY_CLASS);
    if (getContainer()) {
        deleteContainer();
        callback === null || callback === void 0 ? void 0 : callback();
    }
}
async function openIframe(uri, callbacks = {}, { mountTargetSelector } = {}) {
    close();
    init(mountTargetSelector);
    return new Promise((resolve) => {
        function doOpen() {
            if (!getContainer()) {
                throw Error('Membrane container element not found. Was it manually removed?');
            }
            const iframe = document.createElement('iframe');
            iframe.id = POPUP_ELEMENT_ID;
            iframe.src = uri;
            iframe.setAttribute('allow', 'clipboard-write *');
            document.body.classList.add(BODY_CLASS);
            showIframeLoader();
            appendToContainer(iframe);
            handleIframeUnmount(iframe);
            setupIframeConnection({ iframe, callbacks, onComplete: resolve, close });
        }
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            doOpen();
        }
        else {
            document.addEventListener('DOMContentLoaded', doOpen);
        }
    });
}
async function openIframeWithPost(url, postData, callbacks = {}, { mountTargetSelector } = {}) {
    close();
    init(mountTargetSelector);
    return new Promise((resolve) => {
        function doOpen() {
            if (!getContainer()) {
                throw Error('Membrane container element not found. Was it manually removed?');
            }
            const iframeName = `${POPUP_ELEMENT_ID}-${Date.now()}`;
            const iframe = document.createElement('iframe');
            iframe.id = POPUP_ELEMENT_ID;
            iframe.name = iframeName;
            iframe.setAttribute('allow', 'clipboard-write *');
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = url;
            form.target = iframeName;
            form.style.display = 'none';
            for (const [key, value] of Object.entries(postData)) {
                if (value !== undefined) {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = value;
                    form.appendChild(input);
                }
            }
            document.body.classList.add(BODY_CLASS);
            showIframeLoader();
            appendToContainer(iframe);
            handleIframeUnmount(iframe);
            document.body.appendChild(form);
            form.submit();
            form.remove();
            const childOrigin = new URL(url).origin;
            setupIframeConnection({ iframe, callbacks, childOrigin, onComplete: resolve, close });
        }
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            doOpen();
        }
        else {
            document.addEventListener('DOMContentLoaded', doOpen);
        }
    });
}

function routeConnectionEventMessage(message, requestId, handlers) {
    var _a, _b, _c, _d, _e;
    if (!isConnectionMessagePayload(message))
        return;
    if (message.requestId && message.requestId !== requestId)
        return;
    if (message.type === 'newConnectionCreated') {
        (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, message.connection);
        (_b = handlers.onTerminal) === null || _b === void 0 ? void 0 : _b.call(handlers);
        return;
    }
    if (message.type === 'newConnectionFailure') {
        const error = new MembraneError((_c = message.errorData) !== null && _c !== void 0 ? _c : message.error);
        if (message.connectionId) {
            error.data.data = { ...error.data.data, connectionId: message.connectionId };
        }
        (_d = handlers.onFailure) === null || _d === void 0 ? void 0 : _d.call(handlers, error);
        (_e = handlers.onTerminal) === null || _e === void 0 ? void 0 : _e.call(handlers);
    }
}
function isConnectionMessagePayload(value) {
    if (!value || typeof value !== 'object')
        return false;
    const m = value;
    return m.source === 'integration.app' && typeof m.type === 'string';
}
function createConnectionEventsSubscription(options) {
    const { apiUri, getToken, requestId, handlers } = options;
    let eventSource = null;
    let cancelled = false;
    const close = () => {
        cancelled = true;
        eventSource === null || eventSource === void 0 ? void 0 : eventSource.close();
        eventSource = null;
    };
    Promise.resolve(getToken()).then((token) => {
        if (cancelled)
            return;
        const url = urljoin(apiUri, '/sse/connection-events', requestId) + `?token=${encodeURIComponent(token)}`;
        try {
            eventSource = new EventSource(url);
            eventSource.onmessage = (event) => {
                let data;
                try {
                    data = JSON.parse(event.data);
                }
                catch (_a) {
                    return;
                }
                routeConnectionEventMessage(data, requestId, handlers);
            };
            eventSource.onerror = close;
        }
        catch (_a) { }
    });
    return close;
}

class DataSourcesAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'data-sources');
    }
}
class DataSourceAccessor extends ElementAccessor {
    constructor(client, selector) {
        super({
            client,
            selector,
            path: 'data-source',
        });
    }
    async setup() {
        await this.options.client.post(this.getPath('setup'));
    }
    async reset() {
        return await this.options.client.post(this.getPath('reset'));
    }
}
class DataSourceInstancesAccessor extends ElementInstanceListAccessor {
    constructor(client) {
        super(client, 'data-source-instances');
    }
}
class DataSourceInstanceAccessor extends ElementInstanceAccessor {
    constructor(client, selector) {
        super({
            client,
            instancePath: 'data-source-instance',
            selector,
        });
        this.client = client;
    }
    async setup() {
        await this.client.post(this.getPath('setup'));
    }
    async reset() {
        await this.client.post(this.getPath('reset'));
    }
    async openConfiguration({ onClose = () => { } } = {}) {
        const id = await this.getId();
        const uri = await this.client.getEmbedUri(`data-source-instances/${id}/configuration`);
        return openIframe(uri, {
            onClose,
        });
    }
    async unifiedFieldsToNative(unifiedFields) {
        return this.client.post(this.getPath('unified-fields-to-native'), unifiedFields);
    }
    async getCollection() {
        return this.client.get(this.getPath('collection'));
    }
    async listRecords(request = {}) {
        return this.client.post(this.getPath('collection/list'), request);
    }
    async matchRecord(request) {
        return this.client.post(this.getPath('collection/match'), request);
    }
    async searchRecords(request) {
        return this.client.post(this.getPath('collection/search'), request);
    }
    async findRecordById(request) {
        const data = typeof request === 'string' ? { id: request } : request;
        return this.client.post(this.getPath('collection/find-by-id'), data);
    }
    async createRecord(request) {
        return this.client.post(this.getPath('collection/create'), request);
    }
    async updateRecord(request) {
        return this.client.post(this.getPath(`collection/update`), request);
    }
    async deleteRecord(request) {
        const data = typeof request === 'string' ? { id: request } : request;
        return this.client.post(this.getPath(`collection/delete`), data);
    }
    async findRecords(request = {}) {
        return this.client.post(this.getPath('collection/find'), request);
    }
}
class IntegrationLevelDataSourceAccessor extends IntegrationLevelElementAccessor {
    constructor(client, integrationSelector, dataSourceSelector) {
        super(client, integrationSelector, dataSourceSelector, 'data-sources');
        this.client = client;
        this.integrationSelector = integrationSelector;
        this.dataSourceSelector = dataSourceSelector;
    }
}
class IntegrationLevelDataSourcesListAccessor extends IntegrationLevelElementsListAccessor {
    constructor(client, integrationSelector) {
        super(client, integrationSelector, 'data-sources');
        this.client = client;
        this.integrationSelector = integrationSelector;
    }
}
class ConnectionLevelDataSourceAccessor extends ConnectionLevelElementAccessor {
    constructor(client, connectionSelector, dataSourceSelector, query) {
        super(client, connectionSelector, dataSourceSelector, 'data-sources', query);
        this.client = client;
        this.connectionSelector = connectionSelector;
        this.dataSourceSelector = dataSourceSelector;
        this.query = query;
    }
    async openConfiguration(options) {
        var _a;
        const instance = await this.get({ autoCreate: true });
        const uri = await this.client.getEmbedUri(`data-source-instances/${instance.id}/configuration`);
        const onClose = (_a = options === null || options === void 0 ? void 0 : options.onClose) !== null && _a !== void 0 ? _a : (() => { });
        return openIframe(uri, { onClose });
    }
}
class ConnectionLevelDataSourcesAccessor extends ConnectionLevelElementsListAccessor {
    constructor(client, connectionSelector) {
        super(client, connectionSelector, 'data-sources');
        this.client = client;
        this.connectionSelector = connectionSelector;
    }
}

class FieldMappingInstancesAccessor extends ElementInstanceListAccessor {
    constructor(client) {
        super(client, 'field-mapping-instances');
    }
}
class FieldMappingInstanceAccessor extends ElementInstanceAccessor {
    constructor(client, selector) {
        super({
            client,
            instancePath: 'field-mapping-instance',
            selector,
        });
        this.client = client;
    }
    async setup() {
        const id = await this.getId();
        return this.client.post(`/field-mapping-instances/${id}/setup`);
    }
    async reset() {
        const id = await this.getId();
        return this.client.post(`/field-mapping-instances/${id}/reset`);
    }
    async openConfiguration({ onClose = () => { } } = {}) {
        const id = await this.getId();
        const uri = await this.client.getEmbedUri(`field-mapping-instances/${id}/configuration`);
        return openIframe(uri, {
            onClose,
        });
    }
}
class ConnectionLevelFieldMappingAccessor extends ConnectionLevelElementAccessor {
    constructor(client, connectionSelector, fieldMappingSelector, query) {
        super(client, connectionSelector, fieldMappingSelector, 'field-mappings', query);
        this.client = client;
        this.connectionSelector = connectionSelector;
        this.fieldMappingSelector = fieldMappingSelector;
        this.query = query;
    }
    async openConfiguration({ onClose } = {}) {
        const instance = await this.get({ autoCreate: true });
        const uri = await this.client.getEmbedUri(`field-mapping-instances/${instance.id}/configuration`);
        return openIframe(uri, { onClose: onClose !== null && onClose !== void 0 ? onClose : (() => { }) });
    }
}
class ConnectionLevelFieldMappingsAccessor extends ConnectionLevelElementsListAccessor {
    constructor(client, connectionSelector) {
        super(client, connectionSelector, 'field-mappings');
        this.client = client;
        this.connectionSelector = connectionSelector;
    }
}

class FlowRunsAccessor {
    constructor(client) {
        this.client = client;
    }
    async find(query) {
        return this.client.get('/flow-runs', query);
    }
    async create(request) {
        return this.client.post('/flow-runs', request);
    }
}
class FlowRunAccessor {
    constructor(client, id) {
        this.client = client;
        this.id = id;
        this.baseUri = `/flow-runs/${id}`;
    }
    async get() {
        return this.client.get(this.baseUri);
    }
    async stop() {
        await this.client.post(`${this.baseUri}/stop`);
    }
    async getOutput() {
        return this.client.get(`${this.baseUri}/output`);
    }
    async getNodeRuns(nodeKey, parameters) {
        return this.client.get(`${this.baseUri}/nodes/${nodeKey}/runs/${qs.stringify(parameters)}`);
    }
    async getNodeRun(nodeKey, runId) {
        return this.client.get(`${this.baseUri}/nodes/${nodeKey}/runs/${runId}`);
    }
    async getNodeOutputs(nodeKey, parameters) {
        return this.client.get(`${this.baseUri}/nodes/${nodeKey}/outputs?${qs.stringify(parameters)}`);
    }
    async getNodeOutput(nodeKey, outputId) {
        return this.client.get(`${this.baseUri}/nodes/${nodeKey}/output/${outputId}`);
    }
    async repeat() {
        const flowRun = await this.get();
        if (flowRun.state === FlowRunState.RUNNING) {
            throw new BadRequestError('Cannot repeat a running flow run.');
        }
        return this.client.post(`/flow-runs`, {
            flowInstanceId: flowRun.connectionFlowId || flowRun.flowInstanceId,
            startNodeKey: flowRun.startNodeKey,
            input: flowRun.input,
        });
    }
    async openEditor({ onClose = () => { } } = {}) {
        const flowRun = await this.get();
        const id = flowRun.id;
        const uri = await this.client.getEmbedUri(`flow-runs/${id}/editor`, undefined, {
            embedMode: 'popup-embed',
        });
        return openIframe(uri, {
            onClose,
        });
    }
    async embedEditor({ mountTargetSelector, onClose = () => { }, } = {}) {
        const flowRun = await this.get();
        const id = flowRun.id;
        const uri = await this.client.getEmbedUri(`flow-runs/${id}/editor`, undefined, { embedMode: 'isolated-embed' });
        return openIframe(uri, {
            onClose,
        }, { mountTargetSelector });
    }
}

const MAX_POLLING_ERRORS = 10;
class FlowInstancesAccessor extends ElementInstanceListAccessor {
    constructor(client) {
        super(client, 'flow-instances');
    }
    async create(data) {
        return this.client.post('/flow-instances', data);
    }
}
class FlowInstanceAccessor extends ElementInstanceAccessor {
    constructor(client, selector) {
        super({
            client,
            selector,
            instancePath: 'flow-instance',
            type: WorkspaceElementType.FlowInstance,
        });
        this.client = client;
        this.selector = selector;
    }
    async enable() {
        return this.patch({ enabled: true });
    }
    async disable() {
        return this.patch({ enabled: false });
    }
    async updateToLatestFlow() {
        const id = await this.getId();
        return this.client.post(`/flow-instances/${id}/update-to-latest-flow`);
    }
    async setup() {
        const id = await this.getId();
        return this.client.post(`/flow-instances/${id}/setup`);
    }
    async reset(options) {
        const id = await this.getId();
        return this.client.post(`/flow-instances/${id}/reset`, options);
    }
    async openConfiguration({ nodeKey, onClose = () => { } } = {}) {
        const id = await this.getId();
        const uri = await this.client.getEmbedUri(`flow-instances/${id}/configuration`, {
            nodeKey,
        });
        return openIframe(uri, {
            onClose,
        });
    }
    async openEditor({ onClose = () => { } } = {}) {
        const id = await this.getId();
        const uri = await this.client.getEmbedUri(`flow-instances/${id}/editor`);
        return openIframe(uri, {
            onClose,
        });
    }
    async embedEditor({ mountTargetSelector, onClose = () => { }, } = {}) {
        const id = await this.getId();
        const uri = await this.client.getEmbedUri(`flow-instances/${id}/editor`, undefined, { embedMode: 'isolated-embed' });
        return openIframe(uri, {
            onClose,
        }, { mountTargetSelector });
    }
    async startRun(options = {}) {
        const id = await this.getId();
        const flowRunsAccessor = new FlowRunsAccessor(this.client);
        const flowRun = await flowRunsAccessor.create({
            flowInstanceId: id,
            nodeKey: options.nodeKey,
            input: options.input,
        });
        return flowRun;
    }
    async run(options = {}) {
        function onUpdate(flowRun) {
            var _a;
            (_a = options.onUpdate) === null || _a === void 0 ? void 0 : _a.call(options, flowRun);
        }
        const flowRun = await this.startRun(options);
        onUpdate(flowRun);
        let checkFlowRunInterval;
        function cleanup() {
            if (checkFlowRunInterval) {
                clearInterval(checkFlowRunInterval);
            }
        }
        return new Promise((resolve, reject) => {
            const handleFlowRunUpdate = async (flowRun) => {
                var _a, _b;
                onUpdate(flowRun);
                if (flowRun.state === FlowRunState.COMPLETED) {
                    cleanup();
                    resolve(flowRun);
                }
                else if (flowRun.state === FlowRunState.FAILED) {
                    cleanup();
                    reject(new Error(`Flow run ${flowRun.id} failed: ${(_b = (_a = flowRun.errors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.message}`));
                }
            };
            const fetchFlowRun = async () => {
                const flowRunAccessor = new FlowRunAccessor(this.client, flowRun.id);
                await handleFlowRunUpdate(await flowRunAccessor.get());
            };
            let pollingFlowRun = false;
            let errors = 0;
            checkFlowRunInterval = setInterval(async () => {
                if (!pollingFlowRun) {
                    pollingFlowRun = true;
                    try {
                        await fetchFlowRun();
                        errors = 0;
                    }
                    finally {
                        errors += 1;
                        if (errors > MAX_POLLING_ERRORS) {
                            cleanup();
                            reject(new Error(`Got ${errors} errors while polling flow run ${flowRun.id} state - aborting`));
                        }
                        pollingFlowRun = false;
                    }
                }
            }, 1000);
        });
    }
}
class ConnectionLevelFlowAccessor extends ConnectionLevelElementAccessor {
    constructor(client, connectionSelector, flowSelector, query) {
        super(client, connectionSelector, flowSelector, 'flows', query);
        this.client = client;
        this.connectionSelector = connectionSelector;
        this.flowSelector = flowSelector;
        this.query = query;
    }
    async enable() {
        return this.patch({ enabled: true });
    }
    async disable() {
        return this.patch({ enabled: false });
    }
    async run(options) {
        return await this.client.post(this.uri('/run'), options);
    }
    async openConfiguration(options) {
        var _a;
        const instance = await this.get({ autoCreate: true });
        const uri = await this.client.getEmbedUri(`flow-instances/${instance.id}/configuration`, {
            nodeKey: options === null || options === void 0 ? void 0 : options.nodeKey,
        });
        const onClose = (_a = options === null || options === void 0 ? void 0 : options.onClose) !== null && _a !== void 0 ? _a : (() => { });
        return openIframe(uri, { onClose });
    }
    async openEditor(options) {
        var _a;
        const instance = await this.get({ autoCreate: true });
        const uri = await this.client.getEmbedUri(`flow-instances/${instance.id}/editor`);
        const onClose = (_a = options === null || options === void 0 ? void 0 : options.onClose) !== null && _a !== void 0 ? _a : (() => { });
        return openIframe(uri, { onClose });
    }
}
class ConnectionLevelFlowsAccessor extends ConnectionLevelElementsListAccessor {
    constructor(client, connectionSelector) {
        super(client, connectionSelector, 'flows');
        this.client = client;
        this.connectionSelector = connectionSelector;
    }
}

var ConnectionDisplayMode;
(function (ConnectionDisplayMode) {
    ConnectionDisplayMode["IFRAME"] = "iframe";
    ConnectionDisplayMode["POPUP"] = "popup";
    ConnectionDisplayMode["REDIRECT"] = "redirect";
})(ConnectionDisplayMode || (ConnectionDisplayMode = {}));
class ConnectionsAccessor extends ElementInstanceListAccessor {
    constructor(client) {
        super(client, 'connections');
    }
    create(data) {
        return this.client.post('/connections', data);
    }
}
class ConnectionAccessor {
    constructor(client, connectionSelector) {
        this.client = client;
        this.connectionSelector = connectionSelector;
    }
    get actions() {
        return new ConnectionLevelActionsAccessor(this.client, this.connectionSelector);
    }
    action(actionSelector, query) {
        return new ConnectionLevelActionAccessor(this.client, this.connectionSelector, actionSelector, query !== null && query !== void 0 ? query : {});
    }
    get flows() {
        return new ConnectionLevelFlowsAccessor(this.client, this.connectionSelector);
    }
    flow(flowSelector, query) {
        return new ConnectionLevelFlowAccessor(this.client, this.connectionSelector, flowSelector, query !== null && query !== void 0 ? query : {});
    }
    get dataSources() {
        return new ConnectionLevelDataSourcesAccessor(this.client, this.connectionSelector);
    }
    dataSource(dataSourceSelector, query) {
        return new ConnectionLevelDataSourceAccessor(this.client, this.connectionSelector, dataSourceSelector, query !== null && query !== void 0 ? query : {});
    }
    get fieldMappings() {
        return new ConnectionLevelFieldMappingsAccessor(this.client, this.connectionSelector);
    }
    fieldMapping(fieldMappingSelector, query) {
        return new ConnectionLevelFieldMappingAccessor(this.client, this.connectionSelector, fieldMappingSelector, query !== null && query !== void 0 ? query : {});
    }
    async get(query = { includeSecrets: false }) {
        return this.client.get(`/connections/${this.connectionSelector}`, query);
    }
    async patch(data) {
        return this.client.patch(`/connections/${this.connectionSelector}`, data);
    }
    async put(data) {
        return this.client.put(`/connections/${this.connectionSelector}`, data);
    }
    async archive() {
        return this.client.delete(`/connections/${this.connectionSelector}`);
    }
    async restore() {
        return this.client.post(`/connections/${this.connectionSelector}/restore`);
    }
    async request(path, data = null) {
        const pathWithoutSlash = path.startsWith('/') ? path.slice(1) : path;
        return this.client.post(`/connections/${this.connectionSelector}/api/${pathWithoutSlash}`, data);
    }
    get proxy() {
        return new ConnectionProxy(this);
    }
    operation(key) {
        return new ConnectionOperationAccessor(this.client, this, key);
    }
    dataCollection(key, parameters) {
        return new ConnectionDataCollectionAccessor(this.client, this, key, parameters);
    }
    async reconnect({ parameters, authOptionKey, connectorParameters, onPopupClosed, } = {}) {
        const connection = await this.get();
        return createOrUpdateConnection({
            client: this.client,
            connectionId: connection.id,
            input: parameters,
            authOptionKey,
            connectorParameters,
            apiUri: this.client.apiUri,
            onPopupClosed,
        });
    }
    async openReconnectUI(_options = {}) {
        const uri = await this.client.getEmbedUri(`connections/${this.connectionSelector}/refresh`);
        return new Promise((resolve) => {
            void openIframe(uri, {
                onClose: () => resolve(null),
                onSuccess: (connection) => resolve(connection),
            });
        });
    }
    async refreshCredentials() {
        return this.client.post(`connections/${this.connectionSelector}/refresh-credentials`);
    }
    getPath(subpath, query) {
        const queryString = new URLSearchParams(omitBy(query !== null && query !== void 0 ? query : {}, isEmptyValue)).toString();
        return urljoin(`/connections/${this.connectionSelector}`, subpath !== null && subpath !== void 0 ? subpath : '') + (queryString ? `?${queryString}` : '');
    }
}
class ConnectionOperationAccessor {
    constructor(client, connectionAccessor, key) {
        this.client = client;
        this.connectionAccessor = connectionAccessor;
        this.key = key;
    }
    async get() {
        return this.client.get(this.connectionAccessor.getPath(`operations/${this.key}`));
    }
    async run(request) {
        return this.client.post(this.connectionAccessor.getPath(`operations/${this.key}/run`), request);
    }
}
class ConnectionDataCollectionAccessor {
    constructor(client, connectionAccessor, key, parameters) {
        this.client = client;
        this.connectionAccessor = connectionAccessor;
        this.key = key;
        this.parameters = parameters;
    }
    async get() {
        return this.client.get(this.connectionAccessor.getPath(`data/${this.key}`, this.parameters));
    }
    async findById(request) {
        return this.client.post(this.connectionAccessor.getPath(`data/${this.key}/find-by-id`, this.parameters), request);
    }
    async list(request) {
        return this.client.post(this.connectionAccessor.getPath(`data/${this.key}/list`, this.parameters), request);
    }
    async search(request) {
        return this.client.post(this.connectionAccessor.getPath(`data/${this.key}/search`, this.parameters), request);
    }
    async match(request) {
        return this.client.post(this.connectionAccessor.getPath(`data/${this.key}/match`, this.parameters), request);
    }
    async create(request) {
        return this.client.post(this.connectionAccessor.getPath(`data/${this.key}/create`, this.parameters), request);
    }
    async update(request) {
        return this.client.post(this.connectionAccessor.getPath(`data/${this.key}/update`, this.parameters), request);
    }
    async delete(request) {
        return this.client.post(this.connectionAccessor.getPath(`data/${this.key}/delete`, this.parameters), request);
    }
}
class ConnectionProxy {
    constructor(connection) {
        this.connection = connection;
    }
    async get(path) {
        return this.connection.client.get(this.getProxyPath(path));
    }
    async post(path, data) {
        return this.connection.client.post(this.getProxyPath(path), data);
    }
    async put(path, data) {
        return this.connection.client.put(this.getProxyPath(path), data);
    }
    async patch(path, data) {
        return this.connection.client.patch(this.getProxyPath(path), data);
    }
    async delete(path) {
        return this.connection.client.delete(this.getProxyPath(path));
    }
    getProxyPath(path) {
        return urljoin(`/connections/${this.connection.connectionSelector}/proxy`, path);
    }
}
async function createOrUpdateConnection(options) {
    const { client, type, connectionId, connectionKey, integrationKey, integrationId, connectorId, connectorVersion, name, input, connectorParameters, allowMultipleConnections, authOptionKey, connectionRequestId, apiUri, redirectUri, customState, onPopupClosed, lateEvents, } = options !== null && options !== void 0 ? options : {};
    let connectionType = type === 'mcp' ? ConnectionDisplayMode.POPUP : await detectConnectionType(options);
    if (redirectUri) {
        connectionType = ConnectionDisplayMode.REDIRECT;
    }
    const freshToken = await client.getToken();
    const connectPath = 'connect';
    return new Promise((resolve, reject) => {
        var _a, _b;
        const requestId = simpleUniqueId() + simpleUniqueId();
        const payload = {
            token: freshToken,
            type,
            input,
            connectorParameters,
            integrationKey,
            integrationId,
            connectorId,
            connectorVersion,
            connectionId,
            connectionKey,
            name,
            authOptionKey,
            allowMultipleConnections,
            connectionRequestId,
            requestId,
            redirectUri,
            customState,
        };
        let cancelCheckInterval;
        let lateEventsTimeout;
        let promiseSettled = false;
        const handlers = {
            onSuccess: (data) => {
                var _a;
                if (promiseSettled) {
                    (_a = lateEvents === null || lateEvents === void 0 ? void 0 : lateEvents.onConnection) === null || _a === void 0 ? void 0 : _a.call(lateEvents, data);
                }
                else {
                    promiseSettled = true;
                    resolve(data);
                }
            },
            onFailure: (error) => {
                var _a;
                if (promiseSettled) {
                    (_a = lateEvents === null || lateEvents === void 0 ? void 0 : lateEvents.onFailure) === null || _a === void 0 ? void 0 : _a.call(lateEvents, error);
                }
                else {
                    promiseSettled = true;
                    reject(error);
                }
            },
            onTerminal: () => cleanup(),
        };
        const { stopCrossWindow } = createCrossWindowListener({ requestId, handlers });
        const stopSSE = createConnectionEventsSubscription({
            apiUri: apiUri,
            getToken: () => freshToken,
            requestId,
            handlers,
        });
        if (lateEvents) {
            lateEventsTimeout = setTimeout(stopSSE, 30 * 60 * 1000);
            if ((_a = lateEvents.signal) === null || _a === void 0 ? void 0 : _a.aborted) {
                stopSSE();
            }
            else {
                (_b = lateEvents.signal) === null || _b === void 0 ? void 0 : _b.addEventListener('abort', stopSSE, { once: true });
            }
        }
        function cleanup() {
            clearInterval(cancelCheckInterval);
            if (lateEventsTimeout)
                clearTimeout(lateEventsTimeout);
            stopCrossWindow();
            stopSSE();
        }
        const targetElementId = `connection-request-${requestId}`;
        try {
            const connectionUrl = urljoin(apiUri, connectPath);
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = connectionUrl;
            form.target = targetElementId;
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'payload';
            input.value = JSON.stringify(payload);
            form.appendChild(input);
            document.body.appendChild(form);
            if (connectionType === ConnectionDisplayMode.IFRAME) {
                const iframeElement = document.createElement('iframe');
                iframeElement.id = targetElementId;
                iframeElement.name = targetElementId;
                iframeElement.style.position = 'absolute';
                iframeElement.style.top = '-100px';
                iframeElement.style.left = '-100px';
                iframeElement.style.width = '1px';
                iframeElement.style.height = '1px';
                iframeElement.style.border = 'none';
                iframeElement.style.backgroundColor = 'transparent';
                document.body.appendChild(iframeElement);
                form.submit();
                document.body.removeChild(form);
            }
            if (connectionType === ConnectionDisplayMode.REDIRECT) {
                form.target = '_self';
                form.submit();
                document.body.removeChild(form);
            }
            if (connectionType === ConnectionDisplayMode.POPUP) {
                const width = Math.min(Math.round(screen.width * 0.7), 1000);
                const height = Math.min(Math.round(screen.height * 0.6), 800);
                const left = Math.round((screen.width - width) / 2);
                const top = Math.round((screen.height - height) / 2);
                let popup;
                setTimeout(() => {
                    popup = window.open('', targetElementId, `popup,width=${width},height=${height},left=${left},top=${top}`);
                    form.submit();
                    document.body.removeChild(form);
                }, 0);
                setTimeout(() => {
                    if (!popup) {
                        cleanup();
                        reject({
                            message: 'Popup has been blocked.',
                        });
                    }
                }, 1000);
                cancelCheckInterval = setInterval(() => {
                    if (popup === null || popup === void 0 ? void 0 : popup.closed) {
                        clearInterval(cancelCheckInterval);
                        stopCrossWindow();
                        if (!lateEvents)
                            stopSSE();
                        if (!promiseSettled) {
                            promiseSettled = true;
                            onPopupClosed === null || onPopupClosed === void 0 ? void 0 : onPopupClosed();
                            resolve(null);
                        }
                    }
                }, 1000);
            }
        }
        catch (e) {
            reject(e);
        }
    });
}
async function detectConnectionType({ client, authOptionKey, connectorId, connectionId, integrationKey, integrationId, connectorParameters, }) {
    const connectionOptions = await client.post('/connection-options', {
        connectorId,
        connectionId,
        integrationId,
        integrationKey,
        connectorParameters,
    });
    const option = connectionOptions.find((option) => option.key == authOptionKey);
    if ((option === null || option === void 0 ? void 0 : option.type) === 'client-credentials') {
        return ConnectionDisplayMode.IFRAME;
    }
    return ConnectionDisplayMode.POPUP;
}
const createCrossWindowListener = ({ requestId, handlers, }) => {
    const listener = (event) => {
        routeConnectionEventMessage(event.data, requestId, handlers);
    };
    window.addEventListener('message', listener);
    return {
        stopCrossWindow: () => {
            window.removeEventListener('message', listener);
        },
    };
};

class FlowsAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'flows');
    }
}
class FlowAccessor extends ElementAccessor {
    constructor(client, selector) {
        super({
            client,
            selector,
            path: 'flow',
        });
    }
    async reset() {
        return await this.options.client.post(this.getPath('reset'));
    }
}
class IntegrationLevelFlowAccessor extends IntegrationLevelElementAccessor {
    constructor(client, integrationSelector, flowSelector) {
        super(client, integrationSelector, flowSelector, 'flows');
        this.client = client;
        this.integrationSelector = integrationSelector;
        this.flowSelector = flowSelector;
    }
}
class IntegrationLevelFlowsListAccessor extends IntegrationLevelElementsListAccessor {
    constructor(client, integrationSelector) {
        super(client, integrationSelector, 'flows');
        this.client = client;
        this.integrationSelector = integrationSelector;
    }
}

class FieldMappingsAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'field-mappings');
    }
}
class FieldMappingAccessor extends ElementAccessor {
    constructor(client, selector) {
        super({
            client,
            selector,
            path: 'field-mapping',
        });
    }
    async getAppSchema() {
        return await this.options.client.get(this.getPath('app-schema'));
    }
    async setup() {
        return await this.options.client.post(this.getPath('setup'));
    }
    async reset() {
        return await this.options.client.post(this.getPath('reset'));
    }
}
class IntegrationLevelFieldMappingAccessor extends IntegrationLevelElementAccessor {
    constructor(client, integrationSelector, fieldMappingSelector) {
        super(client, integrationSelector, fieldMappingSelector, 'field-mappings');
        this.client = client;
        this.integrationSelector = integrationSelector;
        this.fieldMappingSelector = fieldMappingSelector;
    }
}
class IntegrationLevelFieldMappingsListAccessor extends IntegrationLevelElementsListAccessor {
    constructor(client, integrationSelector) {
        super(client, integrationSelector, 'field-mappings');
        this.client = client;
        this.integrationSelector = integrationSelector;
    }
}

class IntegrationsAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'integrations');
    }
}
class IntegrationAccessor extends ElementAccessor {
    constructor(client, integrationSelector) {
        super({
            client,
            path: 'integration',
            selector: integrationSelector,
        });
        this.client = client;
        this.integrationSelector = integrationSelector;
    }
    get actions() {
        return new IntegrationLevelActionsListAccessor(this.client, this.integrationSelector);
    }
    action(actionSelector) {
        return new IntegrationLevelActionAccessor(this.client, this.integrationSelector, actionSelector);
    }
    get flows() {
        return new IntegrationLevelFlowsListAccessor(this.client, this.integrationSelector);
    }
    flow(flowSelector) {
        return new IntegrationLevelFlowAccessor(this.client, this.integrationSelector, flowSelector);
    }
    get dataSources() {
        return new IntegrationLevelDataSourcesListAccessor(this.client, this.integrationSelector);
    }
    dataSource(dataSourceSelector) {
        return new IntegrationLevelDataSourceAccessor(this.client, this.integrationSelector, dataSourceSelector);
    }
    get fieldMappings() {
        return new IntegrationLevelFieldMappingsListAccessor(this.client, this.integrationSelector);
    }
    fieldMapping(fieldMappingSelector) {
        return new IntegrationLevelFieldMappingAccessor(this.client, this.integrationSelector, fieldMappingSelector);
    }
    async setup() {
        return this.client.post(`/integrations/${this.integrationSelector}/setup`);
    }
    async getConnectorSpec() {
        return this.client.get(`/integrations/${this.integrationSelector}/connector-spec`);
    }
    async open({ onClose = () => { } } = {}) {
        const integration = await this.get();
        const uri = await this.client.getEmbedUri(`integrations/${integration.id}`);
        return openIframe(uri, {
            onClose,
        });
    }
    async openNewConnection({ allowMultipleConnections, name, connectorParameters, } = {}) {
        const uri = await this.client.getEmbedUri(`integrations/${this.integrationSelector}/connect`, {
            allowMultipleConnections: allowMultipleConnections ? '1' : '',
            name,
            connectorParameters: connectorParameters ? JSON.stringify(connectorParameters) : undefined,
        });
        return new Promise((resolve) => {
            return openIframe(uri, {
                onClose: () => resolve(null),
                onSuccess: (connection) => resolve(connection),
            });
        });
    }
    async openNewConnectionV2({ allowMultipleConnections, name, connectorParameters, } = {}) {
        const { url, postData } = await this.client.getScreensPostData(`integrations/${this.integrationSelector}/connect`, {
            allowMultipleConnections: allowMultipleConnections ? '1' : '',
            name,
            connectorParameters: connectorParameters ? JSON.stringify(connectorParameters) : undefined,
        });
        return new Promise((resolve) => {
            return openIframeWithPost(url, postData, {
                onClose: () => resolve(null),
                onSuccess: (connection) => resolve(connection),
            });
        });
    }
    async connect({ name, input, parameters, connectorParameters, authOptionKey, allowMultipleConnections, redirectUri, sameWindow, onPopupClosed, } = {}) {
        const integration = await this.get();
        return createOrUpdateConnection({
            client: this.client,
            integrationId: integration.id,
            name,
            input: input !== null && input !== void 0 ? input : parameters,
            connectorParameters,
            authOptionKey,
            allowMultipleConnections,
            apiUri: this.client.apiUri,
            redirectUri: sameWindow ? redirectUri : undefined,
            onPopupClosed,
        });
    }
    async createConnection({ parameters, name, }) {
        return this.connect({ parameters, name });
    }
    async getOperations() {
        return this.client.get(this.getPath('operations'));
    }
    async getOperation(key) {
        return this.client.get(this.getPath(`operations/${key}`));
    }
    async getDataCollections() {
        return this.client.get(this.getPath('data'));
    }
    async getDataCollection(key) {
        return this.client.get(this.getPath(`data/${key}`));
    }
}

class PackagesAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'packages');
    }
}
class PackageAccessor extends ElementAccessor {
    constructor(client, selector) {
        super({
            client,
            selector,
            path: 'package',
        });
    }
}
const ScenariosAccessor = PackagesAccessor;
const ScenarioAccessor = PackageAccessor;

class ScreensAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'screens');
    }
}
class ScreenAccessor extends ElementAccessor {
    constructor(client, selector) {
        super({
            client,
            selector,
            path: 'screen',
        });
    }
}

class CustomersAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'customers');
    }
}
class CustomerAccessor extends ElementAccessor {
    constructor(client, selector) {
        super({
            client,
            selector,
            path: 'customer',
        });
    }
}
class UsersAccessor extends CustomersAccessor {
    constructor(client) {
        super(client);
    }
}
class UserAccessor extends CustomerAccessor {
    constructor(client, selector) {
        super(client, selector);
    }
}

class ActionInstancesAccessor extends ElementInstanceListAccessor {
    constructor(client) {
        super(client, 'action-instances');
    }
}
class ActionInstanceAccessor extends ElementInstanceAccessor {
    constructor(client, selector) {
        super({
            client,
            instancePath: 'action-instance',
            selector,
        });
        this.client = client;
    }
    async setup() {
        await this.client.post(this.getPath('setup'));
    }
    async reset() {
        await this.client.post(this.getPath('reset'));
    }
    async run(input) {
        return this.client.post(this.getPath('run'), input);
    }
    async open({ onClose = () => { } } = {}) {
        const id = await this.getId();
        const uri = await this.client.getEmbedUri(`action-instances/${id}/configuration`);
        return openIframe(uri, { onClose });
    }
}

class DataLinkTablesAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'data-link-tables');
    }
}
class DataLinkTableAccessor extends ElementAccessor {
    constructor(client, idOrKey) {
        super({
            client,
            path: 'data-link-table',
            selector: idOrKey,
        });
    }
}

class DataLinkTableInstancesAccessor extends ElementInstanceListAccessor {
    constructor(client) {
        super(client, 'data-link-table-instances');
    }
}
class DataLinkTableInstanceAccessor extends ElementInstanceAccessor {
    constructor(client, selector) {
        super({
            client,
            instancePath: 'data-link-table-instance',
            selector,
        });
        this.client = client;
    }
    async findLinks(query) {
        const queryString = query ? qs.stringify(query) : '';
        return this.client.get(this.getPath('links') + (queryString ? `&${queryString}` : ''));
    }
    async createLink(data) {
        return this.client.post(this.getPath('links'), data);
    }
    async deleteLink(data) {
        return this.client.delete(this.getPath('links'), data);
    }
}

class AppEventTypesAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'app-event-types');
    }
}
class AppEventTypeAccessor extends ElementAccessor {
    constructor(client, idOrKey) {
        super({
            client,
            path: 'app-event-type',
            selector: idOrKey,
        });
    }
}

class AppEventSubscriptionsAccessor extends ElementInstanceListAccessor {
    constructor(client) {
        super(client, 'app-event-subscriptions');
    }
}
class AppEventSubscriptionAccessor extends ElementInstanceAccessor {
    constructor(client, selector) {
        super({
            client,
            instancePath: 'app-event-subscription',
            selector,
        });
    }
    async subscribe() {
        return this.options.client.post(this.getPath('subscribe'));
    }
}

class AppDataSchemasAccessor extends ElementListAccessor {
    constructor(client) {
        super(client, 'app-data-schemas');
    }
}
class AppDataSchemaAccessor extends ElementAccessor {
    constructor(client, idOrKey) {
        super({
            client,
            path: 'app-data-schema',
            selector: idOrKey,
        });
    }
}

class AppDataSchemaInstancesAccessor extends ElementInstanceListAccessor {
    constructor(client) {
        super(client, 'app-data-schema-instances');
    }
}
class AppDataSchemaInstanceAccessor extends ElementInstanceAccessor {
    constructor(client, selector) {
        super({
            client,
            instancePath: 'app-data-schema-instance',
            selector,
        });
        this.client = client;
    }
    async setup() {
        await this.client.post(this.getPath('setup'));
    }
}

class ExternalEventSubscriptionsAccessor extends ElementInstanceListAccessor {
    constructor(client) {
        super(client, 'external-event-subscriptions');
    }
}
class ExternalEventSubscriptionAccessor {
    constructor(client, id) {
        this.client = client;
        this.id = id;
    }
    async get() {
        return this.client.get(this.getPath());
    }
    async setup() {
        await this.client.post(this.getPath('setup'));
    }
    async subscribe() {
        await this.client.post(this.getPath('subscribe'));
    }
    async unsubscribe() {
        await this.client.post(this.getPath('unsubscribe'));
    }
    async resubscribe() {
        await this.client.post(this.getPath('resubscribe'));
    }
    async refreshWebhook() {
        await this.client.post(this.getPath('refresh-webhook'));
    }
    async pullEvents() {
        await this.client.post(this.getPath('pull-events'));
    }
    getPath(operation = '') {
        return `external-event-subscriptions/${this.id}${operation ? '/' + operation : ''}`;
    }
}

var WorkspaceSyncEventType;
(function (WorkspaceSyncEventType) {
    WorkspaceSyncEventType["ElementUpdate"] = "element-update";
    WorkspaceSyncEventType["Connected"] = "connected";
})(WorkspaceSyncEventType || (WorkspaceSyncEventType = {}));
var ConnectorFileUpdateType;
(function (ConnectorFileUpdateType) {
    ConnectorFileUpdateType["ConnectorFileUpdated"] = "connector-file-updated";
    ConnectorFileUpdateType["ConnectorFileDeleted"] = "connector-file-deleted";
    ConnectorFileUpdateType["ConnectorDirectoryRenamed"] = "connector-directory-renamed";
    ConnectorFileUpdateType["ConnectorDirectoryDeleted"] = "connector-directory-deleted";
})(ConnectorFileUpdateType || (ConnectorFileUpdateType = {}));

var ScenarioTemplateCategory;
(function (ScenarioTemplateCategory) {
    ScenarioTemplateCategory["DataExport"] = "data-export";
    ScenarioTemplateCategory["DataImport"] = "data-import";
    ScenarioTemplateCategory["BiDirectionalSync"] = "bi-directional-sync";
    ScenarioTemplateCategory["UnifiedApi"] = "unified-api";
    ScenarioTemplateCategory["DataEnrichment"] = "data-enrichment";
})(ScenarioTemplateCategory || (ScenarioTemplateCategory = {}));
const HandyScenarioTemplateElement = z.object({
    type: z.string(),
    integration: z.object({ key: z.string(), connectorId: z.string() }).optional(),
    integrations: z.array(z.object({ key: z.string(), connectorId: z.string() })).optional(),
    fields: z
        .object({
        key: z.string(),
        name: z.string(),
    })
        .and(z.record(z.string(), z.any())),
});
const withIntegrationKeys = (schema) => schema.and(z.object({ integrationKeys: z.array(z.string()).optional() }));
const ElementsExportFields = z.object({
    flows: z.record(z.string(), withIntegrationKeys(z.any())).optional(),
    actions: z.record(z.string(), withIntegrationKeys(z.any())).optional(),
    fieldMappings: z.record(z.string(), withIntegrationKeys(z.any())).optional(),
    dataSources: z.record(z.string(), withIntegrationKeys(z.any())).optional(),
    dataLinkTables: z.record(z.string(), z.any()).optional(),
    appEventTypes: z.record(z.string(), z.any()).optional(),
    appDataSchemas: z.record(z.string(), z.any()).optional(),
});
const ScenarioTemplateElements = ElementsExportFields.and(z.object({
    integrations: z.record(z.string(), ElementsExportFields.and(z.object({ connectorId: z.string() }))).optional(),
}));
const ScenarioTemplate = z.object({
    id: z.string(),
    name: z.string(),
    key: z.string(),
    shortDescription: z.string().optional(),
    description: z.string().optional(),
    demoWorkspaceKey: z.string().optional(),
    demoAppUri: z.string().optional(),
    demoGithubRepo: z.string().optional(),
    demoVideoYoutubeUri: z.string().optional(),
    tags: z.array(z.enum(ScenarioTemplateCategory)).optional(),
    elements: ScenarioTemplateElements.optional(),
    connectors: z.array(MinimalConnector).optional(),
    handyElements: z.array(HandyScenarioTemplateElement).optional(),
});

const AgentInstruction = z.object({
    id: z.string(),
    workspaceId: z.string(),
    name: z.string(),
    key: z.string(),
    text: z.string().min(1),
    revision: z.string().optional(),
    archivedAt: z.iso.datetime().optional(),
    createdAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime().optional(),
});
const CreateAgentInstructionRequest = z.object({
    name: z.string().min(1),
    key: z.string().min(1).optional(),
    text: z.string().min(1),
});
const UpdateAgentInstructionRequest = CreateAgentInstructionRequest.partial();
const FindAgentInstructionQuery = PaginationQuery.merge(SearchQuery).merge(IncludeArchivedQuery);

const AgentSkill = z.object({
    id: z.string(),
    workspaceId: z.string(),
    name: z.string(),
    key: z.string(),
    description: z.string().min(1),
    body: z.string().min(1),
    sourceUrl: z.url().optional(),
    revision: z.string().optional(),
    archivedAt: z.iso.datetime().optional(),
    createdAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime().optional(),
});
const CreateAgentSkillRequest = z.object({
    name: z.string().min(1),
    key: z.string().min(1).optional(),
    description: z.string().min(1),
    body: z.string().min(1),
    sourceUrl: z.url().optional(),
});
const UpdateAgentSkillRequest = CreateAgentSkillRequest.partial();
const PreviewAgentSkillSourceRequest = z.object({
    sourceUrl: z.url(),
});
const PreviewAgentSkillSourceResponse = z.object({
    name: z.string().min(1),
    key: z.string().min(1).optional(),
    description: z.string().min(1),
    body: z.string().min(1),
    sourceUrl: z.url(),
});
const FindAgentSkillQuery = PaginationQuery.merge(SearchQuery).merge(IncludeArchivedQuery);

const AgentTool = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().min(1),
    enabledByDefault: z.boolean().default(false),
});

const AgentHarnessReferenceList = z.array(z.string());
const AgentHarnessReferenceListWithDefault = AgentHarnessReferenceList.default([]);
const AgentHarness = z.object({
    id: z.string(),
    workspaceId: z.string(),
    name: z.string(),
    key: z.string(),
    description: z.string().optional(),
    systemPrompt: z.string().optional(),
    defaultModel: AIGatewaySupportedModelIdSchema,
    toolIds: AgentHarnessReferenceListWithDefault,
    skillIds: AgentHarnessReferenceListWithDefault,
    instructionIds: AgentHarnessReferenceListWithDefault,
    tools: z.array(AgentTool).default([]),
    skills: z.array(AgentSkill).default([]),
    instructions: z.array(AgentInstruction).default([]),
    revision: z.string().optional(),
    archivedAt: z.iso.datetime().optional(),
    createdAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime().optional(),
});
const CreateAgentHarnessRequest = z.object({
    name: z.string().min(1),
    key: z.string().min(1).optional(),
    description: z.string().optional(),
    systemPrompt: z.string().optional(),
    defaultModel: AIGatewaySupportedModelIdSchema,
    toolIds: AgentHarnessReferenceList.optional(),
    skillIds: AgentHarnessReferenceList.optional(),
    instructionIds: AgentHarnessReferenceList.optional(),
});
const UpdateAgentHarnessRequest = CreateAgentHarnessRequest.partial();
const FindAgentHarnessQuery = PaginationQuery.merge(SearchQuery).merge(IncludeArchivedQuery);

const AgentHarnessSnapshotReferenceList = z.array(z.string()).default([]);
const AgentHarnessSnapshot = z.object({
    id: z.string(),
    workspaceId: z.string(),
    agentHarnessId: z.string(),
    agentHarnessRevision: z.string().optional(),
    name: z.string(),
    key: z.string(),
    description: z.string().optional(),
    systemPrompt: z.string().default(''),
    defaultModel: AIGatewaySupportedModelIdSchema,
    hash: z.string().optional(),
    toolIds: AgentHarnessSnapshotReferenceList,
    tools: z.array(AgentTool).default([]),
    skillIds: AgentHarnessSnapshotReferenceList,
    skills: z.array(AgentSkill).default([]),
    instructionIds: AgentHarnessSnapshotReferenceList,
    instructions: z.array(AgentInstruction).default([]),
    createdAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime().optional(),
});
const FindAgentHarnessSnapshotQuery = PaginationQuery.extend({
    agentHarnessId: z.string().optional(),
});

function formatSessionCost(cost) {
    if (cost === undefined || cost === null)
        return '-';
    return cost.toFixed(2);
}

const SessionCredentials = z.object({
    accessKeyId: z.string().min(1),
    secretAccessKey: z.string().min(1),
    sessionToken: z.string().min(1),
});
const SessionParameters = z.object({
    id: z.string().optional(),
    leaseId: z.string().uuid().optional(),
    accessToken: z.string().optional(),
    credentials: SessionCredentials.optional(),
    apiUri: z.string().optional(),
    prompt: z.string().optional(),
    elementType: z.enum(WorkspaceElementType).optional(),
    elementId: z.string().optional(),
    customerId: z.string().optional(),
    opencodeSessionUuid: z.string().optional(),
    sessionFilesZipUri: z.string().optional(),
    openRouterApiKey: z.string().optional(),
    modelId: z.string().optional(),
    agentName: z.enum(AgentName).optional(),
    agentHarnessSnapshotId: z.string().optional(),
    engine: z.enum(['opencode', 'inline']).optional(),
    attachments: z.array(AgentSessionAttachment).optional(),
});

const AGENT_WORKSPACE_ELEMENT_CONTEXT_TYPES = [
    WorkspaceElementType.Integration,
    WorkspaceElementType.Connection,
    WorkspaceElementType.Action,
    WorkspaceElementType.Flow,
    WorkspaceElementType.DataSource,
    WorkspaceElementType.FieldMapping,
    WorkspaceElementType.DataLinkTable,
    WorkspaceElementType.AppDataSchema,
    WorkspaceElementType.AppEventType,
    WorkspaceElementType.ExternalEventType,
];
const AGENT_WORKSPACE_ELEMENT_CONTEXT_TYPE_SET = new Set(AGENT_WORKSPACE_ELEMENT_CONTEXT_TYPES);
function canFetchAgentWorkspaceElementContext(elementType) {
    return !!elementType && AGENT_WORKSPACE_ELEMENT_CONTEXT_TYPE_SET.has(elementType);
}

var AsyncRequestStatus;
(function (AsyncRequestStatus) {
    AsyncRequestStatus["PENDING"] = "pending";
    AsyncRequestStatus["ACTIVE"] = "active";
    AsyncRequestStatus["COMPLETED"] = "completed";
    AsyncRequestStatus["FAILED"] = "failed";
    AsyncRequestStatus["CANCELLED"] = "cancelled";
})(AsyncRequestStatus || (AsyncRequestStatus = {}));
const AsyncRequestTriggerResponse = z.object({
    jobId: z.string(),
    accessKey: z.string(),
});
const ListableAsyncRequestType = z.enum(['export-workspace', 'import-workspace']);
const AsyncRequestStatusSchema = z.nativeEnum(AsyncRequestStatus);
const AsyncRequest = z.object({
    id: z.string(),
    requestType: ListableAsyncRequestType,
    status: AsyncRequestStatusSchema,
    progress: z.number().optional(),
    result: z.record(z.string(), z.any()).optional(),
    error: ErrorDataSchema.optional(),
    meta: z.record(z.string(), z.any()).optional(),
    createdAt: zodDateCoercion(),
    startedAt: zodDateCoercion().optional(),
    completedAt: zodDateCoercion().optional(),
});
const ListAsyncRequestsQuery = z.object({
    requestType: ListableAsyncRequestType,
    status: AsyncRequestStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.coerce.number().int().min(0).optional(),
});
const ListAsyncRequestsResponse = z.object({
    items: z.array(AsyncRequest),
});

const SelfHostingToken = z.object({
    id: z.string(),
    name: z.string(),
    orgId: z.string(),
    tokenPrefix: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    lastUsedAt: z.coerce.date().optional(),
});
const CreateSelfHostingTokenRequest = z.object({
    name: z.string().min(1).max(100),
});
const CreateSelfHostingTokenResponse = SelfHostingToken.extend({
    token: z.string(),
});
const UpdateSelfHostingTokenRequest = z.object({
    name: z.string().min(1).max(100),
});
const ValidateSelfHostingTokenRequest = z.object({});
const ValidateSelfHostingTokenResponse = z.object({
    valid: z.boolean(),
    orgId: z.string().optional(),
    orgName: z.string().optional(),
});

const MEMBRANE_CLI_CLIENT_ID = 'membrane-cli';
const OAUTH_TOKEN_TYPE_PLATFORM_USER = 'platform-user';
const OAUTH_TOKEN_TYPE_TENANT = 'tenant';
const OAUTH_TOKEN_TYPE_CONNECTION = 'connection';
const OAUTH_TOKEN_TYPES = [
    OAUTH_TOKEN_TYPE_PLATFORM_USER,
    OAUTH_TOKEN_TYPE_TENANT,
    OAUTH_TOKEN_TYPE_CONNECTION,
];
const AuthContextSettings = z.object({
    defaultWorkspaceKey: z.string().optional(),
    defaultTenantKey: z.string().optional(),
    connectionId: z.string().optional(),
});
const PatchTokenSettingsRequest = z.object({
    settings: z
        .object({
        defaultWorkspaceKey: z.string().nullable().optional(),
        defaultTenantKey: z.string().nullable().optional(),
        connectionId: z.string().nullable().optional(),
    })
        .optional(),
});
const OAuthTokenResponse = z.object({
    access_token: z.string(),
    token_type: z.string(),
    expires_in: z.number(),
    refresh_token: z.string().optional(),
    default_workspace_key: z.string().optional(),
    default_tenant_key: z.string().optional(),
    email: z.string().optional(),
    org_id: z.string().optional(),
});

const PLATFORM_USER_TOKEN_GRANT_TYPES = ['cli-oauth'];
const PlatformUserTokenGrantType = z.enum(PLATFORM_USER_TOKEN_GRANT_TYPES);
const PlatformUserToken = z.object({
    id: z.string(),
    platformUserId: z.string(),
    grantType: PlatformUserTokenGrantType,
    grantId: z.string().optional(),
    grantName: z.string().optional(),
    settings: AuthContextSettings.optional(),
    createdAt: z.string(),
    lastUsedDate: z.string().optional(),
});
const PlatformUserTokenListResponse = z.object({
    items: z.array(PlatformUserToken),
});

const MCP_PROTOCOL_VERSION = '2025-11-25';
const McpConnectionInput = z.object({
    mcpServerUrl: z.string().url().optional(),
});
const ProtectedResourceMetadata = z.object({
    resource: z.string().url(),
    authorization_servers: z.array(z.string().url()).min(1),
    bearer_methods_supported: z.array(z.string()).optional(),
    scopes_supported: z.array(z.string()).optional(),
});
const AuthorizationServerMetadata = z
    .object({
    issuer: z.string().url(),
    authorization_endpoint: z.string().url(),
    token_endpoint: z.string().url(),
    registration_endpoint: z.string().url().optional(),
    scopes_supported: z.array(z.string()).optional(),
    response_types_supported: z.array(z.string()).optional(),
    grant_types_supported: z.array(z.string()).optional(),
    code_challenge_methods_supported: z.array(z.string()).optional(),
    token_endpoint_auth_methods_supported: z.array(z.string()).optional(),
    client_id_metadata_document_supported: z.boolean().optional(),
})
    .passthrough();
const DynamicClientRegistrationResponse = z
    .object({
    client_id: z.string(),
    client_secret: z.string().optional(),
    client_id_issued_at: z.number().optional(),
    client_secret_expires_at: z.number().optional(),
    registration_access_token: z.string().optional(),
    registration_client_uri: z.string().url().optional(),
})
    .passthrough();
const McpTokenResponse = z
    .object({
    access_token: z.string(),
    token_type: z.string(),
    expires_in: z.number().optional(),
    refresh_token: z.string().optional(),
    scope: z.string().optional(),
})
    .passthrough();
const McpTool = z
    .object({
    name: z.string(),
    description: z.string().optional(),
    inputSchema: z.record(z.string(), z.unknown()),
})
    .passthrough();
const McpSynthesizedAction = z.object({
    mcp: z.literal(true),
    id: z.string(),
    connectionId: z.string(),
    key: z.string(),
    name: z.string(),
    description: z.string().optional(),
    inputSchema: z.record(z.string(), z.unknown()).optional(),
    isReadOnly: z.literal(true),
});
const McpAuthState = z.object({
    asIssuer: z.string().url(),
    tokenEndpoint: z.string().url(),
    clientId: z.string(),
    clientSecret: z.string().optional(),
    canonicalResource: z.string(),
    scope: z.string().optional(),
});
const McpCredentials = z.object({
    accessToken: z.string(),
    refreshToken: z.string().optional(),
    expiresAt: z.coerce.date().optional(),
    tokenType: z.string().default('Bearer'),
});

const WORKSPACE_TOKEN_GRANT_TYPES = ['mcp-oauth', 'agent-session', 'api'];
const WorkspaceTokenGrantType = z.enum(WORKSPACE_TOKEN_GRANT_TYPES);
const WorkspaceToken = z.object({
    id: z.string(),
    workspaceId: z.string(),
    tenantId: z.string().optional(),
    platformUserId: z.string().optional(),
    grantType: WorkspaceTokenGrantType,
    grantId: z.string().optional(),
    grantName: z.string().optional(),
    connectedProductId: z.string().optional(),
    settings: AuthContextSettings.optional(),
    createdAt: z.string(),
    lastUsedDate: z.string().optional(),
});
const CreateWorkspaceTokenResponse = WorkspaceToken.extend({
    accessToken: z.string(),
});
const CreateWorkspaceTokenRequest = z.object({
    grantType: WorkspaceTokenGrantType,
    grantId: z.string(),
    grantName: z.string(),
    tenantId: z.string().optional(),
    clientKey: z.string().optional(),
});
const WorkspaceTokenListResponse = z.object({
    items: z.array(WorkspaceToken),
});

const CONSOLE_ACCOUNT_API_TOKEN_PATH = 'settings/account/api-token';

const PendingQueueCountSchema = z.object({
    pending: z.number().nullable().describe('Number of pending tasks, or null if the queue is unavailable.'),
    error: z.string().optional().describe('Error message if the queue count could not be retrieved.'),
});
const DbBackedCountsSchema = z.object({
    flowRuns: PendingQueueCountSchema.describe('Queued/running flow runs in MongoDB.'),
    eventPulls: PendingQueueCountSchema.describe('Queued/running external event pulls in MongoDB.'),
    eventLogs: PendingQueueCountSchema.describe('Created external event log records in MongoDB.'),
});
const PendingTasksSummarySchema = z.object({
    instant: PendingQueueCountSchema.optional().describe('Instant (MongoDB-backed) queue counts.'),
    queued: PendingQueueCountSchema.optional().describe('Slow (Redis-backed) queue counts.'),
    dbBacked: DbBackedCountsSchema.optional().describe('DB-backed job counts from MongoDB (source of truth).'),
    total: z.number().describe('Sum of available pending task counts. Excludes unavailable queues.'),
});

const HUB_INSTANCE_ID = 'hub';
function isHubInstanceId(instanceId) {
    return instanceId === HUB_INSTANCE_ID;
}
function isHubWorkspace(workspace) {
    return workspace.membraneInstanceId == null;
}
function isSelfHostedInstance(instance) {
    return !isHubInstanceId(instance.id);
}
const MembraneInstance = z.object({
    id: z.string(),
    name: z.string(),
    apiBaseUri: z.string(),
    consoleBaseUri: z.string().optional(),
    orgId: z.string().optional(),
    type: z.string().optional(),
    engineVersion: z.string().optional(),
    commitSHA: z.string().optional(),
    buildDate: z.string().optional(),
    cloudProvider: z.string().optional(),
    lastReportedAt: z.coerce.date().optional(),
    activeTenants: z.number().optional(),
    billableTenants: z.number().optional(),
});
const membraneInstanceInfraFields = {
    clusterName: z.string().optional(),
    argoCdUrl: z.url().optional(),
    grafanaUrl: z.url().optional(),
    mongoAtlasUrl: z.url().optional(),
    helmBranch: z.string().optional(),
    helmValuesPath: z.url().optional(),
    awsAccountName: z.string().optional(),
    awsSsoUrl: z.url().optional(),
    sentryConsoleProjectId: z.string().optional(),
    sentryApiProjectId: z.string().optional(),
};
const MembraneInstanceAdmin = MembraneInstance.extend({
    ...membraneInstanceInfraFields,
    orgName: z.string().optional(),
    workspaceCount: z.number().optional(),
});
const CreateMembraneInstanceRequest = z.object({
    name: z.string().min(1),
    apiBaseUri: z.string().min(1),
    consoleBaseUri: z.string().min(1).optional(),
    orgId: z.string().min(1).optional(),
});
const UpdateMembraneInstanceRequest = z
    .object({
    name: z.string().min(1).optional(),
    apiBaseUri: z.string().min(1).optional(),
    consoleBaseUri: z.string().min(1).nullable().optional(),
    orgId: z.string().min(1).nullable().optional(),
    clusterName: z.string().nullable().optional(),
    argoCdUrl: z.url().nullable().optional(),
    grafanaUrl: z.url().nullable().optional(),
    mongoAtlasUrl: z.url().nullable().optional(),
    helmBranch: z.string().nullable().optional(),
    helmValuesPath: z.url().nullable().optional(),
    awsAccountName: z.string().nullable().optional(),
    awsSsoUrl: z.url().nullable().optional(),
    sentryConsoleProjectId: z.string().nullable().optional(),
    sentryApiProjectId: z.string().nullable().optional(),
})
    .strict();
const ListMembraneInstancesQuery = PaginationQuery.extend({
    orgId: z.string().optional(),
});
const ListMembraneInstancesResponse = createPaginationResponseSchema(MembraneInstance);

const OrgInstanceType = z.enum(['membrane-hosted', 'self-hosted']);
const OrgInstanceWorkspace = z.object({
    workspaceId: z.string(),
    workspaceName: z.string(),
    activeTenants: z.number().optional(),
    billableTenants: z.number().optional(),
});
const OrgInstance = z.object({
    id: z.string(),
    name: z.string(),
    type: OrgInstanceType.optional(),
    apiBaseUri: z.string(),
    consoleBaseUri: z.string().optional(),
    engineVersion: z.string().optional(),
    commitSHA: z.string().optional(),
    buildDate: z.string().optional(),
    cloudProvider: z.string().optional(),
    lastReportedAt: z.string().optional(),
    activeTenants: z.number().optional(),
    billableTenants: z.number().optional(),
    workspaces: z.array(OrgInstanceWorkspace).optional(),
});

const API_VERSIONS = {
    LEGACY: 'legacy',
    '2025-10-21': '2025-10-21',
};
const AVAILABLE_VERSIONS = Object.values(API_VERSIONS);
const CHRONOLOGICAL_VERSIONS = [
    API_VERSIONS.LEGACY,
    ...[...AVAILABLE_VERSIONS].filter((v) => v !== API_VERSIONS.LEGACY).sort(),
];
const LATEST_API_VERSION = CHRONOLOGICAL_VERSIONS[CHRONOLOGICAL_VERSIONS.length - 1];
const VERSION_HEADER = 'membrane-api-version';

function decodeJWT(token) {
    if (typeof token !== 'string') {
        return undefined;
    }
    try {
        if (typeof window === 'undefined') {
            return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
        }
        else {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(window
                .atob(base64)
                .split('')
                .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
                .join(''));
            return JSON.parse(jsonPayload);
        }
    }
    catch (_e) {
        return undefined;
    }
}

const axiosGlobal = ((axiosOriginal === null || axiosOriginal === void 0 ? void 0 : axiosOriginal.default) || axiosOriginal);
const axios = axiosGlobal.create();
const DEFAULT_API_URI = 'https://api.getmembrane.com';
const DEFAULT_UI_URI = 'https://ui.getmembrane.com';
const membraneClientOptionsSchema = z
    .object({
    token: z.string().nullish(),
    fetchToken: z.function().nullish(),
    credentials: z.any().nullish(),
    fetchCredentials: z.function().nullish(),
    apiUri: z.string().nullish(),
    uiUri: z.string().nullish(),
    accessToken: z.string().nullish(),
    workspaceKey: z.string().nullish(),
    workspaceSecret: z.string().nullish(),
    tenantKey: z.string().nullish(),
})
    .strict();
function validateClientOptions(options) {
    try {
        membraneClientOptionsSchema.parse(options);
    }
    catch (error) {
        if (error instanceof z.ZodError) {
            throw fromZodError(error);
        }
        throw error;
    }
}
const REFETCH_EXPIRATION_THRESHOLD = 10 * 1000;
const TOKEN_TTL_SECONDS = 3600;
const TOKEN_MINT_THRESHOLD_SECONDS = 60;
function decodeToken(token) {
    if (token.startsWith('pat-')) {
        return null;
    }
    const payload = decodeJWT(token);
    if (!payload) {
        throw new Error('Access token should be a valid JWT token.');
    }
    return payload;
}
class MembraneApiClient {
    constructor(options = {}) {
        this.logs = [];
        validateClientOptions(options);
        const { uiUri, apiUri, fetchCredentials, credentials } = options;
        this.apiUri = apiUri !== null && apiUri !== void 0 ? apiUri : DEFAULT_API_URI;
        this.uiUri = uiUri !== null && uiUri !== void 0 ? uiUri : DEFAULT_UI_URI;
        if ('workspaceSecret' in options && options.workspaceSecret) {
            if (typeof window !== 'undefined') {
                throw new Error('MembraneClient: workspaceSecret must not be used in browser code. ' +
                    'The workspace secret is a server-side credential that would be exposed to end users if included in a browser bundle.');
            }
            this._workspaceKey = options.workspaceKey;
            this._workspaceSecret = options.workspaceSecret;
            this._tenantKey = options.tenantKey;
        }
        else {
            const token = 'token' in options ? options.token : undefined;
            const accessToken = 'accessToken' in options ? options.accessToken : undefined;
            const fetchToken = 'fetchToken' in options ? options.fetchToken : undefined;
            this.token = token !== null && token !== void 0 ? token : accessToken;
            this.fetchToken = fetchToken;
        }
        if (fetchCredentials) {
            void fetchCredentials().then((c) => this.setCredentials(c));
        }
        else if (credentials) {
            void this.setCredentials(credentials);
        }
    }
    async setCredentials(credentials) {
        return this.patch('/self', { credentials });
    }
    async getToken() {
        if (this._workspaceSecret) {
            return this.mintToken();
        }
        if (this.fetchToken) {
            if (!this.token) {
                this.token = await this.fetchToken();
            }
            else {
                const payload = decodeToken(this.token);
                if (payload === null || payload === void 0 ? void 0 : payload.exp) {
                    const willExpireSoon = new Date(payload.exp * 1000).getTime() - new Date().getTime() < REFETCH_EXPIRATION_THRESHOLD;
                    if (willExpireSoon) {
                        this.token = await this.fetchToken();
                    }
                }
            }
        }
        if (!this.token) {
            throw new Error('Access token is not provided.');
        }
        decodeToken(this.token);
        return this.token;
    }
    async mintToken() {
        if (this.token) {
            const payload = decodeToken(this.token);
            if (payload === null || payload === void 0 ? void 0 : payload.exp) {
                const remainingSeconds = payload.exp - Math.floor(Date.now() / 1000);
                if (remainingSeconds > TOKEN_MINT_THRESHOLD_SECONDS) {
                    return this.token;
                }
            }
        }
        const payload = {
            id: this._tenantKey,
            iss: this._workspaceKey,
            exp: Math.floor(Date.now() / 1000) + TOKEN_TTL_SECONDS,
        };
        const header = { alg: 'HS256', typ: 'JWT' };
        const encoder = new TextEncoder();
        const toB64Url = (bytes) => {
            let s = '';
            for (let i = 0; i < bytes.length; i++)
                s += String.fromCharCode(bytes[i]);
            return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        };
        const headerB64 = toB64Url(encoder.encode(JSON.stringify(header)));
        const payloadB64 = toB64Url(encoder.encode(JSON.stringify(payload)));
        const input = `${headerB64}.${payloadB64}`;
        const key = await crypto.subtle.importKey('raw', encoder.encode(this._workspaceSecret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
        const signatureBuffer = await crypto.subtle.sign('HMAC', key, encoder.encode(input));
        const signature = toB64Url(new Uint8Array(signatureBuffer));
        this.token = `${input}.${signature}`;
        return this.token;
    }
    async get(uri, queryParams, options) {
        if (queryParams) {
            uri += `${uri.includes('?') ? '&' : '?'}${new URLSearchParams(omitBy(queryParams, isEmptyValue)).toString()}`;
        }
        return this.makeApiRequest('GET', { ...(options !== null && options !== void 0 ? options : {}), url: uri });
    }
    async post(uri, data, options) {
        return this.makeApiRequest('POST', { ...(options !== null && options !== void 0 ? options : {}), url: uri, data });
    }
    async put(uri, data, options) {
        return this.makeApiRequest('PUT', { ...(options !== null && options !== void 0 ? options : {}), url: uri, data });
    }
    async patch(uri, data, options) {
        return this.makeApiRequest('PATCH', { ...(options !== null && options !== void 0 ? options : {}), url: uri, data });
    }
    async delete(uri, data, options) {
        return this.makeApiRequest('DELETE', { ...(options !== null && options !== void 0 ? options : {}), url: uri, data });
    }
    retrieveLogs() {
        const logs = [...this.logs];
        this.logs = [];
        return logs;
    }
    async createEventSource(uri, queryParams) {
        const token = await this.getToken();
        const allParams = { ...queryParams, token };
        const queryString = qs.stringify(omitBy(allParams, isEmptyValue));
        const urlWithToken = `${this.apiUri}/${uri}?${queryString}`;
        return new EventSource(urlWithToken);
    }
    async getEmbedUri(page, pageParams, options) {
        var _a;
        const paramsWithToken = {
            token: await this.getToken(),
            ...(pageParams !== null && pageParams !== void 0 ? pageParams : {}),
        };
        const queryParams = qs.stringify(paramsWithToken);
        const baseUriWithoutSlash = (_a = this.uiUri) === null || _a === void 0 ? void 0 : _a.replace(/\/$/, '');
        const pageWithoutSlash = page.replace(/^\//, '');
        const mountingPath = (options === null || options === void 0 ? void 0 : options.embedMode) === 'isolated-embed' ? 'isolated-embed' : 'embed';
        return `${baseUriWithoutSlash}/${mountingPath}/${pageWithoutSlash}${queryParams ? `?${queryParams}` : ''}`;
    }
    async getScreensEmbedUri(page, pageParams) {
        var _a;
        const paramsWithToken = {
            token: await this.getToken(),
            ...(pageParams !== null && pageParams !== void 0 ? pageParams : {}),
        };
        const queryParams = qs.stringify(paramsWithToken);
        const baseUriWithoutSlash = (_a = this.apiUri) === null || _a === void 0 ? void 0 : _a.replace(/\/$/, '');
        const pageWithoutSlash = page.replace(/^\//, '');
        return `${baseUriWithoutSlash}/screens/${pageWithoutSlash}${queryParams ? `?${queryParams}` : ''}`;
    }
    async getScreensPostData(page, pageParams) {
        var _a;
        const baseUriWithoutSlash = (_a = this.apiUri) === null || _a === void 0 ? void 0 : _a.replace(/\/$/, '');
        const pageWithoutSlash = page.replace(/^\//, '');
        return {
            url: `${baseUriWithoutSlash}/screens/${pageWithoutSlash}`,
            postData: {
                token: await this.getToken(),
                ...pageParams,
            },
        };
    }
    async makeApiRequest(method, params = {}) {
        var _a, _b, _c, _d;
        const token = await this.getToken();
        const url = (_a = params.url) !== null && _a !== void 0 ? _a : '';
        const requestWithoutCredentials = {
            method,
            url: `${this.apiUri}${url.startsWith('/') ? '' : '/'}${url}`,
            data: isStream(params.data) ? '<stream>' : truncateData(params.data),
        };
        const log = {
            type: LogRecordType.API,
            data: {
                request: requestWithoutCredentials,
            },
        };
        const startTime = new Date();
        params.method = method;
        params.baseURL = this.apiUri;
        params.headers = {
            ...(params.headers || {}),
            Authorization: `Bearer ${token}`,
            [VERSION_HEADER]: LATEST_API_VERSION,
        };
        params.withCredentials = false;
        let response;
        try {
            response = await axios.request(params);
        }
        catch (err) {
            log.data.timeMs = new Date().getTime() - startTime.getTime();
            if (err.response) {
                log.data.response = {
                    status: (_b = err.response) === null || _b === void 0 ? void 0 : _b.status,
                    data: isStream((_c = err.response) === null || _c === void 0 ? void 0 : _c.data) ? '<stream>' : truncateData((_d = err.response) === null || _d === void 0 ? void 0 : _d.data),
                };
            }
            else {
                log.data.error = {
                    type: typeof err,
                    error: JSON.stringify(err, Object.getOwnPropertyNames(err)),
                };
            }
            this.log(log);
            this.handleRequestError(err);
        }
        log.data.response = {
            status: response.status,
            data: isStream(response.data) ? '<stream>' : truncateData(response.data),
        };
        log.data.timeMs = new Date().getTime() - startTime.getTime();
        this.log(log);
        return response.data;
    }
    log(logRecord) {
        var _a;
        this.logs.push(logRecord);
        if (typeof process !== 'undefined' && ((_a = process.env) === null || _a === void 0 ? void 0 : _a.IS_CUSTOM_CODE_RUNNER)) {
            console.debug(JSON.stringify(logRecord));
        }
    }
    handleRequestError(err) {
        var _a, _b;
        if ((err === null || err === void 0 ? void 0 : err.isAxiosError) && ((_b = (_a = err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.type)) {
            const error = getErrorFromData(err.response.data);
            if (error)
                throw error;
        }
        throw err;
    }
}

class UI {
    constructor(client) {
        this.client = client;
    }
    async connect(options = {}) {
        const { url, postData } = await this.client.getScreensPostData('connect', {
            ...options,
            allowMultipleConnections: options.allowMultipleConnections ? '1' : '',
            connectorParameters: options.connectorParameters ? JSON.stringify(options.connectorParameters) : undefined,
        });
        return new Promise((resolve) => {
            return openIframeWithPost(url, postData, {
                onClose: () => resolve(null),
                onSuccess: (connection) => resolve(connection),
            });
        });
    }
    async connection(options) {
        const { url, postData } = await this.client.getScreensPostData('connection', {
            connectionId: options.connectionId,
            theme: options.theme,
        });
        return new Promise((resolve) => {
            return openIframeWithPost(url, postData, {
                onClose: () => resolve(null),
                onSuccess: (connection) => resolve(connection),
            });
        });
    }
    async UNSAFE_agentSession(options) {
        const { url, postData } = await this.client.getScreensPostData('agent-session', {
            sessionId: options.sessionId,
            theme: options.theme,
        });
        return new Promise((resolve) => {
            return openIframeWithPost(url, postData, {
                onClose: () => resolve(),
            });
        });
    }
}

class MembraneClient extends MembraneApiClient {
    constructor(options) {
        super(options);
    }
    get self() {
        return new SelfAccessor(this);
    }
    get flows() {
        return new FlowsAccessor(this);
    }
    async open({ onClose = () => { } } = {}) {
        const uri = await this.getEmbedUri('main', {});
        return openIframe(uri, {
            onClose,
        });
    }
    action(selector) {
        return new ActionAccessor(this, selector);
    }
    get actions() {
        return new ActionsAccessor(this);
    }
    screen(selector) {
        return new ScreenAccessor(this, selector);
    }
    get screens() {
        return new ScreensAccessor(this);
    }
    actionInstance(selector) {
        return new ActionInstanceAccessor(this, selector);
    }
    get actionInstances() {
        return new ActionInstancesAccessor(this);
    }
    flow(selector) {
        return new FlowAccessor(this, selector);
    }
    get flowInstances() {
        return new FlowInstancesAccessor(this);
    }
    flowInstance(selector) {
        return new FlowInstanceAccessor(this, selector);
    }
    get dataSourceInstances() {
        return new DataSourceInstancesAccessor(this);
    }
    get dataSources() {
        return new DataSourcesAccessor(this);
    }
    dataSource(selector) {
        return new DataSourceAccessor(this, selector);
    }
    dataSourceInstance(selector) {
        return new DataSourceInstanceAccessor(this, selector);
    }
    get fieldMappings() {
        return new FieldMappingsAccessor(this);
    }
    fieldMapping(selector) {
        return new FieldMappingAccessor(this, selector);
    }
    get fieldMappingInstances() {
        return new FieldMappingInstancesAccessor(this);
    }
    fieldMappingInstance(selector) {
        return new FieldMappingInstanceAccessor(this, selector);
    }
    get dataLinkTables() {
        return new DataLinkTablesAccessor(this);
    }
    dataLinkTable(idOrKey) {
        return new DataLinkTableAccessor(this, idOrKey);
    }
    get dataLinkTableInstances() {
        return new DataLinkTableInstancesAccessor(this);
    }
    dataLinkTableInstance(selector) {
        return new DataLinkTableInstanceAccessor(this, selector);
    }
    get integrations() {
        return new IntegrationsAccessor(this);
    }
    externalEventSubscriptions() {
        return new ExternalEventSubscriptionsAccessor(this);
    }
    externalEventSubscription(id) {
        return new ExternalEventSubscriptionAccessor(this, id);
    }
    integration(identifier) {
        return new IntegrationAccessor(this, identifier);
    }
    get connections() {
        return new ConnectionsAccessor(this);
    }
    connection(connectionSelector) {
        return new ConnectionAccessor(this, connectionSelector);
    }
    get ui() {
        return new UI(this);
    }
    async connect(options) {
        return createOrUpdateConnection({
            ...options,
            client: this,
            apiUri: this.apiUri,
            redirectUri: options.sameWindow ? options.redirectUri : undefined,
        });
    }
    get appEventTypes() {
        return new AppEventTypesAccessor(this);
    }
    appEventType(id) {
        return new AppEventTypeAccessor(this, id);
    }
    get appEventSubscriptions() {
        return new AppEventSubscriptionsAccessor(this);
    }
    appEventSubscription(selector) {
        return new AppEventSubscriptionAccessor(this, selector);
    }
    get flowRuns() {
        return new FlowRunsAccessor(this);
    }
    flowRun(id) {
        return new FlowRunAccessor(this, id);
    }
    get appDataSchemas() {
        return new AppDataSchemasAccessor(this);
    }
    appDataSchema(id) {
        return new AppDataSchemaAccessor(this, id);
    }
    get appDataSchemaInstances() {
        return new AppDataSchemaInstancesAccessor(this);
    }
    appDataSchemaInstance(selector) {
        return new AppDataSchemaInstanceAccessor(this, selector);
    }
    customer(selector) {
        return new CustomerAccessor(this, selector);
    }
    get customers() {
        return new CustomersAccessor(this);
    }
    package(selector) {
        return new PackageAccessor(this, selector);
    }
    get packages() {
        return new PackagesAccessor(this);
    }
    async createEventSource(uri, queryParams) {
        return super.createEventSource(uri, queryParams);
    }
    async connectionRequest(connectionId, uri, data) {
        return this.post(`connections/${connectionId}/execute-request`, {
            key: uri,
            payload: data,
        });
    }
}

injectFormulaCatalog(getFormula, isFormula, hasFormulas);

export { ACTIONS, AGENTIC_CONNECTION_REQUEST_SCREEN_PATH, AGENT_WORKSPACE_ELEMENT_CONTEXT_TYPES, AIGatewaySupportedModelIdSchema, AI_GATEWAY_PROVIDER_MODELS, AI_GATEWAY_SUPPORTED_MODELS, AI_GATEWAY_SUPPORTED_MODEL_IDS, ALERT_DELIVERY_METHODS, ALERT_TYPE_CATEGORIES, AccessDeniedError, AccountResponse, ActApiDispatch, ActRequest, ActionAccessor, ActionApiPayload, ActionApiResponse, ActionDependency, ActionDependencyType, ActionEditableProperties, ActionExportProperties, ActionInstanceAccessor, ActionInstanceApiResponse, ActionInstanceSetupError, ActionInstancesAccessor, ActionRunError, ActionRunLogRecordApiResponse, ActionRunLogStatus, ActionRunResponse, ActionRunsStatsQuery, ActionSnapshot, ActionType, ActionsAccessor, ActivityLogRecord, ActivityStatsQuery, AgentHarness, AgentHarnessSnapshot, AgentInstruction, AgentName, AgentSession, AgentSessionAttachment, AgentSessionInputSchema, AgentSessionState, AgentSessionStatus, AgentSkill, AgentTool, AiAgentAudience, AiAgentParameters, AiAgentType, Alert, AlertCategory, AlertDeliverySettingsSchema, AlertSchema, AlertSeverity, AlertStatus, AlertType, AlertTypeDeliverySettingsSchema, And, ApiRequestSpec, AppDataSchemaAccessor, AppDataSchemaApiResponse, AppDataSchemaEditableProperties, AppDataSchemaExportProperties, AppDataSchemaInstanceAccessor, AppDataSchemaInstanceApiResponse, AppDataSchemaInstancesAccessor, AppDataSchemasAccessor, AppEventLogRecordApiResponse, AppEventSubscriptionAccessor, AppEventSubscriptionApiResponse, AppEventSubscriptionsAccessor, AppEventTypeAccessor, AppEventTypeApiResponse, AppEventTypeEditableProperties, AppEventTypeExportProperties, AppEventTypesAccessor, AppSchema, AppliedToIntegrations, AsyncRequest, AsyncRequestStatus, AsyncRequestStatusSchema, AsyncRequestTriggerResponse, AuthContextPermissionsSchema, AuthContextSettings, AuthorizationServerMetadata, BackwardCompatibleDataSourceEditableProperties, BadRequestError, BadRequestErrorKey, BaseAction, BaseActionInstance, BaseActionRunLogRecord, BaseAppDataSchema, BaseAppDataSchemaInstance, BaseAppEventSubscription, BaseAppEventType, BaseConnectedProduct, BaseConnection, BaseConnectionAttemptLog, BaseConnector, BaseCustomer, BaseDataLinkTable, BaseDataLinkTableInstance, BaseDataSource, BaseDataSourceInstance, BaseExportProperties, BaseExternalEvent, BaseExternalEventLogRecord, BaseExternalEventPull, BaseExternalEventSubscription, BaseExternalEventType, BaseFieldMapping, BaseFieldMappingInstance, BaseFlow, BaseFlowInstance, BaseFlowRun, BaseFunctionDefinition, BaseIntegration, BaseIntegrationLevelMembraneInterface, BaseIntegrationLevelMembraneInterfaceEditableProperties, BaseIntegrationLevelMembraneInterfaceExportProperties, BaseIntegrationLevelMembraneInterfaceReadOnlyProperties, BaseMembraneInterface, BaseMembraneInterfaceEditableProperties, BaseMembraneInterfaceReadOnlyProperties, BaseOrgUser, BasePackage, BaseScreen, BaseWorkspaceElement, CONNECTION_REQUEST_ID_PARAM, CONNECTION_REQUEST_SCREEN_PATH, CONNECTOR_AUTH_TYPES, CONNECTOR_CATEGORIES, CONNECTOR_DATA_DIR, CONNECTOR_DOCS_DIR, CONNECTOR_EVENTS_DIR, CONNECTOR_GLOBAL_WEBHOOKS_DIR, CONNECTOR_METHOD_IMPLEMENTATION_SUFFIXES, CONNECTOR_OPERATIONS_DIR, CONNECTOR_VERSION_DEVELOPMENT, CONNECTOR_VERSION_LATEST, CONSOLE_ACCOUNT_API_TOKEN_PATH, CheckpointApiResponse, CheckpointDiffOperationSchema, CheckpointDiffResponseSchema, CommonFindElementsQuery, CommonFindInstancesQuery, CommonInstancesListQuery, CommonIntegrationOrConnectionQuery, CommonListElementsQuery, ConcurrencyError, ConcurrencyErrorKey, ConfigurationError, ConfigurationState, ConnectPayload, ConnectUIOptions, ConnectedProductApiResponse, ConnectedProductAudience, ConnectedProductType, ConnectionAccessor, ConnectionApiResponse, ConnectionApiResponseWithSecrets, ConnectionAttemptLogApiResponse, ConnectionClientAction, ConnectionDataCollectionAccessor, ConnectionEditableProperties, ConnectionEntityMessagePayload, ConnectionError, ConnectionErrorKey, ConnectionExportProperties, ConnectionLevelActionAccessor, ConnectionLevelActionsAccessor, ConnectionLevelDataSourceAccessor, ConnectionLevelDataSourcesAccessor, ConnectionLevelFieldMappingAccessor, ConnectionLevelFieldMappingsAccessor, ConnectionLevelFlowAccessor, ConnectionLevelFlowsAccessor, ConnectionMessagePayload, ConnectionOperationAccessor, ConnectionOperationRunInput, ConnectionOperationRunResponse, ConnectionProxy, ConnectionProxyRequest, ConnectionRequest, ConnectionSelector, ConnectionSpec, ConnectionTestResponse, ConnectionType, ConnectionsAccessor, Connector, ConnectorAuth, ConnectorAuthClientCredentials, ConnectorAuthHandlerBase, ConnectorAuthIntegrationAppToken, ConnectorAuthMembraneToken, ConnectorAuthMethodTypes, ConnectorAuthOAuth1, ConnectorAuthOAuth2, ConnectorAuthProxy, ConnectorAuthSpec, ConnectorAuthSuccessRecord, ConnectorAuthWithFunctions, ConnectorDataCollectionEventImplementationType, ConnectorDataCollectionMethodKeys, ConnectorDataLocationTypes, ConnectorEventHandlerMethods, ConnectorEventImplementationType, ConnectorExportProperties, ConnectorFileUpdateType, ConnectorFunctionSpecs, ConnectorMethodImplementation, ConnectorMethodImplementationBase, ConnectorMethodImplementationGraphqlApiMapping, ConnectorMethodImplementationJavascript, ConnectorMethodImplementationMapping, ConnectorMethodImplementationNotSupported, ConnectorMethodImplementationOperationMapping, ConnectorMethodImplementationRestApiMapping, ConnectorMethodImplementationType, ConnectorOperationMethodImplementationTypes, ConnectorOption, ConnectorOptions, ConnectorSpec, ConnectorStatus, ConnectorStatusValues, ConnectorUdmCollectionMapping, ConnectorUdmListItem, ConnectorUdmSpec, ConnectorUiSpec, ConnectorVersion, ConnectorVersionData, ConnectorVersionExportProperties, CreateActionInstanceRequest, CreateActionRequest, CreateAgentHarnessRequest, CreateAgentInstructionRequest, CreateAgentSession, CreateAgentSkillRequest, CreateAlert, CreateConnectedProductRequest, CreateConnectionRequest, CreateConnectionRequestPayload, CreateConnectorRequest, CreateCustomerRequest, CreateDataLinkTableRequest, CreateDataSourceInstanceRequest, CreateDataSourceRequest, CreateExternalAppRequest, CreateExternalEventSubscriptionRequest, CreateExternalEventTypeRequest, CreateFieldMappingInstanceRequest, CreateFieldMappingRequest, CreateFlowNodeRequest, CreateFlowRequest, CreateFlowRunRequest, CreateIntegrationRequest, CreateMembraneInstanceRequest, CreateOrgInvitationRequest, CreateOrgRequest, CreateOrgUserRequest, CreateOrgWorkspaceRequest, CreatePackageRequest, CreateScreenRequest, CreateSelfHostingTokenRequest, CreateSelfHostingTokenResponse, CreateWorkspaceRequest, CreateWorkspaceTokenRequest, CreateWorkspaceTokenResponse, CustomCodeError, CustomerAccessor, CustomerApiResponse, CustomerLimits, CustomerSelector, CustomersAccessor, DATA_RECORD_SCHEMA, DEFAULT_AI_GATEWAY_MODEL_ID, DEFAULT_ALERT_DELIVERY_SETTINGS, DEFAULT_ALERT_TYPE_DELIVERY_SETTINGS, DEFAULT_FULL_SYNC_INTERVAL_SECONDS, DEFAULT_PULL_UPDATES_INTERVAL_SECONDS, DataBuilderFormulaBase, DataBuilderFormulaCase, DataBuilderFormulaConcat, DataBuilderFormulaCopy, DataBuilderFormulaExtractDate, DataBuilderFormulaExtractTime, DataBuilderFormulaFindAppRecordId, DataBuilderFormulaFindExternalRecordId, DataBuilderFormulaFirstName, DataBuilderFormulaIterate, DataBuilderFormulaLastName, DataBuilderFormulaLookup, DataBuilderFormulaMap, DataBuilderFormulaPlain, DataBuilderFormulaRecord, DataBuilderFormulaTpl, DataBuilderFormulaType, DataBuilderFormulaVar, DataCollectionCreateRequest, DataCollectionCreateResponse, DataCollectionCreateSpec, DataCollectionDeleteRequest, DataCollectionDeleteResponse, DataCollectionDeleteSpec, DataCollectionEventType, DataCollectionEventTypeSpec, DataCollectionEventsSpec, DataCollectionFindByIdRequest, DataCollectionFindByIdResponse, DataCollectionFindByIdSpec, DataCollectionFindRequest, DataCollectionFindResponse, DataCollectionFindSpec, DataCollectionListRequest, DataCollectionListResponse, DataCollectionListResponseDrilldown, DataCollectionListSpec, DataCollectionMatchRequest, DataCollectionMatchResponse, DataCollectionMatchSpec, DataCollectionMethodRequest, DataCollectionMethodSpec, DataCollectionSearchRequest, DataCollectionSearchResponse, DataCollectionSearchSpec, DataCollectionSpec, DataCollectionUdmSpec, DataCollectionUdmsSpec, DataCollectionUpdateRequest, DataCollectionUpdateResponse, DataCollectionUpdateSpec, DataFilterCondition, DataForm, DataLink, DataLinkDirection, DataLinkTableAccessor, DataLinkTableApiResponse, DataLinkTableConfig, DataLinkTableEditableProperties, DataLinkTableExportProperties, DataLinkTableInstanceAccessor, DataLinkTableInstanceApiResponse, DataLinkTableInstancesAccessor, DataLinkTableLayer, DataLinkTablesAccessor, DataLocationMethodImplementationTypes, DataLocationPointer, DataLocationTypeCollection, DataLocatorStep, DataLocatorStepArrayItem, DataLocatorStepObjectProperty, DataLocatorStepType, DataRecordSchema, DataSchema, DataSchemaRef, DataSourceAccessor, DataSourceApiResponse, DataSourceEditableProperties, DataSourceExportProperties, DataSourceInstanceAccessor, DataSourceInstanceApiResponse, DataSourceInstancesAccessor, DataSourceUnitConfig, DataSourcesAccessor, DbBackedCountsSchema, DependencyError, DomainFromEmail, DownstreamFlowNodeRunSchema, DynamicClientRegistrationResponse, EDITABLE_LIMITS, EXTERNAL_EVENT_TYPE_FUNCTION_KEYS, ElementAccessor, ElementInstanceAccessor, ElementInstanceListAccessor, ElementListAccessor, ElementsExportFields, EngineCreditsProjectionResponse, EngineWorkspaceSettingsSchema, EnsureConnectionRequest, ErrorData, ErrorDataSchema, ErrorType, Eval, EventPullLimits, ExternalApiLogApiResponse, ExternalEvent, ExternalEventLogFlowTriggerSkipReason, ExternalEventLogFlowTriggerSkipReasonCode, ExternalEventLogRecordApiResponse, ExternalEventLogStatus, ExternalEventPullApiResponse, ExternalEventPullStatus, ExternalEventPullTelemetry, ExternalEventSubscriptionAccessor, ExternalEventSubscriptionApiResponse, ExternalEventSubscriptionConfig, ExternalEventSubscriptionStatus, ExternalEventSubscriptionType, ExternalEventSubscriptionsAccessor, ExternalEventType, ExternalEventTypeApiResponse, ExternalEventTypeEditableProperties, ExternalEventTypeExportProperties, ExternalEventTypeImplementationType, ExternalEventUnitConfig, FLOW_NODE_SPECS, FieldMappingAccessor, FieldMappingApiResponse, FieldMappingDirection, FieldMappingEditableProperties, FieldMappingExportProperties, FieldMappingInstanceAccessor, FieldMappingInstanceApiResponse, FieldMappingInstancesAccessor, FieldMappingUnitConfig, FieldMappingsAccessor, FindActionInstancesQuery, FindActionRunLogsQuery, FindActionRunLogsResponse, FindActionsQuery, FindAgentHarnessQuery, FindAgentHarnessSnapshotQuery, FindAgentInstructionQuery, FindAgentSkillQuery, FindAlertsQuery, FindAppDataSchemaInstancesQuery, FindAppDataSchemasQuery, FindAppEventSubscriptionsQuery, FindAppEventTypesQuery, FindAppEventsQuery, FindConnectedProductsQuery, FindConnectionAttemptLogsQuery, FindConnectionAttemptLogsResponse, FindConnectionsQuery, FindConnectionsResponse, FindCustomersQuery, FindDataLinkTableInstanceLinksQuery, FindDataLinkTableInstancesQuery, FindDataLinkTablesQuery, FindDataLinksInTableQuery, FindDataLinksQuery, FindDataLinksResponse, FindDataSourceEventsQuery, FindDataSourceInstanceSyncsQuery, FindDataSourceInstancesQuery, FindDataSourceSyncsQuery, FindDataSourcesQuery, FindExternalEventPullsQuery, FindExternalEventTypesQuery, FindFieldMappingInstancesQuery, FindFieldMappingsQuery, FindFlowInstancesQuery, FindFlowRunsQuery, FindFlowRunsResponse, FindFlowsQuery, FindIntegrationLevelMembraneInterfaceQuery, FindIntegrationsQuery, FindOrgWorkspacesQuery, FindOrgsQuery, FindPackagesQuery, FindScreensQuery, FirstNotEmpty, FlowAccessor, FlowApiResponse, FlowEditableProperties, FlowExportProperties, FlowInstanceAccessor, FlowInstanceApiResponse, FlowInstanceSetupError, FlowInstancesAccessor, FlowNode, FlowNodeLink, FlowNodeRunOutputMetadataSchema, FlowNodeRunOutputSchema, FlowNodeRunOutputWithoutDownstreamRunsSchema, FlowNodeRunParametersSchema, FlowNodeRunRecordSchema, FlowNodeRunRecordWithoutOutputsDataSchema, FlowNodeRunResultSchema, FlowNodeRunStatus, FlowNodeSpec, FlowNodeState, FlowNodeType, FlowRunAccessor, FlowRunApiResponse, FlowRunError, FlowRunLaunchedBy, FlowRunLaunchedByApi, FlowRunLaunchedByTrigger, FlowRunNode, FlowRunNodeState, FlowRunState, FlowRunsAccessor, FlowRunsStatsQuery, FlowsAccessor, Formula, FormulaFormula, FullOrgUser, FullPlatformUser, FunctionDefinition, FunctionType, GenerateOptionsRequest, GenerateOrgAccessTokenRequest, GenerateOrgAccessTokenResponse, GenerateWorkspaceAccessTokenRequest, GenerateWorkspaceAccessTokenResponse, GeneratedConnectorOption, GenericFunctionDefinition, GetWorkspaceQuery, GraphQLApiMappingSchema, GraphQLFieldMappingSchema, GraphqlApiMappingFunction, HTTP_REQUEST_SCHEMA, HUB_INSTANCE_ID, HandyScenarioTemplateElement, HttpRequestMethod, HttpRequestSpec, IncludeArchivedQuery, IncomingWebhookApiResponse, IncomingWebhooksState, InsufficientCreditsError, IntegrationAccessor, IntegrationApiResponse, MembraneClient as IntegrationAppClient, IntegrationAppError, IntegrationAuthOption, IntegrationAuthUi, IntegrationEditableProperties, IntegrationElementLevel, IntegrationElementType, IntegrationExportProperties, IntegrationLayerStatsQuery, IntegrationLevelActionAccessor, IntegrationLevelActionsListAccessor, IntegrationLevelDataSourceAccessor, IntegrationLevelDataSourcesListAccessor, IntegrationLevelFieldMappingAccessor, IntegrationLevelFieldMappingsListAccessor, IntegrationLevelFlowAccessor, IntegrationLevelFlowsListAccessor, IntegrationLevelMembraneInterfaceSelectorQuery, IntegrationListItem, IntegrationOptionConfig, IntegrationOptions, IntegrationSpecificElementSelector, IntegrationsAccessor, IntentProperties, InternalError, InvalidLocatorError, JavascriptFunction, JsonataFormula, LimitUnits, ListActionInstancesForConnectionQuery, ListAppEventLogRecordsQuery, ListAsyncRequestsQuery, ListAsyncRequestsResponse, ListConnectorsQuery, ListDataSourceInstancesForConnectionQuery, ListExternalApiLogsQuery, ListExternalAppsQuery, ListExternalEventLogRecordsQuery, ListExternalEventPullsQuery, ListExternalEventSubscriptionsQuery, ListFlowInstancesForConnectionQuery, ListIncomingWebhooksQuery, ListMembraneInstancesQuery, ListMembraneInstancesResponse, ListOrgWorkspacesQuery, ListOrgWorkspacesResponse, ListPublicConnectorsQuery, ListPublicPackagesQuery, ListableAsyncRequestType, LogRecordType, MCP_PROTOCOL_VERSION, MEMBRANE_CLI_CLIENT_ID, MEMBRANE_ELEMENT_CONFIG_FILE_NAME, MEMBRANE_SESSION_ID_HEADER, MIN_FULL_SYNC_INTERVAL_SECONDS, MIN_PULL_UPDATES_INTERVAL_SECONDS, MappingFunction, MappingSchema, McpAuthState, McpConnectionInput, McpCredentials, McpSynthesizedAction, McpTokenResponse, McpTool, MembraneAgentKey, axios as MembraneAxiosInstance, MembraneClient, MembraneCredentials, MembraneElementLayer, MembraneError, MembraneInstance, MembraneInstanceAdmin, MergeObjects, MinimalConnector, NotAuthenticatedError, NotFoundError, OAUTH1_CONFIG_SCHEMA, OAUTH_CONFIG_SCHEMA, OAUTH_TOKEN_TYPES, OAUTH_TOKEN_TYPE_CONNECTION, OAUTH_TOKEN_TYPE_PLATFORM_USER, OAUTH_TOKEN_TYPE_TENANT, OAuthTokenResponse, OpenRouterNotConfiguredError, OpenapiMappingSchema, OperationMappingFunction, OperationMappingSchema, Or, Org, OrgInstance, OrgInstanceType, OrgInstanceWorkspace, OrgInvitation, OrgLimits, OrgLimitsType, OrgPlan, OrgUserRole, OrgUserStatus, OrgWorkspace, OrgWorkspaceUpdateRequest, OrgWorkspaceUser, PACKAGE_VERSION_DEVELOPMENT, PACKAGE_VERSION_LATEST, PARALLEL_EXECUTION_LIMITS, PLATFORM_USER_TOKEN_GRANT_TYPES, PackageAccessor, PackageApiResponse, PackageCalculatedProperties, PackageEditableProperties, PackageElement, PackageElementApi, PackageElementExport, PackageExportProperties, PackageVersionData, PackageVersionListItem, PackagesAccessor, PaginationQuery, PaginationResponse, ParallelExecutionLimits, PatchAgentSessionSchema, PatchConnectionRequestPayload, PatchExternalEventSubscriptionRequest, PatchTokenSettingsRequest, PatchUserWorkspaceSettings, PendingQueueCountSchema, PendingTasksSummarySchema, PlatformUser, PlatformUserToken, PlatformUserTokenGrantType, PlatformUserTokenListResponse, PreviewAgentSkillSourceRequest, PreviewAgentSkillSourceResponse, ProtectedResourceMetadata, RATE_LIMITS, REFERENCE_ELEMENT_TYPE_SELF, RateLimitExceededError, RateLimits, RemoteConnectorCodeUnavailableError, RequestMappingSchema, ResetFlowInstanceOptions, RestApiMappingFunction, RestApiMappingSchema, RotateOrgWorkspaceSecretResponse, RunActionRequest, RunFieldMappingRequest, RunFieldMappingResponse, RunFlowApiRequest, SYSTEM_FIELDS, ScenarioAccessor, ScenarioTemplate, ScenarioTemplateCategory, ScenarioTemplateElements, ScenariosAccessor, ScreenAccessor, ScreenApiResponse, ScreenBlock, ScreenBlockType, ScreenSelector, ScreenType, ScreensAccessor, SearchQuery, SelfAccessor, SelfHostingToken, SessionCredentials, SessionParameters, StatsFilterQuery, TenantLayerElement, TenantSelfResponse, UDM, UNIFIED_DATA_MODELS, UnitRunError, UpdateActionInstanceRequest, UpdateActionRequest, UpdateAgentHarnessRequest, UpdateAgentInstructionRequest, UpdateAgentSkillRequest, UpdateConnectedProductRequest, UpdateConnectionRequest, UpdateConnectorRequest, UpdateCustomerRequest, UpdateDataLinkTableRequest, UpdateDataSourceInstanceRequest, UpdateDataSourceRequest, UpdateExternalAppRequest, UpdateExternalEventTypeRequest, UpdateFieldMappingInstanceRequest, UpdateFieldMappingRequest, UpdateFlowRequest, UpdateIntegrationRequest, UpdateMembraneInstanceRequest, UpdateOrgInvitationRequest, UpdateOrgRequest, UpdateOrgUserRequest, UpdatePackageRequest, UpdateScreenRequest, UpdateSelfHostingTokenRequest, UpdateWorkspaceLimitsRequest, UpdateWorkspaceRequest, UpstreamFlowNodeRunSchema, UsageType, UserAccessor, UserWorkspaceAccess, UserWorkspaceRole, UsersAccessor, ValidateSelfHostingTokenRequest, ValidateSelfHostingTokenResponse, WORKSPACE_DATABASE_RECORD_COLLECTIONS, WORKSPACE_ELEMENTS_STATS_COLLECTIONS, WORKSPACE_ELEMENT_COLLECTIONS, WORKSPACE_SIZE_LIMITS, WORKSPACE_TOKEN_GRANT_TYPES, WebhookTypeEnum, Workspace, WorkspaceAiCreditLimits, WorkspaceEditableFields, WorkspaceElementChangeType, WorkspaceElementDependencyType, WorkspaceElementSearchQuery, WorkspaceElementSearchResult, WorkspaceElementSpecs, WorkspaceElementState, WorkspaceElementType, WorkspaceElementsStatsSchema, WorkspaceEventType, WorkspaceLimitsSchema, WorkspaceNotificationType, WorkspaceOnboardingData, WorkspacePublicKey, WorkspaceSettingsSchema, WorkspaceSizeLimits, WorkspaceSyncEventType, WorkspaceToken, WorkspaceTokenGrantType, WorkspaceTokenListResponse, WorkspaceType, WritableConnectorVersionData, WritablePackageVersionData, WriteableConnectorFields, WriteableConnectorOption, __resolveValue, addRequiredFieldsToSchema, addUdmFallbackFields, addUndefinedWriteableProperties, backwardCompatibleFilterMatch, buildActionSnapshot, buildData, buildDataSchema, buildValue, canFetchAgentWorkspaceElementContext, compareElementMaps, compareWorkspaceExports, compressDataSchema, configToNamedPayload, createCompoundSchema, createFlowInstanceSchema, createObjectFromLocators, createOrUpdateConnection, createPaginationResponseSchema, createSchema, dataCollectionEventTypeToExternalEventType, dataLocationParametersMatch, doesMatchFilter, emptyComparisonResult, excludeFieldsFromSchema, excludeFieldsFromValue, excludeReadOnlyFieldsFromSchema, excludeWriteOnlyFieldsFromSchema, externalEventTypeToDataCollectionEventType, extractFieldLocator, extractMembraneErrorData, findUdmCollectionMapping, findUdmDefaultCollection, findUdmRootLocation, findValueLocators, formatSessionCost, generateExampleFromSchema, getActionInstanceVariableSchema, getActionRunTimeVariablesSchema, getAlertCategoryDisplayName, getAlertDeliveryMethodLabel, getAlertTypeDisplayName, getAlertTypesByCategory, getAllEventMethodFilePaths, getBusinessDaysBetween, getChildNodeKeys, getConnectionRequestUrl, getConnectorSpecPath, getConnectorVersionPath, getConnectorVersionSpecPath, getDataCollectionCreateFields, getDataCollectionUpdateFields, getDataLocationMethodPath, getDownstreamNodeKeys, getEditablePathsForElementType, getEditablePathsFromSchema, getEffectiveConnectorOption, getElementSelector, getErrorFromData, getEventMethodFileKey, getFilterFieldMeta, getFilterFieldValuesByLocator, getFlowInstanceNodeDependency, getFlowNode, getFlowNodeConfigTimeVariablesSchema, getFlowNodeDescription, getFlowNodeRunTimeVariablesSchema, getFlowNodeSpec, getFlowNodeTitle, getFormula$1 as getFormula, getFormulaLocators, getFormula as getFormula_internalDoNotUse, getFullNameForLocator, getFullTitleForLocator, getIconUriForLocator, getLocatorsFromData, getLocatorsFromSchema, getMembraneElementPath, getMissingRequiredFields, getNameComponentsForLocator, getNameForLocator, getNodeInputSchema, getOperatorsBySchema, getParentNodeKeys, getReferenceCollectionPathForSchema, getReferenceCollectionPointerForSchema, getRequiredFieldsFromSchema, getRootNodeKeys, getSchemaByLocator, getSchemaDescription, getSchemaFromValue, getUpstreamNodeKeys, getValueAtLocator, getValueByLocator, getVariableLocators, getWritableFieldsSchema, hasCycles, hasFormulas$1 as hasFormulas, hasFormulas as hasFormulas_internalDoNotUse, injectFormulaCatalog, isAIGatewaySupportedModelId, isBlob, isBusinessDay, isDataActionType, isDataLocationMethodSupported, isDeliveryMethodEnabled, isFormula$1 as isFormula, isFormula as isFormula_internalDoNotUse, isHubInstanceId, isHubWorkspace, isMembraneError, isObject, isPathUserEditable, isSameDataLocation, isSchemaEmpty, isSelfHostedInstance, isSensitiveKey, isStream, isValidAlertType, isValidLocator, jsonPointerToDotPath, lenientParseWithSchema, locatorToField, locatorToSteps, locatorToString, makeDataLocationOperationPath, makeDataLocationPath, makeDataRecordSchema, makeObjectPropertyLocator, makeSchemaForLocator, mergeComparisons, mergeSchemas, mergeWithFormulas, nonEmptyObjectProperties, parseDataLocationPath, parseDate, parseMembraneElementPath, patchSchema, pickFieldsFromSchema, pickFieldsFromValue, populateSchemaTitles, processCopy, redactAllValues, redactSecrets, removeNonExistentVars, removeRequiredFieldsFromSchema, resolveFormulas, schemaAllowsCustomValue, schemaHasFixedValues, schemaHasProperties, schemaIsNumber, schemaIsScalar, schemaTypeFromValue, schemaWithTitle, setEditablePropertiesForWorkspaceElement, setSchemaAtLocator, setValueAtLocator, stepsToLocator, streamToString, transformVariablesWith, transformVars, truncateData, unwrapSchema, unwrapSchemas, updateFlowInstanceSchema, updateImpliedSchema, validateFunctionDefinitions, valueToSchema, valueToString, walkSchema, wrapAnyOfSchema, zodBooleanCoercion, zodDateCoercion, zodIsoDatetime };
//# sourceMappingURL=index.browser.mjs.map
